/**
 * i18n message dictionaries for the DXF editor. Russian is default,
 * English secondary; RU copy follows AutoCAD's CIS terminology. Key
 * naming is dot-separated namespaces, e.g. "ribbon.tab.draw",
 * "prompt.line.first" — domain taxonomy for this convention lives in
 * `./messageDomains.ts` (re-exported below for import-path stability).
 * `{name}` placeholders are substituted by `t()` in `./index.ts`.
 */

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type LanguageCode,
} from "./messageDomains";

export { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type LanguageCode };

/** Russian dictionary — see `./messageDomains.ts` for the key-naming
 * convention. Add new keys to BOTH `ru` and `en`; `MessageKey` enforces
 * this at compile time. */
const ru = {
  // ── Ribbon tabs ──
  "ribbon.tab.file": "Файл",
  "ribbon.tab.draw": "Рисование",
  "ribbon.tab.annotate": "Аннотация",
  "ribbon.tab.modify": "Редактирование",
  "ribbon.tab.layout": "Лист",
  "ribbon.tab.propertyflows": "Свойства",
  // ── Ribbon section titles ──
  "ribbon.section.output": "Вывод",
  "ribbon.button.print": "Печать",
  "ribbon.section.drafting": "Черчение",
  "ribbon.section.blocks_arrays": "Блоки / Массивы",
  "ribbon.section.annotation": "Аннотации",
  "ribbon.section.dimensions": "Размеры",
  "ribbon.section.callouts": "Выноски",
  // ── Drawing commands (entity creation buttons) ──
  "command.line.label": "Отрезок",
  "command.point.label": "Точка",
  "command.circle.label": "Окружность",
  "command.arc.label": "Дуга",
  "command.ellipse.label": "Эллипс",
  "command.polyline.label": "Полилиния",
  "command.rectangle.label": "Прямоугольник",
  "command.polygon.label": "Многоугольник",
  "command.text.label": "Текст",
  "command.mtext.label": "Мтекст",
  "command.block.label": "Блок",
  "command.array.label": "Массив",
  "command.arraypolar.label": "Полярный массив",
  "command.arraypath.label": "Массив по пути",
  "command.arrayedit.label": "Редактирование массива",
  "command.hatch.label": "Штриховка",
  "command.hatchedit.label": "Редактирование штриховки",
  "command.dimension.label": "Линейный",
  "command.dimaligned.label": "Параллельный",
  "command.dimangular.label": "Угловой",
  "command.dim3point.label": "Угловой 3-точечный",
  "command.dimradius.label": "Радиус",
  "command.dimdiameter.label": "Диаметр",
  "command.dimordinate.label": "Ординатный",
  "command.mleader.label": "Мультивыноска",
  "command.mleaderedit.label": "МЛИНИЯРЕД (Редактировать выноску)",
  "ribbon.action.mleader_add_leader": "Добавить выноску",
  "ribbon.action.mleader_remove_leader": "Удалить выноску",
  "command.revcloud.label": "Облако правок",
  "command.revcloudproperties.label": "Свойства облака правок",
  "command.wipeout.label": "Вырез",
  "command.donut.label": "Кольцо",
  "command.spline.label": "Сплайн",
  // ── Drawing stage / canvas labels ──
  "stage.title": "Поле черчения",
  "stage.model_canvas": "Модельное пространство",
  "stage.layout_canvas": "{name}",
  "stage.entities_count_zero": "Объектов: 0",
  "stage.entities_count": "Объектов: {count}",
  "stage.empty_message":
    "Модель пуста. Запустите команду, чтобы начать черчение.",
  // ── Drafting prompts ──
  "prompt.line.first": "ОТРЕЗОК: Укажите первую точку.",
  "prompt.line.next":
    "ОТРЕЗОК: Укажите следующую точку или [Замкнуть]. Нажмите Enter для завершения.",
  "prompt.point.location": "ТОЧКА: Укажите положение точки:",
  "prompt.base.point": "БАЗА: Укажите базовую точку вставки:",
  "prompt.circle.center":
    "ОКРУЖНОСТЬ: Укажите центр или [3Т/2Т/Ккр (касательная, касательная, радиус)].",
  "prompt.circle.radius": "ОКРУЖНОСТЬ: Укажите радиус или [Диаметр].",
  "prompt.circle.diameter": "ОКРУЖНОСТЬ: Укажите диаметр окружности.",
  "prompt.circle.2p_first":
    "ОКРУЖНОСТЬ: Укажите первый конец диаметра окружности.",
  "prompt.circle.2p_second":
    "ОКРУЖНОСТЬ: Укажите второй конец диаметра окружности.",
  "prompt.circle.3p_second": "ОКРУЖНОСТЬ: Укажите вторую точку:",
  "prompt.circle.3p_third": "ОКРУЖНОСТЬ: Укажите третью точку:",
  "prompt.arc.start": "ДУГА: Укажите начальную точку.",
  "prompt.arc.second": "ДУГА: Укажите вторую точку дуги или [Центр].",
  "prompt.arc.end": "ДУГА: Укажите конечную точку.",
  "prompt.arc.sce_center": "ДУГА: Укажите центр дуги:",
  "prompt.arc.sce_end_angle": "ДУГА: Укажите включённый угол:",
  // ── ELLIPSE (Phase D4) — center mode prompts ──
  "prompt.ellipse.center":
    "ЭЛЛИПС: Конечная точка оси эллипса или [Дуга/Центр]:",
  "prompt.ellipse.major_end": "ЭЛЛИПС: Другая конечная точка оси:",
  "prompt.ellipse.minor_distance": "ЭЛЛИПС: Длина другой оси:",
  "prompt.polyline.first": "ПЛИНИЯ: Укажите начальную точку или [Ширина].",
  "prompt.polyline.next":
    "ПЛИНИЯ: Укажите следующую точку, Enter для завершения, C для замыкания или Ш для ширины.",
  "prompt.polyline.width_start": "ПЛИНИЯ: Укажите начальную ширину:",
  "prompt.polyline.width_end": "ПЛИНИЯ: Укажите конечную ширину:",
  "prompt.rectangle.first":
    "ПРЯМОУГ: Укажите первую угловую точку или [Размеры].",
  "prompt.rectangle.second":
    "ПРЯМОУГ: Укажите противоположный угол или [Размеры].",
  "prompt.rectangle.d_length": "ПРЯМОУГ: Длина прямоугольника:",
  "prompt.rectangle.d_width": "ПРЯМОУГ: Ширина прямоугольника:",
  // ── DONUT (Phase D3) — AutoCAD-parity prompts ──
  "prompt.donut.inner_d": "КОЛЬЦО: Внутренний диаметр <{last}>:",
  "prompt.donut.outer_d": "КОЛЬЦО: Внешний диаметр <{last}>:",
  "prompt.donut.center": "КОЛЬЦО: Укажите центр кольца или [Выход]:",
  // ── POLYGON (Phase D2) — AutoCAD-parity prompts ──
  "prompt.polygon.sides": "ПОЛИГОН: Число сторон <{last}>:",
  "prompt.polygon.mode":
    "ПОЛИГОН: Вписан в окружность или Описан вокруг окружности [Вписан/Описан] <Вписан>:",
  "prompt.polygon.center": "ПОЛИГОН: Укажите центр:",
  "prompt.polygon.radius": "ПОЛИГОН: Укажите радиус:",
  "prompt.rectangle.d_place": "ПРЯМОУГ: Укажите положение:",
  "prompt.text.start":
    "ТЕКСТ: Укажите начальную точку или [Выравнивание/Стиль].",
  "prompt.text.height": "ТЕКСТ: Укажите высоту:",
  "prompt.text.rotation": "ТЕКСТ: Укажите угол поворота:",
  "prompt.text.content": "ТЕКСТ: Введите содержимое и нажмите Выполнить.",
  "prompt.mtext.first": "МТЕКСТ: Укажите первый угол текстового поля.",
  "prompt.mtext.second":
    "МТЕКСТ: Укажите противоположный угол текстового поля.",
  "prompt.mtext.content": "МТЕКСТ: Введите содержимое и нажмите Выполнить.",
  "prompt.block.name": "ВСТАВКА: Введите имя блока или [?] <BLOCK_REF>.",
  "prompt.block.insertion": "ВСТАВКА: Укажите точку вставки.",
  "prompt.block.scale": "ВСТАВКА: Укажите общий масштаб <1>.",
  "prompt.block.rotation": "ВСТАВКА: Укажите угол поворота <0>.",
  "prompt.arrayrect.select":
    "МАССИВ: Выберите объекты для массива (Enter когда готово).",
  "prompt.arrayrect.base": "МАССИВ: Укажите базовую точку.",
  "prompt.arrayrect.count":
    "МАССИВ: Введите COL=<n> ROW=<n> или укажите противоположный угол.",
  "prompt.arrayrect.spacing":
    "МАССИВ: Введите CS=<n> RS=<n> AA=<deg> или Enter для подтверждения.",
  "prompt.arraypolar.select":
    "ПОЛЯРНЫЙ МАССИВ: Выберите объекты (Enter когда готово).",
  "prompt.arraypolar.center": "ПОЛЯРНЫЙ МАССИВ: Укажите центр.",
  "prompt.arraypolar.count": "ПОЛЯРНЫЙ МАССИВ: Количество (6).",
  "prompt.arraypolar.fill":
    "ПОЛЯРНЫЙ МАССИВ: Угол заполнения (360), ROW=<n>, RS=<n>.",
  "prompt.arraypath.select":
    "МАССИВ ПО ПУТИ: Выберите объекты (Enter когда готово).",
  "prompt.arraypath.path":
    "МАССИВ ПО ПУТИ: Выберите кривую-путь (отрезок, дуга, окружность или полилиния).",
  "prompt.arraypath.method":
    "МАССИВ ПО ПУТИ: Выберите метод [Поделить/Разметить] <Поделить>.",
  "prompt.arraypath.params":
    "МАССИВ ПО ПУТИ: Введите количество / шаг или Enter для принятия по умолчанию.",
  "prompt.arrayedit.select": "РЕДМАССИВ: Выберите элемент массива.",
  "prompt.hatchedit.select": "РЕДШТРИХ: Выберите штриховку для редактирования.",
  "prompt.hatchedit.pattern":
    "РЕДШТРИХ: Введите имя образца [ANSI31, SOLID, ...] или Enter, чтобы оставить текущий.",
  "prompt.hatchedit.scale":
    "РЕДШТРИХ: Введите масштаб образца или Enter, чтобы оставить текущий.",
  "prompt.hatchedit.angle":
    "РЕДШТРИХ: Введите угол образца (градусы) или Enter, чтобы оставить текущий.",
  "prompt.hatch.pattern":
    "ШТРИХ: Укажите внутреннюю точку или введите имя образца [ANSI31, SOLID, ...]. По умолчанию ANSI31.",
  "prompt.hatch.first": "ШТРИХ: Укажите первый угол области штриховки.",
  "prompt.hatch.second":
    "ШТРИХ: Укажите противоположный угол области штриховки.",
  "prompt.hatch.confirm":
    "ШТРИХ: Нажмите Enter для подтверждения границы штриховки.",
  "prompt.hatch.mode":
    "ШТРИХ: Выберите [Внутренняя точка/Объекты/Прямоугольник] <В>:",
  "prompt.hatch.pick_internal": "ШТРИХ: Укажите внутреннюю точку области:",
  "prompt.hatch.pick_additional":
    "ШТРИХ: Укажите ещё точку или нажмите Enter для применения:",
  "prompt.dimension.first": "РАЗМЕР: Укажите начало первой выносной линии.",
  "prompt.dimension.second": "РАЗМЕР: Укажите начало второй выносной линии.",
  "prompt.dimension.line": "РАЗМЕР: Укажите положение размерной линии.",
  "prompt.dimaligned.first": "РЗМПАРАЛ: Укажите начало первой выносной линии.",
  "prompt.dimaligned.second": "РЗМПАРАЛ: Укажите начало второй выносной линии.",
  "prompt.dimaligned.line": "РЗМПАРАЛ: Укажите положение размерной линии.",
  "prompt.dimangular.first":
    "РЗМУГЛ: Выберите дугу, окружность, отрезок или укажите вершину.",
  "prompt.dimangular.second": "РЗМУГЛ: Выберите второй отрезок.",
  "prompt.dimangular.arc": "РЗМУГЛ: Укажите положение размерной дуги.",
  "prompt.dim3point.vertex": "РЗМ3Т: Укажите вершину угла.",
  "prompt.dim3point.first": "РЗМ3Т: Укажите конец первой стороны угла.",
  "prompt.dim3point.second": "РЗМ3Т: Укажите конец второй стороны угла.",
  "prompt.dim3point.arc": "РЗМ3Т: Укажите положение размерной дуги.",
  "prompt.dimradius.select": "РЗМРАДИУС: Выберите дугу или окружность.",
  "prompt.dimradius.line": "РЗМРАДИУС: Укажите положение размерной линии.",
  "prompt.dimdiameter.select": "РЗМДИАМ: Выберите дугу или окружность.",
  "prompt.dimdiameter.line": "РЗМДИАМ: Укажите положение размерной линии.",
  "prompt.dimordinate.feature": "РЗМОРД: Укажите положение элемента.",
  "prompt.dimordinate.leader": "РЗМОРД: Укажите конец выноски.",
  "prompt.dimbaseline.second":
    "РЗМБАЗ: Укажите начало второй выносной линии или [Выбрать/Отменить] <Выбрать>:",
  "prompt.dimbaseline.no_anchor":
    "РЗМБАЗ: Сначала создайте линейный или параллельный размер.",
  "prompt.dimcontinue.second":
    "РЗМЦЕПЬ: Укажите начало второй выносной линии или [Выбрать/Отменить] <Выбрать>:",
  "prompt.dimcontinue.no_anchor":
    "РЗМЦЕПЬ: Сначала создайте линейный или параллельный размер.",
  "prompt.mleader.arrow": "МЛИНИЯ: Укажите положение стрелки.",
  "prompt.mleader.landing": "МЛИНИЯ: Укажите место посадки выноски.",
  "prompt.mleader.content":
    "МЛИНИЯ: Введите текст выноски и нажмите Выполнить.",
  "prompt.mleaderedit.select":
    "МЛИНИЯРЕД: Выберите мультивыноску для редактирования.",
  "prompt.mleaderedit.options":
    "МЛИНИЯРЕД: Опции: [Добавить выноску/Удалить выноску]",
  "prompt.mleaderedit.add_arrow": "МЛИНИЯРЕД: Укажите точку новой стрелки.",
  "prompt.mleaderedit.remove_pick": "МЛИНИЯРЕД: Выберите выноску для удаления.",
  "prompt.mleaderedit.not_mleader": "Объект не является мультивыноской.",
  "confirm.mleaderedit.last_leader":
    "Это последняя выноска. Удалить мультивыноску целиком?",
  "prompt.revcloud.first":
    "ОБЛАКО: Укажите первую точку или [Длина дуги/Объект/ПРямоугольное/ПОЛигональное/От Руки/Стиль/Изменить] <Объект>:",
  "prompt.revcloud.second":
    "ОБЛАКО: Укажите противоположный угол облака правок.",
  "prompt.revcloud.arc_length":
    "ОБЛАКО: Задайте приблизительную длину дуги:",
  "prompt.revcloud.style":
    "ОБЛАКО: Выберите стиль дуги [Обычный/Каллиграфия] <Обычный>:",
  "prompt.revcloud.polygonal_next":
    "ОБЛАКО: Укажите следующую точку (Enter — замкнуть облако):",
  "prompt.revcloud.freehand":
    "ОБЛАКО: Ведите перекрестье по контуру облака; облако замкнётся у начальной точки.",
  "prompt.revcloud.object_pick": "ОБЛАКО: Выберите объект:",
  "prompt.revcloud.object_reverse":
    "ОБЛАКО: Изменить направление дуг [Да/Нет] <Нет>:",
  "prompt.revcloud.invalid_object":
    "ОБЛАКО: Выбранный объект не является замкнутым кругом, эллипсом, полилинией или сплайном.",
  "prompt.revcloud.no_object_found": "ОБЛАКО: Объект не найден. Выберите объект:",
  "prompt.revcloud.modify_unsupported":
    "ОБЛАКО: Параметр Изменить пока не поддерживается (появится вместе с редактором свойств облака правок).",
  "prompt.wipeout.first_corner": "ВЫРЕЗ: Укажите первый угол.",
  "prompt.wipeout.second_corner": "ВЫРЕЗ: Укажите противоположный угол.",
  "prompt.wipeout.frame_toggle": "ВЫРЕЗ: Показ кадра [ВКЛ/ВЫКЛ] <ВКЛ>:",
  "prompt.spline.first_or_method": "СПЛАЙН: Укажите первую точку или [Метод]:",
  "prompt.spline.next":
    "СПЛАЙН: Укажите следующую точку или [замкнУть/Конец/Касательная]:",
  "prompt.spline.method_fit": "СПЛАЙН: Использовать точки определения (FIT)",
  "prompt.spline.method_cv": "СПЛАЙН: Использовать управляющие точки (CV)",
  "prompt.spline.fit_first": "СПЛАЙН: Укажите первую точку определения:",
  "prompt.spline.fit_next":
    "СПЛАЙН: Укажите следующую точку определения или [замкнУть/Касательная]. Нажмите Enter для завершения.",
  "prompt.spline.fit_next_or_finish":
    "СПЛАЙН: Укажите следующую точку определения или [замкнУть/Касательная]. Нажмите Enter для завершения построения.",
  "prompt.spline.cv_first": "СПЛАЙН: Укажите первую управляющую точку:",
  "prompt.spline.cv_next":
    "СПЛАЙН: Укажите следующую управляющую точку или [замкнУть]. Нажмите Enter для завершения.",
  "prompt.spline.start_tangent": "СПЛАЙН: Укажите начальную касательную:",
  "prompt.spline.end_tangent": "СПЛАЙН: Укажите конечную касательную:",
  "prompt.viewport.first": "МВИД: Укажите угол видового экрана или [Подгон].",
  "prompt.viewport.second":
    "МВИД: Укажите противоположный угол видового экрана.",
  // VPCLIP-INTERACTIVE — AutoCAD's real VPCLIP first prompt.
  "prompt.vpclip.select_viewport":
    "ВЭКРЕЗ: Выберите видовой экран для обрезки:",
  "prompt.vpclip.first_corner":
    "ВЭКРЕЗ: Укажите первую точку угла границы подрезки:",
  "prompt.vpclip.second_corner":
    "ВЭКРЕЗ: Укажите противоположный угол границы подрезки.",
  // ── Dimension properties ──
  "property.mirror_x": "Зеркало X",
  "property.mirror_y": "Зеркало Y",
  // ── Hatch / Block / Viewport / Array ──
  "property.insertion_point": "Точка вставки",
  // ── Subtype labels ──
  "subtype.linear": "Линейный",
  "subtype.aligned": "Параллельный",
  "subtype.angular": "Угловой",
  "subtype.angular_3pt": "Угловой 3-точечный",
  "subtype.diameter": "Диаметр",
  "subtype.radius": "Радиус",
  "subtype.ordinate": "Ординатный",
  // ── Dimension Style Editor dialog ──
  "dimstyle_editor.title": "Изменить размерный стиль: {name}",
  "dimstyle_editor.current_style": "Текущий стиль",
  "dimstyle_editor.set_current": "Сделать текущим",
  "dimstyle_editor.delete_confirm":
    'Удалить размерный стиль "{name}"? Размеры, использующие его, вернутся к "Standard".',
  "dimstyle_editor.new_style_placeholder": "Имя нового стиля...",
  "dimstyle_editor.new_button": "Создать",
  "dimstyle_editor.tab.lines": "Линии",
  "dimstyle_editor.tab.arrows": "Символы и стрелки",
  "dimstyle_editor.tab.text": "Текст",
  "dimstyle_editor.tab.fit": "Размещение",
  "dimstyle_editor.tab.units": "Основные единицы",
  "dimstyle_editor.tab.altunits": "Альт. единицы",
  "dimstyle_editor.tab.tolerances": "Допуски",
  // Lines tab
  "dimstyle.lines.section.dim_lines": "Размерные линии",
  "dimstyle.lines.dim_color": "Цвет (DIMCLRD)",
  "dimstyle.lines.dim_lwd": "Вес линии (DIMLWD)",
  "dimstyle.lines.dim_dle": "Удлинение за выносные (DIMDLE)",
  "dimstyle.lines.dim_dli": "Шаг базовых линий (DIMDLI)",
  "dimstyle.lines.dim_sd1": "Подавить размерную линию 1 (DIMSD1)",
  "dimstyle.lines.dim_sd2": "Подавить размерную линию 2 (DIMSD2)",
  "dimstyle.lines.section.ext_lines": "Выносные линии",
  "dimstyle.lines.ext_color": "Цвет (DIMCLRE)",
  "dimstyle.lines.ext_lwe": "Вес линии (DIMLWE)",
  "dimstyle.lines.ext_dimexe": "Удлинение за размерную (DIMEXE)",
  "dimstyle.lines.ext_dimexo": "Отступ от объекта (DIMEXO)",
  "dimstyle.lines.ext_dimse1": "Подавить выносную линию 1 (DIMSE1)",
  "dimstyle.lines.ext_dimse2": "Подавить выносную линию 2 (DIMSE2)",
  // Arrows tab
  "dimstyle.arrows.section": "Стрелки",
  "dimstyle.arrows.first": "Первая стрелка",
  "dimstyle.arrows.second": "Вторая стрелка",
  "dimstyle.arrows.size": "Размер стрелки",
  "dimstyle.arrows.center.section": "Маркер центра",
  "dimstyle.arrows.center.type": "Тип",
  "dimstyle.arrows.center.size": "Размер",
  "dimstyle.arrows.center.none": "Нет",
  "dimstyle.arrows.center.mark": "Метка",
  "dimstyle.arrows.center.line": "Линия",
  // Text tab
  "dimstyle.text.section.appearance": "Вид текста",
  "dimstyle.text.style": "Текстовый стиль",
  "dimstyle.text.color": "Цвет текста",
  "dimstyle.text.height": "Высота текста (DIMTXT)",
  "dimstyle.text.gap": "Отступ от размерной (DIMGAP)",
  "dimstyle.text.section.placement": "Размещение текста",
  "dimstyle.text.vertical": "По вертикали",
  "dimstyle.text.alignment": "Выравнивание",
  "dimstyle.text.tvp": "Смещение по вертикали (DIMTVP)",
  // Fit tab
  "dimstyle.fit.section.options": "Параметры размещения",
  "dimstyle.fit.atfit": "Подгонка (DIMATFIT)",
  "dimstyle.fit.atfit.both": "Текст и стрелки вместе",
  "dimstyle.fit.atfit.arrows": "Только стрелки",
  "dimstyle.fit.atfit.text": "Только текст",
  "dimstyle.fit.atfit.best": "Что лучше подходит (по умолчанию)",
  "dimstyle.fit.tofl": "Всегда рисовать линию между выносными (DIMTOFL)",
  "dimstyle.fit.soxd": "Подавлять стрелки если не помещаются (DIMSOXD)",
  "dimstyle.fit.tix": "Принудительно текст внутри выносных (DIMTIX)",
  "dimstyle.fit.upt": "Положение текста задаётся пользователем (DIMUPT)",
  "dimstyle.fit.tmove": "Правила перемещения текста (DIMTMOVE)",
  "dimstyle.fit.tmove.with_dim": "Двигать размерную линию с текстом",
  "dimstyle.fit.tmove.add_leader": "Добавлять выноску при переносе",
  "dimstyle.fit.tmove.no_leader": "Двигать текст без выноски",
  "dimstyle.fit.section.scale": "Масштаб",
  "dimstyle.fit.scale": "Общий масштаб (DIMSCALE)",
  "dimstyle.annotative.label": "Аннотативный (DIMANNO)",
  "dimstyle.annotative.tooltip":
    "Общий масштаб управляется текущим аннотативным масштабом листа",
  "dimstyle.fit.section.text_orient": "Ориентация текста",
  "dimstyle.fit.tih": "Текст внутри по горизонтали (DIMTIH)",
  "dimstyle.fit.toh": "Текст снаружи по горизонтали (DIMTOH)",
  // Primary Units tab
  "dimstyle.units.section.linear": "Линейные размеры",
  "dimstyle.units.format": "Формат единиц",
  "dimstyle.units.precision": "Точность",
  "dimstyle.units.prefix": "Префикс",
  "dimstyle.units.suffix": "Суффикс",
  "dimstyle.units.lfac": "Масштаб измерения (DIMLFAC)",
  "dimstyle.units.rnd": "Округление (DIMRND)",
  "dimstyle.units.dsep": "Десятичный разделитель (DIMDSEP)",
  "dimstyle.units.dsep.period": "Точка (.)",
  "dimstyle.units.dsep.comma": "Запятая (,)",
  "dimstyle.units.dsep.space": "Пробел",
  "dimstyle.units.frac": "Формат дроби (DIMFRAC)",
  "dimstyle.units.frac.horizontal": "Горизонтальная сложная",
  "dimstyle.units.frac.diagonal": "Диагональная сложная",
  "dimstyle.units.frac.flat": "Не сложная",
  "dimstyle.units.section.zero": "Подавление нулей",
  "dimstyle.units.zero.leading": "Подавлять ведущие нули",
  "dimstyle.units.zero.trailing": "Подавлять конечные нули",
  "dimstyle.units.section.angular": "Угловые размеры",
  "dimstyle.units.aunit": "Формат единиц (DIMAUNIT)",
  "dimstyle.units.aunit.decimal": "Десятичные градусы",
  "dimstyle.units.aunit.dms": "Градусы / Минуты / Секунды",
  "dimstyle.units.aunit.grads": "Грады",
  "dimstyle.units.aunit.radians": "Радианы",
  "dimstyle.units.aunit.surveyor": "Топографические",
  "dimstyle.units.adec": "Точность (DIMADEC)",
  "dimstyle.units.apost": "Угловой суффикс (DIMAPOST)",
  // Alt Units tab
  "dimstyle.altunits.section": "Альтернативные единицы",
  "dimstyle.altunits.alt": "Показывать альтернативные единицы (DIMALT)",
  "dimstyle.altunits.altu": "Формат единиц (DIMALTU)",
  "dimstyle.altunits.altd": "Точность (DIMALTD)",
  "dimstyle.altunits.altf": "Множитель (DIMALTF)",
  "dimstyle.altunits.altrnd": "Округление (DIMALTRND)",
  "dimstyle.altunits.alttd": "Точность допусков (DIMALTTD)",
  "dimstyle.altunits.altz": "Подавление нулей альт. единиц (DIMALTZ)",
  // Tolerances tab
  "dimstyle.tolerances.section": "Отображение допусков",
  "dimstyle.tolerances.tol": "Показывать допуск (DIMTOL)",
  "dimstyle.tolerances.lim": "Показывать пределы (DIMLIM)",
  "dimstyle.tolerances.tp": "Верхний допуск (DIMTP)",
  "dimstyle.tolerances.tm": "Нижний допуск (DIMTM)",
  "dimstyle.tolerances.tfac": "Масштаб (DIMTFAC)",
  "dimstyle.tolerances.tdec": "Точность (DIMTDEC)",
  "dimstyle.tolerances.tolj": "Выравнивание по вертикали (DIMTOLJ)",
  "dimstyle.tolerances.tolj.bottom": "По нижнему",
  "dimstyle.tolerances.tolj.middle": "По середине",
  "dimstyle.tolerances.tolj.top": "По верхнему",
  "dimstyle.tolerances.tzin": "Подавление нулей допуска (DIMTZIN)",
  // Common dialog buttons
  "dialog.cancel": "Отмена",
  "dialog.close": "Закрыть",
  "dialog.save_style": "Сохранить стиль",
  "dialog.add": "+ Добавить",
  "dialog.delete": "– Удалить",
  "dialog.rename": "Переименовать",
  "dialog.rename_prompt": "Новое имя:",
  "dialog.apply": "Применить",
  "dialog.ok": "ОК",
  // Context toolbar
  "context.dim_style": "Размерный стиль",
  "context.text_height": "Высота текста",
  "context.arrow_size": "Размер стрелки",
  "context.manage_styles": "Управление стилями",
  // Settings panel
  "settings.title": "Настройки",
  "settings.language": "Язык интерфейса",
  "settings.language.ru": "Русский",
  "settings.language.en": "English",
  "settings.open": "Открыть настройки",
  // Preview highlighting (AutoCAD SELECTIONPREVIEW/PREVIEWEFFECT/PREVIEWCOLOR/PREVIEWFILTER)
  "settings.preview.open": "Настройки подсветки",
  "settings.preview.title": "Предпросмотр выделения",
  "settings.preview.selection.label":
    "Предварительное выделение (SELECTIONPREVIEW)",
  "settings.preview.selection.0": "Отключено",
  "settings.preview.selection.1": "Только в режиме ожидания",
  "settings.preview.selection.2": "Только при запросе выбора",
  "settings.preview.selection.3": "Всегда",
  "settings.preview.effect.label": "Эффект подсветки (PREVIEWEFFECT)",
  "settings.preview.effect.0": "Пунктир",
  "settings.preview.effect.1": "Утолщение",
  "settings.preview.effect.2": "Пунктир и утолщение",
  "settings.preview.color.label": "Цвет подсветки (PREVIEWCOLOR)",
  "settings.preview.color.reset": "По умолчанию",
  "settings.preview.filter.label": "Исключить (PREVIEWFILTER)",
  "settings.preview.filter.locked_layer": "Объекты на заблокированных слоях",
  "settings.preview.filter.xrefs": "Внешние ссылки",
  "settings.preview.filter.tables": "Таблицы",
  "settings.preview.filter.mlines": "Мультилинии",
  "settings.preview.filter.hatches": "Штриховки",
  "settings.preview.filter.groups": "Группы",
  "settings.preview.reset_all": "Сбросить всё",
  // ── Header pills ──
  "header.app_title": "DXF AI Editor",
  "header.subtitle": "Stage A production shell",
  "header.subtitle_long":
    "Браузерный редактор DXF-чертежей, подобный AutoCAD: компактная лента, навигация по командам, командная консоль и интерактивное поле черчения.",
  "header.preview_user": "Активный пользователь: {email}",
  "header.auth_disabled": "Авторизация временно отключена",
  "header.role": "Роль: {role}",
  "header.backend": "Бэкенд: {alias}",
  "header.compact_ribbon": "Компактная лента",
  "header.editor_ribbon_aria": "Лента редактора",
  // ── TextStyle Editor dialog ──
  "textstyle_editor.title": "Изменить стиль текста: {name}",
  "textstyle_editor.section.font": "Шрифт",
  "textstyle_editor.font_name": "Имя шрифта",
  "textstyle_editor.section.size": "Размер",
  "textstyle_editor.height": "Фиксированная высота (0 = переменная)",
  "textstyle_editor.section.effects": "Эффекты",
  "textstyle_editor.width_factor": "Коэффициент ширины",
  "textstyle_editor.oblique_angle": "Угол наклона",
  "textstyle_editor.backward": "Зеркало по горизонтали",
  "textstyle_editor.upside_down": "Зеркало по вертикали",
  "textstyle_editor.font_none": "(системный шрифт)",
  "textstyle_editor.font_missing": "{name} (не загружен)",
  "textstyle_editor.open_font_manager": "Менеджер...",
  // ── Font Manager dialog ──
  "fontmanager.title": "Менеджер шрифтов",
  "fontmanager.upload_button": "Загрузить шрифты...",
  "fontmanager.drop_zone": "Или перетащите .shx / .ttf сюда",
  "fontmanager.empty": "Шрифты не загружены",
  "fontmanager.loaded_count": "Загружено шрифтов: {count}",
  "fontmanager.kind_shx": "SHX",
  "fontmanager.kind_ttf": "TTF",
  "fontmanager.remove": "Удалить",
  "fontmanager.missing_title": "Требуются для чертежа:",
  "fontmanager.missing_item": "{name} (не загружен)",
  "fontmanager.success": "Шрифт {name} загружен",
  "fontmanager.removed": "Шрифт {name} удалён",
  "fontmanager.parse_error": "Ошибка {name}: {error}",
  "fontmanager.close": "Закрыть",
  "fontmanager.fetch_button": "🔍 Найти в интернете",
  "fontmanager.fetching": "Поиск...",
  "fontmanager.fetched": "Шрифт {name} загружен из whitelist",
  "fontmanager.fetch_not_in_whitelist":
    "Шрифт {name} отсутствует в публичном источнике",
  "fontmanager.not_in_whitelist_tooltip":
    "Шрифт не в публичном источнике — загрузите файл вручную",
  "fontmanager.fetch_network_error":
    "Сетевая ошибка при загрузке {name}: {message}",
  "fontmanager.fetch_cors_error": "CORS-ошибка при загрузке {name}",
  "fontmanager.fetch_parse_error": "Ошибка парсинга {name}: {message}",
  "fontmanager.fetch_already_loaded": "Шрифт {name} уже загружен",
  "fontmanager.fetch_aborted": "Загрузка {name} отменена",
  // ── Context toolbar (when an entity is selected) ──
  "context.style": "Стиль",
  "context.height": "Высота",
  "context.content": "Содержимое",
  "context.content_block": "Блок",
  "context.content_mtext": "МТекст",
  "context.mleader_style": "Стиль мультивыноски",
  "context.mleader_text_height": "Высота текста",
  "context.mleader_dogleg_length": "Длина посадки",
  "context.mleader_landing_gap": "Зазор посадки",
  "context.mleader_leader_weight": "Вес выноски",
  "context.mleader_annotative": "Аннотативный",
  // ── Drawing stage card ──
  "stage.entities_count_n": "{count} объектов",
  "stage.entities_count_one": "1 объект",
  // ── Sessions / settings card ──
  "sessions.section_label": "Сессии",
  "sessions.title": "Доступ к оболочке с учётом ролей",
  "sessions.reset_state": "Сбросить состояние",
  "sessions.manage_fonts": "SHX/TTF шрифты",
  // ── Spaces / layout routing ──
  "spaces.section_label": "Пространства",
  "spaces.title": "Маршрутизация Модель / Лист / Видовой экран",
  "spaces.setup": "Настройка пространства",
  "spaces.setup_hint":
    "Активно пространство модели. Запустите команду рисования и затем кликайте точки прямо на холсте выше.",
  "spaces.entities_in_space": "Объекты в активном пространстве",
  "spaces.objects_in_drawing": "{count} объектов в чертеже",
  "entity_list.summary_fallback": "Слой: {layer}",
  "entity_list.show_all": "Показать все ({count})",
  "entity_list.collapse": "Свернуть список",
  "spaces.clear_selection": "Снять выделение",
  "spaces.viewport": "Видовой экран",
  "spaces.clipped_boundary": "Подрезанная граница",
  "spaces.rect_boundary": "Прямоугольная граница",
  "spaces.layer_overrides": "переопределений слоёв: {count}",
  // ── Command line shell ──
  "shell.command_line": "Командная строка",
  "shell.command_input_placeholder":
    "Введите команду, псевдоним или координаты: L, MV, @4500,0, @500<45, 1200,3500",
  "shell.command_history_title": "История команд",
  "shell.run_submit": "Выполнить",
  "shell.console.current_prompt": "Текущая подсказка",
  "shell.console.accepted_input": "Принимаемый ввод",
  "shell.console.alias_hotkey": "Псевдоним / Горячая клавиша",
  "shell.legend.absolute": "Абсолютные: 1200,3500",
  "shell.legend.relative": "Относительные: @4500,0",
  "shell.legend.polar": "Полярные: @500<45",
  "shell.legend.direct": "Прямое расстояние: 500",
  "shell.legend.aliases":
    "Псевдонимы: L C A PL REC T DT MT B I AR H BH DIM ML MLD RC RV MV",
  // ── DXF I/O card ──
  "dxfio.title": "Ввод-вывод DXF",
  "dxfio.summary": "Линия / Окружность / Дуга",
  "dxfio.import": "Импорт",
  "dxfio.import_picker": "Выбор файла",
  "dxfio.export": "Экспорт",
  // ── Properties / inspector panel ──
  "panel.overview": "Обзор",
  "panel.properties": "Свойства",
  "panel.history": "История",
  "panel.ai_guardrails": "Защитные правила ИИ",
  "panel.selection_context": "Контекст выделения",
  "panel.apply_properties": "Применить свойства",
  "panel.apply_properties_hint_default":
    "Обновляет свойства объектов по умолчанию для будущих объектов.",
  "panel.apply_properties_hint_commit":
    "Подтверждение через общий движок свойств и журнал аудита.",
  "panel.manage_dim_styles": "Управление размерными стилями",
  "panel.manage_dim_styles_hint":
    "Открывает редактор размерного стиля для изменения стрелок, текста и единиц.",
  "panel.manage_text_styles": "Управление стилями текста",
  "panel.manage_text_styles_hint":
    "Открывает редактор текстового стиля для изменения шрифта, размера и эффектов.",
  // ── Justify options for text/mtext ──
  "justify.left": "По левому краю",
  "justify.center": "По центру",
  "justify.right": "По правому краю",
  "justify.aligned": "Выровненный",
  "justify.middle": "По середине",
  "justify.fit": "Подгонка",
  "justify.tl": "Сверху слева",
  "justify.tc": "Сверху по центру",
  "justify.tr": "Сверху справа",
  "justify.ml": "Посередине слева",
  "justify.mc": "Посередине по центру",
  "justify.mr": "Посередине справа",
  "justify.bl": "Снизу слева",
  "justify.bc": "Снизу по центру",
  "justify.br": "Снизу справа",
  // ── Annotative dropdown (Yes/No) ──
  "annotative.yes": "Да",
  "annotative.no": "Нет",
  // ── Lineweight dropdown (AutoCAD presets) ──
  "lineweight.bylayer": "По слою",
  "lineweight.byblock": "По блоку",
  "lineweight.default": "По умолчанию",
  "lineweight.mm": "{value} мм",
  // ── CHPROP / property edit ──
  "chprop.title": "CHPROP",
  "chprop.subtitle": "Общие свойства объектов",
  "chprop.targets": "цели: {count}",
  "chprop.property": "Свойство",
  "chprop.value": "Значение",
  "chprop.select_property": "Выбрать свойство",
  "chprop.new_value": "Новое значение",
  "chprop.apply": "Применить CHPROP",
  "chprop.mixed_hint":
    "Поддержка смешанного выбора разрешена только для общих свойств объектов.",
  // ── MATCHPROP ──
  "matchprop.title": "MATCHPROP",
  "matchprop.subtitle": "Поток источник / цель",
  "matchprop.no_source": "Источник не задан",
  "matchprop.first_slice_hint":
    "Этот первый срез использует явный исходный объект и применяет консервативные правила карты возможностей для basic, special и all.",
  "matchprop.use_selection_as_source": "Использовать выделение как источник",
  "matchprop.clear_source": "Очистить источник",
  "matchprop.apply_basic": "Применить базовые",
  "matchprop.apply_special": "Применить специальные",
  "matchprop.apply_all": "Применить все",
  "matchprop.source_pill": "источник MATCHPROP",
  "inspector.expand": "Развернуть инспектор",
  "inspector.collapse": "Свернуть инспектор",
  // Defect 2 (properties-provenance-and-untranslated-rows) — the hint used
  // to hardcode the genitive-plural form for every count ("3 объектОВ",
  // "1 слоЯХ", "3 типОВ" — wrong Russian agreement for 1/2-4). `{entityNoun}`
  // / `{layerNoun}` / `{typeNoun}` are resolved by InspectorSidebar.tsx via
  // the shared `pluralFormFor` (Intl.PluralRules one|few|many), mirroring
  // `dxfio.status.opened`'s existing noun-selection pattern — `{entityNoun}`
  // reuses `dxfio.noun.entity.*` (the SAME noun, "объект", already declined
  // there) rather than a second copy.
  "inspector.aggregate_hint":
    "В чертеже {total} {entityNoun} на {layers} {layerNoun}, {types} {typeNoun}.",
  // Locative case ("на N слоЕ"/"на N слоЯХ") — one/few collapse is NOT
  // symmetric with the genitive nouns above: Russian locative plural has no
  // separate few/many form (mirrors `dxfio.noun.space.*`'s
  // "пространстве"/"пространствах"/"пространствах").
  "inspector.noun.layer.one": "слое",
  "inspector.noun.layer.few": "слоях",
  "inspector.noun.layer.many": "слоях",
  "inspector.noun.type.one": "тип",
  "inspector.noun.type.few": "типа",
  "inspector.noun.type.many": "типов",
  "spaces.paper_space": "Лист",
  "spaces.model_in_viewport": "Модель во вьюпорте",
  "overview.banner":
    "Эта панель явно показывает, что реализовано, а что остаётся gated для приёмки этапа A.",
  // ── Audit / history ──
  "audit.audit_first_shell": "Оболочка с приоритетом аудита",
  "audit.recorded_events": "записанных событий: {count}",
  "audit.latest_first": "Сначала последние",
  // ── AI panel ──
  "ai.constraint_1":
    "ИИ остаётся командно-ориентированным в архитектуре. Эта оболочка не позволяет ИИ писать сущности или обходить контракты.",
  "ai.constraint_2":
    "ИИ-черчение генерирует только безопасные команды, а не состояние геометрии.",
  "ai.constraint_3":
    "Возможности явные и консервативные для первого протестированного среза свойств.",
  "ai.constraint_4":
    "Рендерер и предпросмотры остаются read-only потребителями канонического состояния.",
  // ── AI panel (CC-2d) — drafting (review-first) + support (read-only) ──
  "ai.guardrails.badge": "Безопасный контур",
  "ai.draft.section_label": "ИИ-черчение",
  "ai.draft.title": "Черновик команд ИИ",
  "ai.draft.badge": "Сначала проверка",
  "ai.draft.intro":
    "Вставьте предложение ИИ — одну команду или массив команд в формате JSON. Ничего не выполняется без явного подтверждения: сначала предпросмотр, затем «Принять».",
  "ai.draft.input_label": "Предложение ИИ (JSON)",
  "ai.draft.input_placeholder":
    '{"action":"INSERT_BLOCK","blockName":"...","insertionPoint":{"x":0,"y":0}}',
  "ai.draft.preview_label": "Предпросмотр (песочница)",
  "ai.draft.preview_empty":
    "Предпросмотр появится после ввода корректного предложения ИИ — здесь будет показано, какие команды будут выполнены.",
  "ai.draft.preview_count": "Команд к выполнению: {count}",
  "ai.draft.grouped_undo_note":
    "Весь пакет выполняется как одна операция отмены («{label}»).",
  "ai.draft.rejection_label": "Отклонено защитным контуром",
  "ai.draft.invalid_json": "Некорректный JSON: {message}",
  "ai.draft.accept": "Принять",
  "ai.draft.reject": "Отклонить",
  "ai.draft.undo_label": "ИИ-черчение",
  "ai.draft.transcript_label": "Журнал сеанса",
  "ai.draft.transcript_empty":
    "Журнал пуст — предложения, подтверждения и отказы появятся здесь.",
  "ai.draft.entry_accepted_title": "Принято",
  "ai.draft.entry_accepted":
    "{actions} — выполнено команд: {count}; отмена одним шагом («{label}»).",
  "ai.draft.entry_rejected_title": "Отклонено",
  "ai.draft.entry_rejected":
    "{actions} — отклонено пользователем, команды не выполнялись.",
  "ai.draft.entry_rejected_reason": "Предложение отклонено: {reason}",
  "ai.draft.entry_failed_title": "Ошибка выполнения",
  "ai.draft.entry_failed":
    "Пакет отменён (abort-batch), документ не изменён: {reason}",
  "ai.draft.latch_busy_reason":
    "другой клиент на странице (мост ИИ-агента) выполняет операцию; повторите через мгновение",
  "ai.support.section_label": "Только чтение",
  "ai.support.title": "ИИ-помощник по программе",
  "ai.support.badge": "read-only",
  "ai.support.intro":
    "Помощник отвечает на вопросы по командам и текущему сеансу. Он не имеет права изменять чертёж.",
  "ai.support.query_label": "Вопрос",
  "ai.support.query_placeholder": "Например: как нарисовать отрезок?",
  "ai.support.submit": "Спросить",
  "ai.support.readonly_hint": "Чертёж не изменяется.",
  "ai.support.empty": "Задайте вопрос — ответ появится здесь.",
  "ai.support.command_intro":
    "Команда {name} (псевдонимы: {aliases}). {short}",
  "ai.support.command_examples": "Примеры: {examples}",
  "ai.support.topic.layers":
    "Слой объекта меняется в инспекторе свойств (строка «Layer»), через CHPROP (свойство Layer) или MATCHPROP. Управление слоями — кнопка «Управление слоями» (Диспетчер слоёв) на панели «Свойства».",
  "ai.support.context.recent": "Последние команды сеанса: {commands}.",
  "ai.support.context.recent_empty":
    "В этом сеансе команды ещё не выполнялись.",
  "ai.support.context.last_command": "Последняя команда: {command}.",
  "ai.support.context.selection":
    "Сейчас выделено объектов: {count} ({types}).",
  "ai.support.context.selection_empty": "Сейчас ничего не выделено.",
  "ai.support.no_match":
    "Не нашёл точного ответа. Спросите про команду по имени (например, ОТРЕЗОК/LINE, КРУГ/CIRCLE), про слои, выделение или последние команды.",
  // ── Status strip / runtime info ──
  "status.backend_runtime": "Среда выполнения бэкенда",
  "status.mutation_path": "Путь мутации",
  "status.mutation_path_value":
    "UI → dispatch(command) → reducer → command engine → state + audit",
  "status.current_scope": "Текущий контекст",
  "status.auth_session": "Аутентификация / Сессия",
  "status.google_signin_disabled":
    "Вход через Google временно отключён для этого live-контура до настройки валидных OAuth учётных данных на claw.",
  "status.auth_fallback":
    "Сессия откатилась к локальной оболочке, потому что бэкенд не загрузился.",
  // ── Persistent backend slice ──
  "backend.slice_title": "Срез постоянного бэкенда",
  "backend.slice_subtitle": "Workspace / drawings / audit",
  "backend.workspace": "Workspace",
  "backend.workspace_intro":
    "Это постоянный workspace, выставленный сервером claw.",
  "backend.workspace_fallback":
    "Срез workspace бэкенда недоступен — оболочка показывает локальный preview workspace.",
  "backend.tag_backend": "бэкенд",
  "backend.tag_local_fallback": "локальный fallback",
  "backend.slug": "Slug: {slug}",
  "backend.owner": "Владелец: {owner}",
  "backend.members": "Участники: {count}",
  "backend.visibility": "Видимость: {visibility}",
  "backend.deployment": "Развёртывание: {mode}",
  "backend.drawings": "Чертежи",
  "backend.audit": "Аудит",
  "backend.records": "записей: {count}",
  "backend.local_fallback": "Локальный fallback",
  "backend.loading_slices": "Загрузка постоянных срезов из {url}…",
  "backend.loaded_admin":
    "Загружено из {url}/api/v1/workspaces, /drawings и /admin/audit.",
  "backend.loaded_user":
    "Загружено из {url}/api/v1/workspaces и /drawings. Аудит администратора доступен только администраторам.",
  "backend.protected_unloaded":
    "Защищённые постоянные срезы не были загружены — оболочка использует локальное состояние fallback.",
  "backend.guest_fallback":
    "Гостевая среда выполнения использует локальные постоянные fallback'и и не загрузила защищённые срезы бэкенда.",
  "backend.partial_load":
    "Часть срезов бэкенда загружена, часть откатилась к локальному fallback. {error}",
  "backend.unavailable": "Постоянные срезы бэкенда сейчас недоступны. {error}",
  "backend.preview_fallback": "Оболочка использует локальный preview fallback.",
  "backend.failed_to_load":
    "Не удалось загрузить постоянные срезы из {url}: {failures}",
  "status.connecting": "Подключение к {url}",
  "status.fallback_mode": "Режим fallback: {error}",
  "status.ready_via": "{authMode} через {url}",
  "status.local_shell_fallback":
    "Простое редактирование геометрии и свойств остаётся доступным через локальный fallback оболочки",
  "history.via": "через",
  // ── Panel labels (inspector tabs) ──
  "panel.label.overview": "Обзор",
  "panel.label.properties": "Свойства",
  "panel.label.history": "История",
  "panel.label.ai": "Защитные правила ИИ",
  // ── Future delivery gates ──
  "gates.canonical_dto":
    "Канонический DTO / модель для 13 требуемых семейств сущностей",
  "gates.command_engine":
    "Command Engine — единственный путь мутации геометрии",
  "gates.mlv_parity": "Паритет Model / Layout / Viewport до объявления этапа A",
  "gates.properties_capability":
    "Карта возможностей Свойства / CHPROP / MATCHPROP из исследовательской матрицы",
  "gates.audit_telemetry":
    "Аудит + телеметрия command-safe на каждом потоке с мутациями",
  // ── Attachment (MText) display names ──
  "attachment.top_left": "Сверху слева",
  "attachment.top_center": "Сверху по центру",
  "attachment.top_right": "Сверху справа",
  "attachment.middle_left": "Посередине слева",
  "attachment.middle_center": "Посередине по центру",
  "attachment.middle_right": "Посередине справа",
  "attachment.bottom_left": "Снизу слева",
  "attachment.bottom_center": "Снизу по центру",
  "attachment.bottom_right": "Снизу справа",
  // ── DIMSTYLE arrowhead options ──
  "arrowhead.closed_filled": "Закрытая заполненная",
  "arrowhead.closed_blank": "Закрытая пустая",
  "arrowhead.open": "Открытая",
  "arrowhead.dot": "Точка",
  "arrowhead.dot_blank": "Пустая точка",
  "arrowhead.arch_tick": "Архитектурная засечка",
  "arrowhead.oblique": "Наклонная",
  "arrowhead.open_30": "Открытая 30",
  "arrowhead.none": "Нет",
  // ── Text placement options (DIMTAD) ──
  "textplace.above": "Над",
  "textplace.centered": "По центру",
  "textplace.below": "Под",
  // ── Text alignment options (DIMJUST) ──
  "textalign.horizontal": "Горизонтально",
  "textalign.aligned": "Параллельно",
  "textalign.iso": "ISO",
  // ── Unit format options (DIMLUNIT / DIMALTU) ──
  "unitformat.scientific": "Научный",
  "unitformat.decimal": "Десятичный",
  "unitformat.engineering": "Инженерный",
  "unitformat.architectural_stacked": "Архитектурный (сложный)",
  "unitformat.fractional_stacked": "Дробный (сложный)",
  "unitformat.architectural": "Архитектурный",
  "unitformat.fractional": "Дробный",
  "unitformat.windows_desktop": "Microsoft Windows desktop",
  // ── Dimstyle editor error messages ──
  "dimstyle_editor.error.empty_name": "Имя стиля не может быть пустым.",
  "dimstyle_editor.error.create_failed": "Не удалось создать стиль.",
  // ── Ribbon action labels (not primary draw/annotate ones) ──
  "ribbon.action.history": "История",
  "ribbon.action.ai": "ИИ",
  "ribbon.action.properties": "Свойства",
  "ribbon.action.chprop": "CHPROP",
  "ribbon.action.matchprop": "MATCHPROP",
  "ribbon.action.clear": "Очистить",
  "ribbon.action.overview": "Обзор",
  "ribbon.action.audit": "Аудит",
  "ribbon.action.model": "Модель",
  "ribbon.action.layout_1": "Лист 1",
  "ribbon.action.layout_2": "Лист 2",
  "ribbon.action.paper": "Лист",
  "ribbon.action.viewport": "Видовой экран",
  "ribbon.action.viewports": "Видовые экраны",
  "ribbon.action.panel_overview": "Обзор",
  "ribbon.action.panel_properties": "Свойства",
  "ribbon.action.panel_history": "История",
  "ribbon.action.import_dxf": "Импорт DXF",
  "ribbon.action.reset": "Сброс",
  "ribbon.action.sessions": "Сессии",
  // ── Ribbon modify/layout/propertyflows tab section titles ──
  "ribbon.section.modify": "Редактирование",
  "ribbon.section.properties": "Свойства",
  "ribbon.section.selection": "Выделение",
  "ribbon.section.space_routing": "Маршрутизация пространства",
  "ribbon.section.viewport_context": "Контекст видового экрана",
  "ribbon.section.inspectors": "Инспекторы",
  "ribbon.section.data_flow": "Поток данных",
  // ── Ribbon quickbar / toolbar ──
  "ribbon.quickbar.current_layer": "Слой",
  "ribbon.quickbar.current_dimstyle": "Размерный стиль",
  "ribbon.quickbar.current_mleaderstyle": "Стиль мультивыноски",
  "ribbon.quickbar.current_textstyle": "Стиль текста",
  "ribbon.quickbar.live_entities": "активных объектов: {count}",
  "quickbar.undo": "Отменить",
  "quickbar.redo": "Повторить",
  "quickbar.undo.tooltip": "Отменить (Ctrl+Z)",
  "quickbar.redo.tooltip": "Повторить (Ctrl+Y)",
  "ribbon.coord_toggle.cad_title":
    "Ось Y направлена ВВЕРХ (соглашение AutoCAD / DXF). Нажмите, чтобы переключиться на SVG (Y вниз).",
  "ribbon.coord_toggle.svg_title":
    "Ось Y направлена ВНИЗ (устаревшее SVG). Нажмите, чтобы переключиться на CAD (Y вверх).",
  // ── Command line card / feed ──
  "shell.command_label": "Команда",
  "shell.esc_button": "Esc",
  "shell.idle_badge": "Ожидание",
  "shell.command_stream_placeholder":
    "Здесь будут отображаться в стиле AutoCAD имена команд, выбранные точки и введённый текст.",
  "shell.parse_banner.active":
    "Активные подсказки принимают ввод команд: Замкнуть, Диаметр, Отменить, текст и явные числовые значения там, где это поддерживается.",
  "shell.parse_banner.idle":
    "Командная строка уже поддерживает прямые геометрические аргументы для ОТРЕЗОК / ОКРУЖНОСТЬ / ДУГА и ключевые слова; расширенный синтаксис координат AutoCAD вводится команда за командой.",
  "shell.default_hint":
    "Псевдонимы: L, C, A, PL, REC, DT, MT, B, I, AR, H, BH, DIM, MLD, RV, MV. Esc отменяет активную команду.",
  // ── Workspace status badges ──
  "workspace.space_badge.model": "{name} / модельное пространство",
  "workspace.space_badge.paper": "{name} / пространство листа",
  "workspace.space_badge.viewport": "{name} / модель в видовом экране",
  "workspace.mode_badge": "Режим команды: {command}",
  "workspace.mode_badge.idle": "ожидание",
  "workspace.flow_badge.active":
    "Поток: клик в холсте или ввод значений в подсказки",
  "workspace.flow_badge.idle":
    "Поток: старт из ленты, псевдонима или явной команды",
  "workspace.input_badge": "Сейчас ввод: LINE 0,0 4500,0 | C | D | U | текст",
  "workspace.scope_badge": "Импорт: DXF объединяется с текущим чертежом",
  // ── Dxf import ──
  "dxfio.status.default": "Импортируйте DXF в текущий чертёж.",
  "dxfio.status.failed": "Импорт DXF не удался: {message}",
  "dxfio.import_button": "Импорт DXF",
  "dxfio.import_microcopy":
    "Выбранный файл .dxf разбирается и объединяется с текущим холстом чертежа.",
  "dxfio.export_entities_count": "{count} объектов",
  "dxfio.download": "Скачать DXF",
  "dxfio.copy_clipboard": "Копировать в буфер",
  // Truncation note appended to the export preview <textarea> when the
  // serialised DXF exceeds the DOM layout cap (see App.tsx
  // DXF_PREVIEW_DOM_CAP). The full document is still written by
  // Download/Copy — only the on-screen preview is clipped.
  "dxfio.preview_truncated":
    "… [предпросмотр усечён: показаны первые {shown} из {total} символов — экспортируйте файл для полного DXF]",
  // ── DXF import progress (AutoCAD "Processing Objects" dialog) ──
  "dxfio.progress.title": "Обработка объектов",
  "dxfio.progress.fetch": "Загрузка файла",
  "dxfio.progress.decode": "Декодирование (CP1251)",
  "dxfio.progress.tokenize": "Токенизация",
  "dxfio.progress.sections": "Разбор секций",
  "dxfio.progress.entities": "Парсинг объектов",
  "dxfio.progress.bridge": "Построение модели",
  "dxfio.progress.merge": "Слияние документа",
  "dxfio.progress.sync": "Синхронизация сцены",
  "dxfio.progress.items_fmt": "{processed} / {total}",
  "dxfio.progress.cancel": "Отмена",
  "dxfio.status.aborted": "Импорт DXF отменён.",
  // AutoCAD OPEN: успешно открыт новый чертёж (заменяет текущий).
  // `{count}` — число поддерживаемых сущностей, `{entityNoun}` —
  // словоформа существительного "объект" по правилам русского
  // плюрализма, `{spaces}` — число пространств,
  // `{spaceNoun}` — словоформа "пространство". Используется в
  // `importDxfFileAsyncReplacing`. Раздельные множественные формы
  // нужны, потому что число объектов и число пространств
  // независимы (один файл = много объектов в одном пространстве).
  "dxfio.status.opened":
    "Открыто {count} {entityNoun} из DXF в {spaces} {spaceNoun}.",
  // Plural-form noun helpers for `dxfio.status.opened`. Selected by the
  // store's pluralFormFor(n) helper based on the standard Russian
  // CLDR plural rule (one/few/many).
  "dxfio.noun.entity.one": "объект",
  "dxfio.noun.entity.few": "объекта",
  "dxfio.noun.entity.many": "объектов",
  "dxfio.noun.space.one": "пространстве",
  "dxfio.noun.space.few": "пространствах",
  "dxfio.noun.space.many": "пространствах",
  // ── Stage canvas ──
  "stage.empty_title": "Пустой чертёж",
  "stage.empty_hint":
    "Начните команду из ленты или консоли, затем укажите точки на канве.",
  "stage.osnap_title": "Привязки",
  "stage.osnap_hint":
    "Правая кнопка мыши открывает список привязок. Галочка рядом с каждой привязкой включает или отключает её для текущего сеанса.",
  "prompt.idle":
    "Ожидание. Введите команду из ленты, алиаса, горячей клавиши или командной строки в {space}.",
  // ── Backend persistence source labels ──
  "backend.source.loading": "загрузка",
  "backend.source.guest": "гостевой fallback",
  "backend.source.auth": "auth fallback",
  "backend.source.backend_audit_gated": "бэкенд + аудит ограничен",
  "backend.source.backend": "бэкенд",
  "backend.source.mixed": "смешанный",
  "backend.source.fallback": "fallback",
  "backend.offline_fallback": "оффлайн fallback",
  "backend.loading_label": "загрузка",
  "backend.no_persistent_workspace": "Нет постоянного workspace",
  // ── Zoom command feed messages ──
  "zoom.stage_not_ready": "ZOOM: стенд не готов",
  "zoom.extents": "ZOOM Extents",
  "zoom.all": "ZOOM All",
  "zoom.window_exact": "ZOOM Window {arg}",
  "zoom.window_invalid":
    "ZW: ожидается 'x1,y1 x2,y2'. Для интерактивного выбора используйте контекстное меню.",
  "zoom.window_blocked":
    "ZOOM Window заблокирован: сначала отмените активную команду",
  "zoom.window_pick": "ZOOM Window: укажите первый угол",
  "zoom.window_pick_second": "ZOOM Window: укажите противоположный угол",
  "zoom.previous": "ZOOM Previous",
  "zoom.previous_none": "ZOOM Previous: предыдущего вида нет",
  "zoom.object_with_arg": "ZOOM Object {arg}",
  "zoom.object_selection": "ZOOM Object (выделение)",
  "zoom.object_pick": "ZOOM Object: выберите объект на холсте",
  "zoom.object_not_found": "ZOOM Object: объект '{arg}' не найден",
  // #293-4 — blocked ≠ pick-started: распознаваемое сообщение, когда другой
  // интерактивный выбор зума уже владеет холстом (зеркало window_blocked).
  "zoom.object_blocked":
    "ZOOM Object заблокирован: сначала отмените активную команду",
  "zoom.object_selection_no_bounds":
    "ZOOM Object: у выделения нет вычислимых границ",
  // ── PAN command feed / prompt messages (cmd-pan) ──
  "pan.realtime_prompt":
    "PAN: тяните ЛКМ для панорамирования. Esc/Enter — выход.",
  "pan.session_end": "PAN: панорамирование завершено.",
  "pan.displacement": "PAN {arg}",
  "pan.displacement_invalid":
    "-PAN: ожидается 'x1,y1 x2,y2'. Для интерактивного режима введите PAN без аргументов.",
  "pan.displacement_none": "PAN: вид не изменился (нулевое смещение).",
  // ── Command feed: batch / delete / esc ──
  "feed.undo": "ОТМЕНИТЬ",
  "feed.redo": "ПОВТОРИТЬ",
  "feed.undo.mark": "UNDO MARK: группа открыта.",
  "feed.undo.end": "UNDO END: группа зафиксирована.",
  "feed.undo.end.committed":
    "UNDO END: группа зафиксирована в журнале отмены.",
  "feed.undo.end.nested": "UNDO END: уровень вложенности уменьшен.",
  "feed.undo.back": "UNDO BACK: группа отменена.",
  "feed.undo.end.no-batch": "UNDO END: нет открытой группы.",
  "feed.undo.back.no-batch": "UNDO BACK: нет открытой группы.",
  "feed.undo.during-batch":
    "Невозможно отменить во время открытой группы UNDO — сначала UNDO END или UNDO BACK.",
  "feed.undo.empty": "Нечего отменять.",
  "feed.redo.empty": "Нечего повторять.",
  "feed.delete": "УДАЛИТЬ",
  "feed.revcloudproperties.select":
    "ОБЛАКОСВ: выберите облако правок перед вызовом команды.",
  "feed.esc": "ESC",
  "feed.batch.expects_array": "BATCH ожидает JSON-массив строковых команд.",
  "feed.batch.running": "BATCH: выполняется {count} команд…",
  "feed.batch.finished": "BATCH: завершено {count} команд.",
  "feed.batch.parse_error": "BATCH: ошибка разбора: {error}",
  "feed.dxf_error":
    "Не удалось загрузить постоянные срезы из {url}: {failures}",
  // ── Drafting dispatch ──
  "drafting.armed": "{command} активирован в {space}.",
  // ── Drafting accepted-input prompts ──
  "input.idle":
    "Команды, псевдонимы, потоки свойств, импорт DXF и выбор точек в поле черчения.",
  "input.text_content":
    "Текстовое содержимое в командной строке или Esc для отмены.",
  "input.line_continue":
    "Укажите следующую точку в канве, Enter для завершения или C для замыкания к первой точке.",
  "input.line_next": "Укажите следующую точку в канве.",
  "input.circle_radius":
    "Укажите радиус, введите значение радиуса или D для переключения на диаметр.",
  "input.circle_diameter":
    "Введите положительное значение диаметра, затем нажмите Enter для подтверждения.",
  "input.pline_next":
    "Укажите точки в канве, Enter для завершения, C для замыкания, U для отмены последней вершины.",
  "input.pline_continue": "Укажите следующую точку в канве или Esc для отмены.",
  "input.block_insertion":
    "Укажите точку вставки в канве или введите координаты. Esc отменяет.",
  "input.block_insertion_full":
    "Укажите точку вставки в канве или введите координаты вида 1200,3500, @4500,0 или @500<45. Esc отменяет.",
  "input.block_scale":
    "Введите общий коэффициент масштаба или нажмите Enter для 1.",
  "input.block_rotation":
    "Введите угол поворота в градусах или нажмите Enter для 0.",
  "input.arrayrect_points":
    "Укажите базовую точку или противоположный угол в канве, либо введите координаты. Esc отменяет.",
  "input.arraypolar_center":
    "Укажите центр полярного массива в канве или введите координаты. Esc отменяет.",
  "input.arraypath_path":
    "Укажите точку на кривой-пути (отрезок, дуга, окружность или полилиния). Esc отменяет.",
  "input.arraypath_method":
    "Введите D (Поделить) или M (Разметить). Enter принимает D по умолчанию.",
  "input.arraypath_params":
    "Введите количество копий (Поделить) или шаг (Разметить). Enter принимает значение по умолчанию.",
  "input.hatch_corners":
    "Укажите углы области штриховки в канве или введите координаты. Esc отменяет.",
  "input.hatch_internal":
    "Укажите точку внутри замкнутой области. Enter применяет, Esc отменяет.",
  "input.hatch_mode":
    "Введите режим: В (внутренняя точка), О (объекты) или П (прямоугольник). Enter принимает В по умолчанию.",
  "input.viewport_first":
    "Укажите первый угол видового экрана, введите координаты или Fit. Esc отменяет.",
  "input.viewport_second":
    "Укажите противоположный угол видового экрана или введите координаты. Esc отменяет.",
  "input.vpclip_select_viewport":
    "Кликните по видовому экрану для обрезки. Enter отменяет, Esc отменяет.",
  "input.vpclip_first_corner":
    "Укажите первую точку угла границы подрезки или введите координаты. Esc отменяет.",
  "input.vpclip_second_corner":
    "Укажите противоположный угол границы подрезки или введите координаты. Esc отменяет.",
  "input.dim_arc_select":
    "Укажите точку на дуге или окружности в канве или введите координаты. Esc отменяет.",
  "input.dim_line_endpoint":
    "Укажите точку для определения конца отрезка или введите координаты. Esc отменяет.",
  "input.points_generic":
    "Укажите точки в канве или введите абсолютные / относительные координаты вида 1200,3500, @4500,0 или @500<45. Esc отменяет.",
  "input.arrayrect_count":
    "Введите COL=<n> или ROW=<n>, либо укажите противоположный угол мышью. Enter принимает текущие значения.",
  "input.arrayrect_spacing":
    "Введите CS=<n> (расстояние между колонками), RS=<n> (расстояние между строками) или AA=<deg> (угол оси). Enter создаёт массив.",
  "input.arraypolar_count":
    "Введите количество копий (минимум 2). Enter принимает 6 по умолчанию.",
  "input.arraypolar_fill":
    "Введите угол заполнения в градусах (+ против часовой, - по часовой), ROW=<n> для колец, RS=<n> для радиального шага. Enter создаёт массив.",
  "input.hatch_pattern":
    "Введите имя образца, например ANSI31 или SOLID, или нажмите Enter для ANSI31.",
  "input.hatch_ready":
    "Нажмите Enter для создания штриховки с текущим образцом.",
  "input.confirm_or_cancel":
    "Нажмите Enter для подтверждения текущего ввода или Esc для отмены.",
  // ── Drafting command hints (buildCommandHint) ──
  "hint.default":
    "Псевдонимы: L, C, A, PL, REC, DT, MT, B, I, AR, AP, H, BH, DIM, DAL, DAN, DRA, DDI, DOR, MLD, RV, MV. Esc отменяет активную команду.",
  "hint.line_continue":
    "ОТРЕЗОК активен. Укажите следующую точку, Enter завершает, C замыкает к первой точке, Esc отменяет.",
  "hint.line_next": "ОТРЕЗОК активен. Укажите следующую точку.",
  "hint.circle_radius":
    "ОКРУЖНОСТЬ активна. Укажите радиус или введите значение радиуса. D для диаметра. Esc отменяет.",
  "hint.circle_diameter":
    "ОКРУЖНОСТЬ: ввод диаметра активен. Введите положительное значение и нажмите Enter. Esc отменяет.",
  "hint.block_name":
    "ВСТАВКА активна. Введите имя блока или ? для списка, затем укажите точку вставки.",
  "hint.block_scale":
    "ВСТАВКА активна. Введите масштабный коэффициент или Enter для 1.",
  "hint.block_rotation":
    "ВСТАВКА активна. Введите угол поворота или Enter для 0.",
  "hint.arrayrect_params":
    "МАССИВ активен. COL=<n>, ROW=<n>, CS=<n>, RS=<n>, AA=<deg>. Enter подтверждает.",
  "hint.arrayrect_select_or_base":
    "МАССИВ активен. Выберите объекты и укажите базовую точку. Esc отменяет.",
  "hint.arraypolar_params":
    "ПОЛЯРНЫЙ МАССИВ активен. Количество, угол заполнения, ROW=<n>, RS=<n>. Enter подтверждает.",
  "hint.arraypolar_select_or_center":
    "ПОЛЯРНЫЙ МАССИВ активен. Выберите объекты и укажите центр вращения. Esc отменяет.",
  "hint.arraypath_select_or_path":
    "МАССИВ ПО ПУТИ активен. Выберите объекты, затем укажите путь (отрезок, дугу, окружность или полилинию). Esc отменяет.",
  "hint.arraypath_params":
    "МАССИВ ПО ПУТИ активен. Метод (D/M), количество или шаг. Enter подтверждает.",
  "hint.hatch_pattern":
    "ШТРИХ активна. Введите имя образца или Enter для ANSI31.",
  "hint.viewport_first":
    "МВИД активен. Укажите первый угол или введите Fit. Esc отменяет.",
  "hint.vpclip_select_viewport":
    "ВЭКРЕЗ активна. Выберите видовой экран для обрезки. Esc отменяет.",
  "hint.pline":
    "ПЛИНИЯ активна. Enter завершает, C замыкает, U отменяет последнюю вершину, Esc отменяет.",
  "hint.block_insertion":
    "ВСТАВКА активна. Укажите точку вставки или введите координаты. Esc отменяет.",
  "hint.hatch_corners":
    "ШТРИХ активна. Укажите область штриховки. Esc отменяет.",
  "hint.hatch_internal":
    "ШТРИХ активна. Кликните внутри замкнутой области. Enter применяет, Esc отменяет.",
  "hint.viewport_corners":
    "МВИД активен. Укажите противоположные углы видового экрана. Esc отменяет.",
  "hint.vpclip_corners":
    "ВЭКРЕЗ активна. Укажите противоположные углы границы подрезки. Esc отменяет.",
  "hint.generic": "{command} активна. Esc отменяет.",
  // ── Stage status text (buildStageStatusText) ──
  "stage_status.active":
    "{command} активна в {space}. Указывайте точки прямо в канве.",
  "stage_status.empty":
    "Текущее {spaceLower} пусто. Запустите команду, чтобы начать черчение.",
  "stage_status.count_one": "В {space} существует 1 объект.",
  "stage_status.count_many": "В {space} существует {count} объектов.",
  // ── Draft summary / commit messages (shown in history) ──
  "summary.arc_created": "ДУГА создана через три точки.",
  "summary.rectang_created": "ПРЯМОУГ создан по противоположным углам.",
  "summary.polygon_created": "ПОЛИГОН создан с {sides} сторонами.",
  "summary.dimension_created": "РАЗМЕР создан.",
  "summary.dimaligned_created": "РЗМПАРАЛ создан.",
  "summary.dimangular_created": "РЗМУГЛ создан.",
  "summary.dim3point_created": "РЗМ3Т создан.",
  "summary.dimradius_created": "РЗМРАДИУС создан.",
  "summary.dimdiameter_created": "РЗМДИАМ создан.",
  "summary.dimordinate_created": "РЗМОРД создан.",
  "summary.dimbaseline_created": "РЗМБАЗ создан.",
  "summary.dimcontinue_created": "РЗМЦЕПЬ создан.",
  "summary.revcloud_created": "ОБЛАКО создано.",
  "summary.revcloud_payload_invalid":
    "ОБЛАКО: неполные параметры команды (нужны 2 угла либо ≥3 вершин) — облако не создано.",
  "summary.wipeout_created": "ВЫРЕЗ создан.",
  "summary.spline_created": "СПЛАЙН создан.",
  "summary.viewport_created": "Видовой экран создан.",
  "summary.viewport_created_fit": "Видовой экран создан по Fit.",
  "summary.viewport_clip_set": "Подрезка видового экрана {id} задана.",
  "summary.line_closed": "ОТРЕЗОК замкнут.",
  "summary.insert_created": "ВСТАВКА создана.",
  "summary.block_insert_batch_created":
    "ВСТАВКА «{name}» создана ({n} копий).",
  "summary.arrayrect_created": "Массив создан ({n} копий).",
  "summary.arraypolar_created": "Полярный массив создан ({n} копий).",
  "summary.arraypath_created": "Массив по пути создан ({n} копий).",
  "summary.arrayedit_modified": "Массив изменён ({n} копий).",
  "summary.hatchedit_modified": "Штриховка изменена.",
  "summary.hatch_created": "ШТРИХОВКА создана.",
  "summary.text_created": "ТЕКСТ создан.",
  "summary.mtext_created": "МТЕКСТ создан.",
  "summary.mleader_created": "МЛИНИЯ создана.",
  "summary.mleader_leader_added": "Выноска добавлена.",
  "summary.mleader_leader_removed": "Выноска удалена.",
  "summary.mleader_removed": "Мультивыноска удалена.",
  "summary.mleader_not_found": "Мультивыноска не найдена.",
  "summary.mleader_not_multileader":
    "Выбранный объект не является мультивыноской.",
  "summary.mleader_invalid_leader_index": "Неверный индекс выноски.",
  "summary.mleader_confirm_last_leader":
    "Это последняя выноска. Подтвердите удаление мультивыноски.",
  // ── Phase 9 — grip editing audit summaries ──
  "summary.mleader_vertex_moved": "Вершина мультивыноски перемещена.",
  "summary.mleader_text_moved": "Текст мультивыноски перемещён.",
  "summary.mleader_text_rotated": "Текст мультивыноски повёрнут.",
  "summary.mleader_dogleg_adjusted": "Поворот выноски скорректирован.",
  "summary.mleader_vertex_removed": "Вершина мультивыноски удалена.",
  "summary.mleader_vertex_added": "Вершина мультивыноски добавлена.",
  "summary.mleader_invalid_vertex_index":
    "Неверные индексы вершины мультивыноски.",
  // ── Phase 9 — grip context menu items ──
  "grip.menu.add_leader": "Добавить выноску",
  "grip.menu.add_vertex": "Добавить вершину",
  "grip.menu.remove_vertex": "Удалить вершину",
  "grip.menu.stretch": "Растянуть",
  "grip.menu.convert_spline": "Преобразовать в сплайн",
  "grip.menu.convert_polyline": "Преобразовать в полилинию",
  // GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) — command-line
  // echo of the armed hot-grip edit-mode modal prompt ("** STRETCH **" / …),
  // AutoCAD's bracketed mode-cycle token. Enter/Space cycles Stretch → Move →
  // Rotate → Scale → Mirror → (wraps). Distinct from the plain-label
  // "grip.menu.*" context-menu strings above.
  "grip.mode.stretch": "** РАСТЯНУТЬ **",
  "grip.mode.move": "** ПЕРЕНЕСТИ **",
  "grip.mode.rotate": "** ПОВЕРНУТЬ **",
  "grip.mode.scale": "** МАСШТАБ **",
  "grip.mode.mirror": "** ЗЕРКАЛО **",
  "summary.pline_closed": "ПЛИНИЯ замкнута.",
  "summary.pline_finished": "ПЛИНИЯ завершена.",
  "summary.point_created": "ТОЧКА создана в {point}.",
  "summary.base_set": "Базовая точка чертежа установлена в {point}.",
  "summary.circle_created": "ОКРУЖНОСТЬ создана в {point}.",
  "summary.circle_created_diameter":
    "ОКРУЖНОСТЬ создана в {point} с диаметром {diameter}.",
  "summary.circle_created_2p":
    "ОКРУЖНОСТЬ создана: 2 точки диаметра, центр {point}.",
  "summary.circle_created_3p":
    "ОКРУЖНОСТЬ создана через 3 точки, центр {point}.",
  "error.circle.3p_collinear": "Точки коллинеарны — окружность не построена.",
  "error.arc.3p_collinear": "Точки коллинеарны — дуга не построена.",
  "error.hatch.no_boundary":
    "Замкнутая область не найдена. Кликните внутри замкнутой границы.",
  "summary.donut_created": "КОЛЬЦО создано в {point}.",
  "summary.ellipse_created": "ЭЛЛИПС создан в {point}.",
  // ── Entity labels (shown in entity list) ──
  "entity.label.line": "Отрезок {id}",
  "entity.label.point": "Точка {id}",
  "entity.label.circle": "Окружность {id}",
  "entity.label.arc": "Дуга {id}",
  "entity.label.ellipse": "Эллипс {id}",
  "entity.label.polyline": "Полилиния {id}",
  "entity.label.text": "Текст {id}",
  "entity.label.mtext": "МТекст {id}",
  "entity.label.block": "{name} {id}",
  "entity.label.array": "Массив {id}",
  "entity.label.arrayrect": "Массив {id}",
  "entity.label.hatch": "Штриховка {id}",
  "entity.label.dimension": "Размер {id}",
  "entity.label.mleader": "Мультивыноска {id}",
  "entity.label.revcloud": "Облако правок {id}",
  "entity.label.wipeout": "Вырез {id}",
  "entity.label.viewport": "Видовой экран {id}",
  // ── Preview entity labels ──
  "preview.draft_line": "Предпросмотр отрезка",
  "preview.draft_circle": "Предпросмотр окружности",
  "preview.arc_chord": "Предпросмотр хорды дуги",
  "preview.draft_arc": "Предпросмотр дуги",
  "preview.draft_ellipse": "Предпросмотр эллипса",
  "preview.draft_polyline": "Предпросмотр полилинии",
  "preview.draft_spline": "Предпросмотр сплайна",
  "preview.draft_rectangle": "Предпросмотр прямоугольника",
  "preview.draft_mtext": "Предпросмотр мтекста",
  "preview.draft_insert": "Предпросмотр вставки",
  "preview.draft_array": "Предпросмотр массива",
  "preview.draft_arrayrect": "Предпросмотр прямоугольного массива",
  "preview.draft_block": "Предпросмотр блока",
  "preview.draft_hatch": "Предпросмотр штриховки",
  "preview.draft_dimension_baseline": "Базовая линия размера",
  "preview.draft_dimension": "Предпросмотр размера",
  "preview.draft_ordinate_dimension": "Предпросмотр ординатного размера",
  "preview.draft_leader": "Предпросмотр выноски",
  "preview.draft_revcloud": "Предпросмотр облака правок",
  "preview.draft_wipeout": "Предпросмотр выреза",
  "preview.draft_viewport": "Предпросмотр видового экрана",
  "preview.draft_vpclip": "Предпросмотр границы подрезки видового экрана",
  "preview.draft_donut": "Предпросмотр кольца",
  "preview.draft_radius_dim": "Предпросмотр радиусного размера",
  "preview.draft_diameter_dim": "Предпросмотр диаметрального размера",
  "suggestion.coordinate_input": "Ввод координат",
  "keyword.close": "Замкнуть",
  "keyword.diameter": "Диаметр",
  "keyword.undo": "Отменить",
  "keyword.yes": "Да",
  "keyword.no": "Нет",
  "keyword.revcloud.arc_length": "Длина дуги",
  "keyword.revcloud.object": "Объект",
  "keyword.revcloud.rectangular": "Прямоугольное",
  "keyword.revcloud.polygonal": "Полигональное",
  "keyword.revcloud.freehand": "От руки",
  "keyword.revcloud.style": "Стиль",
  "keyword.revcloud.style_normal": "Обычный",
  "keyword.revcloud.style_calligraphy": "Каллиграфия",
  // ── HATCH-EDIT-DIALOG (§5.1) — pattern type + island-detection style ──
  "keyword.hatch.pattern_predefined": "Стандартный",
  "keyword.hatch.pattern_user_defined": "Пользовательский",
  "keyword.hatch.pattern_custom": "Заказной",
  "keyword.hatch.island_normal": "Обычный",
  "keyword.hatch.island_outer": "Внешний",
  "keyword.hatch.island_ignore": "Игнорировать",
  // ── Drawing stage (PrimitiveAtlasStage) ──
  "stage_card.title": "Поле черчения",
  "stage_card.model_canvas": "Модельное пространство",
  "stage_card.layout_canvas": "{name} (холст)",
  "stage_card.entities_count_one": "1 объект",
  "stage_card.entities_count_many": "{count} объектов",
  "stage_card.context.zoom_title": "Масштаб",
  "stage_card.context.zoom_extents": "Масштаб по границам",
  "stage_card.context.zoom_all": "Масштаб все",
  "stage_card.context.zoom_window": "Масштаб окном",
  "stage_card.context.zoom_object": "Масштаб по объекту",
  "stage_card.context.zoom_previous": "Предыдущий масштаб",
  // ── Multileader Style Editor dialog ──
  "mleaderstyle.editor.title": "Редактор стилей мультивыносок: {name}",
  "mleaderstyle.editor.current_style": "Текущий стиль",
  "mleaderstyle.editor.new_style_name": "Имя нового стиля...",
  "mleaderstyle.editor.based_on": "На основе",
  "mleaderstyle.editor.tabs.format": "Формат выноски",
  "mleaderstyle.editor.tabs.structure": "Структура выноски",
  "mleaderstyle.editor.tabs.content": "Содержимое",
  "mleaderstyle.editor.tabs.block": "Блок-содержимое",
  "mleaderstyle.actions.new": "Новый стиль",
  "mleaderstyle.actions.delete": "Удалить",
  "mleaderstyle.actions.save": "Сохранить",
  "mleaderstyle.actions.cancel": "Отмена",
  "mleaderstyle.actions.delete_confirm": "Удалить стиль «{name}»?",
  "mleaderstyle.actions.delete_standard_error":
    "Стиль Standard нельзя удалить.",
  "mleaderstyle.actions.duplicate_name_error":
    "Стиль с таким именем уже существует.",
  "mleaderstyle.actions.empty_name_error": "Укажите имя стиля.",
  // Tab 1 — Leader Format
  "mleaderstyle.format.type": "Тип выноски (171)",
  "mleaderstyle.format.type.straight": "Прямая",
  "mleaderstyle.format.type.spline": "Сплайн",
  "mleaderstyle.format.type.none": "Нет",
  "mleaderstyle.format.color": "Цвет линии (91)",
  "mleaderstyle.format.linetype": "Тип линии (173)",
  "mleaderstyle.format.weight": "Вес линии (90)",
  "mleaderstyle.format.arrow_block": "Стрелка (342)",
  "mleaderstyle.format.arrow_size": "Размер стрелки (44)",
  "mleaderstyle.format.break_enabled": "Прерывание выноски (293)",
  "mleaderstyle.format.break_gap": "Зазор прерывания (143)",
  "mleaderstyle.format.break_size": "Размер прерывания",
  // Tab 2 — Leader Structure
  "mleaderstyle.structure.segments": "Макс. сегментов (90)",
  "mleaderstyle.structure.landing": "Подвод включён (290)",
  "mleaderstyle.structure.dogleg": "Поворот включён (291)",
  "mleaderstyle.structure.dogleg_length": "Длина поворота (42)",
  "mleaderstyle.structure.landing_gap": "Зазор подвода (43)",
  "mleaderstyle.structure.base_point_x": "Базовая точка X",
  "mleaderstyle.structure.base_point_y": "Базовая точка Y",
  "mleaderstyle.structure.scale": "Общий масштаб (142)",
  "mleaderstyle.structure.annotative": "Аннотативный (296)",
  // Tab 3 — Content
  "mleaderstyle.content.type": "Тип содержимого (170)",
  "mleaderstyle.content.type.block": "Блок",
  "mleaderstyle.content.type.mtext": "МТекст",
  "mleaderstyle.content.type.none": "Нет",
  "mleaderstyle.content.text_style": "Стиль текста (340)",
  "mleaderstyle.content.text_height": "Высота текста (40)",
  "mleaderstyle.content.text_width": "Ширина текста (41)",
  "mleaderstyle.content.text_color": "Цвет текста (93)",
  "mleaderstyle.content.attachment": "Привязка текста (172)",
  "mleaderstyle.content.attachment.top": "Сверху",
  "mleaderstyle.content.attachment.middle": "По центру",
  "mleaderstyle.content.attachment.bottom": "Снизу",
  "mleaderstyle.content.attachment.left": "Слева",
  "mleaderstyle.content.attachment.right": "Справа",
  "mleaderstyle.content.attachment_direction": "Направление привязки (271)",
  "mleaderstyle.content.attachment_direction.horizontal": "Горизонтальное",
  "mleaderstyle.content.attachment_direction.vertical": "Вертикальное",
  "mleaderstyle.content.frame": "Рамка текста (292)",
  "mleaderstyle.content.linespacing": "Стиль межстрочный (73)",
  "mleaderstyle.content.linespacing.at_least": "Как минимум",
  "mleaderstyle.content.linespacing.exactly": "Точно",
  "mleaderstyle.content.linespacing.multiple": "Множитель",
  "mleaderstyle.content.linespacing_factor": "Фактор межстрочный (45)",
  "mleaderstyle.content.linespacing_distance": "Расстояние межстрочное",
  // Tab 4 — Block Content
  "mleaderstyle.block.name": "Имя блока (343)",
  "mleaderstyle.block.scale": "Масштаб блока (47)",
  "mleaderstyle.block.color": "Цвет блока (93)",
  "mleaderstyle.block.rotation": "Поворот блока (48)",
  "mleaderstyle.block.connection": "Тип подключения (177)",
  "mleaderstyle.block.connection.center_extents": "Центр габаритов",
  "mleaderstyle.block.connection.insertion_point": "Точка вставки",
  // Properties panel trigger
  "panel.manage_mleader_styles": "Управление стилями мультивыносок",
  "panel.manage_mleader_styles_hint":
    "Открывает редактор для всех стилей мультивыносок.",
  // ── Property labels (shown in Properties panel) ──
  "property.layer": "Слой",
  "property.color": "Цвет",
  "property.linetype": "Тип линий",
  "property.lineweight": "Вес линий",
  "property.thickness": "Толщина",
  "property.start_x": "Начальная X",
  "property.start_y": "Начальная Y",
  // #317 residual R2 — LINE's editable "Start Z"/"End Z" rows
  // (lineEditableProperties, dxfGeometry.ts) had no translation key: the
  // fallback in `translatePropertyLabel` (PropertyGrid.tsx) shows the raw
  // English label when a key is missing, leaving these two rows untranslated
  // inside an otherwise-Russian grid.
  "property.start_z": "Начальная Z",
  "property.end_x": "Конечная X",
  "property.end_y": "Конечная Y",
  "property.end_z": "Конечная Z",
  "property.length": "Длина",
  "property.angle": "Угол",
  "property.center_x": "Центр X",
  "property.center_y": "Центр Y",
  "property.center_z": "Центр Z",
  "property.radius": "Радиус",
  "property.diameter": "Диаметр",
  "property.circumference": "Длина окружности",
  "property.area": "Площадь",
  "property.start_angle": "Начальный угол",
  "property.end_angle": "Конечный угол",
  "property.total_angle": "Полный угол",
  "property.arc_length": "Длина дуги",
  "property.vertices": "Вершины",
  "property.closed": "Замкнутая",
  "property.constant_width": "Постоянная ширина",
  "property.elevation": "Отметка",
  "property.linetype_generation": "Генерация типа линий",
  // Классическая «тяжёлая» полилиния (POLYLINE): 40/41 — ширины по умолчанию,
  // которые наследует каждая вершина без собственных 40/41 (термины AutoCAD:
  // «Начальная ширина сегмента» / «Конечная ширина сегмента»).
  "property.start_segment_width": "Начальная ширина сегмента",
  "property.end_segment_width": "Конечная ширина сегмента",
  "property.polyline_type": "Тип полилинии",
  "property.m_vertex_count": "Число вершин M",
  "property.n_vertex_count": "Число вершин N",
  "property.smooth_surface_m_density": "Плотность сглаживания M",
  "property.smooth_surface_n_density": "Плотность сглаживания N",
  "property.surface_type": "Тип поверхности",
  // Таблица (ACAD_TABLE).
  "property.table_style": "Стиль таблицы",
  // "property.rows" / "property.columns" already exist (the array kinds use
  // the SAME labels) — the table reuses them rather than adding a duplicate.
  "property.table_width": "Ширина таблицы",
  "property.table_height": "Высота таблицы",
  // OLE-объект (OLE2FRAME): углы рамки в МСК + дескрипторы OLE-записи.
  "property.upper_left_x": "Верхний левый X",
  "property.upper_left_y": "Верхний левый Y",
  "property.lower_right_x": "Нижний правый X",
  "property.lower_right_y": "Нижний правый Y",
  "property.ole_object_type": "Тип OLE-объекта",
  "property.ole_version": "Версия OLE",
  "property.draw_aspect": "Аспект отображения",
  "property.data_size": "Размер данных",
  "property.contents": "Содержимое",
  "property.text_style": "Стиль текста",
  "property.height": "Высота",
  "property.rotation": "Поворот",
  "property.width_factor": "Коэффициент ширины",
  "property.oblique_angle": "Наклон",
  "property.justify": "Выравнивание",
  "property.insertion_x": "Вставка X",
  "property.insertion_y": "Вставка Y",
  "property.alignment_x": "Выравнивание X",
  "property.alignment_y": "Выравнивание Y",
  "property.transparency": "Прозрачность",
  "property.plot_style": "Стиль печати",
  "property.material": "Материал",
  "property.annotative": "Аннотативный",
  "property.width": "Ширина",
  "property.attachment": "Привязка",
  "property.direction": "Направление",
  "property.line_spacing": "Межстрочный",
  "property.background_fill": "Фоновая заливка",
  "property.linetype_scale": "Масштаб типа линий",
  "property.subtype": "Подтип",
  "property.dimension_style": "Размерный стиль",
  "property.text_override": "Переопределение текста",
  "property.text_height": "Высота текста",
  "property.text_rotation": "Поворот текста",
  "property.extension_1_x": "Выносная 1 X",
  "property.extension_1_y": "Выносная 1 Y",
  "property.extension_2_x": "Выносная 2 X",
  "property.extension_2_y": "Выносная 2 Y",
  "property.dimension_line_x": "Размерная линия X",
  "property.dimension_line_y": "Размерная линия Y",
  "property.third_point_x": "Третья точка X",
  "property.third_point_y": "Третья точка Y",
  "property.arc_point_x": "Точка дуги X",
  "property.arc_point_y": "Точка дуги Y",
  "property.leader_length": "Длина выноски",
  "property.anonymous_block": "Анонимный блок",
  "property.measurement": "Измерение",
  "property.multileader_style": "Стиль мультивыноски",
  "property.arrow_x": "Стрелка X",
  "property.arrow_y": "Стрелка Y",
  "property.landing_x": "Посадка X",
  "property.landing_y": "Посадка Y",
  "property.arrowhead_size": "Размер стрелки",
  "property.arrowhead_block": "Блок стрелки",
  "property.text_attachment": "Привязка текста",
  "property.text_direction": "Направление текста",
  "property.text_frame": "Рамка текста",
  "property.line_spacing_style": "Стиль межстрочного",
  "property.line_spacing_factor": "Фактор межстрочного",
  "property.leader_color": "Цвет выноски",
  "property.leader_weight": "Вес выноски",
  "property.leader_type": "Тип выноски",
  "property.block_name": "Имя блока",
  "property.block_scale": "Масштаб блока",
  "property.block_rotation": "Поворот блока",
  "property.override_flags": "Флаги переопределения",
  "property.landing_gap": "Зазор подвода",
  "property.leader_count": "Кол-во выносок",
  "property.cloud_style": "Стиль облака",
  "property.approximate_arc_length": "Длина дуги (прибл.)",
  "property.minimum_arc_length": "Мин. длина дуги",
  "property.creation_mode": "Способ построения",
  "property.corner_1_x": "Угол 1 X",
  "property.corner_1_y": "Угол 1 Y",
  "property.corner_2_x": "Угол 2 X",
  "property.corner_2_y": "Угол 2 Y",
  "property.pattern": "Образец",
  "property.scale": "Масштаб",
  "property.associative": "Ассоциативная",
  "property.hatch_style": "Стиль штриховки",
  "property.pattern_type": "Тип образца",
  "property.double": "Двойная",
  "property.name": "Имя",
  "property.scale_x": "Масштаб X",
  "property.scale_y": "Масштаб Y",
  "property.scale_z": "Масштаб Z",
  "property.column_count": "Кол-во столбцов",
  "property.row_count": "Кол-во строк",
  "property.column_spacing": "Шаг столбцов",
  "property.row_spacing": "Шаг строк",
  "property.rows": "Строки",
  "property.columns": "Столбцы",
  "property.base_x": "База X",
  "property.base_y": "База Y",
  "property.display_lock": "Блокировка отображения",
  "property.shade_plot": "Стиль визуализации",
  "property.view_height": "Высота вида",
  // Key-completeness gate (`propertyLabelKeyCompleteness.test.ts`) — the 21
  // editable-property labels below were emitted by `buildShellEntityPreview`
  // but had NO `property.*` translation, so a Russian user selecting a
  // Point/Ellipse/Spline/Wipeout/Viewport/ARRAYRECT/dynamic-Block saw the
  // raw English caption inside the grid (same class of defect as
  // `start_z`/`end_z`, discovered by the new census gate).
  "property.position_x": "Положение X",
  "property.position_y": "Положение Y",
  "property.position_z": "Положение Z",
  "property.major_axis_x": "Большая ось X",
  "property.major_axis_y": "Большая ось Y",
  "property.ratio": "Соотношение осей",
  "property.start_parameter": "Начальный параметр",
  "property.end_parameter": "Конечный параметр",
  "property.degree": "Степень",
  "property.rational": "Рациональный",
  "property.fit_points": "Точки аппроксимации",
  "property.control_points": "Контрольные точки",
  "property.knots": "Узлы",
  "property.weights": "Веса",
  "property.frame": "Рамка",
  "property.vertex_count": "Количество вершин",
  "property.axis_angle": "Угол оси",
  "property.view_center_x": "Центр вида X",
  "property.view_center_y": "Центр вида Y",
  "property.view_twist": "Поворот вида",
  "property.allow_non-uniform_scale": "Разрешить неравномерное масштабирование",
  // ── Attachment position labels (MText grid) ──
  "property.attachment.top_left": "Сверху слева",
  "property.attachment.top_center": "Сверху по центру",
  "property.attachment.top_right": "Сверху справа",
  "property.attachment.middle_left": "По центру слева",
  "property.attachment.middle_center": "По центру",
  "property.attachment.middle_right": "По центру справа",
  "property.attachment.bottom_left": "Снизу слева",
  "property.attachment.bottom_center": "Снизу по центру",
  "property.attachment.bottom_right": "Снизу справа",
  // ── Audit / error messages (state files) ──
  "audit.properties_shell_activated":
    "Оболочка свойств активирована из командной строки.",
  "audit.model_space_activated":
    "Активировано модельное пространство из командной строки.",
  "audit.mspace_activated":
    "Контекст листа переключён на модель-в-видовом-экране.",
  "audit.pspace_activated": "Контекст листа переключён на пространство листа.",
  "audit.space_activated": "Вкладка «{name}» активирована.",
  "audit.matchprop_source_cleared": "Источник MATCHPROP очищен.",
  "audit.selection_cleared": "Выделение очищено.",
  "audit.delete_skipped_no_match":
    "Удаление пропущено, так как не найдено совпадающих выбранных объектов.",
  "audit.imported_line": "Импортирована линия из DXF.",
  "audit.imported_point": "Импортирована точка из DXF.",
  "audit.imported_circle": "Импортирована окружность из DXF.",
  "audit.imported_arc": "Импортирована дуга из DXF.",
  "audit.imported_ellipse": "Импортирован эллипс из DXF.",
  "audit.imported_spline": "Импортирован сплайн из DXF.",
  "audit.imported_polyline": "Импортирована полилиния из DXF.",
  "audit.imported_text": "Импортирован текст из DXF.",
  "audit.imported_mtext": "Импортирован многострочный текст из DXF.",
  "audit.imported_dimension": "Импортирован размер из DXF.",
  "audit.imported_multileader": "Импортирована мультивыноска из DXF.",
  "audit.imported_revcloud": "Импортировано облако правок из DXF.",
  "audit.imported_wipeout": "Импортирован вырез (WIPEOUT) из DXF.",
  "audit.imported_hatch": "Импортирована штриховка из DXF.",
  "audit.imported_block": "Импортирована вставка блока из DXF.",
  "audit.imported_array": "Импортирован прямоугольный массив из DXF.",
  "audit.imported_viewport": "Импортирован видовой экран из DXF.",
  // Наследуемая «тяжёлая» полилиния (POLYLINE + VERTEX), таблица (ACAD_TABLE)
  // и OLE-объект (OLE2FRAME).
  "audit.imported_heavy_polyline":
    "Импортирована классическая полилиния (POLYLINE) из DXF.",
  "audit.imported_table": "Импортирована таблица (ACAD_TABLE) из DXF.",
  "audit.imported_ole_frame": "Импортирован OLE-объект (OLE2FRAME) из DXF.",
  // Defect fix (command-created-line-provenance) — command-authored
  // counterparts of the "audit.imported_*" family above, shown when the
  // entity was created/restored by a command, agent, or collaboration
  // edit in THIS session rather than loaded from a DXF file.
  "audit.authored_line": "Линия создана командой.",
  "audit.authored_point": "Точка создана командой.",
  "audit.authored_circle": "Окружность создана командой.",
  "audit.authored_arc": "Дуга создана командой.",
  "audit.authored_ellipse": "Эллипс создан командой.",
  "audit.authored_spline": "Сплайн создан командой.",
  "audit.authored_polyline": "Полилиния создана командой.",
  "audit.authored_text": "Текст создан командой.",
  "audit.authored_mtext": "Многострочный текст создан командой.",
  "audit.authored_dimension": "Размер создан командой.",
  "audit.authored_multileader": "Мультивыноска создана командой.",
  "audit.authored_revcloud": "Облако правок создано командой.",
  "audit.authored_wipeout": "Вырез (WIPEOUT) создан командой.",
  "audit.authored_hatch": "Штриховка создана командой.",
  "audit.authored_block": "Вставка блока создана командой.",
  "audit.authored_array": "Прямоугольный массив создан командой.",
  "audit.authored_viewport": "Видовой экран создан командой.",
  "audit.authored_heavy_polyline":
    "Классическая полилиния (POLYLINE) создана командой.",
  "audit.authored_table": "Таблица (ACAD_TABLE) создана командой.",
  "audit.authored_ole_frame": "OLE-объект (OLE2FRAME) создан командой.",
  "viewport.label": "ВЭ",
  "viewport.entity_label": "Видовой экран {id}",
  "viewport.scale.custom": "Другой…",
  // ── Phase MC5 — ViewportInspector i18n strings (RU) ──────────────────
  "vp.inspector.title": "Видовой экран",
  "vp.inspector.scale": "Масштаб",
  "vp.inspector.scale.custom": "Другой…",
  "vp.inspector.scale.num": "Числитель",
  "vp.inspector.scale.denom": "Знаменатель",
  "vp.inspector.lock": "Заблокировать",
  // Shade-plot mode (DXF 170) — plot-output только, не влияет на экран.
  // AutoCAD CIS: «Печать с тонированием» (свойство ВЭ на палитре свойств).
  "vp.inspector.shadeplot": "Печать с тонированием",
  "vp.inspector.shadeplot.as_displayed": "Как на экране",
  "vp.inspector.shadeplot.wireframe": "Каркас",
  "vp.inspector.shadeplot.hidden": "Скрытие линий",
  "vp.inspector.shadeplot.rendered": "Тонирование",
  // Fallback label shown when a stored value is outside the supported 0..3
  // range (e.g. imported mode 4 Visual Style / 5 Render Preset — deferred).
  "vp.inspector.shadeplot.other": "Другой ({mode})",
  "vp.inspector.layers": "Слои",
  "vp.inspector.layers.frozen": "Заморожен в ВЭ",
  "vp.inspector.ucs": "ПСК",
  "vp.inspector.clip": "Обрезка",
  "vp.inspector.clip.rect": "Прямоугольная",
  "vp.inspector.clip.polygonal": "Многоугольная",
  // ── Status-bar VP controls (RU) ──────────────────────────────────────────
  "vp.statusbar.scale.label": "Масштаб ВЭ",
  "vp.statusbar.scale.custom": "Другой…",
  "vp.statusbar.lock_viewport": "Заблокировать видовой экран",
  "vp.statusbar.unlock_viewport": "Разблокировать видовой экран",
  "audit.dimstyle_new_requires_name": "DIMSTYLE New требует имя стиля.",
  "audit.dimstyle_created":
    'Размерный стиль "{name}" создан (скопирован из Standard).',
  "audit.dimstyle_delete_requires_name": "DIMSTYLE Delete требует имя стиля.",
  "audit.dimstyle_deleted": 'Размерный стиль "{name}" удалён.',
  "audit.dimstyle_rename_requires_names":
    "DIMSTYLE Rename требует старое и новое имя.",
  "audit.dimstyle_renamed":
    'Размерный стиль "{oldName}" переименован в "{newName}".',
  "audit.dimstyle_not_exist": 'Размерный стиль "{name}" не существует.',
  "audit.dimstyle_set_current":
    'Размерный стиль "{name}" сделан текущим.',
  "audit.dimstyle_action": "DIMSTYLE {action} {name}.",
  "audit.dimstyle_apply_current":
    "DIMSTYLE Apply применяет текущий стиль к выбранным размерам.",
  "audit.dimstyle_requires_name": "DIMSTYLE {action} требует имя стиля.",
  "audit.command_not_wired":
    "{text} записан в командной строке. Исполнитель геометрии пока не подключён.",
  "audit.cecolor_set": "Текущий цвет установлен: {color}.",
  "error.matchprop_source_requires_one":
    "MATCHPROP SOURCE требует ровно один выбранный объект.",
  "error.chprop_requires_selection": "CHPROP требует активного выделения.",
  "error.matchprop_requires_targets":
    "MATCHPROP требует целевые объекты в текущем выделении.",
  "error.matchprop_no_source":
    "MATCHPROP требует предварительно заданный исходный объект.",
  "error.matchprop_no_target":
    "MATCHPROP требует хотя бы один целевой объект, отличный от источника.",
  "error.dimstyle_name_empty": "Имя размерного стиля не может быть пустым.",
  "error.dimstyle_already_exists": 'Размерный стиль "{name}" уже существует.',
  "error.dimstyle_no_base": "Нет базового размерного стиля для копирования.",
  "error.dimstyle_cannot_delete_standard":
    'Невозможно удалить размерный стиль "Standard".',
  "error.dimstyle_not_found": 'Размерный стиль "{name}" не существует.',
  "error.dimstyle_cannot_rename_standard":
    'Невозможно переименовать размерный стиль "Standard".',
  "error.dimstyle_new_name_empty":
    "Новое имя размерного стиля не может быть пустым.",
  "error.mleaderstyle_name_empty":
    "Имя стиля мультивыноски не может быть пустым.",
  "error.mleaderstyle_already_exists":
    'Стиль мультивыноски "{name}" уже существует.',
  "error.mleaderstyle_no_base":
    "Нет базового стиля мультивыноски для копирования.",
  "error.mleaderstyle_cannot_delete_standard":
    'Невозможно удалить стиль мультивыноски "Standard".',
  "error.mleaderstyle_not_found": 'Стиль мультивыноски "{name}" не существует.',
  // ── Demo entity labels & summaries ──
  "demo.drawing.title": "Командно-безопасная оболочка редактора (Этап A)",
  "demo.transcript.shell_ready":
    "Оболочка Этапа A готова: UI маршрутизирует через dispatch(command) -> reducer -> state/audit.",
  "demo.line.label": "Осевая линия",
  "demo.line.summary":
    "Отрезок модельного пространства с редактируемыми общими свойствами объекта.",
  "demo.circle.label": "Окружность circle-main",
  "demo.circle.summary": "Предпросмотр окружности в модельном пространстве.",
  "demo.arc.label": "Дуга arc-main",
  "demo.arc.summary": "Предпросмотр дуги в модельном пространстве.",
  "demo.hatch.label": "Штриховка ядра",
  "demo.hatch.summary":
    "Ассоциативная штриховка, требующая отдельного графового редактора.",
  "demo.block.label": "Блок насосного агрегата",
  "demo.block.summary":
    "Предпросмотр блока с явными семантиками имени, вставки и масштабирования.",
  "demo.array.label": "Массив стеллажей",
  "demo.array.summary":
    "Предпросмотр ассоциативного массива с явными параметрами строк и столбцов.",
  "demo.polyline.label": "Полилиния маршрута",
  "demo.polyline.summary":
    "Предпросмотр полилинии с семантиками ширины и замкнутости в карте свойств.",
  "demo.text.label": "Заметка на сетке",
  "demo.text.summary":
    "Предпросмотр однострочного текста с явными полями стиля и выравнивания.",
  "demo.mtext.label": "Общие примечания",
  "demo.mtext.summary":
    "Предпросмотр многострочной аннотации с семантиками ширины и фоновой заливки.",
  "demo.dimension.label": "Размер пролёта",
  "demo.dimension.summary":
    "Предпросмотр размера с раздельными семантиками стиля и измерения.",
  "demo.viewport.label": "Основной видовой экран",
  "demo.viewport.summary":
    "Оболочка видового экрана с учётом блокировки, масштаба и переопределений.",
  "demo.revcloud.label": "Облако изменений",
  "demo.revcloud.summary":
    "Пометка ревизии с поведенческой моделью и выделенным контрактом свойств.",
  "demo.mleader.label": "Выноска-пометка",
  "demo.mleader.summary":
    "Аннотационный объект, чей граф стиля/содержимого должен оставаться явным.",
  // ── Demo viewport & space names ──
  "demo.viewport.main_plan": "Основной план",
  "demo.viewport.detail_callout": "Выносной фрагмент",
  "demo.viewport.assembly": "Сборочный видовой экран",
  "demo.space.model_setup": "Мировая ПСК / командно-безопасная оболочка",
  "demo.space.a3_issue": "A3 лист выпуска",
  // ── Properties view model ──
  "props.default_title": "Свойства объектов по умолчанию",
  "props.no_selection": "Нет выделения",
  "props.default_note":
    "Этот срез явно сохраняет контракты Свойства / CHPROP / MATCHPROP, но пока не заявляет полный паритет.",
  "props.multi_title": "{count} выделенных объектов",
  "props.multi_select": "Множественное выделение",
  "props.multi_note":
    "Только общие контракты свойств должны отображаться при смешанном выделении. Графовые семантики остаются за пределами общих правил копирования.",
  "props.varies": "*Различается*",
  // ── Layer Manager ──
  "layer.manager_title": "Менеджер слоёв",
  "layer.new": "Новый слой",
  "layer.delete": "Удалить",
  "layer.set_current": "Текущий",
  "layer.close": "Закрыть",
  "layer.column.status": "Ст.",
  "layer.column.name": "Имя",
  "layer.column.on": "Вкл",
  "layer.column.freeze": "Замор.",
  "layer.column.lock": "Блок.",
  "layer.column.color": "Цвет",
  "layer.column.linetype": "Тип линий",
  "layer.column.lineweight": "Вес линий",
  "layer.column.plot": "Печать",
  "layer.column.count": "Объектов",
  "layer.on": "Включён",
  "layer.off": "Выключен",
  "layer.frozen": "Заморожен",
  "layer.thawed": "Разморожен",
  "layer.locked": "Заблокирован",
  "layer.unlocked": "Разблокирован",
  "layer.plottable": "Печатается",
  "layer.not_plottable": "Не печатается",
  "layer.cannot_delete_0": "Нельзя удалить слой «0».",
  "layer.cannot_delete_current": "Нельзя удалить текущий слой.",
  "layer.cannot_rename_0": "Нельзя переименовать слой «0».",
  "layer.name_duplicate": "Слой с таким именем уже существует.",
  "layer.new_default_name": "Слой",
  "layer.lineweight_default": "По умолчанию",
  "layer.lineweight_bylayer": "По слою",
  "layer.lineweight_byblock": "По блоку",
  "panel.manage_layers": "Управление слоями",
  "panel.manage_layers_hint": "Создание, настройка и удаление слоёв.",
  "error.layer_name_empty": "Имя слоя не может быть пустым.",
  "error.layer_not_found": "Слой «{name}» не найден.",
  // ── Modification commands: MOVE, COPY, MIRROR, ERASE ──
  "command.move.label": "Перенести",
  "command.copy.label": "Копировать",
  "command.mirror.label": "Зеркало",
  "command.erase.label": "Стереть",
  "prompt.move.select": "ПЕРЕНЕСТИ: Выберите объекты.",
  "prompt.move.base": "ПЕРЕНЕСТИ: Укажите базовую точку.",
  "prompt.move.destination": "ПЕРЕНЕСТИ: Укажите точку назначения.",
  "prompt.copy.select": "КОПИРОВАТЬ: Выберите объекты.",
  // AutoCAD-parity base-point prompt — includes the [Смещение] keyword
  // so power users can type "С" or "Displacement" to switch to
  // single-vector mode. The legacy "prompt.copy.base" key is kept for
  // backward-compatible callers.
  "prompt.copy.base": "КОПИРОВАТЬ: Укажите базовую точку.",
  "prompt.copy.base_point": "КОПИРОВАТЬ: Укажите базовую точку.",
  "prompt.copy.base_or_displacement":
    "КОПИРОВАТЬ: Базовая точка или [Смещение]:",
  "prompt.copy.destination":
    "КОПИРОВАТЬ: Укажите точку назначения или Enter для выхода.",
  "prompt.copy.second_point": "КОПИРОВАТЬ: Укажите вторую точку.",
  "prompt.copy.second_or_exit":
    "КОПИРОВАТЬ: Вторая точка или [Выход/Отменить] <Выход>:",
  "prompt.copy.displacement": "КОПИРОВАТЬ: Укажите смещение <0,0,0>:",
  "prompt.mirror.select": "ЗЕРКАЛО: Выберите объекты.",
  "prompt.mirror.first": "ЗЕРКАЛО: Укажите первую точку оси.",
  "prompt.mirror.second": "ЗЕРКАЛО: Укажите вторую точку оси.",
  "audit.entities_moved": "Объекты перенесены.",
  "audit.entities_copied": "Объекты скопированы.",
  "audit.entities_mirrored": "Объекты отзеркалены.",
  // AutoCAD-parity COPY audit. Parametrised by the number of source
  // entities cloned in the iteration; command echo format matches
  // AutoCAD's "COPY: 2 объектов скопировано." command-line message.
  "audit.copy": "КОПИРОВАТЬ: скопировано объектов: {n}.",
  "summary.move": "ПЕРЕНЕСТИ завершён.",
  "summary.copy": "КОПИРОВАТЬ завершён.",
  "summary.copy_created": "КОПИРОВАТЬ: создано копий: {n}.",
  "feed.copy.nothing_selected":
    "КОПИРОВАТЬ: не выбрано ни одного объекта для копирования.",
  "summary.mirror": "ЗЕРКАЛО завершён.",
  // Modification commands: ROTATE, SCALE, OFFSET, STRETCH
  "command.rotate.label": "Повернуть",
  "command.scale.label": "Масштаб",
  "command.offset.label": "Подобие",
  "command.stretch.label": "Растянуть",
  "prompt.rotate.select": "ПОВЕРНУТЬ: Выберите объекты.",
  "prompt.rotate.base": "ПОВЕРНУТЬ: Укажите базовую точку.",
  "prompt.rotate.angle": "ПОВЕРНУТЬ: Укажите угол поворота, точку или [Спр].",
  "prompt.rotate.ref_start": "ПОВЕРНУТЬ: Укажите начало опорного отрезка:",
  "prompt.rotate.ref_end": "ПОВЕРНУТЬ: Укажите конец опорного отрезка:",
  "prompt.rotate.ref_new_angle": "ПОВЕРНУТЬ: Введите новый угол:",
  "prompt.scale.select": "МАСШТАБ: Выберите объекты.",
  "prompt.scale.base": "МАСШТАБ: Укажите базовую точку.",
  "prompt.scale.factor": "МАСШТАБ: Укажите масштабный коэффициент или точку.",
  "prompt.offset.distance": "ПОДОБИЕ: Укажите расстояние смещения.",
  "prompt.offset.select": "ПОДОБИЕ: Выберите объект для смещения.",
  "prompt.offset.side": "ПОДОБИЕ: Укажите точку с нужной стороны.",
  "prompt.stretch.first": "РАСТЯНУТЬ: Укажите первый угол рамки выбора.",
  "prompt.stretch.second": "РАСТЯНУТЬ: Укажите второй угол рамки выбора.",
  "prompt.stretch.base": "РАСТЯНУТЬ: Укажите базовую точку.",
  "prompt.stretch.destination": "РАСТЯНУТЬ: Укажите точку назначения.",
  // N4 GRIPS-ARMED-STRETCH-I18N — armed-grip CLICK-ARM modal echo (distinct
  // namespace from the classic multi-step STRETCH command's `prompt.stretch.*`
  // keys above, which are for the crossing-window STRETCH *command*). The
  // "** MODE **" banner line reuses the existing `grip.mode.*` keys (see
  // `gripInteractionConstants.ts`'s `GRIP_ARMED_PROMPT_KEYS`) rather than a
  // parallel `.mode` key per mode — only the imperative "point" prompt line
  // needs its own key here, one per mode (GRIPS-INTERACTION PR-2 origin/main
  // re-sync extended this from stretch-only to all 5 cycle modes).
  "prompt.grip.armed.stretch.point": "Укажите точку растяжения:",
  "prompt.grip.armed.move.point": "Укажите точку переноса:",
  "prompt.grip.armed.rotate.point": "Укажите угол поворота:",
  "prompt.grip.armed.scale.point": "Укажите коэффициент масштабирования:",
  "prompt.grip.armed.mirror.point": "Укажите вторую точку:",
  "audit.entities_rotated": "Объекты повёрнуты.",
  // @codex PR #785 round 13, FINDING C — ROTATE / MIRROR decline an OLE frame
  // whose new placement the entity cannot store: OLE2FRAME holds only two
  // opposite frame corners (groups 10/20/30 and 11/21/31) and carries no
  // rotation group code, so a slanted frame has nowhere to be written (see
  // `command-engine/oleFrameTransformRefusal.ts`; Autodesk: "it is not
  // possible to rotate an inserted OLE object"). The refusal is VISIBLE rather
  // than silent, per CLAUDE.md ПРИОРИТЕТ #1's documented-incomplete-subsystem
  // exception — the same shape as the `audit.*_tables_declined` pair above.
  "audit.entities_rotated_ole_frames_declined":
    "Объекты повёрнуты; OLE-объектов не повёрнуто: {n}. OLE-объект хранится двумя углами рамки и не имеет угла поворота, поэтому повернуть или наклонить его нельзя — эти объекты оставлены без изменений.",
  "audit.rotate_declined_ole_frames_only":
    "Поворот не выполнен: в выборе только OLE-объекты (шт.: {n}). OLE-объект хранится двумя углами рамки и не имеет угла поворота, поэтому повернуть или наклонить его нельзя — объекты оставлены без изменений.",
  "audit.entities_mirrored_ole_frames_declined":
    "Объекты отражены; OLE-объектов не отражено: {n}. OLE-объект хранится двумя углами рамки и не имеет угла поворота, поэтому отразить его относительно наклонной оси нельзя — эти объекты оставлены без изменений.",
  "audit.mirror_declined_ole_frames_only":
    "Зеркальное отражение не выполнено: в выборе только OLE-объекты (шт.: {n}). OLE-объект хранится двумя углами рамки и не имеет угла поворота, поэтому отразить его относительно наклонной оси нельзя — объекты оставлены без изменений.",
  // @codex PR #785 round 15, FINDING 2 — MIRROR declines a Table because a
  // reflection is the one placement its insertion point + horizontal-direction
  // pair cannot name: отражённое направление совпадает с направлением таблицы,
  // повёрнутой на 180°, а `tableAsBlockReference` подставляет единичный
  // масштаб, так что графика *T<N> ушла бы влево-ВВЕРХ вместо влево-ВНИЗ
  // (предикат один на оба сайта — `command-engine/tableMirrorRefusal.ts`; тот
  // же отказ уже делает EXPLODE для дочерней таблицы блока). Отказ ВИДИМЫЙ, а
  // не молчаливый — исключение ПРИОРИТЕТ #1 для документированной
  // незавершённой подсистемы, та же форма, что у пары `*_tables_declined`
  // ниже.
  "audit.entities_mirrored_tables_declined":
    "Объекты отражены; таблиц не отражено: {n}. Таблица задаётся точкой вставки и вектором горизонтального направления и не имеет ни масштаба, ни признака зеркальности, поэтому отражение сохранилось бы как поворот на 180° — таблицы оставлены без изменений.",
  "audit.mirror_declined_tables_only":
    "Зеркальное отражение не выполнено: в выборе только таблицы (шт.: {n}). Таблица задаётся точкой вставки и вектором горизонтального направления и не имеет ни масштаба, ни признака зеркальности, поэтому отражение сохранилось бы как поворот на 180° — таблицы оставлены без изменений.",
  "audit.entities_scaled": "Объекты масштабированы.",
  // GitHub Codex review PR #785 round 2, P2 — SCALE declines a Table because
  // its size lives in the pre-evaluated `*T<N>` graphics block, for which
  // there is no generator (see `command-engine/tableScaleRefusal.ts`). The
  // refusal is VISIBLE rather than silent, per CLAUDE.md ПРИОРИТЕТ #1's
  // documented-incomplete-subsystem exception.
  "audit.entities_scaled_tables_declined":
    "Объекты масштабированы; таблиц не масштабировано: {n}. Размер таблицы изменить нельзя: её графика — это готовый анонимный блок *T<N>, генератора которого пока нет, поэтому таблицы оставлены без изменений.",
  "audit.scale_declined_tables_only":
    "Масштабирование не выполнено: в выборе только таблицы (шт.: {n}). Размер таблицы изменить нельзя: её графика — это готовый анонимный блок *T<N>, генератора которого пока нет, поэтому таблицы оставлены без изменений.",
  "audit.entity_offset": "Объект смещён.",
  // @codex PR #785 round 14, P2 — OFFSET / ARRAYPATH declines a heavy POLYLINE
  // subtype. Предикат один на все точки — `command-engine/
  // heavyPolylineCurveRefusal.ts`; там же ссылка на Autodesk для отказов,
  // которые делает сам AutoCAD, и план завершения для остальных. Отказ
  // ВИДИМЫЙ, а не молчаливая фильтрация в указателе — именно на неё указал
  // Codex, и повторять её в новом месте нельзя.
  "audit.offset_declined_heavy_polyline.mesh":
    "Команда не выполнена: многоугольная или полигранная сеть (POLYLINE, группа 70) не смещается. AutoCAD принимает в OFFSET отрезок, дугу, окружность, эллипс, эллиптическую дугу, 2D-полилинию, прямую, луч и сплайн; на сеть он отвечает «Cannot offset that object». У сети нет единого контура — есть строки, столбцы и рёбра граней.",
  "audit.offset_declined_heavy_polyline.polyline_3d":
    "Команда не выполнена: 3D-полилиния не смещается. В списке объектов OFFSET у AutoCAD только 2D-полилиния; на 3D-полилинию он отвечает «Cannot offset that object». Смещение в плоскости XY потеряло бы отметки вершин.",
  "audit.offset_declined_heavy_polyline.fit":
    "Команда не выполнена: сглаженная полилиния (curve-fit или spline-fit, группа 70) пока не смещается. AutoCAD строит для неё НОВУЮ сглаженную полилинию, то есть пересчитывает сглаживание, а генератора сглаживания полилиний здесь нет; сдвиг готовых вершин дал бы набор записей, который не является результатом никакого сглаживания, при том что группа 70 утверждает обратное. Как путь для массива по траектории такая полилиния работает полностью.",
  "audit.arraypath_declined_heavy_polyline.mesh":
    "Команда не выполнена: многоугольная или полигранная сеть (POLYLINE, группа 70) не может быть траекторией массива. AutoCAD принимает в ARRAYPATH отрезок, полилинию, 3D-полилинию, сплайн, спираль, дугу, окружность или эллипс; сеть — не кривая и не имеет единого порядка обхода.",
  "audit.arraypath_declined_heavy_polyline.polyline_3d":
    "Команда не выполнена: 3D-полилиния пока не может быть траекторией массива. AutoCAD её принимает, но здешний сэмплер траектории двумерный: длина считалась бы по проекции на плоскость XY, то есть короче истинной, деления попадали бы не в те точки, а Z копий молча терялась бы. Лучше не выполнить, чем построить уверенно неверный массив.",
  // @codex PR #785 round 16, P2 — ARRAYPATH отказывается от траектории,
  // плоскость которой не параллельна плоскости XY. Вторая половина той же
  // проверки пригодности: выше — ПОДТИП тяжёлой полилинии, здесь — ПЛОСКОСТЬ
  // любой кривой. Предикат один на все точки —
  // `command-engine/arrayPathPlaneRefusal.ts`; там же план завершения (полный
  // 3D-перенос в `TransformFn`). Отказ ВИДИМЫЙ: тот же текст выдаёт и
  // командная строка на шаге «Выберите кривую траектории».
  "audit.arraypath_declined_tilted_plane":
    "Команда не выполнена: плоскость траектории не параллельна плоскости XY (вектор выдавливания, группа 210). Здешний сэмплер траектории двумерный, поэтому копии встали бы вдоль проекции кривой на плоскость XY, а не вдоль самой кривой: шаг откладывался бы по проекции, которая короче, и каждое деление попадало бы не в свою точку; при выдавливании (0,1,0) ось Y системы объекта совпадает с осью Z мира, проекция вырождается в точку и все копии легли бы на оригинал. Зеркальное выдавливание (0,0,-1) и постоянная ненулевая отметка отказом не являются. Лучше не выполнить, чем построить уверенно неверный массив.",
  // Пол под всеми остальными отказами ARRAYPATH. Вердикт — РЕЗУЛЬТАТ
  // документ-редьюсера (создано ноль сущностей), а не перечень причин: их
  // знает `arrayReducers.ts::handleCreateArrayPath`, а shell — нет, и любой
  // список разъехался бы на следующем добавленном guard'е. Поэтому строка
  // называет исход и не называет причину. См. `state/arrayPathShellAudit.ts`.
  "audit.arraypath_nothing_created":
    "Команда не выполнена: массив по пути не создан, ни одной копии не добавлено. Траектория, способ построения или его параметры не позволяют разместить ни одного элемента; точная причина записана в журнал консоли.",
  "audit.arraypath_declined_sloped_line":
    "Команда не выполнена: отрезок имеет уклон по Z (отметки начала и конца, группа 30, различаются), поэтому он пока не может быть траекторией массива. Здешний сэмплер траектории двумерный: длина отрезка считается как гипотенуза по X и Y, то есть короче истинной на величину подъёма (у вертикального отрезка — ноль), деления попадали бы не в те точки, а Z копий молча терялась бы. Отрезок целиком на постоянной отметке отказом не является. Лучше не выполнить, чем построить уверенно неверный массив.",
  "audit.entities_stretched": "Объекты растянуты.",
  "summary.rotate": "ПОВЕРНУТЬ завершён.",
  "summary.scale": "МАСШТАБ завершён.",
  "summary.offset": "ПОДОБИЕ завершён.",
  "summary.stretch": "РАСТЯНУТЬ завершён.",
  // Modification commands: TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN, EXPLODE, LENGTHEN
  "command.trim.label": "Обрезать",
  "command.extend.label": "Удлинить",
  "command.fillet.label": "Сопряжение",
  "command.chamfer.label": "Фаска",
  "command.break.label": "Разрыв",
  "command.join.label": "Соединить",
  "command.explode.label": "Расчленить",
  "command.lengthen.label": "Увеличить",
  "prompt.trim.select_edges": "ОБРЕЗАТЬ: Выберите режущие кромки, затем Enter.",
  "prompt.trim.pick_entity":
    "ОБРЕЗАТЬ: Укажите обрезаемый объект или Enter для выхода.",
  "prompt.extend.select_edges":
    "УДЛИНИТЬ: Выберите граничные кромки, затем Enter.",
  "prompt.extend.pick_entity":
    "УДЛИНИТЬ: Укажите удлиняемый объект или Enter для выхода.",
  "prompt.fillet.radius": "СОПРЯЖЕНИЕ: Введите радиус сопряжения.",
  "prompt.fillet.first":
    "СОПРЯЖЕНИЕ: Выберите первый объект или [Радиус/Полилиния/Обрезка/Множественный].",
  "prompt.fillet.second": "СОПРЯЖЕНИЕ: Выберите второй объект.",
  "prompt.chamfer.distances": "ФАСКА: Введите расстояния (d1 d2).",
  "prompt.chamfer.first": "ФАСКА: Выберите первый отрезок.",
  "prompt.chamfer.second": "ФАСКА: Выберите второй отрезок.",
  "prompt.break.select": "РАЗРЫВ: Выберите объект.",
  "prompt.break.first": "РАЗРЫВ: Укажите первую точку разрыва.",
  "prompt.break.second": "РАЗРЫВ: Укажите вторую точку разрыва.",
  "prompt.join.select": "СОЕДИНИТЬ: Выберите объекты, затем Enter.",
  "prompt.explode.select": "РАСЧЛЕНИТЬ: Выберите объекты, затем Enter.",
  "prompt.lengthen.select": "УВЕЛИЧИТЬ: Выберите объект.",
  "prompt.lengthen.mode":
    "УВЕЛИЧИТЬ: Укажите режим [ДЕльта/Процент/Всего] (DE/PE/TO).",
  "prompt.lengthen.value": "УВЕЛИЧИТЬ: Введите значение.",
  "prompt.lengthen.pick_end": "УВЕЛИЧИТЬ: Укажите конец объекта для изменения.",
  "audit.entity_trimmed": "Объект обрезан.",
  "audit.entity_extended": "Объект удлинён.",
  // @codex PR #785 round 4, P2 — TRIM/EXTEND declines part of a heavy POLYLINE
  // selection. Предикат один на все точки —
  // `command-engine/heavyPolylineTrim.ts`; там же обоснование и план
  // завершения каждого отказа. Отказ ВИДИМЫЙ, а не молчаливый — исключение
  // CLAUDE.md ПРИОРИТЕТ #1 для задокументированной незавершённой подсистемы.
  "audit.trim_declined_heavy_polyline.mesh":
    "Команда не выполнена: многоугольная или полигранная сеть (POLYLINE, группа 70) не обрезается и не удлиняется. В AutoCAD сеть не входит ни в список обрезаемых объектов, ни в список режущих/граничных кромок — у неё нет единого 2D-контура, а есть строки, столбцы и рёбра граней.",
  "audit.trim_declined_heavy_polyline.polyline_3d":
    "Команда не выполнена: 3D-полилиния пока не обрезается и не удлиняется как объект. Контур TRIM/EXTEND двумерный и не переносит Z вершин, поэтому отметки были бы молча потеряны — объект оставлен без изменений. Как режущая или граничная кромка 3D-полилиния работает полностью.",
  "audit.trim_declined_heavy_polyline.fit":
    "Команда не выполнена: сглаженная полилиния (curve-fit или spline-fit, группа 70) пока не обрезается и не удлиняется как объект. AutoCAD пересчитывает сглаживание после обрезки, а генератора сглаживания полилиний здесь нет; вершина реза, вставленная в готовый набор записей, сделала бы группу 70 неверной. Как режущая или граничная кромка такая полилиния работает полностью.",
  "audit.trim_heavy_polyline_edges_declined":
    "Кромок пропущено: {n}. Сеть (POLYLINE polygon/poly-face mesh) не может быть режущей или граничной кромкой; остальные кромки учтены.",
  "audit.entities_filleted": "Объекты сопряжены.",
  "audit.entities_chamfered": "Фаска создана.",
  "audit.entity_broken": "Объект разорван.",
  "audit.entities_joined": "Объекты соединены.",
  "audit.entities_exploded": "Объекты расчленены.",
  "audit.entity_lengthened": "Объект удлинён/укорочен.",
  // CC-1c — per-viewport edit + history-traverse + view-op audit summaries.
  "audit.viewport_locked": "Видовой экран {id} заблокирован.",
  "audit.viewport_unlocked": "Видовой экран {id} разблокирован.",
  "audit.viewport_vplayer_freeze":
    "Слой «{layer}» заморожен в видовом экране {id}.",
  "audit.viewport_vplayer_thaw":
    "Слой «{layer}» разморожен в видовом экране {id}.",
  "audit.viewport_clip_set": "Подрезка видового экрана {id} задана.",
  "audit.viewport_clip_polygon_set":
    "Многоугольная подрезка видового экрана {id} задана.",
  "audit.viewport_clip_removed": "Подрезка видового экрана {id} снята.",
  "audit.viewport_scale_set": "Масштаб видового экрана {id}: {num}:{denom}.",
  "audit.viewport_shadeplot_set":
    "Режим печати с тонированием видового экрана {id}: {mode}.",
  "audit.viewport_view_updated": "Вид видового экрана {id} обновлён.",
  "audit.history_undo": "Отмена: {command}.",
  "audit.history_redo": "Повтор: {command}.",
  "audit.viewop_zoom": "Зумирование: {mode}.",
  "audit.viewop_regen": "Регенерация чертежа.",
  "audit.viewop_regenall": "Регенерация всех видовых экранов.",
  "audit.viewop_pan": "Панорамирование вида.",
  // ── CC-3 special-editor dialog host chrome ──
  "special_editor.title": "Специальный редактор",
  "special_editor.ok": "ОК",
  "special_editor.cancel": "Отмена",
  "special_editor.apply": "Применить",
  "special_editor.regen_summary":
    "Регенерация объекта после правки в специальном редакторе.",
  // ── REVCLOUD-PROPS-EDITOR (§5.1) ──
  "special_editor.revcloud.title": "Свойства облака правок",
  "special_editor.revcloud.regen_summary":
    "Регенерация дуг облака правок после правки свойств.",
  // ── HATCH-EDIT-DIALOG (§5.1) ──
  "special_editor.hatch.title": "Редактирование штриховки",
  "special_editor.hatch.regen_summary":
    "Регенерация заливки штриховки после правки параметров образца.",
  "audit.block_defined": "Определение блока «{name}» создано.",
  "audit.insert_exploded": "Вставка блока расчленена.",
  // EXPLODE отклонён целиком, потому что определение блока содержит дочерний
  // объект, который не может унести вложение INSERT в своих собственных полях.
  // Предикат один на оба пути — `command-engine/
  // explodeBlockChildPolylineTableOle.ts`; там же вывод границы и план
  // завершения. Отказ ПОЛНЫЙ (исходный INSERT остаётся на месте) и ВИДИМЫЙ —
  // до этого такой дочерний объект молча удалялся вместе с INSERT.
  "audit.explode_declined_table":
    "Расчленение не выполнено: определение блока содержит таблицы (шт.: {n}), а эта вставка масштабирована или зеркально отражена. У ACAD_TABLE нет коэффициентов масштаба: её графика — это готовый анонимный блок *T<N>, поэтому расчленённая таблица вернулась бы к исходному размеру. Вставка оставлена без изменений; расчленение доступно при повороте без масштаба и зеркала.",
  "audit.explode_declined_ole_frame":
    "Расчленение не выполнено: определение блока содержит OLE-рамки (шт.: {n}), а эта вставка повёрнута на угол, не кратный 90°. OLE2FRAME хранит два противоположных угла и не имеет поля поворота, поэтому расчленённая рамка стала бы прямоугольником по осям вместо повёрнутого. Вставка оставлена без изменений; расчленение доступно при повороте, кратном 90° (масштаб и зеркало допустимы).",
  // @codex PR #785 round 14, FINDING 2 — РАСЧЛЕНИТЬ для тяжёлой POLYLINE
  // верхнего уровня. Предикат один на оба пути —
  // `command-engine/explodeHeavyPolyline.ts`; там же построчный разбор
  // справочника AutoCAD по каждому подтипу группы 70 и план завершения.
  // Отказ ПОЧАСТНЫЙ (остальные полилинии выборки расчленяются) и ВИДИМЫЙ —
  // до этого такая полилиния молча оставалась целой при «успехе» команды.
  "audit.explode_declined_polygon_mesh":
    "Не расчленено объектов: {n}. Многоугольная сеть (POLYLINE, группа 70, бит 16) в AutoCAD расчленяется на грани 3DFACE, а примитива 3DFACE в редакторе пока нет: подмена гранями-линиями исказила бы объект (3DFACE — заполненная четырёхугольная грань с видимостью рёбер, а не четыре отрезка). Сеть оставлена без изменений; остальные выбранные объекты расчленены.",
  "audit.explode_declined_polyface_mesh":
    "Не расчленено объектов: {n}. Полигранная сеть (POLYLINE, группа 70, бит 64) в AutoCAD расчленяется на грани 3DFACE, а примитива 3DFACE в редакторе пока нет: подмена гранями-линиями исказила бы объект. Сеть оставлена без изменений; остальные выбранные объекты расчленены.",
  "audit.explode_declined_polyline_no_run":
    "Не расчленено объектов: {n}. У полилинии меньше двух видимых вершин, поэтому расчленять не на что — ни одного сегмента не получилось бы. Объект оставлен без изменений: РАСЧЛЕНИТЬ не удаляет то, что не заменяет.",
  "audit.blocks_purged": "Удалено неиспользуемых определений блоков: {count}.",
  "audit.block_renamed": "Блок «{oldName}» переименован в «{newName}».",
  "audit.attdef_created": "Атрибут «{tag}» добавлен в блок «{block}».",
  "audit.attribute_updated": "Значение атрибута «{tag}» обновлено.",
  "audit.attributes_updated": "Обновлено значений атрибутов: {count}.",
  "audit.attdef_removed": "Атрибут {id} удалён из блока «{block}».",
  "audit.attdef_updated": "Свойства атрибута {id} в блоке «{block}» обновлены.",
  "audit.attdef_tag_renamed":
    "Тег атрибута переименован на «{newTag}» в блоке «{block}».",
  "audit.attdefs_reordered":
    "Порядок атрибутов изменён в блоке «{block}» ({count}).",
  "audit.attsync":
    "Атрибуты вставок блока «{block}» синхронизированы с шаблоном.",
  "summary.trim": "ОБРЕЗАТЬ завершён.",
  "summary.extend": "УДЛИНИТЬ завершён.",
  "summary.fillet": "СОПРЯЖЕНИЕ завершён.",
  "summary.chamfer": "ФАСКА завершён.",
  "summary.break": "РАЗРЫВ завершён.",
  "summary.join": "СОЕДИНИТЬ завершён.",
  "summary.explode": "РАСЧЛЕНИТЬ завершён.",
  "summary.lengthen": "УВЕЛИЧИТЬ завершён.",
  "summary.geom_constraint_added":
    "Геометрическое ограничение «{kind}» добавлено.",
  // ── Drawing aids status bar ──
  "status.ortho": "ОРТО",
  "status.polar": "ПОЛЯР",
  "status.rmb_enter": "ПКМ",
  "status.lwt": "ВЕС",
  "status.dyn": "ДИН",
  // ── Annotation status bar (PR-4) ──
  "status.annotation_scale": "Масштаб аннотаций",
  "status.annoautoscale": "Автомасштаб",
  "status.annoautoscale_title":
    "Автоматически добавлять текущий масштаб к аннотативным объектам (ANNOAUTOSCALE)",
  "status.annoallvisible": "Видимость",
  "status.annoallvisible_title":
    "Показывать аннотативные объекты для всех масштабов (ANNOALLVISIBLE)",
  "status.annomonitor": "Монитор",
  "status.annomonitor_title":
    "Подсвечивать рассинхронизированные аннотации (ANNOMONITOR)",
  "status.scalelistedit": "Список масштабов…",
  "status.scalelistedit_title":
    "Управление списком масштабов аннотаций (SCALELISTEDIT)",
  // ── UCS icon ──
  "ucs.reset_to_world": "Сброс UCS к мировой",
  "ucs.tooltip":
    "Перетащите для перемещения начала координат. Shift+перетащите для вращения",
  // ── Select Color Picker ──
  // Title de-ACI'd: the dialog now hosts Index + True Color tabs, so the parent
  // carries AutoCAD's plain "Выбор цвета" (the model is named per-tab below).
  "color_picker.title": "Выбор цвета",
  "color_picker.standard": "Стандартные",
  "color_picker.grey_ramp": "Серая шкала",
  // True Color tab (EPIC-COLOR-UNION S3a) — AutoCAD "Выбор цвета" → вкладки.
  // Labels are AutoCAD's exact RU tab headings (Autodesk GUID-732A8E2D, 2022/2025):
  // Номер цвета / Полноцветный режим / Альбомы цветов (Color Books deferred).
  "color_picker.tab.index": "Номер цвета",
  "color_picker.tab.true": "Полноцветный режим",
  "color_picker.true.r": "Красный",
  "color_picker.true.g": "Зелёный",
  "color_picker.true.b": "Синий",
  "color_picker.true.hex": "Цвет (HEX)",
  "color_picker.true.preview": "Образец",
  "color_picker.ok": "OK",
  // ── BLOCK / INSERT commands (Phase 1) — `command.explode.label`
  // already exists further up in this file under the modify commands
  // block, so it is NOT re-defined here.
  "command.insert.label": "Вставить",
  // ── BLOCK command flow prompts ──
  "prompt.block.select_objects":
    "БЛОК: Выберите объекты для определения блока. Нажмите Enter для завершения.",
  "prompt.block.base_point": "БЛОК: Укажите базовую точку определения блока.",
  "prompt.block.dialog":
    "БЛОК: Откройте диалог «Определение блока» для ввода имени и параметров.",
  // ── INSERT command flow prompts ──
  "prompt.insert.dialog":
    "ВСТАВИТЬ: Откройте диалог «Вставка блока» для выбора блока.",
  "prompt.insert.specify_point": "ВСТАВИТЬ: Укажите точку вставки блока.",
  "prompt.insert.specify_scale": "ВСТАВИТЬ: Укажите масштаб <1> или [Угол].",
  "prompt.insert.specify_rotation":
    "ВСТАВИТЬ: Укажите угол поворота вставки <0>.",
  "prompt.insert.attribute_value":
    "ВСТАВИТЬ: Введите значение атрибута {tag}: <{default}>",
  // ── EXPLODE command prompts ──
  "prompt.explode.select_inserts":
    "РАСЧЛЕНИТЬ: Выберите вставки блоков для расчленения.",
  // ── BlockDefinitionDialog ──
  "block_def_dialog.title": "Определение блока",
  "block_def_dialog.tab.objects": "Объекты",
  "block_def_dialog.tab.behavior": "Поведение",
  "block_def_dialog.tab.settings": "Настройки",
  "block_def_dialog.tab.description": "Описание",
  "block_def_dialog.label.name": "Имя",
  "block_def_dialog.label.base_point": "Базовая точка",
  "block_def_dialog.label.base_x": "X:",
  "block_def_dialog.label.base_y": "Y:",
  "block_def_dialog.label.pick_on_screen": "Указать на экране",
  "block_def_dialog.label.objects_panel": "Объекты",
  "block_def_dialog.label.action_retain": "Оставить",
  "block_def_dialog.label.action_convert": "Преобразовать в блок",
  "block_def_dialog.label.action_delete": "Удалить",
  "block_def_dialog.label.objects_selected_count": "Выбрано объектов: {count}",
  "block_def_dialog.label.no_objects_selected": "Объекты не выбраны.",
  "block_def_dialog.label.annotative": "Аннотативный",
  "block_def_dialog.label.annotative_scales":
    "Аннотативные масштабы (через запятую)",
  "block_def_dialog.label.scale_uniformly": "Применять одинаковый масштаб",
  "block_def_dialog.label.allow_exploding": "Разрешить расчленение",
  "block_def_dialog.label.block_unit": "Единицы блока",
  "block_def_dialog.label.hyperlink": "Гиперссылка",
  "block_def_dialog.label.description": "Описание",
  "block_def_dialog.placeholder.name": "Введите имя блока",
  "block_def_dialog.placeholder.hyperlink": "URL или путь",
  "block_def_dialog.placeholder.description":
    "Краткое описание блока (необязательно)",
  "block_def_dialog.btn.ok": "OK",
  "block_def_dialog.btn.cancel": "Отмена",
  "block_def_dialog.btn.pick_base_point": "Указать точку",
  // ── BlockDefinitionDialog errors ──
  "block_def_dialog.error.empty_name": "Имя блока не может быть пустым.",
  "block_def_dialog.error.reserved_name":
    "Имя «{name}» зарезервировано системой.",
  "block_def_dialog.error.forbidden_chars":
    'Имя содержит запрещённые символы (< > / \\ " : ; ? * | , = `).',
  "block_def_dialog.error.too_long":
    "Имя блока не может быть длиннее 255 символов.",
  "block_def_dialog.error.duplicate": "Блок с именем «{name}» уже существует.",
  "block_def_dialog.error.no_objects":
    "Не выбрано ни одного объекта для определения блока.",
  // ── InsertBlockDialog ──
  "insert_dialog.title": "Вставка блока",
  "insert_dialog.label.name": "Имя",
  "insert_dialog.label.path": "Путь",
  "insert_dialog.label.path_local": "(локальный блок)",
  "insert_dialog.label.preview": "Образец",
  "insert_dialog.label.preview_empty": "Блок не выбран",
  "insert_dialog.label.insertion_point": "Точка вставки",
  "insert_dialog.label.scale": "Масштаб",
  "insert_dialog.label.rotation": "Поворот",
  "insert_dialog.label.specify_on_screen": "Указать на экране",
  "insert_dialog.label.uniform_scale": "Одинаковый масштаб",
  "insert_dialog.label.angle": "Угол",
  "insert_dialog.label.show_anonymous": "Показывать анонимные блоки",
  "insert_dialog.label.explode": "Расчленить",
  "insert_dialog.label.x": "X:",
  "insert_dialog.label.y": "Y:",
  "insert_dialog.label.z": "Z:",
  "insert_dialog.btn.ok": "OK",
  "insert_dialog.btn.cancel": "Отмена",
  "insert_dialog.error.no_blocks": "Нет доступных блоков для вставки.",
  "insert_dialog.error.cyclic":
    "Циклическая вставка блоков запрещена: «{name}».",
  "insert_dialog.error.not_explodable":
    "Блок «{name}» нельзя расчленить (флаг ALLOW_EXPLODE = 0).",
  // ── P7 Overlap warning dialog (INSERT) ──
  "dialog.overlap_warning.title": "Перекрытие объектов",
  "dialog.overlap_warning.message":
    "Точка вставки ({x}, {y}) перекрывает существующие объекты в области ({left}..{right}, {bottom}..{top}). Продолжить?",
  "dialog.overlap_warning.continue": "Продолжить",
  "dialog.overlap_warning.cancel": "Отмена",
  // ── INSUNITS enum (DXF group 70 in BLOCK_RECORD) ──
  "insunits.0": "Безразмерные",
  "insunits.1": "Дюймы",
  "insunits.2": "Футы",
  "insunits.3": "Мили",
  "insunits.4": "Миллиметры",
  "insunits.5": "Сантиметры",
  "insunits.6": "Метры",
  "insunits.7": "Километры",
  "insunits.8": "Микродюймы",
  "insunits.9": "Милы",
  "insunits.10": "Ярды",
  "insunits.11": "Ангстремы",
  "insunits.12": "Нанометры",
  "insunits.13": "Микроны",
  "insunits.14": "Дециметры",
  "insunits.15": "Декаметры",
  "insunits.16": "Гектометры",
  "insunits.17": "Гигаметры",
  "insunits.18": "Астрономические единицы",
  "insunits.19": "Световые годы",
  "insunits.20": "Парсеки",
  // 21 = US Survey Feet (AutoCAD 2017+) — официальный термин Autodesk в
  // русской документации INSUNITS: «Геодезические футы США».
  "insunits.21": "Геодезические футы США",
  // ── Property palette: dynamic block / INSERT extra fields ──
  "property.hyperlink": "Гиперссылка",
  "property.description": "Описание",
  "property.block_unit": "Единицы блока",
  "property.block_unit_factor": "Коэф. единиц блока",
  "property.allow_exploding": "Разрешить расчленение",
  // ── Summaries (after command commit) ──
  "summary.block_defined": "Создано определение блока «{name}».",
  "summary.block_inserted": "Вставлен блок «{name}».",
  "summary.block_exploded": "Блок «{name}» расчленён на {count} объектов.",
  // ── Toast / error notifications ──
  "toast.block.cyclic_reference":
    "Запрещено: вставка блока «{name}» создаёт цикл.",
  "toast.block.reserved_name":
    "Имя «{name}» зарезервировано системой и не может использоваться.",
  "toast.block.not_explodable":
    "Блок «{name}» имеет флаг ALLOW_EXPLODE = 0 и не может быть расчленён.",
  "toast.block.no_selection":
    "Выберите хотя бы одну вставку блока для расчленения.",
  "toast.block.create_failed":
    "Не удалось создать определение блока: {message}",
  // ── BEDIT / BCLOSE / BSAVE (Phase 2.3) ──
  "command.bedit.label": "РЕДБЛОК",
  "command.bclose.label": "БЗАКРЫТЬ",
  "command.bsave.label": "БСОХРАНИТЬ",
  "prompt.bedit.enter_name": "РЕДБЛОК: Введите имя блока или [?]:",
  "prompt.bedit.confirm_create":
    "Блок «{name}» не существует. Создать новый? [Да/Нет]",
  "prompt.bclose.confirm":
    "Сохранить изменения в блоке «{name}»? [Да/Нет/Отмена]",
  "status.bedit.editing": "Редактируется блок: {name}",
  // ── REFEDIT / REFSET / REFCLOSE (Phase FIN.1) ──
  "command.refedit.label": "ВНЕДРЕД (Редактирование ссылки)",
  "command.refset.label": "ВНЕДНАБ (Изменить рабочий набор)",
  "command.refclose.label": "ВНЕДЗАКР (Закрыть редактирование ссылки)",
  "refedit.toolbar.title": "Редактирование ссылки",
  "refedit.add_to_set": "Добавить в набор",
  "refedit.remove_from_set": "Убрать из набора",
  "refedit.save": "Сохранить изменения",
  "refedit.discard": "Отменить",
  "refedit.working_set_count": "В наборе: {count}",
  "status.refedit.editing": "Редактируется ссылка: {name}",
  "summary.refedit_entered":
    "Открыта правка ссылки «{name}» по INSERT {insert}.",
  "summary.refedit_saved": "Изменения ссылки «{name}» сохранены (ВНЕДЗАКР).",
  "summary.refedit_discarded": "Изменения ссылки «{name}» отменены (ВНЕДЗАКР).",
  "summary.refset_added": "В рабочий набор добавлено объектов: {count}.",
  "summary.refset_removed": "Из рабочего набора удалено объектов: {count}.",
  "toast.refedit.no_insert_selected":
    "Сначала выберите ВСТАВКУ (INSERT), которую нужно править на месте.",
  "toast.refedit.not_in_session":
    "Эта команда доступна только во время правки ссылки (ВНЕДРЕД).",
  "prompt.refedit.select_insert":
    "ВНЕДРЕД: Укажите ВСТАВКУ для правки на месте.",
  "prompt.refedit.refclose_confirm":
    "Сохранить изменения в ссылке «{name}»? [Да/Нет]",
  // ── ANNOUPDATE / OBJECTSCALE / CANNOSCALE (Phase FIN.2) ──
  "command.annoupdate.label": "АННОБНОВ (Аннотативное обновление)",
  "command.objectscale.label": "ОБЪМАСШТАБ (Масштабы аннотаций)",
  "command.cannoscale.label": "ТЕКМСШТАБА (Текущий масштаб аннотаций)",
  "command.base.label": "БАЗА (Базовая точка вставки)",
  // ── CECOLOR — текущий цвет объектов (COLOR / ЦВЕТ) ──
  "command.color.label": "ЦВЕТ (Текущий цвет объектов)",
  "colorcmd.feedback": "Текущий цвет: {value}",
  "colorcmd.open_picker": "ЦВЕТ: выберите цвет в диалоге.",
  "colorcmd.prompt":
    "Введите цвет [Истинный(T)/Альбом(CO)] <{current}>:",
  "colorcmd.invalid": "Недопустимый цвет.",
  "colorcmd.bylayer": "ПОСЛОЮ",
  "colorcmd.byblock": "ПОБЛОКУ",
  "cecolor.swatch.tooltip": "Текущий цвет объектов (CECOLOR)",
  "cecolor.swatch.current": "Текущий: {value}",
  "cecolor.swatch.bylayer": "ПОСЛОЮ",
  "cecolor.swatch.byblock": "ПОБЛОКУ",
  // ── PR-8 — annotative status-bar / scale-list commands ──
  "command.annoreset.label": "АННОСБРОС (Сброс позиций аннотаций)",
  "command.scalelistedit.label": "СПИСМАСШТАБОВ (Редактор списка масштабов)",
  "command.annomonitor.label": "АННОМОНИТОР (Монитор аннотаций)",
  "command.annoallvisible.label": "АННОВИДИМОСТЬ (Видимость аннотаций)",
  "command.annoautoscale.label": "АВТОМАСШТАБ (Автомасштаб аннотаций)",
  "cannoscale.selector": "Масштаб аннотаций",
  "annotative.scales": "Масштабы аннотаций",
  "annotative.add_scale": "Добавить масштаб",
  "annotative.remove_scale": "Убрать масштаб",
  "summary.cannoscale_changed": "Масштаб аннотаций изменён на {name}.",
  "summary.annoupdate_done":
    "АННОБНОВ: пересчитано аннотативных вставок: {count}.",
  "summary.objectscale_added":
    "ОБЪМАСШТАБ: добавлен масштаб «{name}» к INSERT.",
  "summary.objectscale_removed": "ОБЪМАСШТАБ: убран масштаб «{name}» с INSERT.",
  "summary.bedit_entered": "Открыт Редактор блоков для «{name}».",
  "summary.bedit_created":
    "Создан и открыт Редактор блоков для нового блока «{name}».",
  "summary.bsave": "Блок «{name}» сохранён (БСОХРАНИТЬ).",
  "summary.bclose_saved": "Изменения в блоке «{name}» сохранены (БЗАКРЫТЬ).",
  "summary.bclose_discarded": "Изменения в блоке «{name}» отменены (БЗАКРЫТЬ).",
  "toast.bedit.not_in_block_editor":
    "Эта команда доступна только в режиме Редактора блоков (РЕДБЛОК).",
  "toast.bedit.cannot_anonymous":
    "Нельзя редактировать зарезервированный блок «{name}»: имена с префиксом «*» (макетные, размерные, штриховые, xref) принадлежат системе.",
  // ── Phase J — BEDIT UI integration (ribbon swap, dialogs, palettes) ──
  "ribbon.tab.block-editor": "Редактор блока",
  "ribbon.context.block.label": "Контекст блока",
  "ribbon.context.block.enter_editor": "Войти в редактор блоков",
  "ribbon.context.block.test_block": "Тест блока",
  "ribbon.context.block.edit_attributes": "Изменить атрибуты",
  "ribbon.block_editor.parameters": "Параметры",
  "ribbon.block_editor.parameter-sets": "Наборы параметров",
  "ribbon.block_editor.actions": "Действия",
  "ribbon.block_editor.visibility": "Видимость",
  "ribbon.block_editor.management": "Управление",
  "ribbon.block_editor.test_block": "Тест блока",
  "ribbon.block_editor.drawing": "Черчение",
  "ribbon.block_editor.editing": "Редактирование",
  "ribbon.block_editor.erase": "Стереть",
  "ribbon.block_editor.close_section": "Закрыть",
  "ribbon.block_editor.close": "Закрыть редактор блока",
  "toast.block_editor.palette_phase_k":
    "Палитра «{pane}» появится в Phase K (Block Authoring Palettes).",
  // ── BlockEditExitDialog (Phase J.5) ──
  "bedit.exit_dialog.title": "Сохранить изменения в блоке «{blockName}»?",
  "bedit.exit_dialog.body":
    "Вы внесли изменения в определение блока. Сохраните их, чтобы обновить все вставки этого блока, либо отмените, чтобы вернуться к исходному состоянию.",
  "bedit.exit_dialog.save": "Сохранить",
  "bedit.exit_dialog.discard": "Отменить изменения",
  "bedit.exit_dialog.cancel": "Отмена",
  // ── BlockEditorAnnotativePreview (PR-15) ──
  // Tab strip shown at the top of BEDIT canvas for annotative blocks.
  // Lists Master + every annotation scale that has a *A_NNN
  // decomposition block. Hidden entirely for non-annotative blocks.
  "bedit.master_tab": "Мастер",
  "bedit.scale_tab": "Масштаб",
  "bedit.no_decomposition": "Декомпозиция не сгенерирована",
  "bedit.generate_decomposition": "Сгенерировать декомпозицию",
  "audit.bedit.decomposition_preview_deferred":
    "Просмотр декомпозиции отложен (генерация в отдельном PR).",
  "audit.bedit.generate_decomposition_deferred":
    "Генерация декомпозиции отложена (создание *A_NNN блока в отдельном PR).",
  // ── WBLOCK / PURGE / RENAME command labels (Phase 2.6-2.8) ──
  "command.wblock.label": "ЗПБЛОК",
  "command.purge.label": "ОЧИСТИТЬ",
  "command.rename.label": "ПЕРЕИМ",
  // ── PAN / ZOOM view-navigation command labels (u043, ТЗ §10.1) ──
  "command.pan.label": "ПАН (Панорамирование вида)",
  "command.zoom.label": "ПОКАЗАТЬ (Масштабирование вида)",
  // ── REGEN / REGENALL display-refresh command labels (ТЗ §10.1) ──
  "command.regen.label": "РЕГЕН (Регенерация отображения)",
  "command.regenall.label": "РЕГЕНВСЕ (Регенерация всех видовых экранов)",
  // ── D5 — router→registry unification: labels for commands that were
  // dispatched via literal string comparisons in App.tsx before being
  // folded into command-registry descriptors ──
  "command.print.label": "ПЕЧАТЬ (Печать листа)",
  "command.pagesetup.label": "ПАРАМЛИСТ (Диспетчер параметров листов)",
  "command.battman.label": "МЕНАТР (Диспетчер атрибутов блока)",
  "command.eattext.label": "ИЗВЛАТР (Извлечение атрибутов)",
  "command.bcount.label": "СЧЕТБЛОК (Счётчик экземпляров блоков)",
  "command.xref.label": "ВНССЫЛ (Внешние ссылки)",
  "command.xopen.label": "ВНОТКРЫТЬ (Открыть внешнюю ссылку)",
  // "command.bauthorpaletteclose.label" already exists (DYN-1.11 cluster
  // above) — reused verbatim, not redefined here.
  "command.bvhide.label": "BVHIDE (Скрыть в текущем состоянии видимости)",
  "command.bvshow.label": "BVSHOW (Показать в текущем состоянии видимости)",
  "command.bvshowall.label": "BVSHOWALL (Показать во всех состояниях видимости)",
  "command.undo.label": "ОТМ (Отменить)",
  "command.undobegin.label": "UNDO BEGIN (Начать группу отмены)",
  "command.undoend.label": "UNDO END (Завершить группу отмены)",
  "command.undoback.label": "UNDO BACK (Откатить группу отмены)",
  "command.redo.label": "ПВТ (Повторить)",
  "command.mredo.label": "MREDO (Множественный повтор)",
  "command.dsettings.label": "РЕЖИМРИС (Режимы рисования)",
  "command.batch.label": "BATCH (Пакетное выполнение команд)",
  // ── WriteBlockDialog (Phase 2.6) ──
  "wblock_dialog.title": "Запись блока в файл",
  "wblock_dialog.section.source": "Источник",
  "wblock_dialog.source.block": "Блок",
  "wblock_dialog.source.drawing": "Весь чертёж",
  "wblock_dialog.source.objects": "Объекты",
  "wblock_dialog.label.block_name": "Имя блока",
  "wblock_dialog.section.base_point": "Базовая точка",
  "wblock_dialog.label.x": "X:",
  "wblock_dialog.label.y": "Y:",
  "wblock_dialog.section.destination": "Назначение",
  "wblock_dialog.label.file_name": "Имя файла",
  "wblock_dialog.label.insertion_units": "Единицы вставки",
  "wblock_dialog.label.selected_objects": "Выделено объектов: {count}",
  "wblock_dialog.error.no_selection":
    "Не выбрано ни одного объекта. Закройте диалог и выделите объекты.",
  "wblock_dialog.error.no_blocks": "В чертеже нет определений блоков.",
  "wblock_dialog.error.empty_filename": "Имя файла не может быть пустым.",
  "wblock_dialog.btn.ok": "OK",
  "wblock_dialog.btn.cancel": "Отмена",
  "summary.wblock_exported": "Блок экспортирован в файл «{filename}».",
  "toast.wblock.failed": "Не удалось экспортировать блок: {message}",
  // ── PurgeBlocksDialog (Phase 2.7) ──
  "purge_dialog.title": "Очистка неиспользуемых блоков",
  "purge_dialog.column.name": "Имя",
  "purge_dialog.column.references": "Ссылок",
  "purge_dialog.column.can_purge": "Удаление",
  "purge_dialog.column.reason": "Причина",
  "purge_dialog.reason.ready": "Готов к удалению",
  "purge_dialog.reason.reserved": "Системное имя",
  "purge_dialog.reason.in_use": "Используется ({count})",
  "purge_dialog.reason.dependent": "Используется внутри: {names}",
  "purge_dialog.btn.check_all": "Выбрать все",
  "purge_dialog.btn.uncheck_all": "Снять выделение",
  "purge_dialog.btn.ok": "Очистить",
  "purge_dialog.btn.cancel": "Отмена",
  "purge_dialog.empty": "Нет блоков, подходящих для очистки.",
  "purge_dialog.no_selection": "Не выбрано ни одного блока для очистки.",
  "summary.purge_completed": "Очистка блоков завершена: удалено {count}.",
  // ── RenameBlockDialog (Phase 2.8) ──
  "rename_dialog.title": "Переименование блока",
  "rename_dialog.label.old_name": "Исходное имя",
  "rename_dialog.label.new_name": "Новое имя",
  "rename_dialog.label.show_anonymous": "Показывать анонимные блоки",
  "rename_dialog.error.empty": "Новое имя не может быть пустым.",
  "rename_dialog.error.too_long":
    "Имя блока не может быть длиннее 255 символов.",
  "rename_dialog.error.reserved_new": "Имя «{name}» зарезервировано системой.",
  "rename_dialog.error.reserved_old": "Анонимные блоки нельзя переименовывать.",
  "rename_dialog.error.forbidden_chars":
    'Имя содержит запрещённые символы (< > / \\ " : ; ? * | , = `).',
  "rename_dialog.error.duplicate": "Блок с именем «{name}» уже существует.",
  "rename_dialog.error.unknown": "Блок «{name}» не найден.",
  "rename_dialog.error.empty_list": "Нет доступных блоков для переименования.",
  "rename_dialog.btn.ok": "OK",
  "rename_dialog.btn.cancel": "Отмена",
  "summary.rename_completed":
    "Блок «{oldName}» переименован в «{newName}» (обновлено ссылок: {count}).",
  "toast.rename.failed": "Не удалось переименовать блок: {message}",
  // ── MVIEW (paper-space viewport command) ──
  "command.viewport.label": "МВИДЭКР (Видовой экран)",
  // ── VPCLIP (paper-space viewport clip command) ──
  "command.vpclip.label": "ВЭКРЕЗ (Подрезка ВЭ)",
  // ── LAYOUT (paper-space layout tab manager command) ──
  "command.layout.label": "МАКЕТ (Лист)",
  // ── LAYOUT R / LAYOUT RENAME (B2) — layout tab rename ──
  "layout.rename.success": "Лист «{old}» переименован в «{new}».",
  "layout.rename.notfound": "Лист с именем «{name}» не найден.",
  "layout.rename.cannot_rename_model": "Model space переименовать нельзя.",
  "layout.rename.duplicate_name": "Лист с именем «{name}» уже существует.",
  "layout.rename.empty_name": "Имя листа не может быть пустым.",
  "layout.name.too_long": "Имя листа не может превышать 255 символов.",
  "layout.rename.placeholder": "Новое имя листа",
  "layout.rename.usage":
    "ЛИСТ: использование: LAYOUT R <старое имя> <новое имя>",
  "layout.set.already_current": "Лист «{name}» уже является текущим.",
  "layout.switch.notfound":
    "Вкладка «{name}» не найдена. Доступные вкладки: {list}",
  // ── LAYOUT N / LAYOUT D (LAYOUT-COMMAND-HANDLERS) — create / delete ──
  "layout.new.success": "Лист «{name}» создан.",
  "layout.new.limit": "Превышен предел количества листов (255).",
  "layout.delete.success": "Лист «{name}» удалён.",
  "layout.delete.cannot_delete_model": "Вкладку «Модель» удалить нельзя.",
  "layout.delete.last_layout": "Последний лист удалить нельзя.",
  // ── LOCK / UNLOCK (COLLAB-LOCK-COMMAND) — realtime-collab soft locks ──
  "command.collab.lock.label": "БЛОКИР (Блокировка)",
  "command.collab.unlock.label": "РАЗБЛОК (Снятие блокировки)",
  "collab.lock.no_selection": "Выберите объекты для блокировки.",
  "collab.lock.layer_notfound": "Слой с именем «{name}» не найден.",
  "collab.lock.requested": "Запрошена блокировка {count} объект(ов)…",
  "collab.lock.released": "Запрошено снятие блокировки {count} объект(ов)…",
  "collab.lock.granted": "Заблокировано: «{target}» (владелец: {owner}).",
  "collab.lock.denied": "Отклонено: «{target}» уже заблокирован участником {owner}.",
  "collab.lock.not_connected": "Не подключено к сессии совместной работы.",
  "collab.lock.list_empty": "Нет активных блокировок.",
  "collab.lock.list_item": "{kind} «{target}» — {owner}",
  "collab.lock.list_query": "БЛОКИР: запрос списка блокировок…",
  "collab.lock.release_confirmed": "Блокировка снята: «{target}».",
  "collab.lock.release_no_effect": "Без эффекта: «{target}» не был заблокирован вами.",
  "collab.lock.edit_rejected":
    "Изменение отклонено: «{target}» заблокирован участником {owner}.",
  // round-5 — distinct denial-reason wording (P3) — a role/limit denial has
  // no owner to blame, so it must not reuse "denied" (which always names one).
  "collab.lock.denied_role": "Отклонено: у вас нет прав на блокировку объектов.",
  "collab.lock.denied_limit":
    "Отклонено: превышен лимит одновременных блокировок — сначала снимите одну.",
  // round-5 (P1) — the lock snapshot is UNAVAILABLE (reconnecting/errored
  // collab session), so the edit is rejected fail-closed with no specific
  // target/owner to report (unlike `edit_rejected`, which always names one).
  "collab.lock.edit_rejected_unavailable":
    "Изменение отклонено: не удалось проверить статус блокировки (переподключение к сессии совместной работы).",
  // round-5 (P1) — undo/redo of an entry whose inverse/forward touches a
  // NOW-locked entity/layer (locked by someone else since the entry was
  // recorded) is blocked, not silently corrupting the history stacks.
  "collab.lock.undo_vetoed":
    "Отмена заблокирована: объект заблокирован другим участником. Повторите позже.",
  "collab.lock.redo_vetoed":
    "Повтор заблокирован: объект заблокирован другим участником. Повторите позже.",
  // GitHub Codex review (PR #534, round 2) P2 — deleteLayer()/renameLayer()
  // (Layer Manager) now propagate a collab-lock veto as their own
  // `{ ok: false, error }` instead of silently returning `{ ok: true }`.
  "collab.lock.layer_action_vetoed":
    "Действие отклонено: слой «{name}» заблокирован другим участником.",
  "collab.lock.abort_batch_partial":
    "Отмена группы частично заблокирована: часть изменений не удалось откатить — объект заблокирован другим участником.",
  // ── COLLAB-LOCK-PROPERTY (W3.5) — read-only lock-status/owner property rows ──
  "property.locked": "Заблокировано",
  "property.locked_by": "Заблокировано пользователем",
  "property.lock_acquired_at": "Блокировка получена",
  "collab_lock_property.yes": "Да",
  "collab_lock_property.no": "Нет",
  "collab_lock_property.unknown": "Неизвестно",
  "collab_lock_property.you": "Вы",
  // ── Phase MC1 — SpaceContext / SpaceTabsBar ──
  "audit.layout_list": "ЛИСТ: Доступные листы: {list}",
  "space.tabs.model": "Модель",
  "space.tabs.context.rename": "Переименовать лист",
  "space.tabs.context.delete": "Удалить лист",
  "space.tabs.context.new": "Новый лист",
  "space.tabs.context.publish_selected": "Опубликовать выбранные листы ({count})",
  "space.mode_badge.mspace": "МОДЕЛЬ",
  "space.mode_badge.pspace": "ЛИСТ",
  "space.mode_badge.mspace_via_vp": "МОДЕЛЬ/ВЭ",
  // ── ATTDEF / DDATTE / ATTEDIT / EATTEDIT (Phase 2.1 + 2.2) ──
  "command.attdef.label": "Атрибут",
  "command.ddatte.label": "РедАтр",
  "command.eattedit.label": "УлРедАтр",
  "prompt.attdef.dialog_open": "АТРИБУТ: Задайте параметры атрибута в диалоге.",
  "prompt.ddatte.select": "РЕДАТР: Выберите вставку блока с атрибутами.",
  "prompt.ddatte.dialog_open": "РЕДАТР: Отредактируйте значения атрибутов.",
  "prompt.eattedit.select":
    "УЛРЕДАТР: Выберите вставку блока для расширенного редактирования.",
  "prompt.eattedit.dialog_open":
    "УЛРЕДАТР: Отредактируйте атрибуты на вкладках.",
  // AttributeDefinitionDialog (ATTDEF)
  "attdef_dialog.title": "Определение атрибута",
  "attdef_dialog.section.mode": "Режим",
  "attdef_dialog.section.attribute": "Атрибут",
  "attdef_dialog.section.insertion_point": "Точка вставки",
  "attdef_dialog.section.text_settings": "Параметры текста",
  "attdef_dialog.flag.invisible": "Невидимый",
  "attdef_dialog.flag.constant": "Постоянный",
  "attdef_dialog.flag.verify": "Контролируемый",
  "attdef_dialog.flag.preset": "Заданный",
  "attdef_dialog.flag.lock_position": "Зафиксировать позицию",
  "attdef_dialog.flag.multiline": "Многострочный",
  "attdef_dialog.label.tag": "Имя (тег)",
  "attdef_dialog.label.prompt": "Подсказка",
  "attdef_dialog.label.default": "Значение по умолчанию",
  "attdef_dialog.label.specify_on_screen": "Указать на экране",
  "attdef_dialog.label.x": "X:",
  "attdef_dialog.label.y": "Y:",
  "attdef_dialog.label.hj": "Горизонтальное выравнивание",
  "attdef_dialog.label.vj": "Вертикальное выравнивание",
  "attdef_dialog.label.text_style": "Стиль текста",
  "attdef_dialog.label.height": "Высота",
  "attdef_dialog.label.rotation": "Поворот",
  "attdef_dialog.label.width_factor": "Коэф. ширины",
  "attdef_dialog.label.oblique_angle": "Угол наклона",
  "attdef_dialog.placeholder.tag": "Введите имя атрибута",
  "attdef_dialog.hj.left": "По левому краю",
  "attdef_dialog.hj.center": "По центру",
  "attdef_dialog.hj.right": "По правому краю",
  "attdef_dialog.hj.aligned": "По выравниванию",
  "attdef_dialog.hj.middle": "Середина",
  "attdef_dialog.hj.fit": "По размеру",
  "attdef_dialog.vj.baseline": "По базовой линии",
  "attdef_dialog.vj.bottom": "По низу",
  "attdef_dialog.vj.middle": "По середине",
  "attdef_dialog.vj.top": "По верху",
  "attdef_dialog.btn.ok": "OK",
  "attdef_dialog.btn.cancel": "Отмена",
  "attdef_dialog.error.empty_tag": "Имя атрибута не может быть пустым.",
  "attdef_dialog.error.non_positive_height":
    "Высота текста должна быть положительным числом.",
  "attdef_dialog.error.oblique_out_of_range":
    "Угол наклона должен быть в диапазоне от -85 до 85 градусов.",
  // EditAttributesDialog (DDATTE / ATTEDIT)
  "edit_attributes_dialog.title": "Редактирование атрибутов",
  "edit_attributes_dialog.label.block_ref": "Блок: {name}",
  "edit_attributes_dialog.label.no_attributes":
    "У выбранной вставки нет атрибутов для редактирования.",
  "edit_attributes_dialog.label.editable_count":
    "Редактируемых атрибутов: {count}",
  "edit_attributes_dialog.indicator.invisible": "Невидимый",
  "edit_attributes_dialog.indicator.constant": "Постоянный",
  "edit_attributes_dialog.btn.ok": "OK",
  "edit_attributes_dialog.btn.cancel": "Отмена",
  // EnhancedAttributeEditorDialog (EATTEDIT)
  "eattedit_dialog.title": "Расширенный редактор атрибутов",
  "eattedit_dialog.label.block_ref": "Блок: {name}",
  "eattedit_dialog.label.no_attributes": "У выбранной вставки нет атрибутов.",
  "eattedit_dialog.col.tag": "Имя",
  "eattedit_dialog.col.value": "Значение",
  "eattedit_dialog.tab.attribute": "Атрибут",
  "eattedit_dialog.tab.text": "Параметры текста",
  "eattedit_dialog.tab.properties": "Свойства",
  "eattedit_dialog.label.tag": "Имя (тег)",
  "eattedit_dialog.label.prompt": "Подсказка",
  "eattedit_dialog.label.value": "Значение",
  "eattedit_dialog.label.text_style": "Стиль текста",
  "eattedit_dialog.label.height": "Высота",
  "eattedit_dialog.label.rotation": "Поворот",
  "eattedit_dialog.label.width_factor": "Коэф. ширины",
  "eattedit_dialog.label.oblique_angle": "Угол наклона",
  "eattedit_dialog.label.layer": "Слой",
  "eattedit_dialog.label.color": "Цвет",
  "eattedit_dialog.label.linetype": "Тип линий",
  "eattedit_dialog.label.lineweight": "Вес линий",
  "eattedit_dialog.btn.ok": "OK",
  "eattedit_dialog.btn.apply": "Применить",
  "eattedit_dialog.btn.cancel": "Отмена",
  // Summaries
  "summary.attdef_created": "Атрибут «{tag}» добавлен в блок «{block}».",
  "summary.attribute_updated": "Значение атрибута «{tag}» обновлено.",
  "summary.attributes_updated": "Обновлено атрибутов: {count}.",
  // Toasts
  "toast.attdef.not_in_block_editor":
    "Команда АТРИБУТ доступна только в Редакторе блоков (РЕДБЛОК).",
  "toast.attedit.no_insert_selected": "Выберите вставку блока с атрибутами.",
  "toast.attedit.no_attributes":
    "У выбранной вставки нет атрибутов для редактирования.",
  // BlockAttributeManagerDialog (BATTMAN — Phase 2.4)
  "battman_dialog.title": "Менеджер атрибутов блока",
  "battman_dialog.label.block": "Блок:",
  "battman_dialog.column.tag": "Имя",
  "battman_dialog.column.prompt": "Подсказка",
  "battman_dialog.column.default": "По умолчанию",
  "battman_dialog.column.modes": "Режимы",
  "battman_dialog.column.style": "Стиль",
  "battman_dialog.column.height": "Высота",
  "battman_dialog.column.rotation": "Поворот",
  "battman_dialog.empty": "В выбранном блоке нет атрибутов (ATTDEF).",
  "battman_dialog.inserts": "Вставок блока в чертеже: {count}",
  "battman_dialog.edit_title": "Изменение атрибута «{tag}»",
  "battman_dialog.btn.edit": "Изменить…",
  "battman_dialog.btn.remove": "Удалить",
  "battman_dialog.btn.move_up": "Вверх",
  "battman_dialog.btn.move_down": "Вниз",
  "battman_dialog.btn.sync": "Синхронизировать",
  "battman_dialog.btn.close": "Закрыть",
  "battman_dialog.btn.ok": "OK",
  "battman_dialog.btn.cancel": "Отмена",
  "battman_dialog.remove_confirm.title": "Удалить ATTDEF",
  "battman_dialog.remove_confirm.body":
    "Удалить атрибут «{tag}» из определения блока? Существующих вставок: {count}.",
  "battman_dialog.remove_confirm.cascade":
    "Также удалить связанные ATTRIB из существующих вставок",
  // Feed strings
  "feed.attsync":
    "СИНХРАТР: атрибуты вставок блока «{block}» синхронизированы.",
  "feed.battman.edited": "МЕНАТР: атрибут {id} в блоке «{block}» изменён.",
  "feed.battman.removed": "МЕНАТР: атрибут {id} удалён из блока «{block}».",
  "feed.battman.reordered":
    "МЕНАТР: порядок атрибутов блока «{block}» изменён.",
  "feed.dyn.nothing_selected":
    "ДИН: не выбрана вставка блока. Сначала выделите INSERT и повторите команду.",
  "feed.dynset.usage":
    "ДИНЗАД: синтаксис — DYNSET <имя-параметра> <значение>. Имена и значения с пробелами берите в кавычки.",
  "feed.dynset.parse_error_linear":
    "ДИНЗАД: «{value}» — не число с единицей. Используйте мм, см, м, дюйм, фут (например: 12м, 12mm, 0.5м).",
  "feed.dynset.parse_error_rotation":
    "ДИНЗАД: «{value}» — не угол. Используйте градусы (90, 90°), радианы (1.5rad, π/2) или числа (45).",
  "feed.dynset.parse_error_coord":
    "ДИНЗАД: «{value}» — не координаты. Используйте формат «x,y» или «x y».",
  "feed.dynset.invalid_units":
    "ДИНЗАД: неизвестная единица измерения «{unit}».",
  "inspector.dynamic.linear.value": "Значение",
  "inspector.dynamic.rotation.angle": "Угол",
  "inspector.dynamic.xy.x": "X",
  "inspector.dynamic.xy.y": "Y",
  "inspector.dynamic.point.x": "X",
  "inspector.dynamic.point.y": "Y",
  "dynamic_block_params.section_label": "Динамический блок",
  "dynamic_block_params.section_title": "Параметры динамического блока",
  "dynamic_block_params.choices_prefix": "варианты",
  // DXF round-trip warnings for dynamic blocks (DYN-1.2+1.3+1.4)
  "dxf.dynamic.unknown_class":
    "Неизвестный класс динамического блока: {cls} — сохранён как raw",
  "dxf.dynamic.orphan_action":
    "Действие {id} ссылается на неизвестный параметр",
  // ── DYN-1.7 Block Authoring Palette (Parameters + Actions) ──
  "authoring.palette.title": "Палитра создания блоков",
  "authoring.palette.hint":
    "Выберите тип параметра или действия, затем следуйте подсказкам.",
  "authoring.tab.parameters": "Параметры",
  "authoring.tab.actions": "Действия",
  "authoring.tab.parameter_sets": "Наборы параметров",
  "authoring.tab.constraints": "Ограничения",
  "authoring.tab.placeholder.tier2": "Доступно в Tier 2",
  "authoring.section.existing_parameters": "Существующие параметры",
  "authoring.section.existing_actions": "Существующие действия",
  "authoring.empty.parameters": "Параметры ещё не добавлены.",
  "authoring.empty.actions": "Действия ещё не добавлены.",
  "authoring.param.linear": "Линейный",
  "authoring.param.flip": "Отражение",
  "authoring.param.visibility": "Видимость",
  "authoring.param.basepoint": "Базовая точка",
  "authoring.param.point": "Точка",
  "authoring.param.polar": "Полярный",
  "authoring.param.xy": "XY",
  "authoring.param.rotation": "Поворот",
  "authoring.param.lookup": "Выборка",
  "authoring.param.constraint_horizontal": "Горизонтальная",
  "authoring.param.constraint_vertical": "Вертикальная",
  "authoring.param.constraint_angular": "Угловая",
  "authoring.action.move": "Переместить",
  "authoring.action.stretch": "Растянуть",
  "authoring.action.flip": "Отразить",
  "authoring.action.rotate": "Поворот",
  "authoring.action.scale": "Масштаб",
  "authoring.action.array": "Массив",
  "authoring.action.lookup": "Выборка",
  "authoring.action.requires.linear": "Требуется линейный параметр",
  "authoring.action.requires.linear_polar_xy":
    "Требуется параметр «Линейный», «Полярный» или «XY»",
  "authoring.action.requires.flip": "Требуется параметр «Отражение»",
  "authoring.action.requires.rotation": "Требуется параметр «Поворот»",
  "authoring.action.requires.lookup": "Требуется параметр «Выборка»",
  "authoring.btn.add_parameter": "Добавить параметр",
  "authoring.btn.add_action": "Добавить действие",
  "authoring.btn.edit": "Изменить",
  "authoring.btn.delete": "Удалить",
  "authoring.linked_actions": "Связанных действий: {count}",
  "authoring.selection_count": "Объектов: {count}",
  "authoring.param_link": "Параметр: {name}",
  "authoring.visibility_states": "Состояния видимости",
  "authoring.open_visibility_dialog": "Открыть диалог состояний видимости",
  // ── DYN-1.7 ParameterPropertiesDialog ──
  "param_dialog.title.linear": "Свойства линейного параметра",
  "param_dialog.title.flip": "Свойства параметра отражения",
  "param_dialog.title.visibility": "Свойства параметра видимости",
  "param_dialog.title.basepoint": "Свойства базовой точки",
  // ── DYN-2 ParameterPropertiesDialog Tier 2 ──
  "param_dialog.title.point": "Свойства параметра «Точка»",
  "param_dialog.title.polar": "Свойства параметра «Полярный»",
  "param_dialog.title.xy": "Свойства параметра «XY»",
  "param_dialog.title.rotation": "Свойства параметра «Поворот»",
  "param_dialog.title.lookup": "Свойства параметра «Выборка»",
  // ── DYN-2 Batch C constraint params ──
  "param_dialog.title.constraint_horizontal":
    "Свойства горизонтального ограничения",
  "param_dialog.title.constraint_vertical":
    "Свойства вертикального ограничения",
  "param_dialog.title.constraint_angular": "Свойства углового ограничения",
  "param_dialog.label.point_a": "Точка A (X, Y)",
  "param_dialog.label.point_b": "Точка B (X, Y)",
  "param_dialog.label.vertex": "Вершина (X, Y)",
  "param_dialog.label.distance_value_set": "Набор значений расстояния",
  "param_dialog.label.angle_value_set": "Набор значений угла",
  "param_dialog.label.base_point": "Базовая точка (X, Y)",
  "param_dialog.label.current_point": "Текущая точка (X, Y)",
  "param_dialog.label.second_point": "Вторая точка (X, Y)",
  "param_dialog.label.position": "Положение (X, Y)",
  "param_dialog.label.default_x": "X по умолчанию",
  "param_dialog.label.default_y": "Y по умолчанию",
  "param_dialog.label.default_angle": "Угол по умолчанию (°)",
  "param_dialog.label.radius": "Радиус",
  "param_dialog.label.reverse_lookup": "Обратная выборка",
  "param_dialog.label.lookup_table": "Таблица выборки",
  "param_dialog.label.value_set_x": "Набор значений X",
  "param_dialog.label.value_set_y": "Набор значений Y",
  "param_dialog.label.value_set_distance": "Набор значений расстояния",
  "param_dialog.label.value_set_angle": "Набор значений угла",
  "param_dialog.lookup.pick_placeholder": "(выберите таблицу)",
  "param_dialog.lookup.new_table": "Новая таблица…",
  "param_dialog.lookup.new_table_tooltip_disabled":
    "Откройте диалог БТАБЛВЫБОРКИ для создания таблицы (доступно в DYN-2.4).",
  "param_dialog.error.no_lookup_table": "Выберите таблицу выборки.",
  "param_dialog.label.name": "Имя",
  "param_dialog.label.label": "Метка",
  "param_dialog.label.description": "Описание",
  "param_dialog.label.default_distance": "Расстояние по умолчанию",
  "param_dialog.label.value_set_mode": "Набор значений",
  "param_dialog.label.value_set_increment": "Шаг",
  "param_dialog.label.value_set_min": "Минимум",
  "param_dialog.label.value_set_max": "Максимум",
  "param_dialog.label.value_set_list": "Список значений (через запятую)",
  "param_dialog.valueset.none": "Нет",
  "param_dialog.valueset.increment": "Приращение",
  "param_dialog.valueset.list": "Список",
  "param_dialog.label.flip_label": "Метка отражения",
  "param_dialog.label.base_label": "Базовая метка",
  "param_dialog.label.default_value": "Значение по умолчанию",
  "param_dialog.label.show_grip": "Показывать ручку",
  "param_dialog.label.chain_actions": "Цепочка действий",
  "param_dialog.btn.ok": "OK",
  "param_dialog.btn.cancel": "Отмена",
  "param_dialog.error.empty_name": "Имя параметра не может быть пустым.",
  "param_dialog.error.duplicate_name":
    "Параметр с именем «{name}» уже существует.",
  // ── DYN-1.7 ActionPropertiesDialog ──
  "action_dialog.title.move": "Свойства действия Переместить",
  "action_dialog.title.stretch": "Свойства действия Растянуть",
  "action_dialog.title.flip": "Свойства действия Отразить",
  // ── DYN-2 ActionPropertiesDialog Tier 2 ──
  "action_dialog.title.rotate": "Свойства действия «Поворот»",
  "action_dialog.title.scale": "Свойства действия «Масштаб»",
  "action_dialog.title.array": "Свойства действия «Массив»",
  "action_dialog.title.lookup": "Свойства действия «Выборка»",
  // DYN-3 Batch E — PolarStretch action
  "action_dialog.title.polar_stretch":
    "Свойства действия «Полярное растяжение»",
  "action_dialog.error.requires_polar_parameter":
    "Действию «Полярное растяжение» требуется параметр «Полярный».",
  "action_dialog.label.angle_multiplier": "Множитель угла",
  "action_dialog.label.scale_type": "Тип масштаба",
  "action_dialog.label.scale_multiplier": "Множитель масштаба",
  "action_dialog.label.column_spacing": "Шаг по столбцам",
  "action_dialog.label.row_spacing": "Шаг по строкам",
  "action_dialog.scale_type.uniform": "Равномерный",
  "action_dialog.scale_type.x_only": "Только X",
  "action_dialog.scale_type.y_only": "Только Y",
  "action_dialog.error.requires_rotation_parameter":
    "Действию требуется параметр «Поворот».",
  "action_dialog.error.requires_linear_polar_xy":
    "Действию требуется параметр «Линейный», «Полярный» или «XY».",
  "action_dialog.error.requires_lookup_parameter":
    "Действию требуется параметр «Выборка».",
  "action_dialog.error.column_spacing_positive":
    "Шаг по столбцам должен быть положительным числом.",
  "action_dialog.label.name": "Имя",
  "action_dialog.label.parameter": "Параметр",
  "action_dialog.label.distance_multiplier": "Множитель расстояния",
  "action_dialog.label.angle_offset": "Смещение угла (°)",
  "action_dialog.label.crossing_frame": "Рамка пересечения",
  "action_dialog.label.crossing_corner1": "Угол 1 (X, Y)",
  "action_dialog.label.crossing_corner2": "Угол 2 (X, Y)",
  "action_dialog.label.selection": "Выбранные объекты",
  "action_dialog.btn.ok": "OK",
  "action_dialog.btn.cancel": "Отмена",
  "action_dialog.error.empty_name": "Имя действия не может быть пустым.",
  "action_dialog.error.no_parameter": "Необходимо выбрать параметр.",
  "action_dialog.error.wrong_parameter":
    "Выбранный параметр несовместим с действием.",
  "action_dialog.error.no_selection":
    "Выберите хотя бы один объект для действия.",
  // ── DYN-1.8 VisibilityStatesDialog ──
  "visibility_dialog.title": "Состояния видимости",
  "visibility_dialog.parameter_label": "Параметр:",
  "visibility_dialog.section.states": "Состояния",
  "visibility_dialog.section.entities": "Объекты в состоянии «{state}»",
  "visibility_dialog.column.state": "Состояние",
  "visibility_dialog.column.default": "По умолч.",
  "visibility_dialog.column.visible": "Видимые",
  "visibility_dialog.column.hidden": "Скрытые",
  "visibility_dialog.btn.add_state": "Добавить состояние",
  "visibility_dialog.btn.rename": "Переименовать",
  "visibility_dialog.btn.delete": "Удалить",
  "visibility_dialog.btn.move_up": "Вверх",
  "visibility_dialog.btn.move_down": "Вниз",
  "visibility_dialog.btn.make_default": "По умолчанию",
  "visibility_dialog.btn.close": "Закрыть",
  // Phase K.4 — Set Current button + BVHIDE/BVSHOW commands
  "visibility_dialog.btn.set_current": "Сделать текущим",
  "visibility_dialog.btn.bvhide": "BVHIDE: Скрыть выбранные объекты",
  "visibility_dialog.btn.bvshow": "BVSHOW: Показать выбранные объекты",
  "visibility_dialog.btn.bvshow_all":
    "BVSHOW _All: Показать во всех состояниях",
  "visibility_dialog.current_marker": "(текущее)",
  "visibility_dialog.empty_states": "Нет состояний видимости. Добавьте первое.",
  "visibility_dialog.new_state_default_name": "Состояние {n}",
  "visibility_dialog.rename_prompt": "Новое имя состояния:",
  "visibility_dialog.delete_confirm": "Удалить состояние «{name}»?",
  "visibility_dialog.empty_entities":
    "В блоке нет объектов, которыми можно управлять через видимость.",
  "visibility_dialog.entity_visible": "Видимый",
  "visibility_dialog.entity_hidden": "Скрытый",
  // ── DYN-1.7 / DYN-1.8 commands ──
  "command.bparameter.label": "БПАРАМЕТР",
  "command.baction.label": "БДЕЙСТВИЕ",
  "command.bauthorpalette.label": "БПАЛИТРА",
  "command.bvstate.label": "БСОСТ",
  "command.bcparameter.label": "БКПАРАМ",
  "command.dynlist.label": "ДИНСПИСОК (Список динамических свойств)",
  "command.dynset.label": "ДИНУСТАН (Установить динамический параметр)",
  "command.dynreset.label": "ДИНСБРОС (Сбросить динамические параметры)",
  "command.gcconstraint.label": "ГЕОМОГРАН (Геометрическое ограничение)",
  // ── Prompts ──
  "prompt.bparameter.select_kind":
    "БПАРАМЕТР: выберите тип параметра в палитре.",
  "prompt.bparameter.linear.start": "ЛИНЕЙНЫЙ: укажите начальную точку.",
  "prompt.bparameter.linear.end": "ЛИНЕЙНЫЙ: укажите конечную точку.",
  "prompt.bparameter.linear.label_position":
    "ЛИНЕЙНЫЙ: укажите положение метки <совпадает с концом>.",
  "prompt.bparameter.flip.axis_start": "ОТРАЖЕНИЕ: укажите начало оси.",
  "prompt.bparameter.flip.axis_end": "ОТРАЖЕНИЕ: укажите конец оси.",
  "prompt.bparameter.visibility.position":
    "ВИДИМОСТЬ: укажите положение ручки.",
  "prompt.bparameter.basepoint.position":
    "БАЗТОЧКА: укажите положение базовой точки.",
  "prompt.baction.select_parameter": "БДЕЙСТВИЕ: выберите параметр.",
  "prompt.baction.select_objects":
    "БДЕЙСТВИЕ: выберите объекты (Enter для завершения).",
  "prompt.baction.crossing_corner1": "РАСТЯНУТЬ: укажите первый угол рамки.",
  "prompt.baction.crossing_corner2":
    "РАСТЯНУТЬ: укажите противоположный угол рамки.",
  // ── DYN-2 Batch C constraint prompts ──
  "prompt.constraint.horizontal.first_point": "ГОРИЗОНТ: укажите первую точку.",
  "prompt.constraint.horizontal.second_point":
    "ГОРИЗОНТ: укажите вторую точку.",
  "prompt.constraint.vertical.first_point": "ВЕРТИК: укажите первую точку.",
  "prompt.constraint.vertical.second_point": "ВЕРТИК: укажите вторую точку.",
  "prompt.constraint.angular.vertex": "УГЛОВ: укажите вершину угла.",
  "prompt.constraint.angular.first_ray": "УГЛОВ: укажите конец первого луча.",
  "prompt.constraint.angular.second_ray": "УГЛОВ: укажите конец второго луча.",
  "prompt.bcparameter.open":
    "БКПАРАМ: откройте вкладку «Ограничения» в палитре блоков.",
  // ── DYN-2 Batch C palette tab — Constraints ──
  "palette.constraints.dimensional": "Размерные",
  "palette.constraints.geometric": "Геометрические (Tier 3)",
  "palette.constraints.geometric.coincident": "Совпадение",
  "palette.constraints.geometric.collinear": "Коллинеарность",
  "palette.constraints.geometric.parallel": "Параллельность",
  "palette.constraints.geometric.perpendicular": "Перпендикулярность",
  "palette.constraints.geometric.tangent": "Касательность",
  "palette.constraints.geometric.equal": "Равенство",
  "palette.constraints.geometric.symmetric": "Симметрия",
  "palette.constraints.geometric.concentric": "Концентричность",
  "palette.constraints.geometric.fix": "Фиксация",
  "palette.constraints.geometric.smooth": "Гладкое сопряжение",
  "palette.constraints.geometric.horizontal": "Горизонтальность",
  "palette.constraints.geometric.vertical": "Вертикальность",
  // DYN-3 Batch D — geometric constraint command prompts
  "prompt.geom.select_first_entity": "Выберите первый объект:",
  "prompt.geom.select_second_entity": "Выберите второй объект:",
  "prompt.geom.select_entity": "Выберите объект:",
  "prompt.geom.select_first_curve": "Выберите первую кривую:",
  "prompt.geom.select_second_curve": "Выберите вторую кривую:",
  "prompt.geom.select_line": "Выберите отрезок:",
  "prompt.geom.select_symmetry_line": "Выберите ось симметрии:",
  // DYN-3 Batch D — authoring palette labels for Alignment parameter
  "authoring.param.alignment": "Выравнивание",
  "authoring.param.alignment.perpendicular": "Перпендикулярно",
  "authoring.param.alignment.parallel": "Параллельно",
  "authoring.action.polar_stretch": "Полярное растяжение",
  // DYN-3 Batch D — grip label
  "grip.block.alignment": "Грип выравнивания: {name}",
  // DYN-3 Batch E — Tier 3 constraint grip labels
  "grip.block.constraint.aligned": "Выровненное ограничение: {name}",
  "grip.block.constraint.radial": "Радиальное ограничение: {name}",
  "grip.block.constraint.diameter": "Диаметральное ограничение: {name}",
  // DYN-3 Batch D — geometric-constraint commands
  "command.GCCOINCIDENT": "ГКСОВПАД",
  "command.GCCOLLINEAR": "ГККОЛЛИН",
  "command.GCCONCENTRIC": "ГККОНЦЕНТР",
  "command.GCFIX": "ГКФИКС",
  "command.GCPARALLEL": "ГКПАРАЛ",
  "command.GCPERPENDICULAR": "ГКПЕРП",
  "command.GCTANGENT": "ГККАС",
  "command.GCEQUAL": "ГКРАВЕНСТВО",
  "command.GCSYMMETRIC": "ГКСИММЕТР",
  "command.GCSMOOTH": "ГКГЛАДК",
  "command.GCHORIZONTAL": "ГКГОРИЗ",
  "command.GCVERTICAL": "ГКВЕРТ",
  // ── Audit events ──
  "audit.dynamic_parameter_added":
    "БПАРАМЕТР: параметр добавлен в блок «{block}».",
  "audit.dynamic_parameter_updated":
    "БПАРАМЕТР: параметр {id} обновлён в блоке «{block}».",
  "audit.dynamic_parameter_removed":
    "БПАРАМЕТР: параметр {id} удалён из блока «{block}».",
  "audit.dynlist":
    "ДИНСПИС: параметры вставки {id} выведены в командной строке.",
  "audit.dynset":
    "ДИНЗАД: параметр «{param}» вставки {id} установлен в «{value}».",
  "audit.dynreset":
    "ДИНСБРОС: параметры вставки {id} сброшены к значениям блока по умолчанию.",
  "audit.dynamic_action_added":
    "БДЕЙСТВИЕ: действие добавлено в блок «{block}».",
  "audit.dynamic_action_updated":
    "БДЕЙСТВИЕ: действие {id} обновлено в блоке «{block}».",
  "audit.dynamic_action_removed":
    "БДЕЙСТВИЕ: действие {id} удалено из блока «{block}».",
  "audit.visibility_state_added":
    "БСОСТ: добавлено состояние «{name}» в блоке «{block}».",
  "audit.visibility_state_renamed":
    "БСОСТ: состояние переименовано в «{name}» (блок «{block}»).",
  "audit.visibility_state_deleted":
    "БСОСТ: состояние {id} удалено из блока «{block}».",
  "audit.visibility_states_reordered":
    "БСОСТ: порядок состояний блока «{block}» изменён.",
  "audit.visibility_default_set":
    "БСОСТ: состояние {id} назначено по умолчанию (блок «{block}»).",
  "audit.visibility_entity_toggled":
    "БСОСТ: видимость объекта {entity} изменена (блок «{block}»).",
  // Phase K.4 — Visibility States Manager additions
  "audit.visibility_current_set":
    "БСОСТ: текущее состояние установлено в {id} (блок «{block}»).",
  "audit.bvhide":
    "BVHIDE: скрыто {count} объект(ов) в текущем состоянии (блок «{block}»).",
  "audit.bvshow":
    "BVSHOW: показано {count} объект(ов) в текущем состоянии (блок «{block}»).",
  "audit.bvshow_all":
    "BVSHOW _All: показано {count} объект(ов) во всех состояниях (блок «{block}»).",
  // ── Toasts ──
  "toast.authoring.not_in_block_editor":
    "Эта команда доступна только в Редакторе блоков (РЕДБЛОК).",
  "toast.authoring.no_visibility_param":
    "В блоке нет параметра видимости. Сначала добавьте его.",
  // ── DYN-1.6 Dynamic block grips ──
  "grip.block.linear": "Линейный параметр: {name}",
  "grip.block.flip": "Параметр отражения: {name}",
  "grip.block.visibility": "Параметр видимости: {name}",
  "grip.block.basepoint": "Базовая точка: {name}",
  "grip.block.visibility.select_state": "Выберите состояние видимости",
  // ── DYN-2 grip tooltips ──
  "grip.block.point": "Точка: {name}",
  "grip.block.polar.distance": "Полярный параметр — расстояние: {name}",
  "grip.block.polar.angle": "Полярный параметр — угол: {name}",
  "grip.block.xy.x": "XY — X: {name}",
  "grip.block.xy.y": "XY — Y: {name}",
  "grip.block.rotation": "Параметр поворота: {name}",
  "grip.block.lookup": "Параметр выборки: {name}",
  "grip.block.lookup.select_row": "Выберите строку выборки",
  // ── DYN-2 Batch C constraint grip tooltips ──
  "grip.block.constraint.horizontal": "Горизонтальное ограничение: {name}",
  "grip.block.constraint.vertical": "Вертикальное ограничение: {name}",
  "grip.block.constraint.angular": "Угловое ограничение: {name}",
  // ── DYN-1.10 Properties palette — dynamic block Custom Properties ──
  "properties.custom": "Пользовательские свойства",
  "property.custom.visibility": "Видимость",
  "property.custom.param_prefix": "Параметр",
  // ── DYN-2 property labels for Tier 2 parameters ──
  "property.custom.x": "X",
  "property.custom.y": "Y",
  "property.custom.distance": "Расстояние",
  "property.custom.angle": "Угол",
  "property.custom.lookup": "Выборка",
  // ── DYN-1.9 TESTBLOCK / CLOSETESTBLOCK ──
  "command.testblock.label": "ТЕСТБЛОК",
  "command.closetestblock.label": "ЗАКРЫТЬТЕСТ",
  "summary.testblock_entered":
    "Открыт тестовый режим блока «{name}» (ТЕСТБЛОК).",
  "summary.testblock_exited": "Тестовый режим блока закрыт (ЗАКРЫТЬТЕСТ).",
  "toast.testblock.not_in_block_editor":
    "Команда ТЕСТБЛОК доступна только в Редакторе блоков (РЕДБЛОК).",
  "toast.testblock.no_block":
    "Не удалось определить имя блока для тестового режима.",
  "toast.testblock.not_in_test_block":
    "Команда ЗАКРЫТЬТЕСТ доступна только в тестовом режиме блока.",
  "banner.testblock.title":
    "Тестовый режим блока: {name}. Изменения не сохраняются.",
  "banner.testblock.exit": "Выйти из теста",
  "authoring.toolbar.test_block": "Тест блока",
  // ── DYN-1.11 Authoring palette command-line commands ──
  "command.bauthorpaletteclose.label": "БПАЛИТРАЗАКР",
  "command.bgripset.label": "БГРИПНАБОР",
  "summary.bauthorpalette_toggled":
    "Панель авторинга блоков переключена (БПАЛИТРА).",
  "summary.bauthorpalette_closed":
    "Панель авторинга блоков закрыта (БПАЛИТРАЗАКР).",
  "feed.bgripset.tier1_notice":
    "БГРИПНАБОР: полный диалог доступен в Tier 2. Видимость грипсов настраивается через свойства параметра.",
  // ── DYN-2 B.2 — Lookup Table editor (BLOOKUPTABLE) ──
  "lookup.dialog.title": "Таблица выборки: {name}",
  "lookup.dialog.untitled": "без имени",
  "lookup.dialog.table_name": "Имя таблицы",
  "lookup.dialog.add_row": "+ Строка",
  "lookup.dialog.remove_row": "Удалить",
  "lookup.dialog.add_input_column": "+ Входной столбец",
  "lookup.dialog.remove_column": "Убрать столбец",
  "lookup.dialog.edit_output": "Метка выходного столбца",
  "lookup.dialog.pick_parameter": "Выберите параметр",
  "lookup.dialog.pick_property": "Выберите свойство",
  "lookup.dialog.no_parameters":
    "В блоке нет параметров для столбцов выборки. Сначала добавьте параметр Linear / Polar / XY / Point / Rotation / Flip / Visibility.",
  "lookup.dialog.empty_rows":
    "Строк пока нет. Нажмите «+ Строка», чтобы добавить.",
  "lookup.dialog.btn.apply": "Применить",
  "lookup.dialog.btn.ok": "OK",
  "lookup.dialog.btn.cancel": "Отмена",
  "lookup.dialog.error.no_columns":
    "Таблица должна содержать хотя бы один входной столбец.",
  "lookup.dialog.error.duplicate_name":
    "Таблица с именем «{name}» уже существует.",
  "lookup.column.parameter": "Параметр",
  "lookup.column.property.currentDistance": "Расстояние",
  "lookup.column.property.currentAngle": "Угол",
  "lookup.column.property.currentX": "X",
  "lookup.column.property.currentY": "Y",
  "lookup.column.property.currentValue": "Значение",
  "lookup.column.property.currentStateId": "Состояние",
  "lookup.column.output": "Значение",
  "lookup.picker.title": "Выбор таблицы выборки",
  "lookup.picker.new": "Создать новую…",
  "lookup.picker.new_prompt": "Имя новой таблицы выборки:",
  "lookup.picker.new_default_name": "Таблица{n}",
  // ── DYN-2 B.2 — Block Properties Table editor (BTABLE) ──
  "block_props.dialog.title": "Таблица свойств блока",
  "block_props.dialog.add_property": "+ Свойство",
  "block_props.dialog.move_up": "▲",
  "block_props.dialog.move_down": "▼",
  "block_props.dialog.remove": "Удалить",
  "block_props.dialog.empty":
    "Свойства не добавлены. Нажмите «+ Свойство», чтобы добавить.",
  "block_props.dialog.btn.apply": "Применить",
  "block_props.dialog.btn.ok": "OK",
  "block_props.dialog.btn.cancel": "Отмена",
  "block_props.dialog.error.duplicate_key":
    "Ключ «{key}» уже существует в таблице.",
  "block_props.dialog.error.empty_key": "Ключ не может быть пустым.",
  "block_props.col.key": "Ключ",
  "block_props.col.label": "Метка",
  "block_props.col.type": "Тип",
  "block_props.col.default": "Значение по умолчанию",
  "block_props.col.formula": "Формула",
  "block_props.type.string": "Текст",
  "block_props.type.number": "Число",
  "block_props.type.boolean": "Да / Нет",
  "block_props.default.true": "Да",
  "block_props.default.false": "Нет",
  // ── DYN-4.1 — Formula expressions ──
  "formula.test": "Проверить",
  "formula.test.ok": "Результат: {value}",
  "formula.error": "Ошибка в формуле: {message}",
  "formula.circular_reference": "Циклическая ссылка",
  "formula.help": "Выражение: арифметика, функции Math.*, ссылки на параметры",
  // ── DYN-4.2 — Reverse Lookup ──
  "lookup.reverse.placeholder": "Выберите значение",
  "lookup.reverse.none": "(нет строк)",
  // ── DYN-4.4 — Performance profiler ──
  "profile.title": "Профилирование блока",
  "profile.total": "Всего",
  "profile.formulas": "Формулы",
  "profile.actions": "Действия",
  "profile.parameters": "Параметры",
  "profile.solver": "Решатель",
  "profile.solver.iterations": "Итерации: {count}",
  "profile.solver.residual": "Остаток: {value}",
  "profile.cache_misses": "Промахи кэша: {count}",
  "profile.ms": "{value} мс",
  // ── DYN-2 B.2 — Parameter Sets presets ──
  "authoring.param_set.heading": "Готовые наборы",
  "authoring.param_set.hint":
    "Щёлкните набор — параметр и действие создаются одним действием.",
  "authoring.param_set.linear_move": "Линейное перемещение",
  "authoring.param_set.linear_stretch": "Линейное растягивание",
  "authoring.param_set.linear_array": "Линейный массив",
  "authoring.param_set.linear_scale": "Линейный масштаб",
  "authoring.param_set.polar_move": "Полярное перемещение",
  "authoring.param_set.polar_stretch": "Полярное растягивание",
  "authoring.param_set.polar_scale": "Полярный масштаб",
  "authoring.param_set.xy_move": "XY перемещение",
  "authoring.param_set.xy_scale_uniform": "XY масштаб (одинаковый)",
  "authoring.param_set.xy_array": "XY массив",
  "authoring.param_set.rotation_set": "Набор поворота",
  "authoring.param_set.flip_set": "Набор отражения",
  "authoring.param_set.visibility_set": "Набор видимости",
  "authoring.param_set.lookup_set": "Набор выборки",
  "authoring.param_set.basepoint": "Базовая точка",
  "authoring.param_set.user_defined_placeholder":
    "Пользовательские наборы появятся в следующей итерации.",
  // ── DYN-2 B.2 — Shell commands (BLOOKUPTABLE / BTABLE) ──
  "command.blookuptable.label": "БТАБЛВЫБОРКА",
  "command.btable.label": "БТАБЛСВОЙСТВА",
  "prompt.blookuptable.select":
    "БТАБЛВЫБОРКА: выберите таблицу для редактирования.",
  "prompt.blookuptable.none":
    "БТАБЛВЫБОРКА: таблицы отсутствуют. Создать новую?",
  "prompt.btable.opened": "БТАБЛСВОЙСТВА: открыт редактор таблицы свойств.",
  "toast.blookuptable.not_in_block_editor":
    "Команда БТАБЛВЫБОРКА доступна только в Редакторе блоков (РЕДБЛОК).",
  "toast.btable.not_in_block_editor":
    "Команда БТАБЛСВОЙСТВА доступна только в Редакторе блоков (РЕДБЛОК).",
  "summary.lookup_table_opened": "Открыт диалог таблицы выборки «{name}».",
  "summary.lookup_table_created": "Создана таблица выборки «{name}».",
  "summary.lookup_table_saved": "Таблица выборки «{name}» сохранена.",
  "summary.block_properties_opened":
    "Открыт диалог таблицы свойств блока «{block}».",
  "summary.block_properties_saved":
    "Таблица свойств блока «{block}» сохранена.",
  // ── DYN-2 B.2 — Audit entries ──
  "audit.lookup_table_created":
    "БТАБЛВЫБОРКА: создана таблица выборки в блоке «{block}».",
  "audit.lookup_table_updated":
    "БТАБЛВЫБОРКА: таблица {id} обновлена в блоке «{block}».",
  "audit.lookup_table_deleted":
    "БТАБЛВЫБОРКА: таблица {id} удалена из блока «{block}».",
  "audit.block_properties_table_set":
    "БТАБЛСВОЙСТВА: таблица свойств блока «{block}» сохранена.",
  "audit.block_property_added":
    "БТАБЛСВОЙСТВА: добавлено свойство в блок «{block}».",
  "audit.block_property_updated":
    "БТАБЛСВОЙСТВА: свойство {key} обновлено в блоке «{block}».",
  "audit.block_property_removed":
    "БТАБЛСВОЙСТВА: свойство {key} удалено из блока «{block}».",
  "audit.block_properties_reordered":
    "БТАБЛСВОЙСТВА: порядок свойств блока «{block}» изменён.",

  // ── Phase N.5 — Block Properties Manager (BPM) audits ──
  "audit.bpm_parameter_renamed":
    "ДСБЛОК: параметр {id} переименован в «{name}» в блоке «{block}».",
  "audit.bpm_parameter_description_updated":
    "ДСБЛОК: описание параметра {id} обновлено в блоке «{block}».",
  "audit.bpm_parameter_show_toggled":
    "ДСБЛОК: видимость параметра {id} в палитре свойств переключена в блоке «{block}».",
  "audit.bpm_parameters_reordered":
    "ДСБЛОК: порядок параметров блока «{block}» изменён.",
  "audit.bpm_action_renamed":
    "ДСБЛОК: действие {id} переименовано в «{name}» в блоке «{block}».",
  "audit.bpm_action_description_updated":
    "ДСБЛОК: описание действия {id} обновлено в блоке «{block}».",
  "audit.bpm_actions_reordered":
    "ДСБЛОК: порядок действий блока «{block}» изменён.",

  // ── Phase N.5 — Block Properties Manager dialog labels ──
  "bpm.dialog.title": "Диспетчер свойств блока",
  "bpm.dialog.blocks_section": "Блоки",
  "bpm.dialog.empty_blocks": "В чертеже нет определений блоков.",
  "bpm.dialog.no_block_selected":
    "Выберите блок слева, чтобы просмотреть его параметры и действия.",
  "bpm.tab.parameters": "Параметры",
  "bpm.tab.actions": "Действия",
  "bpm.tab.visibility": "Видимость",
  "bpm.tab.attributes": "Атрибуты",
  "bpm.col.name": "Имя",
  "bpm.col.description": "Описание",
  "bpm.col.type": "Тип",
  "bpm.col.default": "По умолч.",
  "bpm.col.linked_parameter": "Параметр",
  "bpm.col.selected_count": "Объектов",
  "bpm.col.show_in_palette": "В палитре свойств",
  "bpm.col.attribute_tag": "Тэг",
  "bpm.col.attribute_prompt": "Подсказка",
  "bpm.col.attribute_default": "Значение",
  "bpm.col.row_handle": "↕",
  "bpm.empty.parameters": "У блока нет параметров.",
  "bpm.empty.actions": "У блока нет действий.",
  "bpm.empty.visibility": "У блока нет параметров видимости.",
  "bpm.empty.attributes": "У блока нет атрибутов (ATTDEF).",
  "bpm.btn.rename": "Переименовать",
  "bpm.btn.delete": "Удалить",
  "bpm.btn.move_up": "Вверх",
  "bpm.btn.move_down": "Вниз",
  "bpm.btn.sync": "Синхронизация",
  "bpm.btn.audit": "Аудит",
  "bpm.btn.export": "Экспорт",
  "bpm.btn.close": "Закрыть",
  "bpm.toolbar.sync_done":
    "Граф вычислений синхронизирован для блока «{block}».",
  "bpm.toolbar.audit_clean": "Аудит блока «{block}»: проблем не обнаружено.",
  "bpm.toolbar.audit_issues":
    "Аудит блока «{block}»: найдено проблем — {count}.",
  "bpm.toolbar.export_done":
    "CSV-экспорт блока «{block}» скопирован в буфер обмена.",
  "bpm.confirm.delete_parameter":
    "Удалить параметр «{name}»? Привязанные действия ({count}) станут оторванными и тоже будут удалены.",
  "bpm.prompt.rename_parameter": "Новое имя параметра:",
  "bpm.prompt.rename_action": "Новое имя действия:",
  "bpm.visibility.parameter_label": "Параметр видимости",
  "bpm.visibility.state": "Состояние",
  "bpm.visibility.default_marker": "(по умолч.)",
  "bpm.visibility.current_marker": "(текущее)",
  "command.bpm.label": "Диспетчер свойств блока",

  // ── DYN-3 Batch C — Value Set editor (shared across parameter kinds) ──
  "value_set.mode.none": "Нет",
  "value_set.mode.increment": "Приращение",
  "value_set.mode.list": "Список",
  "value_set.increment_field": "Шаг",
  "value_set.min": "Мин",
  "value_set.max": "Макс",
  "value_set.list_values": "Значения",
  "value_set.list_add": "Добавить значение",
  "value_set.list_remove": "Удалить",
  "value_set.list_move_up": "Переместить вверх",
  "value_set.list_move_down": "Переместить вниз",
  "value_set.list_empty": "Список пуст — нажмите «Добавить значение».",

  // ── DYN-3 Tier 3 — Alignment / Aligned / Radial / Diameter forms ──
  "param_dialog.title.alignment": "Параметр выравнивания",
  "param_dialog.title.constraint_aligned": "Параметр-ограничение «Выровнено»",
  "param_dialog.title.constraint_radial": "Параметр-ограничение «Радиус»",
  "param_dialog.title.constraint_diameter": "Параметр-ограничение «Диаметр»",
  "param_dialog.label.alignment_type": "Тип выравнивания",
  "param_dialog.label.alignment_perpendicular": "Перпендикулярно",
  "param_dialog.label.alignment_parallel": "Параллельно",
  "param_dialog.label.center": "Центр",
  "param_dialog.label.radius_point": "Точка радиуса",
  "param_dialog.label.point_1": "Точка 1",
  "param_dialog.label.point_2": "Точка 2",
  "param_dialog.label.diameter": "Диаметр",
  "param_dialog.label.default_radius": "Радиус по умолчанию",
  "param_dialog.label.default_diameter": "Диаметр по умолчанию",
  "param_dialog.label.radius_value_set": "Набор значений радиуса",
  "param_dialog.label.diameter_value_set": "Набор значений диаметра",

  // ── DYN-3 Batch C — Chain Actions (pipeline authoring) ──
  "action.chain.section": "Цепочка действий",
  "action.chain.include": "Включить в цепочку",
  "action.chain.group.new": "Новая группа",
  "action.chain.group.existing": "Существующая группа",
  "action.chain.group.label": "Группа цепочки",
  "action.chain.group.pick_placeholder": "— Независимое действие —",
  "action.chain.group.option_none": "Независимое действие",
  "action.chain.group.option_new": "Новая группа",
  "action.chain.group.option_existing": "Группа {index}",
  "action.chain.remove": "Убрать из цепочки",
  "audit.chain_group_updated":
    "ЦЕПОЧКА: группа цепочки действий обновлена в блоке «{block}».",

  // ── DYN-3 Batch C — Nested dynamic blocks (evaluator diagnostics) ──
  "evaluator.nested.cycle_detected":
    "Обнаружен цикл во вложенных динамических блоках: {chain}",
  "evaluator.nested.depth_exceeded":
    "Превышена глубина вложенности динамических блоков (макс. {max}).",

  // ── XREF suite (Phase FIN.3) ──────────────────────────────
  "command.xattach.label": "ССВСТАВИТЬ (Присоединить внешнюю ссылку)",
  "command.xbind.label": "ССВНЕДРИТЬ (Внедрить внешнюю ссылку)",
  "command.xclip.label": "ССКОНТУР (Подрезать внешнюю ссылку)",
  "command.xdetach.label": "ССОТСОЕД (Отсоединить внешнюю ссылку)",
  "command.xreload.label": "ССПЕРЕЗАГР (Перезагрузить внешнюю ссылку)",
  "command.xunload.label": "ССВЫГРУЗИТЬ (Выгрузить внешнюю ссылку)",
  "xref.manager.title": "Менеджер ссылок",
  "xref.manager.attach": "Присоединить...",
  "xref.manager.attachment_type": "Тип:",
  "xref.manager.empty": "В чертеже нет внешних ссылок.",
  "xref.manager.close": "Закрыть",
  "xref.column.name": "Имя",
  "xref.column.path": "Путь",
  "xref.column.status": "Состояние",
  "xref.column.attachment_type": "Тип",
  "xref.column.actions": "Действия",
  "xref.attachment.attach": "Вставка",
  "xref.attachment.overlay": "Наложение",
  "xref.status.resolved": "Разрешено",
  "xref.status.unloaded": "Выгружено",
  "xref.status.not_found": "Не найдено",
  "xref.action.unload": "Выгрузить",
  "xref.action.reload": "Перезагрузить",
  "xref.action.bind": "Внедрить",
  "xref.action.detach": "Отсоединить",
  "xref.action.clip": "Обрезка",
  "xref.action.open": "Открыть",
  "audit.xref_attached": "Внешняя ссылка «{name}» присоединена ({path}).",
  "audit.xref_bound": "Внешняя ссылка «{name}» внедрена в чертёж.",
  "audit.xref_unloaded": "Внешняя ссылка «{name}» выгружена.",
  "audit.xref_reloaded": "Внешняя ссылка «{name}» перезагружена.",
  "audit.xref_detached": "Внешняя ссылка «{name}» отсоединена.",
  "audit.xref_clipped": "Применена обрезка к ссылке «{name}».",
  "audit.xref_clip_cleared": "Обрезка снята с ссылки «{name}».",
  "summary.xref_attached": "Внешняя ссылка «{name}» присоединена из «{path}».",
  "summary.xref_bound": "Ссылка «{name}» преобразована в локальный блок.",
  "summary.xref_unloaded": "Ссылка «{name}» выгружена.",
  "summary.xref_reloaded": "Ссылка «{name}» перезагружена.",
  "summary.xref_detached": "Ссылка «{name}» отсоединена.",
  "summary.xref_clipped": "Обрезка применена к «{name}».",
  "toast.xref.attach_failed": "Не удалось присоединить ссылку: {message}",
  "toast.xref.reload_failed": "Не удалось перезагрузить ссылку: {message}",
  "toast.xref.unload_failed": "Не удалось выгрузить ссылку: {message}",
  "toast.xref.bind_failed": "Не удалось внедрить ссылку: {message}",
  "toast.xref.detach_failed": "Не удалось отсоединить ссылку: {message}",
  "toast.xref.clip_failed": "Не удалось применить обрезку: {message}",
  "toast.xref.open_unsupported":
    "Открытие ссылки «{name}» в новом окне пока не поддерживается.",
  // ── EATTEXT (Phase FIN.5) ─────────────────────────────────
  "eattext.dialog.title": "Извлечение атрибутов",
  "eattext.filter.blocks": "Блоки",
  "eattext.filter.tags": "Теги",
  "eattext.empty_blocks": "Нет доступных блоков",
  "eattext.empty_tags": "Нет доступных тегов",
  "eattext.include_invisible": "Включать невидимые",
  "eattext.include_constant": "Включать константные",
  "eattext.preview": "Предпросмотр: {shown} из {total} строк",
  "eattext.col.block": "Блок",
  "eattext.col.insert_id": "ID вставки",
  "eattext.download": "Скачать CSV",
  "eattext.btn.cancel": "Отмена",
  "summary.eattext_downloaded": "Файл атрибутов сохранён ({count} строк).",
  // ── BCOUNT (Phase FIN.6) ──────────────────────────────────
  "bcount.summary": "Количество вставок блоков",
  // ── MINSERT grips (Phase FIN.7) ───────────────────────────
  "grip.minsert.columns": "MINSERT: число столбцов",
  "grip.minsert.rows": "MINSERT: число строк",
  "grip.minsert.column_spacing": "MINSERT: шаг столбцов",
  "grip.minsert.row_spacing": "MINSERT: шаг строк",
  // ── Variant A: editable dynamic-block parameter controls ──
  "dynamic_block_params.edit_aria": "Изменить параметр {name}",
  "dynamic_block_params.reset_defaults": "Сбросить к значениям по умолчанию",
  "dynamic_block_params.reset_defaults_hint":
    "Восстанавливает все параметры из мастер-блока (DYNRESET).",
  "dynamic_block_params.no_typed_metadata":
    "Нет типизированной метаинформации — поля редактируются как текст/число.",
  "dynamic_block_params.unit_mm": "мм",
  "dynamic_block_params.unit_deg": "°",
  "dynamic_block_params.axis_x": "X",
  "dynamic_block_params.axis_y": "Y",
  // ── PROPERTIES / CHPROP / MATCHPROP — property system commands ──
  "command.properties.label": "СВОЙСТВА (Свойства)",
  "command.chprop.label": "ИЗМСВ (Изменить свойства)",
  "command.matchprop.label": "КОПИРСВ (Копировать свойства)",
  // ── T7 (agent-bridge) — LAYER table-management command ──
  "command.layer.label": "СЛОЙ (Диспетчер слоёв)",
  // ── Unified PROPERTY <id> <name> <value> command (Variant C) ──
  // AutoCAD-style command-line entry that routes to dynamic/set or
  // chprop/apply behind a single syntax. Russian uses "СВОЙСТВО" so
  // AI agents drafting in Russian produce CIS-localized command text.
  "command.property.label": "Свойство",
  "prompt.property.usage":
    "СВОЙСТВО: использование: СВОЙСТВО <id> <имя> <значение>",
  "audit.property.changed": "СВОЙСТВО: {name} на {id} → {value}.",
  "error.property.entity_not_found": "СВОЙСТВО: объект «{id}» не найден.",
  "error.property.unknown_name": "СВОЙСТВО: неизвестное свойство «{name}».",
  "error.property.empty_name": "СВОЙСТВО: имя свойства не указано.",
  // ── SETVAR / sysvar command-line feedback (Dynamic Input Phase 0) ──
  "sysvar.feedback": "{name} = {value}",
  "sysvar.unknown": "Неизвестная переменная: {name}",
  "sysvar.invalid_value": "Недопустимое значение для {name}",
  "sysvar.current_value": "Текущее значение {name}: {value}",
  // ── Dynamic Input pointer field labels (Phase 1) ──
  "dynin.field.distance": "Расстояние",
  "dynin.field.angle": "Угол",
  "dynin.field.x": "X",
  "dynin.field.y": "Y",
  // ── Dynamic Input dimension field labels (Phase 2) ──
  "dynin.field.length": "Длина",
  "dynin.field.radius": "Радиус",
  "dynin.field.diameter": "Диаметр",
  "dynin.field.rotation": "Угол поворота",
  "dynin.field.height": "Высота",
  "dynin.field.width": "Ширина",
  "dynin.field.scale": "Масштаб",
  // ── DI coord-expression labels (AutoCAD parity: @ / # prefix splits field into two inputs) ──
  "dynin.field.coord_prefix_rel_cart": "@ΔX",
  "dynin.field.coord_prefix_abs_cart": "X",
  "dynin.field.coord_prefix_rel_polar": "@дист",
  "dynin.field.coord_prefix_abs_polar": "дист",
  "dynin.field.coord_sec_rel_cart": "ΔY",
  "dynin.field.coord_sec_abs_cart": "Y",
  "dynin.field.coord_sec_angle": "угол",
  // ── DI overlay (B8) ──
  "dynin.overlay.aria": "Динамический ввод",
  "dynin.overlay.settings": "Настройки динамического ввода",
  // ── DSettings Dialog (Phase 3) ──
  "dsettings.title": "Режимы рисования",
  "dsettings.tab.snap": "Шаг и сетка",
  "dsettings.tab.polar": "Отслеживание",
  "dsettings.tab.osnap": "Объектная привязка",
  "dsettings.tab.dyninput": "Динамический ввод",
  "dsettings.ok": "ОК",
  "dsettings.cancel": "Отмена",
  "dsettings.dyninput.pointer.title": "Ввод указателя",
  "dsettings.dyninput.pointer.enable": "Включить ввод указателя",
  "dsettings.dyninput.pointer.settings": "Настройки...",
  "dsettings.dyninput.dim.title": "Ввод размеров",
  "dsettings.dyninput.dim.enable": "Включить ввод размеров",
  "dsettings.dyninput.dim.settings": "Настройки...",
  "dsettings.dyninput.prompt.title": "Динамические подсказки",
  "dsettings.dyninput.prompt.enable":
    "Показывать подсказки команд возле курсора",
  "dsettings.dyninput.appearance": "Оформление...",
  "dsettings.pi.polar": "Полярные",
  "dsettings.pi.cartesian": "Декартовы",
  "dsettings.pi.relative": "Относительные",
  "dsettings.pi.absolute": "Абсолютные",
  "dsettings.pi.vis.always": "Всегда показывать",
  "dsettings.pi.vis.typing": "При начале ввода",
  "dsettings.pi.vis.command": "При запросе точки",
  "dsettings.di.one": "Одно поле размера",
  "dsettings.di.two": "Два поля размеров",
  "dsettings.snap.title": "Шаг (привязка)",
  "dsettings.snap.enable": "Включить шаг",
  "dsettings.snap.hint": "F9 переключает шаг",
  "dsettings.grid.title": "Сетка",
  "dsettings.grid.enable": "Включить сетку",
  "dsettings.grid.hint": "F7 переключает сетку",
  "dsettings.polar.enable": "Полярное отслеживание",
  "dsettings.polar.hint": "F10 переключает отслеживание",
  "dsettings.polar.increment.label": "Приращение угла:",
  "dsettings.polar.additional.enable": "Дополнительные углы",
  "dsettings.polar.additional.placeholder": "напр. 15;22.5",
  "dsettings.polar.additional.hint":
    "До 10 углов через ; или , (POLARADDANG)",
  "dsettings.polar.basis.title": "Измерение полярных углов",
  "dsettings.polar.basis.absolute": "Абсолютное",
  "dsettings.polar.basis.relative": "Относительно последнего сегмента",
  "dsettings.osnap.otrackEnable": "Отслеживание объектной привязки (F11)",
  "dsettings.osnap.title": "Режимы объектной привязки",
  "dsettings.osnap.endpoint": "Конточка",
  "dsettings.osnap.midpoint": "Середина",
  "dsettings.osnap.center": "Центр",
  "dsettings.osnap.node": "Узел",
  "dsettings.osnap.quadrant": "Квадрант",
  "dsettings.osnap.intersection": "Пересечение",
  "dsettings.osnap.extension": "Продолжение",
  "dsettings.osnap.insertion": "Вставка",
  "dsettings.osnap.perpendicular": "Нормаль",
  "dsettings.osnap.tangent": "Касательная",
  "dsettings.osnap.nearest": "Ближайшая",
  "dsettings.osnap.geomcenter": "Геометрический центр",
  "dsettings.osnap.apparent": "Кажущееся пересечение",
  "dsettings.osnap.parallel": "Параллельно",
  // OSNAP-OVERRIDE-MODES — the two temporary override-only modes (ТЗ §8.6).
  "dsettings.osnap.extint": "Продолженное пересечение",
  "dsettings.osnap.extappint": "Продолженное кажущееся пересечение",
  "dsettings.osnap.pending":
    "Режим привязки ещё не реализован — см. docs/test-gaps/sub-osnap.md",
  "dsettings.pi.title": "Настройки ввода указателя",
  "dsettings.pi.format.title": "Формат координат",
  "dsettings.pi.vis.title": "Видимость полей",
  "dsettings.di.title": "Настройки ввода размеров",
  "dsettings.di.fields.title": "Поля ввода",
  // ── Grip DYNDIGRIP field labels (Phase 4) ──
  "dynin.grip.resize": "Размер",
  "dynin.grip.length-change": "Изменение длины",
  "dynin.grip.angle": "Угол",
  "dynin.grip.arc-radius": "Радиус",
  "dynin.grip.move-x": "ΔX",
  "dynin.grip.move-y": "ΔY",
  "dynin.grip.major-radius": "Большая полуось",
  "dynin.grip.minor-radius": "Малая полуось",
  // ── Block Authoring Palette shell (Phase K.1) ──
  "block_authoring.title": "Палитра создания блоков",
  "block_authoring.close.aria": "Закрыть палитру",
  "block_authoring.tablist.aria": "Разделы палитры создания блоков",
  "block_authoring.tab.parameters": "Параметры",
  "block_authoring.tab.actions": "Действия",
  "block_authoring.tab.parameter-sets": "Наборы параметров",
  // 10 parameter items (BPARAMETER kinds)
  "block_authoring.params.point": "Параметр Точка",
  "block_authoring.params.linear": "Параметр Линейный",
  "block_authoring.params.polar": "Параметр Полярный",
  "block_authoring.params.xy": "Параметр XY",
  "block_authoring.params.rotation": "Параметр Поворот",
  "block_authoring.params.alignment": "Параметр Выравнивание",
  "block_authoring.params.flip": "Параметр Отражение",
  "block_authoring.params.visibility": "Параметр Видимость",
  "block_authoring.params.lookup": "Параметр Поиск",
  "block_authoring.params.basepoint": "Базовая точка",
  // 8 action items (BACTIONTOOL kinds)
  "block_authoring.actions.move": "Действие Перемещение",
  "block_authoring.actions.scale": "Действие Масштаб",
  "block_authoring.actions.stretch": "Действие Растягивание",
  "block_authoring.actions.polar-stretch": "Полярное растягивание",
  "block_authoring.actions.rotation": "Действие Поворот",
  "block_authoring.actions.flip": "Действие Отражение",
  "block_authoring.actions.array": "Действие Массив",
  "block_authoring.actions.lookup": "Действие Поиск",
  // 4 starter parameter sets (full 15 land in Phase K.4)
  "block_authoring.sets.linear-move": "Линейное перемещение",
  "block_authoring.sets.linear-stretch": "Линейное растягивание",
  "block_authoring.sets.polar-move": "Полярное перемещение",
  "block_authoring.sets.rotation-set": "Поворот",
  // Toast surfaced when a placeholder placement fires (Phase K.2 wiring TODO).
  "block_authoring.toast.begin_place":
    "Размещение «{label}» появится в Phase K.2/K.3.",
  // ── Phase K.2a — Linear parameter placement prompts ──
  // 3-click flow inside BEDIT block-edit space. The prompt strings
  // follow the BPARAMETER/ЛИНЕЙНЫЙ command-line conventions used by
  // AutoCAD CIS for dynamic block authoring.
  "prompt.bparam.linear.base": "БПАРАМЕТР: Точка начала линейного параметра:",
  "prompt.bparam.linear.end": "БПАРАМЕТР: Конечная точка линейного параметра:",
  "prompt.bparam.linear.label": "БПАРАМЕТР: Положение метки параметра:",
  "summary.bparam.linear_created":
    "Линейный параметр «{name}» добавлен в блок.",
  // ── Phase K.2b — remaining parameter placement prompts (RU) ──
  "prompt.bparam.point.place": "БПАРАМЕТР: Точка положения параметра:",
  "prompt.bparam.basepoint.place": "БПАРАМЕТР: Базовая точка блока:",
  "prompt.bparam.visibility.place": "БПАРАМЕТР: Положение параметра видимости:",
  "prompt.bparam.lookup.place": "БПАРАМЕТР: Положение параметра поиска:",
  "prompt.bparam.flip.base": "БПАРАМЕТР: Начальная точка оси отражения:",
  "prompt.bparam.flip.end": "БПАРАМЕТР: Конечная точка оси отражения:",
  "prompt.bparam.flip.label": "БПАРАМЕТР: Положение метки параметра отражения:",
  "prompt.bparam.polar.base": "БПАРАМЕТР: Базовая точка полярного параметра:",
  "prompt.bparam.polar.end": "БПАРАМЕТР: Конечная точка полярного параметра:",
  "prompt.bparam.polar.label":
    "БПАРАМЕТР: Положение метки полярного параметра:",
  "prompt.bparam.xy.base": "БПАРАМЕТР: Базовая точка XY-параметра:",
  "prompt.bparam.xy.end": "БПАРАМЕТР: Конечная точка XY-параметра:",
  "prompt.bparam.xy.label": "БПАРАМЕТР: Положение метки XY-параметра:",
  "prompt.bparam.rotation.base": "БПАРАМЕТР: Базовая точка поворота:",
  "prompt.bparam.rotation.radius_end": "БПАРАМЕТР: Точка радиуса поворота:",
  "prompt.bparam.rotation.label":
    "БПАРАМЕТР: Положение метки параметра поворота:",
  "prompt.bparam.alignment.base": "БПАРАМЕТР: Положение точки выравнивания:",
  "prompt.bparam.alignment.end": "БПАРАМЕТР: Точка направления выравнивания:",
  "summary.bparam.point_created": "Параметр-точка «{name}» добавлен в блок.",
  "summary.bparam.basepoint_created":
    "Параметр базовой точки «{name}» добавлен в блок.",
  "summary.bparam.visibility_created":
    "Параметр видимости «{name}» добавлен в блок.",
  "summary.bparam.lookup_created": "Параметр поиска «{name}» добавлен в блок.",
  "summary.bparam.flip_created": "Параметр отражения «{name}» добавлен в блок.",
  "summary.bparam.polar_created": "Полярный параметр «{name}» добавлен в блок.",
  "summary.bparam.xy_created": "XY-параметр «{name}» добавлен в блок.",
  "summary.bparam.rotation_created":
    "Параметр поворота «{name}» добавлен в блок.",
  "summary.bparam.alignment_created":
    "Параметр выравнивания «{name}» добавлен в блок.",
  // ── Phase K.3 — Block Authoring action placement prompts ──
  // Per-action multi-step prompts. The "pick_parameter" prompt asks the
  // user to click an existing parameter glyph; "select_objects" gathers
  // entities (Enter via text handler ends the selection); the optional
  // "frame_corner_1/2" pair captures the crossing-frame for STRETCH and
  // POLAR-STRETCH; "place" drops the action glyph at the clicked point.
  "prompt.baction.move.pick_parameter":
    "БДЕЙСТВИЕ Перемещение: выберите параметр.",
  "prompt.baction.move.select_objects":
    "БДЕЙСТВИЕ Перемещение: выберите объекты (Enter для завершения).",
  "prompt.baction.move.place":
    "БДЕЙСТВИЕ Перемещение: укажите положение значка действия.",
  "prompt.baction.scale.pick_parameter":
    "БДЕЙСТВИЕ Масштаб: выберите параметр.",
  "prompt.baction.scale.select_objects":
    "БДЕЙСТВИЕ Масштаб: выберите объекты (Enter для завершения).",
  "prompt.baction.scale.place":
    "БДЕЙСТВИЕ Масштаб: укажите положение значка действия.",
  "prompt.baction.stretch.pick_parameter":
    "БДЕЙСТВИЕ Растягивание: выберите параметр.",
  "prompt.baction.stretch.select_objects":
    "БДЕЙСТВИЕ Растягивание: выберите объекты (Enter для завершения).",
  "prompt.baction.stretch.frame_corner_1":
    "БДЕЙСТВИЕ Растягивание: укажите первый угол рамки выбора.",
  "prompt.baction.stretch.frame_corner_2":
    "БДЕЙСТВИЕ Растягивание: укажите противоположный угол рамки.",
  "prompt.baction.stretch.place":
    "БДЕЙСТВИЕ Растягивание: укажите положение значка действия.",
  "prompt.baction.polar_stretch.pick_parameter":
    "БДЕЙСТВИЕ Полярное растягивание: выберите параметр.",
  "prompt.baction.polar_stretch.select_objects":
    "БДЕЙСТВИЕ Полярное растягивание: выберите объекты (Enter для завершения).",
  "prompt.baction.polar_stretch.frame_corner_1":
    "БДЕЙСТВИЕ Полярное растягивание: укажите первый угол рамки.",
  "prompt.baction.polar_stretch.frame_corner_2":
    "БДЕЙСТВИЕ Полярное растягивание: укажите противоположный угол рамки.",
  "prompt.baction.polar_stretch.place":
    "БДЕЙСТВИЕ Полярное растягивание: укажите положение значка действия.",
  "prompt.baction.rotation.pick_parameter":
    "БДЕЙСТВИЕ Поворот: выберите параметр.",
  "prompt.baction.rotation.select_objects":
    "БДЕЙСТВИЕ Поворот: выберите объекты (Enter для завершения).",
  "prompt.baction.rotation.place":
    "БДЕЙСТВИЕ Поворот: укажите положение значка действия.",
  "prompt.baction.flip.pick_parameter":
    "БДЕЙСТВИЕ Отражение: выберите параметр.",
  "prompt.baction.flip.select_objects":
    "БДЕЙСТВИЕ Отражение: выберите объекты (Enter для завершения).",
  "prompt.baction.flip.place":
    "БДЕЙСТВИЕ Отражение: укажите положение значка действия.",
  "prompt.baction.array.pick_parameter": "БДЕЙСТВИЕ Массив: выберите параметр.",
  "prompt.baction.array.select_objects":
    "БДЕЙСТВИЕ Массив: выберите объекты (Enter для завершения).",
  "prompt.baction.array.place":
    "БДЕЙСТВИЕ Массив: укажите положение значка действия.",
  "prompt.baction.lookup.pick_parameter": "БДЕЙСТВИЕ Поиск: выберите параметр.",
  "prompt.baction.lookup.select_objects":
    "БДЕЙСТВИЕ Поиск: выберите объекты (Enter для завершения).",
  "prompt.baction.lookup.place":
    "БДЕЙСТВИЕ Поиск: укажите положение значка действия.",
  // Action commit summaries.
  "summary.baction.move_created":
    "Действие Перемещение «{name}» добавлено в блок.",
  "summary.baction.scale_created":
    "Действие Масштаб «{name}» добавлено в блок.",
  "summary.baction.stretch_created":
    "Действие Растягивание «{name}» добавлено в блок.",
  "summary.baction.polar_stretch_created":
    "Действие Полярное растягивание «{name}» добавлено в блок.",
  "summary.baction.rotation_created":
    "Действие Поворот «{name}» добавлено в блок.",
  "summary.baction.flip_created":
    "Действие Отражение «{name}» добавлено в блок.",
  "summary.baction.array_created": "Действие Массив «{name}» добавлено в блок.",
  "summary.baction.lookup_created": "Действие Поиск «{name}» добавлено в блок.",
  // Compatibility error: user clicked a parameter glyph whose `kind` is
  // not in the action's allowed list (per spec §1.1). Action stays at
  // pick-parameter step; user can click another glyph or Esc to cancel.
  "error.baction.incompatible_parameter":
    "Параметр «{paramKind}» не совместим с действием «{actionKind}».",

  // ── PR-9 — SCALELISTEDIT / Annotative Object Scale dialogs ────────
  "audit.scalelistedit_opened": "Открыт редактор списка масштабов.",
  "audit.annotative_object_scale_opened":
    'Открыт диалог "Масштаб объекта аннотации" для {entityId}.',
  "audit.scalelist_added":
    'Масштаб "{name}" (1:{factor}) добавлен в список аннотаций.',
  "audit.scalelist_removed": 'Масштаб "{name}" удалён из списка аннотаций.',
  // ── PR-17 — annotative command audit events ─────────────────────────
  // Command-line / dispatcher feedback for the 9 annotative commands
  // wired through annotativeShellReducer.ts. Each summary mirrors what
  // AutoCAD would echo to the command line after the matching command.
  "audit.cannoscale_changed": "Масштаб аннотаций изменён на {scale}.",
  "audit.context_added":
    'Контекст масштаба "{scale}" добавлен к объекту {entityId}.',
  "audit.context_removed":
    'Контекст масштаба "{scale}" снят с объекта {entityId}.',
  "audit.annoreset":
    "Позиции аннотативных объектов сброшены к мастер-геометрии.",
  "audit.annoupdate_done":
    "АННОБНОВ: позиции аннотативных объектов синхронизированы.",
  "audit.annoallvisible_toggled":
    "Видимость всех аннотативных объектов: {state}.",
  "audit.annoautoscale_toggled": "Автомасштаб аннотаций: {state}.",
  "audit.annomonitor_toggled": "Монитор аннотаций: {state}.",
  // ── Generic on/off labels for status-style audit summaries ─────────
  "status.on": "вкл",
  "status.off": "выкл",
  "dialogs.scalelistedit.title": "Редактор списка масштабов",
  "dialogs.scalelistedit.description":
    "Управление списком масштабов аннотаций (SCALELISTEDIT). Изменения сразу отражаются в строке состояния и сохраняются при экспорте DXF.",
  "dialogs.scalelistedit.col.name": "Имя",
  "dialogs.scalelistedit.col.ratio": "Соотношение (бумага : чертёж)",
  "dialogs.scalelistedit.col.factor": "Коэффициент",
  "dialogs.scalelistedit.add": "Добавить",
  "dialogs.scalelistedit.edit": "Изменить",
  "dialogs.scalelistedit.delete": "Удалить",
  "dialogs.scalelistedit.move_up": "Вверх",
  "dialogs.scalelistedit.move_down": "Вниз",
  "dialogs.scalelistedit.reset": "По умолчанию",
  "dialogs.scalelistedit.scale_name": "Имя масштаба",
  "dialogs.scalelistedit.scale_factor": "Коэффициент масштаба",
  "dialogs.scalelistedit.add_form_title": "Добавить масштаб",
  "dialogs.scalelistedit.add_form_hint":
    "Имя в формате 1:50 или нестандартное (например, А3 1:100).",
  "dialogs.scalelistedit.cancel": "Отмена",
  "dialogs.scalelistedit.ok": "OK",
  "dialogs.scalelistedit.empty": "Список масштабов пуст. Добавьте первый.",
  "dialogs.scalelistedit.error.duplicate":
    'Масштаб с именем "{name}" уже существует.',
  "dialogs.scalelistedit.error.invalid_factor":
    "Коэффициент должен быть положительным числом.",
  "dialogs.scalelistedit.error.empty_name":
    "Имя масштаба не может быть пустым.",
  "dialogs.annotative_object_scale.title": "Масштаб объекта аннотации",
  "dialogs.annotative_object_scale.description":
    "Привязанные к выбранному объекту масштабы аннотаций. AutoCAD-парирует диалог OBJECTSCALE.",
  "dialogs.annotative_object_scale.attached": "Привязанные масштабы",
  "dialogs.annotative_object_scale.available": "Доступные масштабы",
  "dialogs.annotative_object_scale.attach": "Добавить",
  "dialogs.annotative_object_scale.detach": "Удалить",
  "dialogs.annotative_object_scale.empty_attached":
    "Объект не имеет привязанных контекстов масштаба.",
  "dialogs.annotative_object_scale.empty_available":
    "Все масштабы из списка уже привязаны к объекту.",
  "dialogs.annotative_object_scale.cancel": "Закрыть",
  "dialogs.annotative_object_scale.ok": "OK",
  "properties.annotative": "Аннотативный",
  "properties.annotation_scale": "Масштаб аннотации",
  "properties.annotative_scales": "Масштабы аннотаций",
  "properties.edit_scales": "Изменить масштабы…",
  "properties.annotative_yes": "Да",
  "properties.annotative_no": "Нет",
  // PR-10 — annotative-style propagation prompts (DimStyle / MLeaderStyle).
  // Shown when the user toggles the annotative flag on a style; offer to
  // propagate behaviour onto existing entities of that style. Real
  // propagation is deferred (see audit.*_propagation_deferred) — DIM and
  // MLEADER schemas still use the legacy annotativeContexts shape and
  // their migration lives in a follow-up PR.
  "dialogs.dimstyle.propagation_prompt_title":
    "Распространить аннотативность на существующие размеры?",
  "dialogs.dimstyle.propagation_prompt_message_enable":
    'Стиль "{name}" стал аннотативным. Добавить текущий масштаб ({scale}) в annotativeContexts всех размеров этого стиля?',
  "dialogs.dimstyle.propagation_prompt_message_disable":
    'Стиль "{name}" перестал быть аннотативным. Удалить аннотативные контексты у всех размеров этого стиля?',
  "dialogs.mleaderstyle.propagation_prompt_title":
    "Распространить аннотативность на существующие мультивыноски?",
  "dialogs.mleaderstyle.propagation_prompt_message":
    'Стиль "{name}" {action}. Применить изменение к существующим мультивыноскам этого стиля?',
  "dialogs.mleaderstyle.propagation_action_enable": "стал аннотативным",
  "dialogs.mleaderstyle.propagation_action_disable":
    "перестал быть аннотативным",
  "dialogs.common.confirm": "Применить",
  "dialogs.common.skip": "Пропустить",
  "audit.dimstyle.annotative_propagation_deferred":
    'DIMSTYLE "{name}": распространение аннотативности на существующие размеры отложено (миграция схемы DIMENSION в отдельном PR).',
  "audit.mleaderstyle.annotative_propagation_deferred":
    'MLEADERSTYLE "{name}": распространение аннотативности на существующие мультивыноски отложено (миграция схемы MLEADER в отдельном PR).',
  // ── Right-click context menu (SHORTCUTMENU parity) ──
  "context_menu.title.default": "Поле черчения",
  "context_menu.title.edit": "Объекты",
  "context_menu.title.command": "Команда",
  // W2.5 OSNAP-OVERRIDE-MODES-S2 — Shift+right-click Object Snap cursor menu.
  "context_menu.title.osnap": "Объектная привязка",
  "context_menu.repeat_last": "Повторить",
  "context_menu.recent_input": "Последние команды",
  "context_menu.clipboard": "Буфер обмена",
  "context_menu.clipboard.cut": "Вырезать",
  "context_menu.clipboard.copy": "Копировать",
  "context_menu.clipboard.copy_with_base": "Копировать с базовой точкой",
  "context_menu.clipboard.paste": "Вставить",
  "context_menu.clipboard.paste_as_block": "Вставить как блок",
  "context_menu.clipboard.paste_to_original": "Вставить в исходные координаты",
  "context_menu.undo": "Отменить",
  "context_menu.redo": "Повторить отменённое",
  "context_menu.pan": "Панорамирование",
  "context_menu.zoom": "Зумирование",
  "context_menu.steeringwheels": "Штурвалы",
  "context_menu.action_recorder": "Регистратор действий",
  "context_menu.subobject_filter": "Фильтр подобъектов",
  "context_menu.quick_select": "Быстрый выбор",
  "context_menu.quickcalc": "Быстрый калькулятор",
  "context_menu.find": "Найти",
  "context_menu.options": "Параметры",
  "context_menu.isolate": "Изолировать объекты",
  "context_menu.erase": "Стереть",
  "context_menu.move": "Переместить",
  "context_menu.copy": "Копировать",
  "context_menu.scale": "Масштабировать",
  "context_menu.rotate": "Повернуть",
  "context_menu.draw_order": "Порядок прорисовки",
  "context_menu.group": "Группа",
  "context_menu.add_selected": "Добавить выделенное",
  "context_menu.select_similar": "Выбрать подобные",
  "context_menu.deselect_all": "Снять выделение",
  "context_menu.properties": "Свойства",
  "context_menu.quick_properties": "Быстрые свойства",
  "context_menu.command.enter": "ВВОД",
  "context_menu.command.cancel": "Отмена",
  "context_menu.command.recent": "Последние команды",
  "context_menu.command.options": "Параметры команды",
  // W2.5 OSNAP-OVERRIDE-MODES-S2 — "osnap" mode menu item (opens DSETTINGS).
  "context_menu.osnap.settings": "Настройки объектной привязки...",
  // ROLLOVERTIPS card field labels
  "tooltip.type": "Тип",
  "tooltip.layer": "Слой",
  "tooltip.color": "Цвет",
  "tooltip.linetype": "Тип линий",
  "tooltip.handle": "Дескриптор",
  // ── B12 SELECTIONCYCLING (Ctrl+W / Shift+Space) ──
  "audit.selectioncycling_toggle":
    "SELECTIONCYCLING переключён в значение {value}.",
  "selection_cycling.dialog.title": "Выбор объекта",
  "selection_cycling.dialog.subtitle":
    "Курсор находится над {count} наложенными объектами. Выберите нужный.",
  "selection_cycling.badge_label": "Циклический выбор",
  "selection_cycling.row.handle": "Дескриптор: {handle}",
  "selection_cycling.row.layer": "Слой: {layer}",
  // ── ABB-S7c agent-bridge Connect dialog + header status pill ──
  "agent_connect.open": "Агент",
  "agent_connect.dialog.title": "Подключение к агенту",
  "agent_connect.label.url": "Адрес компаньона",
  "agent_connect.label.token": "Токен сопряжения",
  "agent_connect.label.status": "Статус",
  "agent_connect.btn.connect": "Подключиться",
  "agent_connect.btn.disconnect": "Отключиться",
  "agent_connect.btn.close": "Закрыть",
  "agent_connect.pill.disconnected": "Агент: отключён",
  "agent_connect.pill.connecting": "Агент: подключение…",
  "agent_connect.pill.connected": "Агент: подключён",
  "agent_connect.pill.paired": "Агент: активен",
  "agent_connect.pill.error": "Агент: ошибка",
  "agent_connect.status.disconnected": "Не подключено",
  "agent_connect.status.connecting": "Подключение к компаньону…",
  "agent_connect.status.connected": "Подключено (наблюдатель)",
  "agent_connect.status.paired": "Сопряжено — активный сеанс",
  "agent_connect.status.error": "Ошибка подключения",
  "agent_connect.error.companion_unreachable":
    "Компаньон агент-моста недоступен на локальном порту.",
  "agent_connect.error.token_rejected":
    "Токен сопряжения отклонён — введите токен из компаньона заново.",
  "agent_connect.error.protocol_mismatch":
    "Версия протокола компаньона не совпадает с этой сборкой.",
  "agent_connect.error.connect_failed":
    "Не удалось подключиться к агент-мосту.",
  // ── ABB enable-toggle — persisted default-OFF enable switch + consent copy ──
  "agent_connect.toggle.label": "ИИ-агент (локальный мост)",
  "agent_connect.toggle.aria": "Включить локальный ИИ-агент-мост",
  "agent_connect.toggle.on": "Вкл",
  "agent_connect.toggle.off": "Выкл",
  "agent_connect.consent.title": "Что это включает",
  "agent_connect.consent.body":
    "Включение разрешает локальному ИИ-агенту читать ваш чертёж и выполнять команды редактирования через безопасный контур (с проверкой, отменяемые, с журналом аудита). Соединение работает только через локальный адрес (loopback) и защищено токеном сопряжения. Включайте только для агента, которому вы доверяете, запущенного на этом компьютере. Любой локальный процесс, знающий токен, получает право на редактирование.",
  // ── B15 drag-and-drop drop-zone ──
  "dxfio.drop_zone_label": "Перетащите файл DXF для импорта",
  "dxfio.drop_zone_hint":
    "Поддерживаются файлы .dxf — модель и листы будут загружены автоматически.",
  "dxfio.drop_zone_invalid_type":
    "Неподдерживаемый файл: ожидается .dxf, получено «{name}».",
  // ── B11 PRINT / PLOT dialog ──
  "print.dialog.title": "Печать — Параметры листа",
  "print.dialog.printer_label": "Принтер/плоттер:",
  "print.dialog.paper_size_label": "Формат листа:",
  "print.dialog.orientation_label": "Ориентация:",
  "print.dialog.orientation.portrait": "Книжная",
  "print.dialog.orientation.landscape": "Альбомная",
  "print.dialog.margin_label": "Поля (мм):",
  "print.dialog.margin_left": "Слева",
  "print.dialog.margin_right": "Справа",
  "print.dialog.margin_top": "Сверху",
  "print.dialog.margin_bottom": "Снизу",
  "print.dialog.area_label": "Область печати:",
  "print.dialog.area.extents": "По границам",
  "print.dialog.area.layout": "Лист",
  "print.dialog.area.window": "Рамкой",
  "print.dialog.window_pick": "Указать рамку <",
  "print.dialog.preview_label": "Просмотр (рамка показана на чертеже):",
  "print.dialog.confirm": "Печать",
  "print.dialog.cancel": "Отмена",
  "print.feed.opened": "ПЕЧАТЬ: Открыт диалог параметров листа.",
  "print.feed.completed": "ПЕЧАТЬ: Параметры подтверждены ({size}, {orientation}).",
  "print.feed.cancelled": "ПЕЧАТЬ: Команда отменена.",
  "print.feed.exported": "ПЕЧАТЬ: PDF создан ({paths} объект(ов), {size}).",
  "print.feed.export_failed": "ПЕЧАТЬ: Не удалось создать PDF — {message}.",
  "print.feed.window_pick_start": "ПЕЧАТЬ: Укажите первый угол рамки печати.",
  "print.feed.window_pick_set": "ПЕЧАТЬ: Рамка печати задана.",
  "print.feed.window_pick_cancelled": "ПЕЧАТЬ: Выбор рамки отменён.",
  // ── PDF-6.9a PAGESETUP / ПАРАМЛИСТ — Page Setup Manager ──
  "page_setup.title": "Диспетчер параметров листов",
  "page_setup.layout_list_title": "Листы",
  "page_setup.paper_size_label": "Формат листа:",
  "page_setup.orientation_label": "Ориентация:",
  "page_setup.orientation.portrait": "Книжная",
  "page_setup.orientation.landscape": "Альбомная",
  "page_setup.plot_area_label": "Что печатать:",
  "page_setup.plot_area.layout": "Лист",
  "page_setup.plot_area.extents": "Границы",
  "page_setup.plot_area.window": "Рамкой",
  "page_setup.scale_label": "Масштаб:",
  "page_setup.scale.custom": "Пользовательский",
  "page_setup.scale.fit_to_paper": "Вписать",
  "page_setup.plot_style_label": "Таблица стилей печати:",
  "page_setup.plot_style.none": "Нет",
  "page_setup.rotation_label": "Поворот чертежа:",
  "page_setup.rotation.none": "0°",
  "page_setup.rotation.90": "90° против часовой",
  "page_setup.rotation.180": "180° (вверх ногами)",
  "page_setup.rotation.270": "270° против часовой",
  "page_setup.offset_label": "Смещение печати (мм):",
  "page_setup.offset.x": "X:",
  "page_setup.offset.y": "Y:",
  "page_setup.margins_label": "Поля (мм):",
  "page_setup.margin_left": "Слева",
  "page_setup.margin_right": "Справа",
  "page_setup.margin_top": "Сверху",
  "page_setup.margin_bottom": "Снизу",
  "page_setup.center_plot": "Центрировать на листе",
  "page_setup.scale_lineweights": "Масштабировать веса линий",
  "page_setup.print_lineweights": "Печатать веса линий",
  "page_setup.apply": "Применить",
  "page_setup.ok": "OK",
  "page_setup.cancel": "Отмена",
  "page_setup.feed.opened": "ПАРАМЛИСТ: Открыт диспетчер параметров листов.",
  "page_setup.feed.applied": "ПАРАМЛИСТ: Применено изменений: {count}.",
  "page_setup.feed.cancelled": "ПАРАМЛИСТ: Изменения отменены.",
  "ribbon.button.page_setup": "Параметры листа",
  // ── PDF-6.9b PDF Options sub-dialog ──
  "page_setup.pdf_options_button": "Параметры PDF…",
  "pdf_options.title": "Параметры PDF",
  "pdf_options.vector_dpi_label": "Векторное качество (DPI):",
  "pdf_options.vector_dpi.custom": "Пользовательское…",
  "pdf_options.font_mode_label": "Режим шрифтов:",
  "pdf_options.font_mode.ttf_embed": "Внедрение TTF",
  "pdf_options.font_mode.shx_note":
    "Текст SHX отрисовывается как штриховая геометрия; поддержка PDFSHX будет добавлена в следующем этапе.",
  "pdf_options.layers_label": "Включать слои:",
  "pdf_options.bookmarks_label": "Создавать закладки PDF",
  "pdf_options.bookmarks_note": "Только для ПУБЛ (нескольких листов).",
  // ── PDF-6.4 — Lines merge / Overwrite (PC3 parity) ──
  "pdf_options.merge_control.label": "Слияние линий",
  "pdf_options.merge_control.overwrite":
    "Перезаписать (объекты сверху перекрывают нижние)",
  "pdf_options.merge_control.merge": "Сливать (пересечения темнее)",
  "pdf_options.merge_control.note":
    "Аналог Lines merge в PC3 AutoCAD. PDF blend mode = Multiply.",
  "pdf_options.ok": "OK",
  "pdf_options.cancel": "Отмена",
  "pdf_options.feed.opened": "ПАРАМPDF: Открыт диалог параметров PDF.",
  "pdf_options.feed.confirmed": "ПАРАМPDF: Параметры PDF применены.",
  "pdf_options.feed.cancelled": "ПАРАМPDF: Изменения отменены.",
  // ── PDF-6.3b — CTB / STB plot-style table loader ──
  "pdf_options.plot_style.label": "Таблица стилей печати:",
  "pdf_options.plot_style.load_button": "Загрузить CTB/STB…",
  "pdf_options.plot_style.clear_button": "Очистить",
  "pdf_options.plot_style.loaded_summary":
    "{kind} · записей: {count} · {file}",
  "pdf_options.plot_style.error_prefix": "Ошибка загрузки таблицы:",
  // ── B19 Block lookup grip menu ──
  "block.lookup.menu.title": "Lookup: {name}",
  "block.lookup.menu.empty": "Нет вариантов в таблице.",
  "block.lookup.menu.cancel": "Отмена",
  "block.lookup.feed.applied": "БЛОК: Значение «{value}» применено к параметру «{name}».",
  // ── B18 MLEADER grip context menu ──
  "mleader.menu.add_leader": "Добавить выноску",
  "mleader.menu.remove_leader": "Удалить выноску",
  "mleader.menu.add_vertex": "Добавить вершину",
  "mleader.menu.remove_vertex": "Удалить вершину",
  "mleader.menu.stretch": "Растянуть",
  "mleader.feed.add_leader_prompt": "МЛИНИЯРЕД: Укажите точку новой стрелки.",
  "mleader.feed.add_vertex_prompt": "МЛИНИЯРЕД: Укажите положение новой вершины.",
  "mleader.feed.vertex_added": "МЛИНИЯРЕД: Вершина добавлена.",
  "mleader.feed.vertex_removed": "МЛИНИЯРЕД: Вершина удалена.",
  "mleader.feed.leader_removed": "МЛИНИЯРЕД: Выноска удалена.",
  "mleader.feed.stretch_hint": "МЛИНИЯРЕД: Перетащите ручку для растяжения.",
  // ── PR1b: восстановление ссылок при импорте (AUDIT/RECOVER) ──
  // Транскрипт-сводка после нормализации висячих ссылок на симв.
  // таблицы при открытии «грязного, но допустимого» DXF. `{count}` —
  // число исправленных ссылок, `{breakdown}` — разбивка по видам.
  "reference.heal.summary": "Восстановлено ссылок: {count} ({breakdown})",
  // Хвост сводки, когда часть ссылок исправить нельзя (напр. UNDEFINED_BLOCK
  // — отсутствующее определение блока подменить нечем). `{count}` — число.
  "reference.heal.unhealed": "; не восстановлено: {count}",
  // Один элемент разбивки: «{label}: {n}».
  "reference.heal.breakdown_item": "{label}: {n}",
  // Виды ссылок + целевое значение-замена (всегда присутствующий
  // зарезервированный default AutoCAD).
  "reference.heal.kind.layer": "слой→0",
  "reference.heal.kind.textstyle": "текстовый стиль→Standard",
  "reference.heal.kind.dimstyle": "размерный стиль→Standard",
  "reference.heal.kind.mleaderstyle": "стиль мультивыноски→Standard",
  "reference.heal.kind.linetype": "тип линий→Continuous",
  "reference.heal.kind.annoscale": "масштаб аннотаций→1:1",
  // Предупреждение перед экспортом (warn-only — сохранение НЕ блокируется).
  // `{count}` — число неразрешённых ссылок в документе.
  "reference.export.audit_warning":
    "Экспорт DXF: обнаружено неразрешённых ссылок: {count}. Файл сохранён, но может содержать висячие ссылки.",
  // ── CC-1d: консоль аудита администратора (/admin/audit) ──
  "admin.audit.title": "Консоль аудита",
  "admin.audit.subtitle": "Журнал команд редактора и телеметрия (ТЗ §14)",
  "admin.audit.tab.events": "Журнал",
  "admin.audit.tab.dashboard": "Дашборд",
  "admin.audit.refresh": "Обновить",
  "admin.audit.back_to_editor": "В редактор",
  "admin.audit.loading": "Загрузка…",
  "admin.audit.error": "Не удалось загрузить данные: {message}",
  "admin.audit.retry": "Повторить",
  "admin.audit.unauthorized.title": "Требуется вход",
  "admin.audit.unauthorized.body":
    "Войдите под учётной записью администратора, чтобы просматривать журнал аудита.",
  "admin.audit.forbidden.title": "Доступ запрещён",
  "admin.audit.forbidden.body":
    "Журнал аудита доступен только администраторам.",
  "admin.audit.empty": "Событий не найдено.",
  "admin.audit.filter.user": "Пользователь",
  "admin.audit.filter.command": "Команда",
  "admin.audit.filter.source": "Источник",
  "admin.audit.filter.source.all": "Все источники",
  "admin.audit.filter.space": "Пространство",
  "admin.audit.filter.date_from": "С даты",
  "admin.audit.filter.date_to": "По дату",
  "admin.audit.filter.apply": "Применить",
  "admin.audit.filter.reset": "Сбросить",
  "admin.audit.col.time": "Время",
  "admin.audit.col.user": "Пользователь",
  "admin.audit.col.command": "Команда",
  "admin.audit.col.source": "Источник",
  "admin.audit.col.space": "Пространство",
  "admin.audit.col.status": "Статус",
  "admin.audit.pagination.range": "{from}–{to} из {total}",
  "admin.audit.pagination.prev": "Назад",
  "admin.audit.pagination.next": "Вперёд",
  "admin.audit.detail.title": "Детали события",
  "admin.audit.detail.close": "Закрыть",
  "admin.audit.detail.empty":
    "Выберите событие в таблице, чтобы увидеть детали.",
  "admin.audit.detail.field.role": "Роль",
  "admin.audit.detail.field.alias": "Псевдоним команды",
  "admin.audit.detail.field.hotkey": "Горячая клавиша",
  "admin.audit.detail.field.workspace": "Рабочая область",
  "admin.audit.detail.field.drawing": "Чертёж",
  "admin.audit.detail.field.entities": "Объекты",
  "admin.audit.detail.field.before": "До",
  "admin.audit.detail.field.after": "После",
  "admin.audit.detail.params.title": "Параметры операции",
  "admin.audit.detail.params.category": "Категория",
  "admin.audit.detail.params.op": "Операция",
  "admin.audit.detail.params.absent":
    "Сервер не передал типизированные параметры для этого события.",
  "admin.audit.detail.raw": "Исходный JSON",
  "admin.value.yes": "Да",
  "admin.value.no": "Нет",
  // CC-1d: дашборд телеметрии (sub-telemetry·ui)
  "admin.dashboard.total": "Всего событий: {count}",
  "admin.dashboard.note":
    "Агрегация выполняется на сервере по всему потоку телеметрии.",
  "admin.dashboard.empty": "Телеметрия пока не накоплена.",
  "admin.dashboard.commands.title": "Частота команд",
  "admin.dashboard.top_note": "топ-{count}",
  "admin.dashboard.sources.title": "Распределение по источникам",
  "admin.dashboard.status.title": "Распределение по статусам",
  "admin.dashboard.timeline.title": "События по дням",
  // CC-1d: метки категорий типизированного payload (auditTaxonomy.ts)
  "admin.params.category.session": "Сессия",
  "admin.params.category.ui": "Интерфейс",
  "admin.params.category.layout": "Лист",
  "admin.params.category.selection": "Выбор",
  "admin.params.category.lifecycle": "Жизненный цикл",
  "admin.params.category.property": "Свойства",
  "admin.params.category.commandline": "Командная строка",
  "admin.params.category.create": "Создание",
  "admin.params.category.viewport": "Видовой экран",
  "admin.params.category.modify": "Редактирование",
  "admin.params.category.block": "Блок",
  "admin.params.category.attribute": "Атрибут",
  "admin.params.category.multileader": "Мультивыноска",
  "admin.params.category.annotation": "Аннотация",
  "admin.params.category.constraint": "Зависимость",
  "admin.params.category.header": "Заголовок DXF",
  "admin.params.category.history": "История",
  "admin.params.category.space": "Пространство",
  "admin.params.category.file": "Файл",
  "admin.params.category.system": "Система",
  "admin.params.category.dynamic": "Динамический блок",
  "admin.params.category.geometry": "Геометрия",
  "admin.params.category.view-op": "Видовая операция",
  "admin.params.category.collab": "Совместная работа",
  "admin.params.category.ui-click": "Клик UI",
  "admin.params.category.ui-toggle": "Переключатель UI",
  "admin.params.category.ui-nav": "Навигация UI",
  "admin.params.category.other": "Прочее",
  // CC-1d: метки полей типизированного payload (auditTaxonomy.ts)
  "admin.params.field.spaceId": "Пространство (ID)",
  "admin.params.field.contentLength": "Длина содержимого",
  "admin.params.field.styleName": "Стиль",
  "admin.params.field.nominalHeight": "Номинальная высота",
  "admin.params.field.referenceWidth": "Ширина рамки",
  "admin.params.field.insertionX": "Точка вставки X",
  "admin.params.field.insertionY": "Точка вставки Y",
  "admin.params.field.viewportId": "Видовой экран (ID)",
  "admin.params.field.locked": "Блокировка",
  "admin.params.field.shadePlotMode": "Режим печати с тонированием",
  "admin.params.field.vplayerAction": "Действие VPLAYER",
  "admin.params.field.layerName": "Слой",
  "admin.params.field.boundsX1": "Граница X1",
  "admin.params.field.boundsY1": "Граница Y1",
  "admin.params.field.boundsX2": "Граница X2",
  "admin.params.field.boundsY2": "Граница Y2",
  "admin.params.field.scaleNum": "Масштаб: числитель",
  "admin.params.field.scaleDenom": "Масштаб: знаменатель",
  "admin.params.field.viewHeight": "Высота вида",
  "admin.params.field.viewCenterX": "Центр вида X",
  "admin.params.field.viewCenterY": "Центр вида Y",
  "admin.params.field.entityId": "Объект (ID)",
  "admin.params.field.cuttingEdgeCount": "Число режущих кромок",
  "admin.params.field.boundaryEdgeCount": "Число граничных кромок",
  "admin.params.field.entityCount": "Число объектов",
  "admin.params.field.propertyCount": "Число свойств",
  "admin.params.field.targetCount": "Число целей",
  "admin.params.field.mode": "Режим",
  "admin.params.field.deltaX": "Смещение X",
  "admin.params.field.deltaY": "Смещение Y",
  "admin.params.field.factor": "Коэффициент",
  "admin.params.field.target": "Поверхность",
  "admin.params.field.controlId": "Элемент управления",
  "admin.params.field.setting": "Параметр (sysvar)",
  "admin.params.field.enabled": "Включено",
  "admin.params.field.navKind": "Тип навигации",
  "admin.params.field.from": "Откуда",
  "admin.params.field.to": "Куда",
  "admin.params.field.reversedCommandType": "Отменённая команда",
  "admin.params.field.reappliedCommandType": "Повторённая команда",
  "admin.params.field.headIndex": "Позиция в истории",
} as const;

export type MessageKey = keyof typeof ru;

/** English dictionary; `Record<MessageKey, string>` enforces ru/en key
 * parity at compile time (see `./messageKeyCensus.ts` for the same
 * invariant re-checked at runtime). */
const en: Record<MessageKey, string> = {
  // Ribbon tabs
  "ribbon.tab.file": "File",
  "ribbon.tab.draw": "Draw",
  "ribbon.tab.annotate": "Annotate",
  "ribbon.tab.modify": "Modify",
  "ribbon.tab.layout": "Layout",
  "ribbon.tab.propertyflows": "Property Flows",
  // Ribbon sections
  "ribbon.section.output": "Output",
  "ribbon.button.print": "Print",
  "ribbon.section.drafting": "Drafting",
  "ribbon.section.blocks_arrays": "Blocks / Arrays",
  "ribbon.section.annotation": "Annotation",
  "ribbon.section.dimensions": "Dimensions",
  "ribbon.section.callouts": "Callouts",
  // Drawing commands
  "command.line.label": "Line",
  "command.point.label": "Point",
  "command.circle.label": "Circle",
  "command.arc.label": "Arc",
  "command.ellipse.label": "Ellipse",
  "command.polyline.label": "Polyline",
  "command.rectangle.label": "Rectang",
  "command.polygon.label": "Polygon",
  "command.text.label": "Text",
  "command.mtext.label": "MText",
  "command.block.label": "Block",
  "command.array.label": "Array",
  "command.arraypolar.label": "Polar Array",
  "command.arraypath.label": "Path Array",
  "command.arrayedit.label": "Array Edit",
  "command.hatch.label": "Hatch",
  "command.hatchedit.label": "Hatch Edit",
  "command.dimension.label": "Linear",
  "command.dimaligned.label": "Aligned",
  "command.dimangular.label": "Angular",
  "command.dim3point.label": "Angular 3pt",
  "command.dimradius.label": "Radius",
  "command.dimdiameter.label": "Diameter",
  "command.dimordinate.label": "Ordinate",
  "command.mleader.label": "MLeader",
  "command.mleaderedit.label": "MLEADEREDIT",
  "ribbon.action.mleader_add_leader": "Add Leader",
  "ribbon.action.mleader_remove_leader": "Remove Leader",
  "command.revcloud.label": "Revcloud",
  "command.revcloudproperties.label": "Revision Cloud Properties",
  "command.wipeout.label": "Wipeout",
  "command.donut.label": "Donut",
  "command.spline.label": "Spline",
  // Stage / canvas
  "stage.title": "Drawing stage",
  "stage.model_canvas": "Model space canvas",
  "stage.layout_canvas": "{name} canvas",
  "stage.entities_count_zero": "0 entities",
  "stage.entities_count": "{count} entities",
  "stage.empty_message":
    "The current model is empty. Start a command to begin drawing.",
  // Drafting prompts
  "prompt.line.first": "LINE: Specify first point.",
  "prompt.line.next":
    "LINE: Specify next point or [Close]. Press Enter to finish.",
  "prompt.point.location": "POINT: Specify a point:",
  "prompt.base.point": "BASE: Specify the insertion base point:",
  "prompt.circle.center":
    "CIRCLE: Specify center point or [3P/2P/Ttr (tan tan radius)].",
  "prompt.circle.radius": "CIRCLE: Specify radius or [Diameter].",
  "prompt.circle.diameter": "CIRCLE: Specify circle diameter.",
  "prompt.circle.2p_first":
    "CIRCLE: Specify first end point of circle's diameter.",
  "prompt.circle.2p_second":
    "CIRCLE: Specify second end point of circle's diameter.",
  "prompt.circle.3p_second": "CIRCLE: Specify second point:",
  "prompt.circle.3p_third": "CIRCLE: Specify third point:",
  "prompt.arc.start": "ARC: Specify start point.",
  "prompt.arc.second": "ARC: Specify second point on arc or [Center].",
  "prompt.arc.end": "ARC: Specify end point.",
  "prompt.arc.sce_center": "ARC: Specify center of arc:",
  "prompt.arc.sce_end_angle": "ARC: Specify included angle:",
  // ── ELLIPSE (Phase D4) — center mode prompts ──
  "prompt.ellipse.center":
    "ELLIPSE: Specify endpoint of ellipse axis or [Arc/Center]:",
  "prompt.ellipse.major_end": "ELLIPSE: Specify other endpoint of axis:",
  "prompt.ellipse.minor_distance": "ELLIPSE: Specify distance to other axis:",
  "prompt.polyline.first": "PLINE: Specify start point or [Width].",
  "prompt.polyline.next":
    "PLINE: Specify next point, Enter to finish, C to close, or W for width.",
  "prompt.polyline.width_start": "PLINE: Specify start width:",
  "prompt.polyline.width_end": "PLINE: Specify end width:",
  "prompt.rectangle.first":
    "RECTANG: Specify first corner point or [Dimensions].",
  "prompt.rectangle.second":
    "RECTANG: Specify opposite corner point or [Dimensions].",
  "prompt.rectangle.d_length": "RECTANG: Rectangle length:",
  "prompt.rectangle.d_width": "RECTANG: Rectangle width:",
  "prompt.rectangle.d_place": "RECTANG: Specify other corner:",
  // ── DONUT (Phase D3) — AutoCAD-parity prompts ──
  "prompt.donut.inner_d": "DONUT: Specify inside diameter <{last}>:",
  "prompt.donut.outer_d": "DONUT: Specify outside diameter <{last}>:",
  "prompt.donut.center": "DONUT: Specify center of donut or [Exit]:",
  // ── POLYGON (Phase D2) — AutoCAD-parity prompts ──
  "prompt.polygon.sides": "POLYGON: Enter number of sides <{last}>:",
  "prompt.polygon.mode":
    "POLYGON: Inscribed in circle or Circumscribed about circle [Inscribed/Circumscribed] <Inscribed>:",
  "prompt.polygon.center": "POLYGON: Specify center:",
  "prompt.polygon.radius": "POLYGON: Specify radius:",
  "prompt.text.start": "TEXT: Specify start point or [Justify/Style].",
  "prompt.text.height": "TEXT: Specify height:",
  "prompt.text.rotation": "TEXT: Specify rotation angle:",
  "prompt.text.content": "TEXT: Enter text content, then press Run / Submit.",
  "prompt.mtext.first": "MTEXT: Specify first corner of text box.",
  "prompt.mtext.second": "MTEXT: Specify opposite corner of text box.",
  "prompt.mtext.content":
    "MTEXT: Enter paragraph content, then press Run / Submit.",
  "prompt.block.name": "INSERT: Enter block name or [?] <BLOCK_REF>.",
  "prompt.block.insertion": "INSERT: Specify insertion point.",
  "prompt.block.scale": "INSERT: Specify uniform scale factor <1>.",
  "prompt.block.rotation": "INSERT: Specify rotation angle <0>.",
  "prompt.arrayrect.select":
    "ARRAYRECT: Select objects (press Enter when done).",
  "prompt.arrayrect.base": "ARRAYRECT: Specify base point.",
  "prompt.arrayrect.count":
    "ARRAYRECT: Enter COL=<n> ROW=<n>, or pick opposite corner.",
  "prompt.arrayrect.spacing":
    "ARRAYRECT: Enter CS=<n> RS=<n> AA=<deg>, or press Enter to accept.",
  "prompt.arraypolar.select":
    "ARRAYPOLAR: Select objects (press Enter when done).",
  "prompt.arraypolar.center": "ARRAYPOLAR: Specify center point.",
  "prompt.arraypolar.count": "ARRAYPOLAR: Number of items (6).",
  "prompt.arraypolar.fill": "ARRAYPOLAR: Fill angle (360), ROW=<n>, RS=<n>.",
  "prompt.arraypath.select":
    "ARRAYPATH: Select objects (press Enter when done).",
  "prompt.arraypath.path":
    "ARRAYPATH: Select path curve (line, arc, circle or polyline).",
  "prompt.arraypath.method":
    "ARRAYPATH: Select method [Divide/Measure] <Divide>.",
  "prompt.arraypath.params":
    "ARRAYPATH: Enter item count / spacing, or Enter to accept default.",
  "prompt.arrayedit.select": "ARRAYEDIT: Select an array item.",
  "prompt.hatchedit.select": "HATCHEDIT: Select a hatch to edit.",
  "prompt.hatchedit.pattern":
    "HATCHEDIT: Enter pattern name [ANSI31, SOLID, ...] or press Enter to keep the current one.",
  "prompt.hatchedit.scale":
    "HATCHEDIT: Enter pattern scale or press Enter to keep the current one.",
  "prompt.hatchedit.angle":
    "HATCHEDIT: Enter pattern angle (degrees) or press Enter to keep the current one.",
  "prompt.hatch.pattern":
    "HATCH: Pick an internal point or enter pattern name [ANSI31, SOLID, ...]. Default ANSI31.",
  "prompt.hatch.first": "HATCH: Specify first hatch boundary corner.",
  "prompt.hatch.second": "HATCH: Specify opposite hatch boundary corner.",
  "prompt.hatch.confirm": "HATCH: Press Enter to accept the hatch boundary.",
  "prompt.hatch.mode":
    "HATCH: Pick [Internal point/Select objects/Rectangle] <I>:",
  "prompt.hatch.pick_internal":
    "HATCH: Pick internal point of a closed region:",
  "prompt.hatch.pick_additional":
    "HATCH: Pick another internal point or press Enter to apply:",
  "prompt.dimension.first": "DIMENSION: Specify first extension line origin.",
  "prompt.dimension.second": "DIMENSION: Specify second extension line origin.",
  "prompt.dimension.line": "DIMENSION: Specify dimension line location.",
  "prompt.dimaligned.first": "DIMALIGNED: Specify first extension line origin.",
  "prompt.dimaligned.second":
    "DIMALIGNED: Specify second extension line origin.",
  "prompt.dimaligned.line": "DIMALIGNED: Specify dimension line location.",
  "prompt.dimangular.first":
    "DIMANGULAR: Select arc, circle, line, or specify vertex.",
  "prompt.dimangular.second": "DIMANGULAR: Select second line.",
  "prompt.dimangular.arc": "DIMANGULAR: Specify dimension arc line location.",
  "prompt.dim3point.vertex": "DIM3POINT: Specify angle vertex.",
  "prompt.dim3point.first": "DIM3POINT: Specify first arm endpoint.",
  "prompt.dim3point.second": "DIM3POINT: Specify second arm endpoint.",
  "prompt.dim3point.arc": "DIM3POINT: Specify dimension arc line location.",
  "prompt.dimradius.select": "DIMRADIUS: Select arc or circle.",
  "prompt.dimradius.line": "DIMRADIUS: Specify dimension line location.",
  "prompt.dimdiameter.select": "DIMDIAMETER: Select arc or circle.",
  "prompt.dimdiameter.line": "DIMDIAMETER: Specify dimension line location.",
  "prompt.dimordinate.feature": "DIMORDINATE: Specify feature location.",
  "prompt.dimordinate.leader": "DIMORDINATE: Specify leader endpoint.",
  "prompt.dimbaseline.second":
    "DIMBASELINE: Specify a second extension line origin or [Select/Undo] <Select>:",
  "prompt.dimbaseline.no_anchor":
    "DIMBASELINE: Create a linear or aligned dimension first.",
  "prompt.dimcontinue.second":
    "DIMCONTINUE: Specify a second extension line origin or [Select/Undo] <Select>:",
  "prompt.dimcontinue.no_anchor":
    "DIMCONTINUE: Create a linear or aligned dimension first.",
  "prompt.mleader.arrow": "MLEADER: Specify arrowhead point.",
  "prompt.mleader.landing": "MLEADER: Specify leader landing point.",
  "prompt.mleader.content":
    "MLEADER: Enter leader text, then press Run / Submit.",
  "prompt.mleaderedit.select": "MLEADEREDIT: Select a multileader to edit.",
  "prompt.mleaderedit.options": "MLEADEREDIT: Options: [Add/Remove]",
  "prompt.mleaderedit.add_arrow":
    "MLEADEREDIT: Specify the new arrowhead point.",
  "prompt.mleaderedit.remove_pick":
    "MLEADEREDIT: Pick a leader line to remove.",
  "prompt.mleaderedit.not_mleader": "Selected entity is not a multileader.",
  "confirm.mleaderedit.last_leader":
    "This is the last leader. Remove the entire multileader?",
  "prompt.revcloud.first":
    "REVCLOUD: Specify first point or [Arc length/Object/Rectangular/Polygonal/Freehand/Style/Modify] <Object>:",
  "prompt.revcloud.second":
    "REVCLOUD: Specify opposite corner of revision cloud.",
  "prompt.revcloud.arc_length":
    "REVCLOUD: Specify approximate length of arc:",
  "prompt.revcloud.style":
    "REVCLOUD: Select arc style [Normal/Calligraphy] <Normal>:",
  "prompt.revcloud.polygonal_next":
    "REVCLOUD: Specify next point (Enter to close the cloud):",
  "prompt.revcloud.freehand":
    "REVCLOUD: Guide crosshairs along cloud path; the cloud closes at the start point.",
  "prompt.revcloud.object_pick": "REVCLOUD: Select object:",
  "prompt.revcloud.object_reverse":
    "REVCLOUD: Reverse direction [Yes/No] <No>:",
  "prompt.revcloud.invalid_object":
    "REVCLOUD: The selected object is not a closed circle, ellipse, polyline or spline.",
  "prompt.revcloud.no_object_found": "REVCLOUD: No object found. Select object:",
  "prompt.revcloud.modify_unsupported":
    "REVCLOUD: The Modify option is not supported yet (lands with the revision-cloud properties editor).",
  "prompt.wipeout.first_corner": "WIPEOUT: Specify first corner.",
  "prompt.wipeout.second_corner": "WIPEOUT: Specify opposite corner.",
  "prompt.wipeout.frame_toggle": "WIPEOUT: Frames [ON/OFF] <ON>:",
  "prompt.spline.first_or_method": "SPLINE: Specify first point or [Method]:",
  "prompt.spline.next":
    "SPLINE: Specify next point or [Close/End tangent/Tolerance]:",
  "prompt.spline.method_fit": "SPLINE: Use fit points (FIT)",
  "prompt.spline.method_cv": "SPLINE: Use control vertices (CV)",
  "prompt.spline.fit_first": "SPLINE: Specify first fit point:",
  "prompt.spline.fit_next":
    "SPLINE: Specify next fit point or [Close/Tangent]. Press Enter to finish.",
  "prompt.spline.fit_next_or_finish":
    "SPLINE: Specify next fit point or [Close/Tangent]. Press Enter to finish creation.",
  "prompt.spline.cv_first": "SPLINE: Specify first control vertex:",
  "prompt.spline.cv_next":
    "SPLINE: Specify next control vertex or [Close]. Press Enter to finish.",
  "prompt.spline.start_tangent": "SPLINE: Specify start tangent:",
  "prompt.spline.end_tangent": "SPLINE: Specify end tangent:",
  "prompt.viewport.first": "MVIEW: Specify corner of viewport or [Fit].",
  "prompt.viewport.second":
    "MVIEW: Specify opposite corner of layout viewport.",
  // VPCLIP-INTERACTIVE — AutoCAD's real VPCLIP first prompt.
  "prompt.vpclip.select_viewport": "VPCLIP: Select viewport to clip:",
  "prompt.vpclip.first_corner":
    "VPCLIP: Specify first corner of the clip boundary:",
  "prompt.vpclip.second_corner":
    "VPCLIP: Specify opposite corner of the clip boundary.",
  // Dimension properties
  "property.mirror_x": "Mirror X",
  "property.mirror_y": "Mirror Y",
  // Hatch / Block / Viewport / Array
  "property.insertion_point": "Insertion point",
  // Subtype labels
  "subtype.linear": "Linear",
  "subtype.aligned": "Aligned",
  "subtype.angular": "Angular",
  "subtype.angular_3pt": "Angular 3pt",
  "subtype.diameter": "Diameter",
  "subtype.radius": "Radius",
  "subtype.ordinate": "Ordinate",
  // Dimension Style Editor
  "dimstyle_editor.title": "Modify Dimension Style: {name}",
  "dimstyle_editor.current_style": "Current Style",
  "dimstyle_editor.set_current": "Set Current",
  "dimstyle_editor.delete_confirm":
    'Delete dimension style "{name}"? Dimensions using it revert to "Standard".',
  "dimstyle_editor.new_style_placeholder": "New style name...",
  "dimstyle_editor.new_button": "New",
  "dimstyle_editor.tab.lines": "Lines",
  "dimstyle_editor.tab.arrows": "Arrows",
  "dimstyle_editor.tab.text": "Text",
  "dimstyle_editor.tab.fit": "Fit",
  "dimstyle_editor.tab.units": "Primary Units",
  "dimstyle_editor.tab.altunits": "Alternate Units",
  "dimstyle_editor.tab.tolerances": "Tolerances",
  // Lines tab
  "dimstyle.lines.section.dim_lines": "Dimension lines",
  "dimstyle.lines.dim_color": "Color (DIMCLRD)",
  "dimstyle.lines.dim_lwd": "Line weight (DIMLWD)",
  "dimstyle.lines.dim_dle": "Extend beyond ticks (DIMDLE)",
  "dimstyle.lines.dim_dli": "Baseline spacing (DIMDLI)",
  "dimstyle.lines.dim_sd1": "Suppress dim line 1 (DIMSD1)",
  "dimstyle.lines.dim_sd2": "Suppress dim line 2 (DIMSD2)",
  "dimstyle.lines.section.ext_lines": "Extension lines",
  "dimstyle.lines.ext_color": "Color (DIMCLRE)",
  "dimstyle.lines.ext_lwe": "Line weight (DIMLWE)",
  "dimstyle.lines.ext_dimexe": "Extend beyond dim lines (DIMEXE)",
  "dimstyle.lines.ext_dimexo": "Offset from origin (DIMEXO)",
  "dimstyle.lines.ext_dimse1": "Suppress ext line 1 (DIMSE1)",
  "dimstyle.lines.ext_dimse2": "Suppress ext line 2 (DIMSE2)",
  // Arrows tab
  "dimstyle.arrows.section": "Arrowheads",
  "dimstyle.arrows.first": "First arrowhead",
  "dimstyle.arrows.second": "Second arrowhead",
  "dimstyle.arrows.size": "Arrowhead size",
  "dimstyle.arrows.center.section": "Center marks",
  "dimstyle.arrows.center.type": "Type",
  "dimstyle.arrows.center.size": "Size",
  "dimstyle.arrows.center.none": "None",
  "dimstyle.arrows.center.mark": "Mark",
  "dimstyle.arrows.center.line": "Line",
  // Text tab
  "dimstyle.text.section.appearance": "Text appearance",
  "dimstyle.text.style": "Text style",
  "dimstyle.text.color": "Text color",
  "dimstyle.text.height": "Text height (DIMTXT)",
  "dimstyle.text.gap": "Text gap (DIMGAP)",
  "dimstyle.text.section.placement": "Text placement",
  "dimstyle.text.vertical": "Vertical",
  "dimstyle.text.alignment": "Alignment",
  "dimstyle.text.tvp": "Vertical position offset (DIMTVP)",
  // Fit tab
  "dimstyle.fit.section.options": "Fit options",
  "dimstyle.fit.atfit": "Fit (DIMATFIT)",
  "dimstyle.fit.atfit.both": "Text and arrows together",
  "dimstyle.fit.atfit.arrows": "Arrows only",
  "dimstyle.fit.atfit.text": "Text only",
  "dimstyle.fit.atfit.best": "Whichever fits best (default)",
  "dimstyle.fit.tofl": "Always draw dim line between ext lines (DIMTOFL)",
  "dimstyle.fit.soxd": "Suppress arrows when not fitting (DIMSOXD)",
  "dimstyle.fit.tix": "Force text inside ext lines (DIMTIX)",
  "dimstyle.fit.upt": "User positioned text (DIMUPT)",
  "dimstyle.fit.tmove": "Text movement rules (DIMTMOVE)",
  "dimstyle.fit.tmove.with_dim": "Move dimension line with text",
  "dimstyle.fit.tmove.add_leader": "Add leader when text moves",
  "dimstyle.fit.tmove.no_leader": "Move text without leader",
  "dimstyle.fit.section.scale": "Scale",
  "dimstyle.fit.scale": "Overall scale (DIMSCALE)",
  "dimstyle.annotative.label": "Annotative (DIMANNO)",
  "dimstyle.annotative.tooltip":
    "Overall scale is driven by the current layout annotation scale",
  "dimstyle.fit.section.text_orient": "Text orientation",
  "dimstyle.fit.tih": "Text inside horizontal (DIMTIH)",
  "dimstyle.fit.toh": "Text outside horizontal (DIMTOH)",
  // Primary Units tab
  "dimstyle.units.section.linear": "Linear dimensions",
  "dimstyle.units.format": "Unit format",
  "dimstyle.units.precision": "Precision",
  "dimstyle.units.prefix": "Prefix",
  "dimstyle.units.suffix": "Suffix",
  "dimstyle.units.lfac": "Measurement scale (DIMLFAC)",
  "dimstyle.units.rnd": "Round distances to (DIMRND)",
  "dimstyle.units.dsep": "Decimal separator (DIMDSEP)",
  "dimstyle.units.dsep.period": "Period (.)",
  "dimstyle.units.dsep.comma": "Comma (,)",
  "dimstyle.units.dsep.space": "Space",
  "dimstyle.units.frac": "Fraction format (DIMFRAC)",
  "dimstyle.units.frac.horizontal": "Horizontal stacked",
  "dimstyle.units.frac.diagonal": "Diagonal stacked",
  "dimstyle.units.frac.flat": "Not stacked",
  "dimstyle.units.section.zero": "Zero suppression",
  "dimstyle.units.zero.leading": "Suppress leading zeros",
  "dimstyle.units.zero.trailing": "Suppress trailing zeros",
  "dimstyle.units.section.angular": "Angular dimensions",
  "dimstyle.units.aunit": "Unit format (DIMAUNIT)",
  "dimstyle.units.aunit.decimal": "Decimal degrees",
  "dimstyle.units.aunit.dms": "Degrees/Minutes/Seconds",
  "dimstyle.units.aunit.grads": "Gradians",
  "dimstyle.units.aunit.radians": "Radians",
  "dimstyle.units.aunit.surveyor": "Surveyor's units",
  "dimstyle.units.adec": "Precision (DIMADEC)",
  "dimstyle.units.apost": "Angular suffix (DIMAPOST)",
  // Alt Units tab
  "dimstyle.altunits.section": "Alternate units",
  "dimstyle.altunits.alt": "Display alternate units (DIMALT)",
  "dimstyle.altunits.altu": "Unit format (DIMALTU)",
  "dimstyle.altunits.altd": "Precision (DIMALTD)",
  "dimstyle.altunits.altf": "Multiplier (DIMALTF)",
  "dimstyle.altunits.altrnd": "Round distances to (DIMALTRND)",
  "dimstyle.altunits.alttd": "Tolerance precision (DIMALTTD)",
  "dimstyle.altunits.altz": "Alternate zero suppression (DIMALTZ)",
  // Tolerances tab
  "dimstyle.tolerances.section": "Tolerance display",
  "dimstyle.tolerances.tol": "Display tolerance (DIMTOL)",
  "dimstyle.tolerances.lim": "Display limits (DIMLIM)",
  "dimstyle.tolerances.tp": "Upper tolerance (DIMTP)",
  "dimstyle.tolerances.tm": "Lower tolerance (DIMTM)",
  "dimstyle.tolerances.tfac": "Scale (DIMTFAC)",
  "dimstyle.tolerances.tdec": "Precision (DIMTDEC)",
  "dimstyle.tolerances.tolj": "Vertical justification (DIMTOLJ)",
  "dimstyle.tolerances.tolj.bottom": "Bottom",
  "dimstyle.tolerances.tolj.middle": "Middle",
  "dimstyle.tolerances.tolj.top": "Top",
  "dimstyle.tolerances.tzin": "Tolerance zero suppression (DIMTZIN)",
  // Common dialog buttons
  "dialog.cancel": "Cancel",
  "dialog.close": "Close",
  "dialog.save_style": "Save Style",
  "dialog.add": "+ Add",
  "dialog.delete": "– Delete",
  "dialog.rename": "Rename",
  "dialog.rename_prompt": "New name:",
  "dialog.apply": "Apply",
  "dialog.ok": "OK",
  // Context toolbar
  "context.dim_style": "Dim Style",
  "context.text_height": "Text Height",
  "context.arrow_size": "Arrow Size",
  "context.manage_styles": "Manage Styles",
  // Settings panel
  "settings.title": "Settings",
  "settings.language": "Interface language",
  "settings.language.ru": "Русский",
  "settings.language.en": "English",
  "settings.open": "Open settings",
  // Preview highlighting (AutoCAD SELECTIONPREVIEW/PREVIEWEFFECT/PREVIEWCOLOR/PREVIEWFILTER)
  "settings.preview.open": "Display preferences",
  "settings.preview.title": "Selection preview",
  "settings.preview.selection.label": "Selection preview (SELECTIONPREVIEW)",
  "settings.preview.selection.0": "Off",
  "settings.preview.selection.1": "Idle only",
  "settings.preview.selection.2": "Select prompt only",
  "settings.preview.selection.3": "Always",
  "settings.preview.effect.label": "Preview effect (PREVIEWEFFECT)",
  "settings.preview.effect.0": "Dashed",
  "settings.preview.effect.1": "Thicken",
  "settings.preview.effect.2": "Dashed and thicken",
  "settings.preview.color.label": "Preview color (PREVIEWCOLOR)",
  "settings.preview.color.reset": "Default",
  "settings.preview.filter.label": "Exclude (PREVIEWFILTER)",
  "settings.preview.filter.locked_layer": "Objects on locked layers",
  "settings.preview.filter.xrefs": "External references",
  "settings.preview.filter.tables": "Tables",
  "settings.preview.filter.mlines": "Multilines",
  "settings.preview.filter.hatches": "Hatches",
  "settings.preview.filter.groups": "Groups",
  "settings.preview.reset_all": "Reset all",
  // Header pills
  "header.app_title": "DXF AI Editor",
  "header.subtitle": "Stage A production shell",
  "header.subtitle_long":
    "Claw-hosted editor shell aligned to the agreed prototype: compact chrome, ribbon-first navigation, command console and an interactive drafting stage for a new empty drawing.",
  "header.preview_user": "Preview user: {email}",
  "header.auth_disabled": "Auth temporarily disabled",
  "header.role": "Role: {role}",
  "header.backend": "Backend: {alias}",
  "header.compact_ribbon": "Compact ribbon",
  "header.editor_ribbon_aria": "Editor ribbon",
  // TextStyle Editor
  "textstyle_editor.title": "Modify Text Style: {name}",
  "textstyle_editor.section.font": "Font",
  "textstyle_editor.font_name": "Font name",
  "textstyle_editor.section.size": "Size",
  "textstyle_editor.height": "Fixed height (0 = variable)",
  "textstyle_editor.section.effects": "Effects",
  "textstyle_editor.width_factor": "Width factor",
  "textstyle_editor.oblique_angle": "Oblique angle",
  "textstyle_editor.backward": "Backward (mirror X)",
  "textstyle_editor.upside_down": "Upside-down (mirror Y)",
  "textstyle_editor.font_none": "(system font)",
  "textstyle_editor.font_missing": "{name} (not loaded)",
  "textstyle_editor.open_font_manager": "Manager...",
  // Font Manager dialog
  "fontmanager.title": "Font Manager",
  "fontmanager.upload_button": "Upload fonts...",
  "fontmanager.drop_zone": "Or drop .shx / .ttf here",
  "fontmanager.empty": "No fonts loaded",
  "fontmanager.loaded_count": "Loaded fonts: {count}",
  "fontmanager.kind_shx": "SHX",
  "fontmanager.kind_ttf": "TTF",
  "fontmanager.remove": "Remove",
  "fontmanager.missing_title": "Required by drawing:",
  "fontmanager.missing_item": "{name} (not loaded)",
  "fontmanager.success": "Font {name} loaded",
  "fontmanager.removed": "Font {name} removed",
  "fontmanager.parse_error": "Error {name}: {error}",
  "fontmanager.close": "Close",
  "fontmanager.fetch_button": "🔍 Find online",
  "fontmanager.fetching": "Searching...",
  "fontmanager.fetched": "Font {name} loaded from whitelist",
  "fontmanager.fetch_not_in_whitelist":
    "Font {name} is not in the public whitelist",
  "fontmanager.not_in_whitelist_tooltip":
    "Font is not in public source — upload the file manually",
  "fontmanager.fetch_network_error": "Network error loading {name}: {message}",
  "fontmanager.fetch_cors_error": "CORS error loading {name}",
  "fontmanager.fetch_parse_error": "Parse error for {name}: {message}",
  "fontmanager.fetch_already_loaded": "Font {name} is already loaded",
  "fontmanager.fetch_aborted": "Loading {name} cancelled",
  // Context toolbar
  "context.style": "Style",
  "context.height": "Height",
  "context.content": "Content",
  "context.content_block": "Block",
  "context.content_mtext": "MText",
  "context.mleader_style": "MLeader Style",
  "context.mleader_text_height": "Text Height",
  "context.mleader_dogleg_length": "Dogleg Length",
  "context.mleader_landing_gap": "Landing Gap",
  "context.mleader_leader_weight": "Leader Weight",
  "context.mleader_annotative": "Annotative",
  // Drawing stage card
  "stage.entities_count_n": "{count} entities",
  "stage.entities_count_one": "1 entity",
  // Sessions
  "sessions.section_label": "Sessions",
  "sessions.title": "Role-aware shell access",
  "sessions.reset_state": "Reset state",
  "sessions.manage_fonts": "SHX/TTF Fonts",
  // Spaces
  "spaces.section_label": "Spaces",
  "spaces.title": "Model / Layout / Viewport routing",
  "spaces.setup": "Space setup",
  "spaces.setup_hint":
    "Model space is active. Run a drafting command, then click points directly on the canvas above.",
  "spaces.entities_in_space": "Entities in active space",
  "spaces.objects_in_drawing": "{count} objects in drawing",
  "entity_list.summary_fallback": "Layer: {layer}",
  "entity_list.show_all": "Show all ({count})",
  "entity_list.collapse": "Collapse list",
  "spaces.clear_selection": "Clear selection",
  "spaces.viewport": "Viewport",
  "spaces.clipped_boundary": "Clipped boundary",
  "spaces.rect_boundary": "Rectangular boundary",
  "spaces.layer_overrides": "layer overrides: {count}",
  // Command line shell
  "shell.command_line": "Command line",
  "shell.command_input_placeholder":
    "Type a command, alias, keyword or coordinates: L, MV, @4500,0, @500<45, 1200,3500",
  "shell.command_history_title": "Command history",
  "shell.run_submit": "Run / Submit",
  "shell.console.current_prompt": "Current prompt",
  "shell.console.accepted_input": "Accepted input",
  "shell.console.alias_hotkey": "Alias / Hotkey",
  "shell.legend.absolute": "Absolute: 1200,3500",
  "shell.legend.relative": "Relative: @4500,0",
  "shell.legend.polar": "Polar: @500<45",
  "shell.legend.direct": "Direct distance: 500",
  "shell.legend.aliases":
    "Aliases: L C A PL REC T DT MT B I AR H BH DIM ML MLD RC RV MV",
  // DXF I/O card
  "dxfio.title": "DXF I/O",
  "dxfio.summary": "Line / Circle / Arc",
  "dxfio.import": "Import",
  "dxfio.import_picker": "File picker",
  "dxfio.export": "Export",
  // Inspector panels
  "panel.overview": "Overview",
  "panel.properties": "Properties",
  "panel.history": "History",
  "panel.ai_guardrails": "AI Guardrails",
  "panel.selection_context": "Selection context",
  "panel.apply_properties": "Apply Properties",
  "panel.apply_properties_hint_default":
    "Updates default object properties for future objects.",
  "panel.apply_properties_hint_commit":
    "Commits through the shared property engine and audit trail.",
  "panel.manage_dim_styles": "Manage Dimension Styles",
  "panel.manage_dim_styles_hint":
    "Opens the dimension style editor to modify arrows, text and unit settings.",
  "panel.manage_text_styles": "Manage Text Styles",
  "panel.manage_text_styles_hint":
    "Opens the text style editor to modify font, size and effect settings.",
  // Justify
  "justify.left": "Left",
  "justify.center": "Center",
  "justify.right": "Right",
  "justify.aligned": "Aligned",
  "justify.middle": "Middle",
  "justify.fit": "Fit",
  "justify.tl": "Top Left",
  "justify.tc": "Top Center",
  "justify.tr": "Top Right",
  "justify.ml": "Middle Left",
  "justify.mc": "Middle Center",
  "justify.mr": "Middle Right",
  "justify.bl": "Bottom Left",
  "justify.bc": "Bottom Center",
  "justify.br": "Bottom Right",
  // Annotative dropdown (Yes/No)
  "annotative.yes": "Yes",
  "annotative.no": "No",
  // Lineweight dropdown (AutoCAD presets)
  "lineweight.bylayer": "ByLayer",
  "lineweight.byblock": "ByBlock",
  "lineweight.default": "Default",
  "lineweight.mm": "{value} mm",
  // CHPROP
  "chprop.title": "CHPROP",
  "chprop.subtitle": "Common object properties",
  "chprop.targets": "targets: {count}",
  "chprop.property": "Property",
  "chprop.value": "Value",
  "chprop.select_property": "Select property",
  "chprop.new_value": "New value",
  "chprop.apply": "Apply CHPROP",
  "chprop.mixed_hint":
    "Mixed selection support is allowed only for shared object properties.",
  // MATCHPROP
  "matchprop.title": "MATCHPROP",
  "matchprop.subtitle": "Source / target workflow",
  "matchprop.no_source": "No source",
  "matchprop.first_slice_hint":
    "This first slice uses an explicit source object and applies conservative capability-map rules for basic, special and all.",
  "matchprop.use_selection_as_source": "Use Selection As Source",
  "matchprop.clear_source": "Clear Source",
  "matchprop.apply_basic": "Apply Basic",
  "matchprop.apply_special": "Apply Special",
  "matchprop.apply_all": "Apply All",
  "matchprop.source_pill": "MATCHPROP source",
  "inspector.expand": "Expand inspector",
  "inspector.collapse": "Collapse inspector",
  "inspector.aggregate_hint":
    "Drawing has {total} {entityNoun} on {layers} {layerNoun}, {types} {typeNoun}.",
  // English has no few/many distinction; both slots read the plural form
  // (mirrors `dxfio.noun.*.few === *.many` for English elsewhere).
  "inspector.noun.layer.one": "layer",
  "inspector.noun.layer.few": "layers",
  "inspector.noun.layer.many": "layers",
  "inspector.noun.type.one": "type",
  "inspector.noun.type.few": "types",
  "inspector.noun.type.many": "types",
  "spaces.paper_space": "Paper space",
  "spaces.model_in_viewport": "Model in viewport",
  "overview.banner":
    "This panel is intentionally explicit about what is implemented versus what remains gated for Stage A acceptance.",
  // Audit / history
  "audit.audit_first_shell": "Audit-first shell",
  "audit.recorded_events": "recorded events: {count}",
  "audit.latest_first": "Latest first",
  // AI panel
  "ai.constraint_1":
    "AI remains command-only in the architecture. This shell does not let AI write entities or bypass contracts.",
  "ai.constraint_2":
    "AI drafting will only emit safe commands, not geometry state.",
  "ai.constraint_3":
    "Capabilities are explicit and conservative for the first tested property slice.",
  "ai.constraint_4":
    "Renderer and previews stay read-only consumers of canonical state.",
  // AI panel (CC-2d) — drafting (review-first) + support (read-only)
  "ai.guardrails.badge": "Safe contour",
  "ai.draft.section_label": "AI drafting",
  "ai.draft.title": "AI command draft",
  "ai.draft.badge": "Review first",
  "ai.draft.intro":
    "Paste an AI proposal — a single command or an array of commands as JSON. Nothing is dispatched without explicit confirmation: review the preview, then press “Accept”.",
  "ai.draft.input_label": "AI proposal (JSON)",
  "ai.draft.input_placeholder":
    '{"action":"INSERT_BLOCK","blockName":"...","insertionPoint":{"x":0,"y":0}}',
  "ai.draft.preview_label": "Sandbox preview",
  "ai.draft.preview_empty":
    "The preview appears once a valid AI proposal is entered — it shows exactly which commands will be dispatched.",
  "ai.draft.preview_count": "Commands to dispatch: {count}",
  "ai.draft.grouped_undo_note":
    "The whole batch executes as a single undo step (“{label}”).",
  "ai.draft.rejection_label": "Rejected by the safe contour",
  "ai.draft.invalid_json": "Invalid JSON: {message}",
  "ai.draft.accept": "Accept",
  "ai.draft.reject": "Reject",
  "ai.draft.undo_label": "AI drafting",
  "ai.draft.transcript_label": "Session transcript",
  "ai.draft.transcript_empty":
    "The transcript is empty — proposals, accepts and rejections will appear here.",
  "ai.draft.entry_accepted_title": "Accepted",
  "ai.draft.entry_accepted":
    "{actions} — {count} command(s) dispatched; one undo step (“{label}”).",
  "ai.draft.entry_rejected_title": "Rejected",
  "ai.draft.entry_rejected":
    "{actions} — rejected by the user, nothing was dispatched.",
  "ai.draft.entry_rejected_reason": "Proposal rejected: {reason}",
  "ai.draft.entry_failed_title": "Execution failed",
  "ai.draft.entry_failed":
    "Batch aborted (abort-batch), the document is unchanged: {reason}",
  "ai.draft.latch_busy_reason":
    "another in-page client (the AI agent bridge) is mid-turn; try again in a moment",
  "ai.support.section_label": "Read-only",
  "ai.support.title": "AI support assistant",
  "ai.support.badge": "read-only",
  "ai.support.intro":
    "The assistant answers questions about commands and the current session. It is not allowed to modify the drawing.",
  "ai.support.query_label": "Question",
  "ai.support.query_placeholder": "For example: how do I draw a line?",
  "ai.support.submit": "Ask",
  "ai.support.readonly_hint": "The drawing is never modified.",
  "ai.support.empty": "Ask a question — the answer will appear here.",
  "ai.support.command_intro": "Command {name} (aliases: {aliases}). {short}",
  "ai.support.command_examples": "Examples: {examples}",
  "ai.support.topic.layers":
    "An entity's layer is changed in the Properties inspector (the “Layer” row), via CHPROP (the Layer property) or MATCHPROP. Layer management lives behind the “Manage layers” button (Layer Manager) on the Properties panel.",
  "ai.support.context.recent": "Recent session commands: {commands}.",
  "ai.support.context.recent_empty":
    "No commands have been executed in this session yet.",
  "ai.support.context.last_command": "Last command: {command}.",
  "ai.support.context.selection":
    "Currently selected entities: {count} ({types}).",
  "ai.support.context.selection_empty": "Nothing is selected right now.",
  "ai.support.no_match":
    "No exact answer found. Ask about a command by name (e.g. LINE/ОТРЕЗОК, CIRCLE/КРУГ), or about layers, the selection, or recent commands.",
  // Status strip
  "status.backend_runtime": "Backend runtime",
  "status.mutation_path": "Mutation path",
  "status.mutation_path_value":
    "UI → dispatch(command) → reducer → command engine → state + audit",
  "status.current_scope": "Current scope",
  "status.auth_session": "Auth / Session",
  "status.google_signin_disabled":
    "Google sign-in is temporarily disabled for this live contour until valid OAuth credentials are configured on claw.",
  "status.auth_fallback":
    "Auth/session fell back to the local shell because the backend could not be loaded.",
  // Persistent backend slice
  "backend.slice_title": "Persistent backend slice",
  "backend.slice_subtitle": "Workspace / drawings / audit",
  "backend.workspace": "Workspace",
  "backend.workspace_intro":
    "This is the persistent workspace exposed by claw.",
  "backend.workspace_fallback":
    "Backend workspace slice is unavailable, so the shell shows the local preview workspace.",
  "backend.tag_backend": "backend",
  "backend.tag_local_fallback": "local fallback",
  "backend.slug": "Slug: {slug}",
  "backend.owner": "Owner: {owner}",
  "backend.members": "Members: {count}",
  "backend.visibility": "Visibility: {visibility}",
  "backend.deployment": "Deployment: {mode}",
  "backend.drawings": "Drawings",
  "backend.audit": "Audit",
  "backend.records": "records: {count}",
  "backend.local_fallback": "Local fallback",
  "backend.loading_slices": "Loading persistent slices from {url}…",
  "backend.loaded_admin":
    "Loaded from {url}/api/v1/workspaces, /drawings and /admin/audit.",
  "backend.loaded_user":
    "Loaded from {url}/api/v1/workspaces and /drawings. Admin audit stays gated to admin roles.",
  "backend.protected_unloaded":
    "Protected persistent slices were not loaded, so the shell keeps local fallback state.",
  "backend.guest_fallback":
    "Guest runtime is using local persistent fallbacks and did not load protected backend slices.",
  "backend.partial_load":
    "Some backend slices loaded and some fell back locally. {error}",
  "backend.unavailable":
    "Backend persistent slices are not available right now. {error}",
  "backend.preview_fallback": "The shell keeps a local preview fallback.",
  "backend.failed_to_load":
    "Failed to load persistent slices from {url}: {failures}",
  "status.connecting": "Connecting to {url}",
  "status.fallback_mode": "Fallback mode: {error}",
  "status.ready_via": "{authMode} via {url}",
  "status.local_shell_fallback":
    "Simple geometry and property editing stay available with local shell fallback",
  "history.via": "via",
  // Panel labels
  "panel.label.overview": "Overview",
  "panel.label.properties": "Properties",
  "panel.label.history": "History",
  "panel.label.ai": "AI Guardrails",
  // Future delivery gates
  "gates.canonical_dto":
    "Canonical DTO/model for the 13 required entity families",
  "gates.command_engine": "Command Engine as the only geometry mutation path",
  "gates.mlv_parity": "Model/Layout/Viewport parity before claiming Stage A",
  "gates.properties_capability":
    "Properties / CHPROP / MATCHPROP capability map from research matrix",
  "gates.audit_telemetry":
    "Audit + command-safe telemetry on every mutation-capable flow",
  // Attachment names
  "attachment.top_left": "Top left",
  "attachment.top_center": "Top center",
  "attachment.top_right": "Top right",
  "attachment.middle_left": "Middle left",
  "attachment.middle_center": "Middle center",
  "attachment.middle_right": "Middle right",
  "attachment.bottom_left": "Bottom left",
  "attachment.bottom_center": "Bottom center",
  "attachment.bottom_right": "Bottom right",
  // Arrowhead options
  "arrowhead.closed_filled": "Closed filled",
  "arrowhead.closed_blank": "Closed blank",
  "arrowhead.open": "Open",
  "arrowhead.dot": "Dot",
  "arrowhead.dot_blank": "Dot blank",
  "arrowhead.arch_tick": "Arch tick",
  "arrowhead.oblique": "Oblique",
  "arrowhead.open_30": "Open 30",
  "arrowhead.none": "None",
  // Text placement
  "textplace.above": "Above",
  "textplace.centered": "Centered",
  "textplace.below": "Below",
  // Text alignment
  "textalign.horizontal": "Horizontal",
  "textalign.aligned": "Aligned",
  "textalign.iso": "ISO",
  // Unit format
  "unitformat.scientific": "Scientific",
  "unitformat.decimal": "Decimal",
  "unitformat.engineering": "Engineering",
  "unitformat.architectural_stacked": "Architectural (stacked)",
  "unitformat.fractional_stacked": "Fractional (stacked)",
  "unitformat.architectural": "Architectural",
  "unitformat.fractional": "Fractional",
  "unitformat.windows_desktop": "Microsoft Windows desktop",
  // Dimstyle editor error messages
  "dimstyle_editor.error.empty_name": "Style name cannot be empty.",
  "dimstyle_editor.error.create_failed": "Failed to create style.",
  // Ribbon action labels
  "ribbon.action.history": "History",
  "ribbon.action.ai": "AI",
  "ribbon.action.properties": "Properties",
  "ribbon.action.chprop": "CHPROP",
  "ribbon.action.matchprop": "MATCHPROP",
  "ribbon.action.clear": "Clear",
  "ribbon.action.overview": "Overview",
  "ribbon.action.audit": "Audit",
  "ribbon.action.model": "Model",
  "ribbon.action.layout_1": "Layout 1",
  "ribbon.action.layout_2": "Layout 2",
  "ribbon.action.paper": "Paper",
  "ribbon.action.viewport": "Viewport",
  "ribbon.action.viewports": "Viewports",
  "ribbon.action.panel_overview": "Overview",
  "ribbon.action.panel_properties": "Properties",
  "ribbon.action.panel_history": "History",
  "ribbon.action.import_dxf": "Import DXF",
  "ribbon.action.reset": "Reset",
  "ribbon.action.sessions": "Sessions",
  // Ribbon section titles
  "ribbon.section.modify": "Modify",
  "ribbon.section.properties": "Properties",
  "ribbon.section.selection": "Selection",
  "ribbon.section.space_routing": "Space Routing",
  "ribbon.section.viewport_context": "Viewport Context",
  "ribbon.section.inspectors": "Inspectors",
  "ribbon.section.data_flow": "Data Flow",
  // Ribbon quickbar
  "ribbon.quickbar.current_layer": "Layer",
  "ribbon.quickbar.current_dimstyle": "Dim Style",
  "ribbon.quickbar.current_mleaderstyle": "MLeader Style",
  "ribbon.quickbar.current_textstyle": "Text Style",
  "ribbon.quickbar.live_entities": "{count} live entities",
  "quickbar.undo": "Undo",
  "quickbar.redo": "Redo",
  "quickbar.undo.tooltip": "Undo (Ctrl+Z)",
  "quickbar.redo.tooltip": "Redo (Ctrl+Y)",
  "ribbon.coord_toggle.cad_title":
    "Y axis points UP (AutoCAD / DXF convention). Click to switch to SVG (Y down).",
  "ribbon.coord_toggle.svg_title":
    "Y axis points DOWN (legacy SVG convention). Click to switch to CAD (Y up).",
  // Command line card / feed
  "shell.command_label": "Command",
  "shell.esc_button": "Esc",
  "shell.idle_badge": "Idle",
  "shell.command_stream_placeholder":
    "AutoCAD-like command stream will echo command names, picked points and typed input here.",
  "shell.parse_banner.active":
    "Active prompts accept command-safe typed input such as Close, Diameter, Undo, text content and explicit numeric values where that command supports them.",
  "shell.parse_banner.idle":
    "The command line already supports direct geometry arguments for LINE/CIRCLE/ARC and prompt-aware keywords; fuller AutoCAD-style coordinate grammar is being brought online command by command.",
  "shell.default_hint":
    "Aliases: L, C, A, PL, REC, DT, MT, B, I, AR, H, BH, DIM, MLD, RV, MV. Esc cancels active command.",
  // Workspace status badges
  "workspace.space_badge.model": "{name} / model space",
  "workspace.space_badge.paper": "{name} / paper space",
  "workspace.space_badge.viewport": "{name} / model in viewport",
  "workspace.mode_badge": "Command mode: {command}",
  "workspace.mode_badge.idle": "idle",
  "workspace.flow_badge.active":
    "Flow: click in canvas or submit prompt-aware values",
  "workspace.flow_badge.idle":
    "Flow: start from ribbon, alias or explicit command line input",
  "workspace.input_badge":
    "Input now: LINE 0,0 4500,0 | C | D | U | text content",
  "workspace.scope_badge": "Import: DXF merges into the current drawing",
  // Dxf import
  "dxfio.status.default": "Import a DXF file into the current drawing.",
  "dxfio.status.failed": "DXF import failed: {message}",
  "dxfio.import_button": "Import DXF",
  "dxfio.import_microcopy":
    "The selected .dxf file is parsed and merged into the current drawing canvas.",
  "dxfio.export_entities_count": "{count} entities",
  "dxfio.download": "Download DXF",
  "dxfio.copy_clipboard": "Copy to clipboard",
  // Truncation note appended to the export preview <textarea> when the
  // serialised DXF exceeds the DOM layout cap (see App.tsx
  // DXF_PREVIEW_DOM_CAP). The full document is still written by
  // Download/Copy — only the on-screen preview is clipped.
  "dxfio.preview_truncated":
    "… [preview truncated: showing first {shown} of {total} chars — export for the full DXF]",
  // DXF import progress (AutoCAD "Processing Objects" dialog)
  "dxfio.progress.title": "Processing Objects",
  "dxfio.progress.fetch": "Loading file",
  "dxfio.progress.decode": "Decoding (CP1251)",
  "dxfio.progress.tokenize": "Tokenizing",
  "dxfio.progress.sections": "Parsing sections",
  "dxfio.progress.entities": "Parsing entities",
  "dxfio.progress.bridge": "Building model",
  "dxfio.progress.merge": "Merging document",
  "dxfio.progress.sync": "Syncing scene",
  "dxfio.progress.items_fmt": "{processed} / {total}",
  "dxfio.progress.cancel": "Cancel",
  "dxfio.status.aborted": "DXF import cancelled.",
  // AutoCAD OPEN: new drawing opened successfully (replaces current).
  // Single template; `{entityNoun}` and `{spaceNoun}` carry the
  // one/many English forms ("entity"/"entities", "space"/"spaces").
  "dxfio.status.opened":
    "Opened {count} {entityNoun} from the DXF across {spaces} {spaceNoun}.",
  // English uses two forms (one / many); the `few` slot duplicates the
  // many form so the same store-side selector works across languages.
  "dxfio.noun.entity.one": "entity",
  "dxfio.noun.entity.few": "entities",
  "dxfio.noun.entity.many": "entities",
  "dxfio.noun.space.one": "space",
  "dxfio.noun.space.few": "spaces",
  "dxfio.noun.space.many": "spaces",
  // Stage canvas
  "stage.empty_title": "Empty drawing",
  "stage.empty_hint":
    "Start a command from the ribbon or command console, then pick points in this canvas.",
  "stage.osnap_title": "Object Snaps",
  "stage.osnap_hint":
    "Right-click opens the snap list. The checkbox next to each snap enables or disables it for the current session.",
  "prompt.idle":
    "Idle. Start a command from the ribbon, alias, hotkey or command line in {space}.",
  // Backend persistence source labels
  "backend.source.loading": "loading",
  "backend.source.guest": "guest fallback",
  "backend.source.auth": "auth fallback",
  "backend.source.backend_audit_gated": "backend + audit gated",
  "backend.source.backend": "backend",
  "backend.source.mixed": "mixed",
  "backend.source.fallback": "fallback",
  "backend.offline_fallback": "offline fallback",
  "backend.loading_label": "loading",
  "backend.no_persistent_workspace": "No persistent workspace returned",
  // Zoom feed messages
  "zoom.stage_not_ready": "ZOOM: stage not ready",
  "zoom.extents": "ZOOM Extents",
  "zoom.all": "ZOOM All",
  "zoom.window_exact": "ZOOM Window {arg}",
  "zoom.window_invalid":
    "ZW: expected 'x1,y1 x2,y2'. Use context menu for interactive picking.",
  "zoom.window_blocked":
    "ZOOM Window blocked: cancel active drawing command first",
  "zoom.window_pick": "ZOOM Window: pick first corner",
  "zoom.window_pick_second": "ZOOM Window: pick opposite corner",
  "zoom.previous": "ZOOM Previous",
  "zoom.previous_none": "ZOOM Previous: no previous view",
  "zoom.object_with_arg": "ZOOM Object {arg}",
  "zoom.object_selection": "ZOOM Object (selection)",
  "zoom.object_pick": "ZOOM Object: select an object on canvas",
  "zoom.object_not_found": "ZOOM Object: entity '{arg}' not found",
  // #293-4 — blocked ≠ pick-started: distinct line when another interactive
  // zoom pick already owns the canvas (mirrors window_blocked).
  "zoom.object_blocked":
    "ZOOM Object blocked: cancel active drawing command first",
  "zoom.object_selection_no_bounds":
    "ZOOM Object: selection has no resolvable bounds",
  // ── PAN command feed / prompt messages (cmd-pan) ──
  "pan.realtime_prompt": "PAN: drag LMB to pan. Esc/Enter to exit.",
  "pan.session_end": "PAN: panning ended.",
  "pan.displacement": "PAN {arg}",
  "pan.displacement_invalid":
    "-PAN: expected 'x1,y1 x2,y2'. For interactive mode type PAN with no arguments.",
  "pan.displacement_none": "PAN: view unchanged (zero displacement).",
  // Command feed: batch / delete / esc
  "feed.undo": "UNDO",
  "feed.redo": "REDO",
  "feed.undo.mark": "UNDO MARK: group opened.",
  "feed.undo.end": "UNDO END: group committed.",
  "feed.undo.end.committed":
    "UNDO END: group committed to the undo log.",
  "feed.undo.end.nested": "UNDO END: nesting level decremented.",
  "feed.undo.back": "UNDO BACK: group aborted.",
  "feed.undo.end.no-batch": "UNDO END: no open group.",
  "feed.undo.back.no-batch": "UNDO BACK: no open group.",
  "feed.undo.during-batch":
    "Cannot undo while UNDO group is open — use UNDO END or UNDO BACK first.",
  "feed.undo.empty": "Nothing to undo.",
  "feed.redo.empty": "Nothing to redo.",
  "feed.delete": "DELETE",
  "feed.revcloudproperties.select":
    "REVCLOUDPROPERTIES: select a revision cloud before running the command.",
  "feed.esc": "ESC",
  "feed.batch.expects_array": "BATCH expects a JSON array of command strings.",
  "feed.batch.running": "BATCH: running {count} commands...",
  "feed.batch.finished": "BATCH: finished {count} commands.",
  "feed.batch.parse_error": "BATCH parse error: {error}",
  "feed.dxf_error": "Failed to load persistent slices from {url}: {failures}",
  // Drafting dispatch
  "drafting.armed": "{command} armed in {space}.",
  // Drafting accepted-input prompts
  "input.idle":
    "Commands, aliases, property flows, DXF import and point-picking in the drawing area.",
  "input.text_content": "Text content in the command line, or Esc to cancel.",
  "input.line_continue":
    "Pick the next point in canvas, press Enter to finish, or type C to close back to the start.",
  "input.line_next": "Pick the next point in canvas.",
  "input.circle_radius":
    "Pick a radius point, type a radius, or type D to switch to diameter entry.",
  "input.circle_diameter":
    "Type a positive diameter value, then press Enter to confirm the value.",
  "input.pline_next":
    "Pick points in canvas, Enter to finish, type C to close, or U to undo the last vertex.",
  "input.pline_continue": "Pick the next point in canvas, or Esc to cancel.",
  "input.block_insertion":
    "Pick the insertion point in canvas or type coordinates. Esc cancels.",
  "input.block_insertion_full":
    "Pick the insertion point in canvas or type coordinates such as 1200,3500, @4500,0 or @500<45. Esc cancels.",
  "input.block_scale": "Type a uniform scale factor, or press Enter to keep 1.",
  "input.block_rotation":
    "Type a rotation angle in degrees, or press Enter to keep 0.",
  "input.arrayrect_points":
    "Pick the base point or opposite corner in canvas, or type coordinates. Esc cancels.",
  "input.arraypolar_center":
    "Pick the polar array center in canvas, or type coordinates. Esc cancels.",
  "input.arraypath_path":
    "Pick a point on a path curve (line, arc, circle or polyline). Esc cancels.",
  "input.arraypath_method":
    "Type D (Divide) or M (Measure). Enter keeps Divide as default.",
  "input.arraypath_params":
    "Type the item count (Divide) or spacing (Measure). Enter keeps the default value.",
  "input.hatch_corners":
    "Pick hatch boundary corners in canvas or type coordinates. Esc cancels.",
  "input.hatch_internal":
    "Pick a point inside a closed region. Enter applies, Esc cancels.",
  "input.hatch_mode":
    "Type a mode: I (internal point), S (select objects) or R (rectangle). Enter accepts I as default.",
  "input.viewport_first":
    "Pick the first viewport corner, type coordinates, or type Fit. Esc cancels.",
  "input.viewport_second":
    "Pick the opposite viewport corner or type coordinates. Esc cancels.",
  "input.vpclip_select_viewport":
    "Click a viewport to clip. Enter cancels, Esc cancels.",
  "input.vpclip_first_corner":
    "Specify the first clip-boundary corner or type coordinates. Esc cancels.",
  "input.vpclip_second_corner":
    "Specify the opposite clip-boundary corner or type coordinates. Esc cancels.",
  "input.dim_arc_select":
    "Pick a point on an arc or circle in canvas, or type coordinates. Esc cancels.",
  "input.dim_line_endpoint":
    "Pick a point to define a line endpoint, or type coordinates. Esc cancels.",
  "input.points_generic":
    "Pick points in canvas or type absolute / relative coordinates such as 1200,3500, @4500,0 or @500<45. Esc cancels.",
  "input.arrayrect_count":
    "Type COL=<n> or ROW=<n>, or pick the opposite corner in canvas. Enter accepts current values.",
  "input.arrayrect_spacing":
    "Type CS=<n> (column spacing), RS=<n> (row spacing) or AA=<deg> (axis angle). Enter creates the array.",
  "input.arraypolar_count":
    "Type the number of items (min 2). Enter accepts the default of 6.",
  "input.arraypolar_fill":
    "Type fill angle in degrees (+ CCW, - CW), ROW=<n> for concentric rings, RS=<n> for radial spacing. Enter creates the array.",
  "input.hatch_pattern":
    "Type a pattern name such as ANSI31 or SOLID, or press Enter to keep ANSI31.",
  "input.hatch_ready":
    "Press Enter to create the hatch with the current pattern.",
  "input.confirm_or_cancel":
    "Press Enter to confirm the current command input or Esc to cancel.",
  // Drafting command hints
  "hint.default":
    "Aliases: L, C, A, PL, REC, DT, MT, B, I, AR, AP, H, BH, DIM, DAL, DAN, DRA, DDI, DOR, MLD, RV, MV. Esc cancels active command.",
  "hint.line_continue":
    "LINE active. Pick next point, Enter finishes, C closes back to the first point, Esc cancels.",
  "hint.line_next": "LINE active. Pick the next point.",
  "hint.circle_radius":
    "CIRCLE active. Pick radius or type a radius value. Type D for diameter. Esc cancels.",
  "hint.circle_diameter":
    "CIRCLE diameter entry active. Type a positive value and press Enter. Esc cancels.",
  "hint.block_name":
    "INSERT active. Type block name or ? for a placeholder list, then place the insertion point.",
  "hint.block_scale": "INSERT active. Type scale factor or press Enter for 1.",
  "hint.block_rotation": "INSERT active. Type rotation or press Enter for 0.",
  "hint.arrayrect_params":
    "ARRAYRECT active. COL=<n>, ROW=<n>, CS=<n>, RS=<n>, AA=<deg>. Enter accepts.",
  "hint.arrayrect_select_or_base":
    "ARRAYRECT active. Select objects and pick base point. Esc cancels.",
  "hint.arraypolar_params":
    "ARRAYPOLAR active. Item count, fill angle, ROW=<n>, RS=<n>. Enter accepts.",
  "hint.arraypolar_select_or_center":
    "ARRAYPOLAR active. Select objects and pick center point. Esc cancels.",
  "hint.arraypath_select_or_path":
    "ARRAYPATH active. Select objects, then pick the path (line, arc, circle or polyline). Esc cancels.",
  "hint.arraypath_params":
    "ARRAYPATH active. Method (D/M), item count or spacing. Enter accepts.",
  "hint.hatch_pattern":
    "HATCH active. Type pattern name or press Enter for ANSI31.",
  "hint.viewport_first":
    "MVIEW active. Pick first corner or type Fit. Esc cancels.",
  "hint.vpclip_select_viewport":
    "VPCLIP active. Select the viewport to clip. Esc cancels.",
  "hint.pline":
    "PLINE active. Enter finishes, C closes, U undoes the last vertex, Esc cancels.",
  "hint.block_insertion":
    "INSERT active. Pick insertion point or type coordinates. Esc cancels.",
  "hint.hatch_corners": "HATCH active. Pick the hatch area. Esc cancels.",
  "hint.hatch_internal":
    "HATCH active. Click inside a closed region. Enter applies, Esc cancels.",
  "hint.viewport_corners":
    "MVIEW active. Pick opposite viewport corners. Esc cancels.",
  "hint.vpclip_corners":
    "VPCLIP active. Pick opposite corners of the clip boundary. Esc cancels.",
  "hint.generic": "{command} active. Esc cancels.",
  // Stage status text
  "stage_status.active":
    "{command} is active in {space}. Pick points directly on the canvas.",
  "stage_status.empty":
    "The current {spaceLower} is empty. Start a command to begin drawing.",
  "stage_status.count_one": "1 object currently exists in {space}.",
  "stage_status.count_many": "{count} objects currently exist in {space}.",
  // Draft summary / commit messages
  "summary.arc_created": "ARC created through three picked points.",
  "summary.rectang_created": "RECTANG created from opposite corners.",
  "summary.polygon_created": "POLYGON created with {sides} sides.",
  "summary.dimension_created": "DIMENSION created.",
  "summary.dimaligned_created": "DIMALIGNED created.",
  "summary.dimangular_created": "DIMANGULAR created.",
  "summary.dim3point_created": "DIM3POINT created.",
  "summary.dimradius_created": "DIMRADIUS created.",
  "summary.dimdiameter_created": "DIMDIAMETER created.",
  "summary.dimordinate_created": "DIMORDINATE created.",
  "summary.dimbaseline_created": "DIMBASELINE created.",
  "summary.dimcontinue_created": "DIMCONTINUE created.",
  "summary.revcloud_created": "REVCLOUD created.",
  "summary.revcloud_payload_invalid":
    "REVCLOUD: incomplete command payload (needs 2 corners or >=3 vertices) — no cloud created.",
  "summary.wipeout_created": "WIPEOUT created.",
  "summary.spline_created": "SPLINE created.",
  "summary.viewport_created": "Viewport created.",
  "summary.viewport_created_fit": "Viewport created with Fit.",
  "summary.viewport_clip_set": "Viewport {id} clip boundary set.",
  "summary.line_closed": "LINE closed.",
  "summary.insert_created": "INSERT created.",
  "summary.block_insert_batch_created":
    "INSERT '{name}' created ({n} copies).",
  "summary.arrayrect_created": "Array created ({n} copies).",
  "summary.arraypolar_created": "Polar array created ({n} copies).",
  "summary.arraypath_created": "Path array created ({n} copies).",
  "summary.arrayedit_modified": "Array modified ({n} copies).",
  "summary.hatchedit_modified": "Hatch modified.",
  "summary.hatch_created": "HATCH created.",
  "summary.text_created": "TEXT created.",
  "summary.mtext_created": "MTEXT created.",
  "summary.mleader_created": "MLEADER created.",
  "summary.mleader_leader_added": "Leader added.",
  "summary.mleader_leader_removed": "Leader removed.",
  "summary.mleader_removed": "Multileader removed.",
  "summary.mleader_not_found": "Multileader not found.",
  "summary.mleader_not_multileader": "Selected entity is not a multileader.",
  "summary.mleader_invalid_leader_index": "Invalid leader index.",
  "summary.mleader_confirm_last_leader":
    "This is the last leader. Confirm removal of the entire multileader.",
  // ── Phase 9 — grip editing audit summaries ──
  "summary.mleader_vertex_moved": "Multileader vertex moved.",
  "summary.mleader_text_moved": "Multileader text moved.",
  "summary.mleader_text_rotated": "Multileader text rotated.",
  "summary.mleader_dogleg_adjusted": "Multileader dogleg adjusted.",
  "summary.mleader_vertex_removed": "Multileader vertex removed.",
  "summary.mleader_vertex_added": "Multileader vertex added.",
  "summary.mleader_invalid_vertex_index": "Invalid multileader vertex indices.",
  // ── Phase 9 — grip context menu items ──
  "grip.menu.add_leader": "Add Leader",
  "grip.menu.add_vertex": "Add Vertex",
  "grip.menu.remove_vertex": "Remove Vertex",
  "grip.menu.stretch": "Stretch",
  "grip.menu.convert_spline": "Convert to Spline",
  "grip.menu.convert_polyline": "Convert to Polyline",
  // GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) — command-line
  // echo of the armed hot-grip edit-mode modal prompt ("** STRETCH **" / …),
  // AutoCAD's bracketed mode-cycle token. Enter/Space cycles Stretch → Move →
  // Rotate → Scale → Mirror → (wraps). Distinct from the plain-label
  // "grip.menu.*" context-menu strings above.
  "grip.mode.stretch": "** STRETCH **",
  "grip.mode.move": "** MOVE **",
  "grip.mode.rotate": "** ROTATE **",
  "grip.mode.scale": "** SCALE **",
  "grip.mode.mirror": "** MIRROR **",
  "summary.pline_closed": "PLINE closed.",
  "summary.pline_finished": "PLINE finished.",
  "summary.point_created": "POINT created at {point}.",
  "summary.base_set": "Drawing base point set to {point}.",
  "summary.circle_created": "CIRCLE created at {point}.",
  "summary.circle_created_diameter":
    "CIRCLE created at {point} with diameter {diameter}.",
  "summary.circle_created_2p":
    "CIRCLE created: 2-point diameter, center {point}.",
  "summary.circle_created_3p":
    "CIRCLE created through 3 points, center {point}.",
  "error.circle.3p_collinear": "Points are collinear — circle not created.",
  "error.arc.3p_collinear": "Points are collinear — arc not created.",
  "error.hatch.no_boundary":
    "Closed boundary not found. Click inside a closed region.",
  "summary.donut_created": "DONUT created at {point}.",
  "summary.ellipse_created": "ELLIPSE created at {point}.",
  // Entity labels
  "entity.label.line": "Line {id}",
  "entity.label.point": "Point {id}",
  "entity.label.circle": "Circle {id}",
  "entity.label.arc": "Arc {id}",
  "entity.label.ellipse": "Ellipse {id}",
  "entity.label.polyline": "Polyline {id}",
  "entity.label.text": "Text {id}",
  "entity.label.mtext": "MText {id}",
  "entity.label.block": "{name} {id}",
  "entity.label.array": "Array {id}",
  "entity.label.arrayrect": "Array {id}",
  "entity.label.hatch": "Hatch {id}",
  "entity.label.dimension": "Dimension {id}",
  "entity.label.mleader": "MLeader {id}",
  "entity.label.revcloud": "Revision cloud {id}",
  "entity.label.wipeout": "Wipeout {id}",
  "entity.label.viewport": "Viewport {id}",
  // Preview entity labels
  "preview.draft_line": "Draft line preview",
  "preview.draft_circle": "Draft circle preview",
  "preview.arc_chord": "Arc chord preview",
  "preview.draft_arc": "Draft arc preview",
  "preview.draft_ellipse": "Draft ellipse preview",
  "preview.draft_polyline": "Draft polyline preview",
  "preview.draft_spline": "Draft spline preview",
  "preview.draft_rectangle": "Draft rectangle preview",
  "preview.draft_mtext": "Draft mtext preview",
  "preview.draft_insert": "Draft insert preview",
  "preview.draft_array": "Draft array preview",
  "preview.draft_arrayrect": "Draft rectangular array preview",
  "preview.draft_block": "Draft block preview",
  "preview.draft_hatch": "Draft hatch preview",
  "preview.draft_dimension_baseline": "Draft dimension baseline",
  "preview.draft_dimension": "Draft dimension preview",
  "preview.draft_ordinate_dimension": "Draft ordinate dimension preview",
  "preview.draft_leader": "Draft leader preview",
  "preview.draft_revcloud": "Draft revision cloud preview",
  "preview.draft_wipeout": "Draft wipeout preview",
  "preview.draft_viewport": "Draft viewport preview",
  "preview.draft_vpclip": "Draft VPCLIP clip boundary preview",
  "preview.draft_donut": "Draft donut preview",
  "preview.draft_radius_dim": "Draft radius dimension preview",
  "preview.draft_diameter_dim": "Draft diameter dimension preview",
  "suggestion.coordinate_input": "Coordinate input",
  "keyword.close": "Close",
  "keyword.diameter": "Diameter",
  "keyword.undo": "Undo",
  "keyword.yes": "Yes",
  "keyword.no": "No",
  "keyword.revcloud.arc_length": "Arc length",
  "keyword.revcloud.object": "Object",
  "keyword.revcloud.rectangular": "Rectangular",
  "keyword.revcloud.polygonal": "Polygonal",
  "keyword.revcloud.freehand": "Freehand",
  "keyword.revcloud.style": "Style",
  "keyword.revcloud.style_normal": "Normal",
  "keyword.revcloud.style_calligraphy": "Calligraphy",
  // HATCH-EDIT-DIALOG (§5.1) — pattern type + island-detection style
  "keyword.hatch.pattern_predefined": "Predefined",
  "keyword.hatch.pattern_user_defined": "User defined",
  "keyword.hatch.pattern_custom": "Custom",
  "keyword.hatch.island_normal": "Normal",
  "keyword.hatch.island_outer": "Outer",
  "keyword.hatch.island_ignore": "Ignore",
  // Drawing stage card
  "stage_card.title": "Drawing stage",
  "stage_card.model_canvas": "Model space canvas",
  "stage_card.layout_canvas": "{name} canvas",
  "stage_card.entities_count_one": "1 entity",
  "stage_card.entities_count_many": "{count} entities",
  "stage_card.context.zoom_title": "Zoom",
  "stage_card.context.zoom_extents": "Zoom Extents",
  "stage_card.context.zoom_all": "Zoom All",
  "stage_card.context.zoom_window": "Zoom Window",
  "stage_card.context.zoom_object": "Zoom Object",
  "stage_card.context.zoom_previous": "Zoom Previous",
  // Multileader Style Editor dialog
  "mleaderstyle.editor.title": "Multileader Style Manager: {name}",
  "mleaderstyle.editor.current_style": "Current style",
  "mleaderstyle.editor.new_style_name": "New style name...",
  "mleaderstyle.editor.based_on": "Based on",
  "mleaderstyle.editor.tabs.format": "Leader Format",
  "mleaderstyle.editor.tabs.structure": "Leader Structure",
  "mleaderstyle.editor.tabs.content": "Content",
  "mleaderstyle.editor.tabs.block": "Block Content",
  "mleaderstyle.actions.new": "New Style",
  "mleaderstyle.actions.delete": "Delete",
  "mleaderstyle.actions.save": "Save",
  "mleaderstyle.actions.cancel": "Cancel",
  "mleaderstyle.actions.delete_confirm": 'Delete style "{name}"?',
  "mleaderstyle.actions.delete_standard_error":
    "Standard style cannot be deleted.",
  "mleaderstyle.actions.duplicate_name_error":
    "A style with this name already exists.",
  "mleaderstyle.actions.empty_name_error": "Style name is required.",
  // Tab 1 — Leader Format
  "mleaderstyle.format.type": "Leader type (171)",
  "mleaderstyle.format.type.straight": "Straight",
  "mleaderstyle.format.type.spline": "Spline",
  "mleaderstyle.format.type.none": "None",
  "mleaderstyle.format.color": "Leader color (91)",
  "mleaderstyle.format.linetype": "Leader linetype (173)",
  "mleaderstyle.format.weight": "Leader weight (90)",
  "mleaderstyle.format.arrow_block": "Arrowhead (342)",
  "mleaderstyle.format.arrow_size": "Arrowhead size (44)",
  "mleaderstyle.format.break_enabled": "Leader break (293)",
  "mleaderstyle.format.break_gap": "Break gap (143)",
  "mleaderstyle.format.break_size": "Break size",
  // Tab 2 — Leader Structure
  "mleaderstyle.structure.segments": "Max leader segments (90)",
  "mleaderstyle.structure.landing": "Landing enabled (290)",
  "mleaderstyle.structure.dogleg": "Dogleg enabled (291)",
  "mleaderstyle.structure.dogleg_length": "Dogleg length (42)",
  "mleaderstyle.structure.landing_gap": "Landing gap (43)",
  "mleaderstyle.structure.base_point_x": "Base point X",
  "mleaderstyle.structure.base_point_y": "Base point Y",
  "mleaderstyle.structure.scale": "Overall scale (142)",
  "mleaderstyle.structure.annotative": "Annotative (296)",
  // Tab 3 — Content
  "mleaderstyle.content.type": "Content type (170)",
  "mleaderstyle.content.type.block": "Block",
  "mleaderstyle.content.type.mtext": "MText",
  "mleaderstyle.content.type.none": "None",
  "mleaderstyle.content.text_style": "Text style (340)",
  "mleaderstyle.content.text_height": "Text height (40)",
  "mleaderstyle.content.text_width": "Text width (41)",
  "mleaderstyle.content.text_color": "Text color (93)",
  "mleaderstyle.content.attachment": "Text attachment (172)",
  "mleaderstyle.content.attachment.top": "Top",
  "mleaderstyle.content.attachment.middle": "Middle",
  "mleaderstyle.content.attachment.bottom": "Bottom",
  "mleaderstyle.content.attachment.left": "Left",
  "mleaderstyle.content.attachment.right": "Right",
  "mleaderstyle.content.attachment_direction": "Attachment direction (271)",
  "mleaderstyle.content.attachment_direction.horizontal": "Horizontal",
  "mleaderstyle.content.attachment_direction.vertical": "Vertical",
  "mleaderstyle.content.frame": "Text frame (292)",
  "mleaderstyle.content.linespacing": "Line spacing style (73)",
  "mleaderstyle.content.linespacing.at_least": "At Least",
  "mleaderstyle.content.linespacing.exactly": "Exactly",
  "mleaderstyle.content.linespacing.multiple": "Multiple",
  "mleaderstyle.content.linespacing_factor": "Line spacing factor (45)",
  "mleaderstyle.content.linespacing_distance": "Line spacing distance",
  // Tab 4 — Block Content
  "mleaderstyle.block.name": "Block name (343)",
  "mleaderstyle.block.scale": "Block scale (47)",
  "mleaderstyle.block.color": "Block color (93)",
  "mleaderstyle.block.rotation": "Block rotation (48)",
  "mleaderstyle.block.connection": "Connection type (177)",
  "mleaderstyle.block.connection.center_extents": "Center Extents",
  "mleaderstyle.block.connection.insertion_point": "Insertion Point",
  // Properties panel trigger
  "panel.manage_mleader_styles": "Manage multileader styles",
  "panel.manage_mleader_styles_hint":
    "Opens the editor for all multileader styles.",
  // ── Property labels (shown in Properties panel) ──
  "property.layer": "Layer",
  "property.color": "Color",
  "property.linetype": "Linetype",
  "property.lineweight": "Lineweight",
  "property.thickness": "Thickness",
  "property.start_x": "Start X",
  "property.start_y": "Start Y",
  "property.start_z": "Start Z",
  "property.end_x": "End X",
  "property.end_y": "End Y",
  "property.end_z": "End Z",
  "property.length": "Length",
  "property.angle": "Angle",
  "property.center_x": "Center X",
  "property.center_y": "Center Y",
  "property.center_z": "Center Z",
  "property.radius": "Radius",
  "property.diameter": "Diameter",
  "property.circumference": "Circumference",
  "property.area": "Area",
  "property.start_angle": "Start angle",
  "property.end_angle": "End angle",
  "property.total_angle": "Total angle",
  "property.arc_length": "Arc length",
  "property.vertices": "Vertices",
  "property.closed": "Closed",
  "property.constant_width": "Constant width",
  "property.elevation": "Elevation",
  "property.linetype_generation": "Linetype generation",
  // Legacy heavy POLYLINE: 40/41 are the DEFAULT widths every vertex without
  // its own 40/41 inherits (AutoCAD's "Start segment width" / "End segment
  // width" rows).
  "property.start_segment_width": "Start segment width",
  "property.end_segment_width": "End segment width",
  "property.polyline_type": "Polyline type",
  "property.m_vertex_count": "M vertex count",
  "property.n_vertex_count": "N vertex count",
  "property.smooth_surface_m_density": "Smooth surface M density",
  "property.smooth_surface_n_density": "Smooth surface N density",
  "property.surface_type": "Surface type",
  // Table (ACAD_TABLE).
  "property.table_style": "Table style",
  // "property.rows" / "property.columns" already exist (the array kinds use
  // the SAME labels) — the table reuses them rather than adding a duplicate.
  "property.table_width": "Table width",
  "property.table_height": "Table height",
  // OLE object (OLE2FRAME): the WCS frame corners + the OLE record descriptors.
  "property.upper_left_x": "Upper left X",
  "property.upper_left_y": "Upper left Y",
  "property.lower_right_x": "Lower right X",
  "property.lower_right_y": "Lower right Y",
  "property.ole_object_type": "OLE object type",
  "property.ole_version": "OLE version",
  "property.draw_aspect": "Draw aspect",
  "property.data_size": "Data size",
  "property.contents": "Contents",
  "property.text_style": "Text style",
  "property.height": "Height",
  "property.rotation": "Rotation",
  "property.width_factor": "Width factor",
  "property.oblique_angle": "Oblique angle",
  "property.justify": "Justify",
  "property.insertion_x": "Insertion X",
  "property.insertion_y": "Insertion Y",
  "property.alignment_x": "Alignment X",
  "property.alignment_y": "Alignment Y",
  "property.transparency": "Transparency",
  "property.plot_style": "Plot Style",
  "property.material": "Material",
  "property.annotative": "Annotative",
  "property.width": "Width",
  "property.attachment": "Attachment",
  "property.direction": "Direction",
  "property.line_spacing": "Line spacing",
  "property.background_fill": "Background fill",
  "property.linetype_scale": "Linetype Scale",
  "property.subtype": "Subtype",
  "property.dimension_style": "Dimension style",
  "property.text_override": "Text override",
  "property.text_height": "Text height",
  "property.text_rotation": "Text rotation",
  "property.extension_1_x": "Extension 1 X",
  "property.extension_1_y": "Extension 1 Y",
  "property.extension_2_x": "Extension 2 X",
  "property.extension_2_y": "Extension 2 Y",
  "property.dimension_line_x": "Dimension line X",
  "property.dimension_line_y": "Dimension line Y",
  "property.third_point_x": "Third point X",
  "property.third_point_y": "Third point Y",
  "property.arc_point_x": "Arc point X",
  "property.arc_point_y": "Arc point Y",
  "property.leader_length": "Leader length",
  "property.anonymous_block": "Anonymous block",
  "property.measurement": "Measurement",
  "property.multileader_style": "Multileader style",
  "property.arrow_x": "Arrow X",
  "property.arrow_y": "Arrow Y",
  "property.landing_x": "Landing X",
  "property.landing_y": "Landing Y",
  "property.arrowhead_size": "Arrowhead size",
  "property.arrowhead_block": "Arrowhead block",
  "property.text_attachment": "Text attachment",
  "property.text_direction": "Text direction",
  "property.text_frame": "Text frame",
  "property.line_spacing_style": "Line spacing style",
  "property.line_spacing_factor": "Line spacing factor",
  "property.leader_color": "Leader color",
  "property.leader_weight": "Leader weight",
  "property.leader_type": "Leader type",
  "property.block_name": "Block name",
  "property.block_scale": "Block scale",
  "property.block_rotation": "Block rotation",
  "property.override_flags": "Override flags",
  "property.landing_gap": "Landing gap",
  "property.leader_count": "Leader count",
  "property.cloud_style": "Cloud style",
  "property.approximate_arc_length": "Approximate arc length",
  "property.minimum_arc_length": "Minimum arc length",
  "property.creation_mode": "Creation mode",
  "property.corner_1_x": "Corner 1 X",
  "property.corner_1_y": "Corner 1 Y",
  "property.corner_2_x": "Corner 2 X",
  "property.corner_2_y": "Corner 2 Y",
  "property.pattern": "Pattern",
  "property.scale": "Scale",
  "property.associative": "Associative",
  "property.hatch_style": "Hatch style",
  "property.pattern_type": "Pattern type",
  "property.double": "Double",
  "property.name": "Name",
  "property.scale_x": "Scale X",
  "property.scale_y": "Scale Y",
  "property.scale_z": "Scale Z",
  "property.column_count": "Column count",
  "property.row_count": "Row count",
  "property.column_spacing": "Column spacing",
  "property.row_spacing": "Row spacing",
  "property.rows": "Rows",
  "property.columns": "Columns",
  "property.base_x": "Base X",
  "property.base_y": "Base Y",
  "property.display_lock": "Display lock",
  "property.shade_plot": "Shade plot",
  "property.view_height": "View height",
  // Key-completeness gate — EN siblings of the RU block above.
  "property.position_x": "Position X",
  "property.position_y": "Position Y",
  "property.position_z": "Position Z",
  "property.major_axis_x": "Major axis X",
  "property.major_axis_y": "Major axis Y",
  "property.ratio": "Ratio",
  "property.start_parameter": "Start parameter",
  "property.end_parameter": "End parameter",
  "property.degree": "Degree",
  "property.rational": "Rational",
  "property.fit_points": "Fit points",
  "property.control_points": "Control points",
  "property.knots": "Knots",
  "property.weights": "Weights",
  "property.frame": "Frame",
  "property.vertex_count": "Vertex count",
  "property.axis_angle": "Axis angle",
  "property.view_center_x": "View center X",
  "property.view_center_y": "View center Y",
  "property.view_twist": "View twist",
  "property.allow_non-uniform_scale": "Allow Non-Uniform Scale",
  // ── Attachment position labels (MText grid) ──
  "property.attachment.top_left": "Top left",
  "property.attachment.top_center": "Top center",
  "property.attachment.top_right": "Top right",
  "property.attachment.middle_left": "Middle left",
  "property.attachment.middle_center": "Middle center",
  "property.attachment.middle_right": "Middle right",
  "property.attachment.bottom_left": "Bottom left",
  "property.attachment.bottom_center": "Bottom center",
  "property.attachment.bottom_right": "Bottom right",
  // ── Audit / error messages (state files) ──
  "audit.properties_shell_activated":
    "Properties shell activated from command line.",
  "audit.model_space_activated":
    "Model space activated from command line shell.",
  "audit.mspace_activated":
    "Layout context switched to model-in-viewport shell.",
  "audit.pspace_activated": "Layout context switched to paper-space shell.",
  "audit.space_activated": "Tab '{name}' activated.",
  "audit.matchprop_source_cleared": "MATCHPROP source cleared.",
  "audit.selection_cleared": "Selection cleared.",
  "audit.delete_skipped_no_match":
    "Delete skipped because no matching selected objects were found.",
  "audit.imported_line": "Imported supported DXF line geometry.",
  "audit.imported_point": "Imported supported DXF point geometry.",
  "audit.imported_circle": "Imported supported DXF circle geometry.",
  "audit.imported_arc": "Imported supported DXF arc geometry.",
  "audit.imported_ellipse": "Imported supported DXF ellipse geometry.",
  "audit.imported_spline": "Imported supported DXF spline entity.",
  "audit.imported_polyline": "Imported supported DXF polyline geometry.",
  "audit.imported_text": "Imported supported DXF text entity.",
  "audit.imported_mtext": "Imported supported DXF multiline text entity.",
  "audit.imported_dimension": "Imported supported DXF dimension entity.",
  "audit.imported_multileader": "Imported supported DXF multileader entity.",
  "audit.imported_revcloud": "Imported supported DXF revision cloud entity.",
  "audit.imported_wipeout": "Imported DXF wipeout entity.",
  "audit.imported_hatch": "Imported supported DXF hatch entity.",
  "audit.imported_block": "Imported supported DXF block insert entity.",
  "audit.imported_array":
    "Imported supported DXF rectangular associative array entity.",
  "audit.imported_viewport": "Imported supported DXF viewport entity.",
  // Legacy heavy polyline (POLYLINE + VERTEX), table (ACAD_TABLE) and OLE
  // object (OLE2FRAME).
  "audit.imported_heavy_polyline":
    "Imported supported DXF legacy polyline (POLYLINE) entity.",
  "audit.imported_table": "Imported supported DXF table (ACAD_TABLE) entity.",
  "audit.imported_ole_frame":
    "Imported supported DXF OLE object (OLE2FRAME) entity.",
  // Defect fix (command-created-line-provenance) — command-authored
  // counterparts of the "audit.imported_*" family above, shown when the
  // entity was created/restored by a command, agent, or collaboration
  // edit in THIS session rather than loaded from a DXF file.
  "audit.authored_line": "Line geometry created via a command.",
  "audit.authored_point": "Point geometry created via a command.",
  "audit.authored_circle": "Circle geometry created via a command.",
  "audit.authored_arc": "Arc geometry created via a command.",
  "audit.authored_ellipse": "Ellipse geometry created via a command.",
  "audit.authored_spline": "Spline entity created via a command.",
  "audit.authored_polyline": "Polyline geometry created via a command.",
  "audit.authored_text": "Text entity created via a command.",
  "audit.authored_mtext": "Multiline text entity created via a command.",
  "audit.authored_dimension": "Dimension entity created via a command.",
  "audit.authored_multileader": "Multileader entity created via a command.",
  "audit.authored_revcloud": "Revision cloud entity created via a command.",
  "audit.authored_wipeout": "Wipeout entity created via a command.",
  "audit.authored_hatch": "Hatch entity created via a command.",
  "audit.authored_block": "Block insert entity created via a command.",
  "audit.authored_array":
    "Rectangular associative array entity created via a command.",
  "audit.authored_viewport": "Viewport entity created via a command.",
  "audit.authored_heavy_polyline":
    "Legacy polyline (POLYLINE) entity created via a command.",
  "audit.authored_table": "Table (ACAD_TABLE) entity created via a command.",
  "audit.authored_ole_frame":
    "OLE object (OLE2FRAME) entity created via a command.",
  "viewport.label": "VP",
  "viewport.entity_label": "Viewport {id}",
  "viewport.scale.custom": "Custom…",
  // ── Phase MC5 — ViewportInspector i18n strings (EN) ──────────────────
  "vp.inspector.title": "Viewport",
  "vp.inspector.scale": "Scale",
  "vp.inspector.scale.custom": "Custom…",
  "vp.inspector.scale.num": "Numerator",
  "vp.inspector.scale.denom": "Denominator",
  "vp.inspector.lock": "Locked",
  // Shade-plot mode (DXF 170) — plot-output only; no on-screen effect.
  "vp.inspector.shadeplot": "Shade plot",
  "vp.inspector.shadeplot.as_displayed": "As Displayed",
  "vp.inspector.shadeplot.wireframe": "Wireframe",
  "vp.inspector.shadeplot.hidden": "Hidden",
  "vp.inspector.shadeplot.rendered": "Rendered",
  // Fallback label shown when a stored value is outside the supported 0..3
  // range (e.g. imported mode 4 Visual Style / 5 Render Preset — deferred).
  "vp.inspector.shadeplot.other": "Other ({mode})",
  "vp.inspector.layers": "Layers",
  "vp.inspector.layers.frozen": "Frozen in VP",
  "vp.inspector.ucs": "UCS",
  "vp.inspector.clip": "Clip",
  "vp.inspector.clip.rect": "Rectangular",
  "vp.inspector.clip.polygonal": "Polygonal",
  // ── Status-bar VP controls (EN) ──────────────────────────────────────────
  "vp.statusbar.scale.label": "VP Scale",
  "vp.statusbar.scale.custom": "Custom…",
  "vp.statusbar.lock_viewport": "Lock viewport",
  "vp.statusbar.unlock_viewport": "Unlock viewport",
  "audit.dimstyle_new_requires_name": "DIMSTYLE New requires a style name.",
  "audit.dimstyle_created":
    'Dimension style "{name}" created (copied from Standard).',
  "audit.dimstyle_delete_requires_name":
    "DIMSTYLE Delete requires a style name.",
  "audit.dimstyle_deleted": 'Dimension style "{name}" deleted.',
  "audit.dimstyle_rename_requires_names":
    "DIMSTYLE Rename requires an old and a new name.",
  "audit.dimstyle_renamed":
    'Dimension style "{oldName}" renamed to "{newName}".',
  "audit.dimstyle_not_exist": 'Dimension style "{name}" does not exist.',
  "audit.dimstyle_set_current":
    'Dimension style "{name}" set current.',
  "audit.dimstyle_action": "DIMSTYLE {action} {name}.",
  "audit.dimstyle_apply_current":
    "DIMSTYLE Apply uses the current style on selected dimensions.",
  "audit.dimstyle_requires_name": "DIMSTYLE {action} requires a style name.",
  "audit.command_not_wired":
    "{text} recorded in command shell. Geometry executor is intentionally not wired yet.",
  "audit.cecolor_set": "Current colour set: {color}.",
  "error.matchprop_source_requires_one":
    "MATCHPROP SOURCE requires exactly one selected object.",
  "error.chprop_requires_selection": "CHPROP requires an active selection.",
  "error.matchprop_requires_targets":
    "MATCHPROP requires target objects in the current selection.",
  "error.matchprop_no_source":
    "MATCHPROP requires an armed source object first.",
  "error.matchprop_no_target":
    "MATCHPROP requires at least one target object different from the source.",
  "error.dimstyle_name_empty": "Dimension style name cannot be empty.",
  "error.dimstyle_already_exists": 'Dimension style "{name}" already exists.',
  "error.dimstyle_no_base": "No base dimension style available to copy.",
  "error.dimstyle_cannot_delete_standard":
    'Cannot delete the "Standard" dimension style.',
  "error.dimstyle_not_found": 'Dimension style "{name}" does not exist.',
  "error.dimstyle_cannot_rename_standard":
    'Cannot rename the "Standard" dimension style.',
  "error.dimstyle_new_name_empty": "New dimension style name cannot be empty.",
  "error.mleaderstyle_name_empty": "Multileader style name cannot be empty.",
  "error.mleaderstyle_already_exists":
    'Multileader style "{name}" already exists.',
  "error.mleaderstyle_no_base": "No base multileader style available to copy.",
  "error.mleaderstyle_cannot_delete_standard":
    'Cannot delete the "Standard" multileader style.',
  "error.mleaderstyle_not_found": 'Multileader style "{name}" does not exist.',
  // ── Demo entity labels & summaries ──
  "demo.drawing.title": "Stage A command-safe editor shell",
  "demo.transcript.shell_ready":
    "Stage A shell ready: UI routes through dispatch(command) -> reducer -> state/audit.",
  "demo.line.label": "Axis line",
  "demo.line.summary":
    "Model-space baseline line with editable general object properties.",
  "demo.circle.label": "Circle circle-main",
  "demo.circle.summary": "Simple circle preview in model space.",
  "demo.arc.label": "Arc arc-main",
  "demo.arc.summary": "Simple arc preview in model space.",
  "demo.hatch.label": "Core hatch",
  "demo.hatch.summary":
    "Associative hatch requiring dedicated graph-backed editor later.",
  "demo.block.label": "Pump assembly block",
  "demo.block.summary":
    "Block preview that keeps name, insertion and scaling semantics explicit.",
  "demo.array.label": "Rack array",
  "demo.array.summary":
    "Associative array preview with explicit row and column parameters.",
  "demo.polyline.label": "Route polyline",
  "demo.polyline.summary":
    "Polyline preview with width and closed-state semantics in the property map.",
  "demo.text.label": "Grid note",
  "demo.text.summary":
    "Single-line text preview with explicit style and justification fields.",
  "demo.mtext.label": "General notes",
  "demo.mtext.summary":
    "Multiline annotation preview with width and background semantics.",
  "demo.dimension.label": "Bay dimension",
  "demo.dimension.summary":
    "Dimension preview that keeps style and measurement semantics separate from raw geometry.",
  "demo.viewport.label": "Primary viewport",
  "demo.viewport.summary":
    "Viewport shell with lock, scale and override awareness.",
  "demo.revcloud.label": "Issue cloud",
  "demo.revcloud.summary":
    "Behavior-backed review markup with dedicated properties contract.",
  "demo.mleader.label": "Callout leader",
  "demo.mleader.summary":
    "Annotation object whose style/content graph must stay explicit.",
  // ── Demo viewport & space names ──
  "demo.viewport.main_plan": "Main plan",
  "demo.viewport.detail_callout": "Detail callout",
  "demo.viewport.assembly": "Assembly viewport",
  "demo.space.model_setup": "World UCS / command-safe shell",
  "demo.space.a3_issue": "A3 issue sheet",
  // ── Properties view model ──
  "props.default_title": "Default object properties",
  "props.no_selection": "No selection",
  "props.default_note":
    "This slice keeps Properties / CHPROP / MATCHPROP explicit in contracts, but does not claim full parity yet.",
  "props.multi_title": "{count} selected objects",
  "props.multi_select": "Multi-select",
  "props.multi_note":
    "Only shared property contracts should surface in mixed selection. Graph-backed semantics stay outside generic copy rules.",
  "props.varies": "*Varies*",
  // ── Layer Manager ──
  "layer.manager_title": "Layer Manager",
  "layer.new": "New Layer",
  "layer.delete": "Delete",
  "layer.set_current": "Set Current",
  "layer.close": "Close",
  "layer.column.status": "St.",
  "layer.column.name": "Name",
  "layer.column.on": "On",
  "layer.column.freeze": "Freeze",
  "layer.column.lock": "Lock",
  "layer.column.color": "Color",
  "layer.column.linetype": "Linetype",
  "layer.column.lineweight": "Lineweight",
  "layer.column.plot": "Plot",
  "layer.column.count": "Entities",
  "layer.on": "On",
  "layer.off": "Off",
  "layer.frozen": "Frozen",
  "layer.thawed": "Thawed",
  "layer.locked": "Locked",
  "layer.unlocked": "Unlocked",
  "layer.plottable": "Plottable",
  "layer.not_plottable": "Not plottable",
  "layer.cannot_delete_0": 'Cannot delete layer "0".',
  "layer.cannot_delete_current": "Cannot delete the current layer.",
  "layer.cannot_rename_0": 'Cannot rename layer "0".',
  "layer.name_duplicate": "Layer with this name already exists.",
  "layer.new_default_name": "Layer",
  "layer.lineweight_default": "Default",
  "layer.lineweight_bylayer": "ByLayer",
  "layer.lineweight_byblock": "ByBlock",
  "panel.manage_layers": "Manage layers",
  "panel.manage_layers_hint": "Create, configure and delete layers.",
  "error.layer_name_empty": "Layer name cannot be empty.",
  "error.layer_not_found": 'Layer "{name}" not found.',
  // Modification commands: MOVE, COPY, MIRROR, ERASE
  "command.move.label": "Move",
  "command.copy.label": "Copy",
  "command.mirror.label": "Mirror",
  "command.erase.label": "Erase",
  "prompt.move.select": "MOVE: Select objects.",
  "prompt.move.base": "MOVE: Specify base point.",
  "prompt.move.destination": "MOVE: Specify destination point.",
  "prompt.copy.select": "COPY: Select objects.",
  // AutoCAD-parity base-point prompt with the [Displacement] keyword.
  // Power users type "D"/"Displacement" to commit a single-vector copy
  // from the origin. Legacy "prompt.copy.base" key kept for compat.
  "prompt.copy.base": "COPY: Specify base point.",
  "prompt.copy.base_point": "COPY: Specify base point.",
  "prompt.copy.base_or_displacement":
    "COPY: Specify base point or [Displacement]:",
  "prompt.copy.destination":
    "COPY: Specify destination point or Enter to exit.",
  "prompt.copy.second_point": "COPY: Specify second point.",
  "prompt.copy.second_or_exit":
    "COPY: Specify second point or [Exit/Undo] <Exit>:",
  "prompt.copy.displacement": "COPY: Specify displacement <0,0,0>:",
  "prompt.mirror.select": "MIRROR: Select objects.",
  "prompt.mirror.first": "MIRROR: Specify first point of mirror line.",
  "prompt.mirror.second": "MIRROR: Specify second point of mirror line.",
  "audit.entities_moved": "Entities moved.",
  "audit.entities_copied": "Entities copied.",
  "audit.entities_mirrored": "Entities mirrored.",
  // AutoCAD-parity COPY audit — parametrised by iteration entity count.
  "audit.copy": "COPY: {n} object(s) copied.",
  "summary.move": "MOVE completed.",
  "summary.copy": "COPY completed.",
  "summary.copy_created": "COPY: created {n} clone(s).",
  "feed.copy.nothing_selected": "COPY: nothing selected to copy.",
  "summary.mirror": "MIRROR completed.",
  // Modification commands: ROTATE, SCALE, OFFSET, STRETCH
  "command.rotate.label": "Rotate",
  "command.scale.label": "Scale",
  "command.offset.label": "Offset",
  "command.stretch.label": "Stretch",
  "prompt.rotate.select": "ROTATE: Select objects.",
  "prompt.rotate.base": "ROTATE: Specify base point.",
  "prompt.rotate.angle":
    "ROTATE: Specify rotation angle, point, or [Reference].",
  "prompt.rotate.ref_start": "ROTATE: Specify reference start:",
  "prompt.rotate.ref_end": "ROTATE: Specify reference end:",
  "prompt.rotate.ref_new_angle": "ROTATE: Specify new angle:",
  "prompt.scale.select": "SCALE: Select objects.",
  "prompt.scale.base": "SCALE: Specify base point.",
  "prompt.scale.factor": "SCALE: Specify scale factor or point.",
  "prompt.offset.distance": "OFFSET: Specify offset distance.",
  "prompt.offset.select": "OFFSET: Select object to offset.",
  "prompt.offset.side": "OFFSET: Specify point on desired side.",
  "prompt.stretch.first": "STRETCH: Specify first corner of crossing window.",
  "prompt.stretch.second": "STRETCH: Specify second corner of crossing window.",
  "prompt.stretch.base": "STRETCH: Specify base point.",
  "prompt.stretch.destination": "STRETCH: Specify destination point.",
  // N4 GRIPS-ARMED-STRETCH-I18N — armed-grip CLICK-ARM modal echo (distinct
  // namespace from the classic multi-step STRETCH command's `prompt.stretch.*`
  // keys above, which are for the crossing-window STRETCH *command*). The
  // "** MODE **" banner line reuses the existing `grip.mode.*` keys (see
  // `gripInteractionConstants.ts`'s `GRIP_ARMED_PROMPT_KEYS`) rather than a
  // parallel `.mode` key per mode — only the imperative "point" prompt line
  // needs its own key here, one per mode (GRIPS-INTERACTION PR-2 origin/main
  // re-sync extended this from stretch-only to all 5 cycle modes), AutoCAD-
  // verified per mode ("Just Grip It!":
  // https://help.autodesk.com/cloudhelp/2021/ENU/AutoCAD-DidYouKnow/files/GUID-BBEA1F71-EB16-4D49-80D9-970A6909F508.htm).
  "prompt.grip.armed.stretch.point": "Specify stretch point:",
  "prompt.grip.armed.move.point": "Specify move point:",
  "prompt.grip.armed.rotate.point": "Specify rotation angle:",
  "prompt.grip.armed.scale.point": "Specify scale factor:",
  "prompt.grip.armed.mirror.point": "Specify second point:",
  "audit.entities_rotated": "Entities rotated.",
  // Twin of the RU block above — @codex PR #785 round 13, FINDING C.
  "audit.entities_rotated_ole_frames_declined":
    "Entities rotated; {n} OLE object(s) left unrotated. An OLE object is stored as two frame corners and has no rotation angle, so it cannot be rotated or skewed — those objects were left unchanged.",
  "audit.rotate_declined_ole_frames_only":
    "Nothing rotated: the selection holds only OLE object(s) ({n}). An OLE object is stored as two frame corners and has no rotation angle, so it cannot be rotated or skewed — the object(s) were left unchanged.",
  "audit.entities_mirrored_ole_frames_declined":
    "Entities mirrored; {n} OLE object(s) left unmirrored. An OLE object is stored as two frame corners and has no rotation angle, so it cannot be mirrored about a slanted axis — those objects were left unchanged.",
  "audit.mirror_declined_ole_frames_only":
    "Nothing mirrored: the selection holds only OLE object(s) ({n}). An OLE object is stored as two frame corners and has no rotation angle, so it cannot be mirrored about a slanted axis — the object(s) were left unchanged.",
  // Twin of the RU pair above — @codex PR #785 round 15, FINDING 2.
  "audit.entities_mirrored_tables_declined":
    "Entities mirrored; {n} table(s) left unmirrored. A table is placed by an insertion point and a horizontal-direction vector and carries no scale and no mirror flag, so a reflection would be stored as a 180° rotation — the table(s) were left unchanged.",
  "audit.mirror_declined_tables_only":
    "Nothing mirrored: the selection holds only table(s) ({n}). A table is placed by an insertion point and a horizontal-direction vector and carries no scale and no mirror flag, so a reflection would be stored as a 180° rotation — the table(s) were left unchanged.",
  "audit.entities_scaled": "Entities scaled.",
  // Twin of the RU pair above — GitHub Codex review PR #785 round 2, P2.
  "audit.entities_scaled_tables_declined":
    "Entities scaled; {n} table(s) left unscaled. A table cannot be resized: its graphics are the pre-built anonymous *T<N> block, which has no generator yet, so the table(s) were left unchanged.",
  "audit.scale_declined_tables_only":
    "Nothing scaled: the selection holds only table(s) ({n}). A table cannot be resized: its graphics are the pre-built anonymous *T<N> block, which has no generator yet, so the table(s) were left unchanged.",
  "audit.entity_offset": "Entity offset.",
  // Twin of the RU block above — @codex PR #785 round 14, P2.
  "audit.offset_declined_heavy_polyline.mesh":
    "Nothing done: a polygon or poly-face mesh (POLYLINE, group 70) cannot be offset. AutoCAD's OFFSET accepts a line, arc, circle, ellipse, elliptical arc, 2D polyline, construction line, ray or spline, and answers a mesh with \"Cannot offset that object.\" A mesh has no single path — only rows, columns and face edges.",
  "audit.offset_declined_heavy_polyline.polyline_3d":
    "Nothing done: a 3D polyline cannot be offset. AutoCAD's OFFSET object list names only the 2D polyline and answers a 3D one with \"Cannot offset that object.\" Offsetting it in the XY plane would drop the vertex elevations.",
  "audit.offset_declined_heavy_polyline.fit":
    "Nothing done: a fitted polyline (curve-fit or spline-fit, group 70) cannot yet be offset. AutoCAD builds a NEW fitted polyline for it — it re-fits rather than moving the generated vertices — and this editor has no polyline fitter; shifting the existing run would emit a record set that is the output of no fit while group 70 still claims it is. As an array path such a polyline is fully supported.",
  "audit.arraypath_declined_heavy_polyline.mesh":
    "Nothing done: a polygon or poly-face mesh (POLYLINE, group 70) cannot drive a path array. AutoCAD's ARRAYPATH accepts a line, polyline, 3D polyline, spline, helix, arc, circle or ellipse; a mesh is not a curve and has no single traversal order.",
  "audit.arraypath_declined_heavy_polyline.polyline_3d":
    "Nothing done: a 3D polyline cannot yet drive a path array. AutoCAD accepts one, but this editor's path sampler is 2D end to end: the length would be measured on the XY shadow of the curve and so come out short, every division would land at the wrong parameter, and each copy's Z would be silently dropped. Declining beats building a confidently wrong array.",
  // Twin of the RU pair above — @codex PR #785 round 16, P2.
  "audit.arraypath_declined_tilted_plane":
    "Nothing done: the path curve's plane is not parallel to the XY plane (extrusion, group 210). This editor's path sampler is 2D end to end, so the copies would be placed along the curve's plan shadow rather than along the curve itself: the spacing would be laid out on a shadow that is shorter than the curve, so every division would land at the wrong point, and for extrusion (0,1,0) the object's Y axis is world Z, the shadow collapses to a single point, and every copy would stack on the source. A mirrored extrusion (0,0,-1) and a constant non-zero elevation are NOT declined. Declining beats building a confidently wrong array.",
  "audit.arraypath_nothing_created":
    "Nothing done: the path array was not created — not a single copy was added. The path curve, the method or its parameters allow no item to be placed; the exact reason is written to the console log.",
  "audit.arraypath_declined_sloped_line":
    "Nothing done: the line climbs in Z (its start and end elevations, group 30, differ), so it cannot yet drive a path array. This editor's path sampler is 2D end to end: the line's length is measured as the hypotenuse of its X and Y spans and so comes out short by the climb (zero for a vertical line), every division would land at the wrong point, and each copy's Z would be silently dropped. A line lying wholly at one constant elevation is NOT declined. Declining beats building a confidently wrong array.",
  "audit.entities_stretched": "Entities stretched.",
  "summary.rotate": "ROTATE completed.",
  "summary.scale": "SCALE completed.",
  "summary.offset": "OFFSET completed.",
  "summary.stretch": "STRETCH completed.",
  // Modification commands: TRIM, EXTEND, FILLET, CHAMFER, BREAK, JOIN, EXPLODE, LENGTHEN
  "command.trim.label": "Trim",
  "command.extend.label": "Extend",
  "command.fillet.label": "Fillet",
  "command.chamfer.label": "Chamfer",
  "command.break.label": "Break",
  "command.join.label": "Join",
  "command.explode.label": "Explode",
  "command.lengthen.label": "Lengthen",
  "prompt.trim.select_edges": "TRIM: Select cutting edges, then Enter.",
  "prompt.trim.pick_entity": "TRIM: Select object to trim or Enter to exit.",
  "prompt.extend.select_edges": "EXTEND: Select boundary edges, then Enter.",
  "prompt.extend.pick_entity":
    "EXTEND: Select object to extend or Enter to exit.",
  "prompt.fillet.radius": "FILLET: Enter fillet radius.",
  "prompt.fillet.first":
    "FILLET: Select first object or [Radius/Polyline/Trim/Multiple].",
  "prompt.fillet.second": "FILLET: Select second object.",
  "prompt.chamfer.distances": "CHAMFER: Enter distances (d1 d2).",
  "prompt.chamfer.first": "CHAMFER: Select first line.",
  "prompt.chamfer.second": "CHAMFER: Select second line.",
  "prompt.break.select": "BREAK: Select object.",
  "prompt.break.first": "BREAK: Specify first break point.",
  "prompt.break.second": "BREAK: Specify second break point.",
  "prompt.join.select": "JOIN: Select objects, then Enter.",
  "prompt.explode.select": "EXPLODE: Select objects, then Enter.",
  "prompt.lengthen.select": "LENGTHEN: Select object.",
  "prompt.lengthen.mode":
    "LENGTHEN: Specify mode [DElta/Percent/Total] (DE/PE/TO).",
  "prompt.lengthen.value": "LENGTHEN: Enter value.",
  "prompt.lengthen.pick_end": "LENGTHEN: Specify endpoint to modify.",
  "audit.entity_trimmed": "Entity trimmed.",
  "audit.entity_extended": "Entity extended.",
  // Twin of the RU block above — @codex PR #785 round 4, P2.
  "audit.trim_declined_heavy_polyline.mesh":
    "Nothing done: a polygon or poly-face mesh (POLYLINE, group 70) cannot be trimmed or extended. AutoCAD lists a mesh neither among the objects that can be trimmed nor among the valid cutting/boundary edges — it has no single 2D path, only rows, columns and face edges.",
  "audit.trim_declined_heavy_polyline.polyline_3d":
    "Nothing done: a 3D polyline cannot yet be trimmed or extended as an object. The TRIM/EXTEND contour is 2D and carries no vertex Z, so the elevations would be silently flattened — the object was left unchanged. As a cutting or boundary edge a 3D polyline is fully supported.",
  "audit.trim_declined_heavy_polyline.fit":
    "Nothing done: a fitted polyline (curve-fit or spline-fit, group 70) cannot yet be trimmed or extended as an object. AutoCAD re-fits the remainder after a trim and this editor has no polyline fitter; a hand-computed cut vertex spliced into a fitted record run would make group 70 a lie. As a cutting or boundary edge such a polyline is fully supported.",
  "audit.trim_heavy_polyline_edges_declined":
    "{n} edge(s) skipped: a mesh (POLYLINE polygon/poly-face) cannot serve as a cutting or boundary edge. The remaining edges were used.",
  "audit.entities_filleted": "Entities filleted.",
  "audit.entities_chamfered": "Chamfer created.",
  "audit.entity_broken": "Entity broken.",
  "audit.entities_joined": "Entities joined.",
  "audit.entities_exploded": "Entities exploded.",
  "audit.entity_lengthened": "Entity lengthened.",
  // CC-1c — per-viewport edit + history-traverse + view-op audit summaries.
  "audit.viewport_locked": "Viewport {id} locked.",
  "audit.viewport_unlocked": "Viewport {id} unlocked.",
  "audit.viewport_vplayer_freeze": "Layer '{layer}' frozen in viewport {id}.",
  "audit.viewport_vplayer_thaw": "Layer '{layer}' thawed in viewport {id}.",
  "audit.viewport_clip_set": "Viewport {id} clip boundary set.",
  "audit.viewport_clip_polygon_set":
    "Viewport {id} polygonal clip boundary set.",
  "audit.viewport_clip_removed": "Viewport {id} clip boundary removed.",
  "audit.viewport_scale_set": "Viewport {id} scale {num}:{denom}.",
  "audit.viewport_shadeplot_set": "Viewport {id} shade plot: {mode}.",
  "audit.viewport_view_updated": "Viewport {id} view updated.",
  "audit.history_undo": "Undo: {command}.",
  "audit.history_redo": "Redo: {command}.",
  "audit.viewop_zoom": "Zoom: {mode}.",
  "audit.viewop_regen": "Drawing regenerated.",
  "audit.viewop_regenall": "All viewports regenerated.",
  "audit.viewop_pan": "View panned.",
  // ── CC-3 special-editor dialog host chrome ──
  "special_editor.title": "Special editor",
  "special_editor.ok": "OK",
  "special_editor.cancel": "Cancel",
  "special_editor.apply": "Apply",
  "special_editor.regen_summary":
    "Object regenerated after a special-editor edit.",
  // ── REVCLOUD-PROPS-EDITOR (§5.1) ──
  "special_editor.revcloud.title": "Revision Cloud Properties",
  "special_editor.revcloud.regen_summary":
    "Revision-cloud arcs regenerated after a property edit.",
  // ── HATCH-EDIT-DIALOG (§5.1) ──
  "special_editor.hatch.title": "Hatch Edit",
  "special_editor.hatch.regen_summary":
    "Hatch fill regenerated after a pattern-property edit.",
  "audit.block_defined": "Block definition '{name}' created.",
  "audit.insert_exploded": "Block insert exploded.",
  // Twin of the RU pair above.
  "audit.explode_declined_table":
    "Nothing exploded: the block definition holds {n} table(s) and this insert is scaled or mirrored. ACAD_TABLE carries no scale factors — its graphics are the pre-built anonymous *T<N> block — so an exploded table would snap back to its authored size. The insert was left unchanged; EXPLODE works for a rotation with no scale and no mirror.",
  "audit.explode_declined_ole_frame":
    "Nothing exploded: the block definition holds {n} OLE frame(s) and this insert is rotated by a non-multiple of 90 degrees. OLE2FRAME stores two opposite corners and has no rotation field, so an exploded frame would become the axis-aligned box instead of the rotated rectangle. The insert was left unchanged; EXPLODE works for a rotation that is a multiple of 90 degrees (any scale or mirror is fine).",
  // Twin of the RU block above — @codex PR #785 round 14, FINDING 2.
  "audit.explode_declined_polygon_mesh":
    "{n} object(s) not exploded: AutoCAD explodes a polygon mesh (POLYLINE, group 70 bit 16) into 3DFACE records, and this editor has no 3DFACE primitive yet — substituting line edges would misrepresent the object (a 3DFACE is a filled four-corner face with per-edge visibility, not four segments). The mesh was left unchanged; every other selected object was exploded.",
  "audit.explode_declined_polyface_mesh":
    "{n} object(s) not exploded: AutoCAD explodes a poly-face mesh (POLYLINE, group 70 bit 64) into 3DFACE records, and this editor has no 3DFACE primitive yet — substituting line edges would misrepresent the object. The mesh was left unchanged; every other selected object was exploded.",
  "audit.explode_declined_polyline_no_run":
    "{n} object(s) not exploded: the polyline has fewer than two visible vertices, so there is no span to become a line or an arc. The object was left unchanged — EXPLODE never removes what it does not replace.",
  "audit.blocks_purged": "Purged {count} unreferenced block definition(s).",
  "audit.block_renamed": "Block '{oldName}' renamed to '{newName}'.",
  "audit.attdef_created": "Attribute '{tag}' added to block '{block}'.",
  "audit.attribute_updated": "Attribute '{tag}' value updated.",
  "audit.attributes_updated": "Updated {count} attribute(s).",
  "audit.attdef_removed": "Attribute {id} removed from block '{block}'.",
  "audit.attdef_updated":
    "Attribute {id} properties updated in block '{block}'.",
  "audit.attdef_tag_renamed":
    "Attribute tag renamed to '{newTag}' in block '{block}'.",
  "audit.attdefs_reordered":
    "Attribute order updated in block '{block}' ({count}).",
  "audit.attsync":
    "ATTRIB on block '{block}' inserts synchronised with template.",
  "summary.trim": "TRIM completed.",
  "summary.extend": "EXTEND completed.",
  "summary.fillet": "FILLET completed.",
  "summary.chamfer": "CHAMFER completed.",
  "summary.break": "BREAK completed.",
  "summary.join": "JOIN completed.",
  "summary.explode": "EXPLODE completed.",
  "summary.lengthen": "LENGTHEN completed.",
  "summary.geom_constraint_added": "Geometric constraint '{kind}' added.",
  // Drawing aids status bar
  "status.ortho": "ORTHO",
  "status.polar": "POLAR",
  "status.rmb_enter": "RMB",
  "status.lwt": "LWT",
  "status.dyn": "DYN",
  // Annotation status bar (PR-4)
  "status.annotation_scale": "Annotation scale",
  "status.annoautoscale": "AutoScale",
  "status.annoautoscale_title":
    "Automatically add current scale to annotative objects (ANNOAUTOSCALE)",
  "status.annoallvisible": "Visibility",
  "status.annoallvisible_title":
    "Show annotative objects for all scales (ANNOALLVISIBLE)",
  "status.annomonitor": "Monitor",
  "status.annomonitor_title": "Highlight out-of-sync annotations (ANNOMONITOR)",
  "status.scalelistedit": "Scale List…",
  "status.scalelistedit_title": "Manage annotation scale list (SCALELISTEDIT)",
  // UCS icon
  "ucs.reset_to_world": "Reset UCS to World",
  "ucs.tooltip": "Drag to move origin. Shift+drag to rotate",
  // Select Color Picker (title de-ACI'd — Index + True Color tabs beneath it)
  "color_picker.title": "Select Color",
  "color_picker.standard": "Standard",
  "color_picker.grey_ramp": "Grey ramp",
  // True Color tab (EPIC-COLOR-UNION S3a) — AutoCAD "Select Color" → tabs
  "color_picker.tab.index": "Index Color",
  "color_picker.tab.true": "True Color",
  "color_picker.true.r": "Red",
  "color_picker.true.g": "Green",
  "color_picker.true.b": "Blue",
  "color_picker.true.hex": "Color (HEX)",
  "color_picker.true.preview": "Preview",
  "color_picker.ok": "OK",
  // BLOCK / INSERT commands (Phase 1) — `command.explode.label`
  // already exists further up under the modify commands block.
  "command.insert.label": "Insert",
  // BLOCK command flow prompts
  "prompt.block.select_objects":
    "BLOCK: Select objects to define the block. Press Enter to finish.",
  "prompt.block.base_point":
    "BLOCK: Specify base point of the block definition.",
  "prompt.block.dialog":
    "BLOCK: Open the Block Definition dialog to enter name and parameters.",
  // INSERT command flow prompts
  "prompt.insert.dialog":
    "INSERT: Open the Insert Block dialog to choose a block.",
  "prompt.insert.specify_point":
    "INSERT: Specify insertion point of the block.",
  "prompt.insert.specify_scale": "INSERT: Specify scale factor <1> or [Angle].",
  "prompt.insert.specify_rotation":
    "INSERT: Specify rotation angle of the insertion <0>.",
  "prompt.insert.attribute_value":
    "INSERT: Enter attribute value for {tag}: <{default}>",
  // EXPLODE command prompts
  "prompt.explode.select_inserts":
    "EXPLODE: Select block insertions to explode.",
  // BlockDefinitionDialog
  "block_def_dialog.title": "Block Definition",
  "block_def_dialog.tab.objects": "Objects",
  "block_def_dialog.tab.behavior": "Behavior",
  "block_def_dialog.tab.settings": "Settings",
  "block_def_dialog.tab.description": "Description",
  "block_def_dialog.label.name": "Name",
  "block_def_dialog.label.base_point": "Base point",
  "block_def_dialog.label.base_x": "X:",
  "block_def_dialog.label.base_y": "Y:",
  "block_def_dialog.label.pick_on_screen": "Pick point on-screen",
  "block_def_dialog.label.objects_panel": "Objects",
  "block_def_dialog.label.action_retain": "Retain",
  "block_def_dialog.label.action_convert": "Convert to block",
  "block_def_dialog.label.action_delete": "Delete",
  "block_def_dialog.label.objects_selected_count": "Objects selected: {count}",
  "block_def_dialog.label.no_objects_selected": "No objects selected.",
  "block_def_dialog.label.annotative": "Annotative",
  "block_def_dialog.label.annotative_scales":
    "Annotative scales (comma-separated)",
  "block_def_dialog.label.scale_uniformly": "Scale uniformly",
  "block_def_dialog.label.allow_exploding": "Allow exploding",
  "block_def_dialog.label.block_unit": "Block unit",
  "block_def_dialog.label.hyperlink": "Hyperlink",
  "block_def_dialog.label.description": "Description",
  "block_def_dialog.placeholder.name": "Enter block name",
  "block_def_dialog.placeholder.hyperlink": "URL or path",
  "block_def_dialog.placeholder.description":
    "Brief description of the block (optional)",
  "block_def_dialog.btn.ok": "OK",
  "block_def_dialog.btn.cancel": "Cancel",
  "block_def_dialog.btn.pick_base_point": "Pick point",
  // BlockDefinitionDialog errors
  "block_def_dialog.error.empty_name": "Block name cannot be empty.",
  "block_def_dialog.error.reserved_name":
    "Name '{name}' is reserved by the system.",
  "block_def_dialog.error.forbidden_chars":
    'Name contains forbidden characters (< > / \\ " : ; ? * | , = `).',
  "block_def_dialog.error.too_long":
    "Block name cannot be longer than 255 characters.",
  "block_def_dialog.error.duplicate": "A block named '{name}' already exists.",
  "block_def_dialog.error.no_objects":
    "No objects selected for the block definition.",
  // InsertBlockDialog
  "insert_dialog.title": "Insert Block",
  "insert_dialog.label.name": "Name",
  "insert_dialog.label.path": "Path",
  "insert_dialog.label.path_local": "(local block)",
  "insert_dialog.label.preview": "Preview",
  "insert_dialog.label.preview_empty": "No block selected",
  "insert_dialog.label.insertion_point": "Insertion point",
  "insert_dialog.label.scale": "Scale",
  "insert_dialog.label.rotation": "Rotation",
  "insert_dialog.label.specify_on_screen": "Specify on-screen",
  "insert_dialog.label.uniform_scale": "Uniform scale",
  "insert_dialog.label.angle": "Angle",
  "insert_dialog.label.show_anonymous": "Show anonymous blocks",
  "insert_dialog.label.explode": "Explode",
  "insert_dialog.label.x": "X:",
  "insert_dialog.label.y": "Y:",
  "insert_dialog.label.z": "Z:",
  "insert_dialog.btn.ok": "OK",
  "insert_dialog.btn.cancel": "Cancel",
  "insert_dialog.error.no_blocks": "No blocks available for insertion.",
  "insert_dialog.error.cyclic":
    "Cyclic block insertion is not allowed: '{name}'.",
  "insert_dialog.error.not_explodable":
    "Block '{name}' is marked non-explodable (ALLOW_EXPLODE = 0).",
  // P7 Overlap warning dialog (INSERT)
  "dialog.overlap_warning.title": "Content Overlap",
  "dialog.overlap_warning.message":
    "Insertion point ({x}, {y}) overlaps existing content in region ({left}..{right}, {bottom}..{top}). Continue?",
  "dialog.overlap_warning.continue": "Continue",
  "dialog.overlap_warning.cancel": "Cancel",
  // INSUNITS enum (DXF group 70 in BLOCK_RECORD)
  "insunits.0": "Unitless",
  "insunits.1": "Inches",
  "insunits.2": "Feet",
  "insunits.3": "Miles",
  "insunits.4": "Millimeters",
  "insunits.5": "Centimeters",
  "insunits.6": "Meters",
  "insunits.7": "Kilometers",
  "insunits.8": "Microinches",
  "insunits.9": "Mils",
  "insunits.10": "Yards",
  "insunits.11": "Angstroms",
  "insunits.12": "Nanometers",
  "insunits.13": "Microns",
  "insunits.14": "Decimeters",
  "insunits.15": "Decameters",
  "insunits.16": "Hectometers",
  "insunits.17": "Gigameters",
  "insunits.18": "Astronomical Units",
  "insunits.19": "Light Years",
  "insunits.20": "Parsecs",
  // 21 = US Survey Feet (AutoCAD 2017+).
  "insunits.21": "US Survey Feet",
  // Property palette: dynamic block / INSERT extra fields
  "property.hyperlink": "Hyperlink",
  "property.description": "Description",
  "property.block_unit": "Block Unit",
  "property.block_unit_factor": "Block Unit Factor",
  "property.allow_exploding": "Allow Exploding",
  // Summaries (after command commit)
  "summary.block_defined": "Block definition '{name}' created.",
  "summary.block_inserted": "Block '{name}' inserted.",
  "summary.block_exploded": "Block '{name}' exploded into {count} objects.",
  // Toast / error notifications
  "toast.block.cyclic_reference":
    "Forbidden: inserting block '{name}' creates a cyclic reference.",
  "toast.block.reserved_name":
    "Name '{name}' is reserved by the system and cannot be used.",
  "toast.block.not_explodable":
    "Block '{name}' has ALLOW_EXPLODE = 0 and cannot be exploded.",
  "toast.block.no_selection": "Select at least one block insertion to explode.",
  "toast.block.create_failed": "Failed to create block definition: {message}",
  // BEDIT / BCLOSE / BSAVE (Phase 2.3)
  "command.bedit.label": "BEDIT",
  "command.bclose.label": "BCLOSE",
  "command.bsave.label": "BSAVE",
  "prompt.bedit.enter_name": "BEDIT: Enter block name or [?]:",
  "prompt.bedit.confirm_create":
    'Block "{name}" does not exist. Create new? [Yes/No]',
  "prompt.bclose.confirm": 'Save changes to block "{name}"? [Yes/No/Cancel]',
  "status.bedit.editing": "Editing block: {name}",
  // ── REFEDIT / REFSET / REFCLOSE (Phase FIN.1) ──
  "command.refedit.label": "REFEDIT (In-Place Reference Edit)",
  "command.refset.label": "REFSET (Modify Working Set)",
  "command.refclose.label": "REFCLOSE (Close Reference Editing)",
  "refedit.toolbar.title": "Reference Editing",
  "refedit.add_to_set": "Add to working set",
  "refedit.remove_from_set": "Remove from working set",
  "refedit.save": "Save reference edits",
  "refedit.discard": "Discard",
  "refedit.working_set_count": "Working set: {count}",
  "status.refedit.editing": "Editing reference: {name}",
  "summary.refedit_entered":
    'Reference edit opened for "{name}" via INSERT {insert}.',
  "summary.refedit_saved": 'Reference "{name}" changes saved (REFCLOSE).',
  "summary.refedit_discarded":
    'Reference "{name}" changes discarded (REFCLOSE).',
  "summary.refset_added": "Added {count} object(s) to the working set.",
  "summary.refset_removed": "Removed {count} object(s) from the working set.",
  "toast.refedit.no_insert_selected":
    "Select an INSERT first to edit it in place.",
  "toast.refedit.not_in_session":
    "This command is only available during reference editing (REFEDIT).",
  "prompt.refedit.select_insert": "REFEDIT: Select INSERT to edit in place.",
  "prompt.refedit.refclose_confirm":
    'Save changes to reference "{name}"? [Yes/No]',
  // ── ANNOUPDATE / OBJECTSCALE / CANNOSCALE (Phase FIN.2) ──
  "command.annoupdate.label": "ANNOUPDATE (Annotative Update)",
  "command.objectscale.label": "OBJECTSCALE (Object Annotation Scales)",
  "command.cannoscale.label": "CANNOSCALE (Current Annotation Scale)",
  "command.base.label": "BASE (Insertion Base Point)",
  // ── CECOLOR — current object colour (COLOR) ──
  "command.color.label": "COLOR (Current Object Colour)",
  "colorcmd.feedback": "Current colour: {value}",
  "colorcmd.open_picker": "COLOR: choose a colour in the dialog.",
  "colorcmd.prompt":
    "Enter color [Truecolor(T)/COlorbook(CO)] <{current}>:",
  "colorcmd.invalid": "Invalid color.",
  "colorcmd.bylayer": "ByLayer",
  "colorcmd.byblock": "ByBlock",
  "cecolor.swatch.tooltip": "Current object colour (CECOLOR)",
  "cecolor.swatch.current": "Current: {value}",
  "cecolor.swatch.bylayer": "ByLayer",
  "cecolor.swatch.byblock": "ByBlock",
  // ── PR-8 — annotative status-bar / scale-list commands ──
  "command.annoreset.label": "ANNORESET (Reset Annotation Positions)",
  "command.scalelistedit.label": "SCALELISTEDIT (Scale List Editor)",
  "command.annomonitor.label": "ANNOMONITOR (Annotation Monitor)",
  "command.annoallvisible.label": "ANNOALLVISIBLE (Show All Annotations)",
  "command.annoautoscale.label": "ANNOAUTOSCALE (Annotation Autoscale)",
  "cannoscale.selector": "Annotation scale",
  "annotative.scales": "Annotation scales",
  "annotative.add_scale": "Add scale",
  "annotative.remove_scale": "Remove scale",
  "summary.cannoscale_changed": "Annotation scale changed to {name}.",
  "summary.annoupdate_done":
    "ANNOUPDATE: rescaled annotative INSERT(s): {count}.",
  "summary.objectscale_added": 'OBJECTSCALE: added scale "{name}" to INSERT.',
  "summary.objectscale_removed":
    'OBJECTSCALE: removed scale "{name}" from INSERT.',
  "summary.bedit_entered": 'Block Editor opened for "{name}".',
  "summary.bedit_created": 'Block Editor opened for new block "{name}".',
  "summary.bsave": 'Block "{name}" saved (BSAVE).',
  "summary.bclose_saved": 'Changes to block "{name}" saved (BCLOSE).',
  "summary.bclose_discarded": 'Changes to block "{name}" discarded (BCLOSE).',
  "toast.bedit.not_in_block_editor":
    "This command is only available in Block Editor mode (BEDIT).",
  "toast.bedit.cannot_anonymous":
    'Cannot edit reserved block "{name}": names with the "*" prefix (layout, dimension, hatch, xref) are owned by the system.',
  // Phase J — BEDIT UI integration (ribbon swap, dialogs, palettes)
  "ribbon.tab.block-editor": "Block Editor",
  "ribbon.context.block.label": "Block Reference",
  "ribbon.context.block.enter_editor": "Edit Block In-Place",
  "ribbon.context.block.test_block": "Test Block",
  "ribbon.context.block.edit_attributes": "Edit Attributes",
  "ribbon.block_editor.parameters": "Parameters",
  "ribbon.block_editor.parameter-sets": "Parameter Sets",
  "ribbon.block_editor.actions": "Actions",
  "ribbon.block_editor.visibility": "Visibility",
  "ribbon.block_editor.management": "Manage",
  "ribbon.block_editor.test_block": "Test Block",
  "ribbon.block_editor.drawing": "Drawing",
  "ribbon.block_editor.editing": "Editing",
  "ribbon.block_editor.erase": "Erase",
  "ribbon.block_editor.close_section": "Close",
  "ribbon.block_editor.close": "Close Block Editor",
  "toast.block_editor.palette_phase_k":
    'The "{pane}" palette will be available in Phase K (Block Authoring Palettes).',
  // BlockEditExitDialog (Phase J.5)
  "bedit.exit_dialog.title": 'Save changes to block "{blockName}"?',
  "bedit.exit_dialog.body":
    "You have edited this block definition. Save to update every insertion of this block, or discard to return to the previous state.",
  "bedit.exit_dialog.save": "Save",
  "bedit.exit_dialog.discard": "Discard Changes",
  "bedit.exit_dialog.cancel": "Cancel",
  // BlockEditorAnnotativePreview (PR-15)
  "bedit.master_tab": "Master",
  "bedit.scale_tab": "Scale",
  "bedit.no_decomposition": "No decomposition generated",
  "bedit.generate_decomposition": "Generate decomposition",
  "audit.bedit.decomposition_preview_deferred":
    "Decomposition preview is deferred (generation in a follow-up PR).",
  "audit.bedit.generate_decomposition_deferred":
    "Generate decomposition is deferred (*A_NNN block creation in a follow-up PR).",
  // WBLOCK / PURGE / RENAME command labels (Phase 2.6-2.8)
  "command.wblock.label": "WBLOCK",
  "command.purge.label": "PURGE",
  "command.rename.label": "RENAME",
  // PAN / ZOOM view-navigation command labels (u043, ТЗ §10.1)
  "command.pan.label": "PAN (Pan the view)",
  "command.zoom.label": "ZOOM (Scale the view)",
  // REGEN / REGENALL display-refresh command labels (ТЗ §10.1)
  "command.regen.label": "REGEN (Regenerate the display)",
  "command.regenall.label": "REGENALL (Regenerate all viewports)",
  // D5 — router→registry unification: labels for commands that were
  // dispatched via literal string comparisons in App.tsx before being
  // folded into command-registry descriptors
  "command.print.label": "PRINT (Print the layout)",
  "command.pagesetup.label": "PAGESETUP (Page Setup Manager)",
  "command.battman.label": "BATTMAN (Block Attribute Manager)",
  "command.eattext.label": "EATTEXT (Extract Attributes)",
  "command.bcount.label": "BCOUNT (Block instance counter)",
  "command.xref.label": "XREF (External References)",
  "command.xopen.label": "XOPEN (Open an external reference)",
  // "command.bauthorpaletteclose.label" already exists (DYN-1.11 cluster
  // above) — reused verbatim, not redefined here.
  "command.bvhide.label": "BVHIDE (Hide in current visibility state)",
  "command.bvshow.label": "BVSHOW (Show in current visibility state)",
  "command.bvshowall.label": "BVSHOWALL (Show in all visibility states)",
  "command.undo.label": "U (Undo)",
  "command.undobegin.label": "UNDO BEGIN (Start an undo group)",
  "command.undoend.label": "UNDO END (Commit an undo group)",
  "command.undoback.label": "UNDO BACK (Abort an undo group)",
  "command.redo.label": "REDO (Redo)",
  "command.mredo.label": "MREDO (Multiple redo)",
  "command.dsettings.label": "DSETTINGS (Drafting Settings)",
  "command.batch.label": "BATCH (Run a batch of commands)",
  // WriteBlockDialog (Phase 2.6)
  "wblock_dialog.title": "Write Block",
  "wblock_dialog.section.source": "Source",
  "wblock_dialog.source.block": "Block",
  "wblock_dialog.source.drawing": "Entire drawing",
  "wblock_dialog.source.objects": "Objects",
  "wblock_dialog.label.block_name": "Block name",
  "wblock_dialog.section.base_point": "Base point",
  "wblock_dialog.label.x": "X:",
  "wblock_dialog.label.y": "Y:",
  "wblock_dialog.section.destination": "Destination",
  "wblock_dialog.label.file_name": "File name",
  "wblock_dialog.label.insertion_units": "Insertion units",
  "wblock_dialog.label.selected_objects": "Selected objects: {count}",
  "wblock_dialog.error.no_selection":
    "No objects selected. Close this dialog and select objects first.",
  "wblock_dialog.error.no_blocks": "No block definitions in the drawing.",
  "wblock_dialog.error.empty_filename": "File name cannot be empty.",
  "wblock_dialog.btn.ok": "OK",
  "wblock_dialog.btn.cancel": "Cancel",
  "summary.wblock_exported": 'Block exported to file "{filename}".',
  "toast.wblock.failed": "Failed to export block: {message}",
  // PurgeBlocksDialog (Phase 2.7)
  "purge_dialog.title": "Purge Unreferenced Blocks",
  "purge_dialog.column.name": "Name",
  "purge_dialog.column.references": "References",
  "purge_dialog.column.can_purge": "Purge",
  "purge_dialog.column.reason": "Reason",
  "purge_dialog.reason.ready": "Ready to purge",
  "purge_dialog.reason.reserved": "Reserved",
  "purge_dialog.reason.in_use": "In use ({count})",
  "purge_dialog.reason.dependent": "Referenced by: {names}",
  "purge_dialog.btn.check_all": "Check all",
  "purge_dialog.btn.uncheck_all": "Uncheck all",
  "purge_dialog.btn.ok": "Purge",
  "purge_dialog.btn.cancel": "Cancel",
  "purge_dialog.empty": "No blocks are eligible for purge.",
  "purge_dialog.no_selection": "No blocks selected for purge.",
  "summary.purge_completed": "Purge completed: removed {count} block(s).",
  // RenameBlockDialog (Phase 2.8)
  "rename_dialog.title": "Rename Block",
  "rename_dialog.label.old_name": "Old name",
  "rename_dialog.label.new_name": "New name",
  "rename_dialog.label.show_anonymous": "Show anonymous blocks",
  "rename_dialog.error.empty": "New name cannot be empty.",
  "rename_dialog.error.too_long":
    "Block name cannot be longer than 255 characters.",
  "rename_dialog.error.reserved_new":
    "Name '{name}' is reserved by the system.",
  "rename_dialog.error.reserved_old": "Anonymous blocks cannot be renamed.",
  "rename_dialog.error.forbidden_chars":
    'Name contains forbidden characters (< > / \\ " : ; ? * | , = `).',
  "rename_dialog.error.duplicate": "A block named '{name}' already exists.",
  "rename_dialog.error.unknown": "Block '{name}' not found.",
  "rename_dialog.error.empty_list": "No blocks available to rename.",
  "rename_dialog.btn.ok": "OK",
  "rename_dialog.btn.cancel": "Cancel",
  "summary.rename_completed":
    "Block '{oldName}' renamed to '{newName}' ({count} reference(s) updated).",
  "toast.rename.failed": "Failed to rename block: {message}",
  // MVIEW (paper-space viewport command)
  "command.viewport.label": "MVIEW (Viewport)",
  // VPCLIP (paper-space viewport clip command)
  "command.vpclip.label": "VPCLIP (Viewport Clip)",
  // LAYOUT (paper-space layout tab manager command)
  "command.layout.label": "LAYOUT (Layout)",
  // LAYOUT R / LAYOUT RENAME (B2) — layout tab rename
  "layout.rename.success": "Layout '{old}' renamed to '{new}'.",
  "layout.rename.notfound": "Layout named '{name}' not found.",
  "layout.rename.cannot_rename_model": "Model space cannot be renamed.",
  "layout.rename.duplicate_name": "Layout named '{name}' already exists.",
  "layout.rename.empty_name": "Layout name cannot be empty.",
  "layout.name.too_long": "Layout name cannot exceed 255 characters.",
  "layout.rename.placeholder": "New layout name",
  "layout.rename.usage": "LAYOUT: usage: LAYOUT R <old name> <new name>",
  "layout.set.already_current": "Layout '{name}' is already current.",
  "layout.switch.notfound":
    "Tab '{name}' not found. Available tabs: {list}",
  // LAYOUT N / LAYOUT D (LAYOUT-COMMAND-HANDLERS) — create / delete
  "layout.new.success": "Layout '{name}' created.",
  "layout.new.limit": "The maximum number of layouts (255) has been reached.",
  "layout.delete.success": "Layout '{name}' deleted.",
  "layout.delete.cannot_delete_model": "The Model tab cannot be deleted.",
  "layout.delete.last_layout": "The last layout cannot be deleted.",
  // LOCK / UNLOCK (COLLAB-LOCK-COMMAND) — realtime-collab soft locks
  "command.collab.lock.label": "LOCK (Collaboration Lock)",
  "command.collab.unlock.label": "UNLOCK (Collaboration Unlock)",
  "collab.lock.no_selection": "Select objects to lock.",
  "collab.lock.layer_notfound": "Layer named '{name}' not found.",
  "collab.lock.requested": "Lock requested for {count} object(s)…",
  "collab.lock.released": "Unlock requested for {count} object(s)…",
  "collab.lock.granted": "Locked '{target}' (owner: {owner}).",
  "collab.lock.denied": "Denied: '{target}' is already locked by {owner}.",
  "collab.lock.not_connected": "Not connected to a collaboration session.",
  "collab.lock.list_empty": "No locks currently held.",
  "collab.lock.list_item": "{kind} '{target}' — {owner}",
  "collab.lock.list_query": "LOCK: querying held locks…",
  "collab.lock.release_confirmed": "Released lock: '{target}'.",
  "collab.lock.release_no_effect": "No effect: '{target}' was not locked by you.",
  "collab.lock.edit_rejected": "Edit rejected: '{target}' is locked by {owner}.",
  // round-5 — distinct denial-reason wording (P3) — a role/limit denial has
  // no owner to blame, so it must not reuse "denied" (which always names one).
  "collab.lock.denied_role": "Denied: you don't have permission to lock objects.",
  "collab.lock.denied_limit": "Denied: too many locks held — release one first.",
  // round-5 (P1) — the lock snapshot is UNAVAILABLE (reconnecting/errored
  // collab session), so the edit is rejected fail-closed with no specific
  // target/owner to report (unlike `edit_rejected`, which always names one).
  "collab.lock.edit_rejected_unavailable":
    "Edit rejected: could not verify lock status (reconnecting to the collaboration session).",
  // round-5 (P1) — undo/redo of an entry whose inverse/forward touches a
  // NOW-locked entity/layer (locked by someone else since the entry was
  // recorded) is blocked, not silently corrupting the history stacks.
  "collab.lock.undo_vetoed": "Undo blocked: the object is locked by another participant. Try again later.",
  "collab.lock.redo_vetoed": "Redo blocked: the object is locked by another participant. Try again later.",
  // GitHub Codex review (PR #534, round 2) P2 — deleteLayer()/renameLayer()
  // (Layer Manager) now propagate a collab-lock veto as their own
  // `{ ok: false, error }` instead of silently returning `{ ok: true }`.
  "collab.lock.layer_action_vetoed": "Action rejected: layer \"{name}\" is locked by another participant.",
  "collab.lock.abort_batch_partial":
    "Batch rollback partially blocked: some changes could not be reverted — an object is locked by another participant.",
  // COLLAB-LOCK-PROPERTY (W3.5) — read-only lock-status/owner property rows
  "property.locked": "Locked",
  "property.locked_by": "Locked by",
  "property.lock_acquired_at": "Lock acquired at",
  "collab_lock_property.yes": "Yes",
  "collab_lock_property.no": "No",
  "collab_lock_property.unknown": "Unknown",
  "collab_lock_property.you": "You",
  // Phase MC1 — SpaceContext / SpaceTabsBar
  "audit.layout_list": "LAYOUT: Available layouts: {list}",
  "space.tabs.model": "Model",
  "space.tabs.context.rename": "Rename Layout",
  "space.tabs.context.delete": "Delete Layout",
  "space.tabs.context.new": "New Layout",
  "space.tabs.context.publish_selected": "Publish Selected Layouts ({count})",
  "space.mode_badge.mspace": "MODEL",
  "space.mode_badge.pspace": "PAPER",
  "space.mode_badge.mspace_via_vp": "MODEL/VP",
  // ATTDEF / DDATTE / ATTEDIT / EATTEDIT (Phase 2.1 + 2.2)
  "command.attdef.label": "Attribute",
  "command.ddatte.label": "DDATTE",
  "command.eattedit.label": "EATTEDIT",
  "prompt.attdef.dialog_open":
    "ATTDEF: Enter attribute parameters in the dialog.",
  "prompt.ddatte.select": "DDATTE: Select a block insertion with attributes.",
  "prompt.ddatte.dialog_open": "DDATTE: Edit attribute values.",
  "prompt.eattedit.select":
    "EATTEDIT: Select a block insertion for enhanced editing.",
  "prompt.eattedit.dialog_open": "EATTEDIT: Edit attributes across tabs.",
  // AttributeDefinitionDialog (ATTDEF)
  "attdef_dialog.title": "Attribute Definition",
  "attdef_dialog.section.mode": "Mode",
  "attdef_dialog.section.attribute": "Attribute",
  "attdef_dialog.section.insertion_point": "Insertion Point",
  "attdef_dialog.section.text_settings": "Text Settings",
  "attdef_dialog.flag.invisible": "Invisible",
  "attdef_dialog.flag.constant": "Constant",
  "attdef_dialog.flag.verify": "Verify",
  "attdef_dialog.flag.preset": "Preset",
  "attdef_dialog.flag.lock_position": "Lock position",
  "attdef_dialog.flag.multiline": "Multiple lines",
  "attdef_dialog.label.tag": "Tag",
  "attdef_dialog.label.prompt": "Prompt",
  "attdef_dialog.label.default": "Default",
  "attdef_dialog.label.specify_on_screen": "Specify on-screen",
  "attdef_dialog.label.x": "X:",
  "attdef_dialog.label.y": "Y:",
  "attdef_dialog.label.hj": "Horizontal justification",
  "attdef_dialog.label.vj": "Vertical justification",
  "attdef_dialog.label.text_style": "Text style",
  "attdef_dialog.label.height": "Height",
  "attdef_dialog.label.rotation": "Rotation",
  "attdef_dialog.label.width_factor": "Width factor",
  "attdef_dialog.label.oblique_angle": "Oblique angle",
  "attdef_dialog.placeholder.tag": "Enter tag",
  "attdef_dialog.hj.left": "Left",
  "attdef_dialog.hj.center": "Center",
  "attdef_dialog.hj.right": "Right",
  "attdef_dialog.hj.aligned": "Aligned",
  "attdef_dialog.hj.middle": "Middle",
  "attdef_dialog.hj.fit": "Fit",
  "attdef_dialog.vj.baseline": "Baseline",
  "attdef_dialog.vj.bottom": "Bottom",
  "attdef_dialog.vj.middle": "Middle",
  "attdef_dialog.vj.top": "Top",
  "attdef_dialog.btn.ok": "OK",
  "attdef_dialog.btn.cancel": "Cancel",
  "attdef_dialog.error.empty_tag": "Tag cannot be empty.",
  "attdef_dialog.error.non_positive_height":
    "Text height must be a positive number.",
  "attdef_dialog.error.oblique_out_of_range":
    "Oblique angle must be between -85 and 85 degrees.",
  // EditAttributesDialog (DDATTE / ATTEDIT)
  "edit_attributes_dialog.title": "Edit Attributes",
  "edit_attributes_dialog.label.block_ref": "Block: {name}",
  "edit_attributes_dialog.label.no_attributes":
    "The selected insertion has no editable attributes.",
  "edit_attributes_dialog.label.editable_count": "Editable attributes: {count}",
  "edit_attributes_dialog.indicator.invisible": "Invisible",
  "edit_attributes_dialog.indicator.constant": "Constant",
  "edit_attributes_dialog.btn.ok": "OK",
  "edit_attributes_dialog.btn.cancel": "Cancel",
  // EnhancedAttributeEditorDialog (EATTEDIT)
  "eattedit_dialog.title": "Enhanced Attribute Editor",
  "eattedit_dialog.label.block_ref": "Block: {name}",
  "eattedit_dialog.label.no_attributes":
    "The selected insertion has no attributes.",
  "eattedit_dialog.col.tag": "Tag",
  "eattedit_dialog.col.value": "Value",
  "eattedit_dialog.tab.attribute": "Attribute",
  "eattedit_dialog.tab.text": "Text Options",
  "eattedit_dialog.tab.properties": "Properties",
  "eattedit_dialog.label.tag": "Tag",
  "eattedit_dialog.label.prompt": "Prompt",
  "eattedit_dialog.label.value": "Value",
  "eattedit_dialog.label.text_style": "Text style",
  "eattedit_dialog.label.height": "Height",
  "eattedit_dialog.label.rotation": "Rotation",
  "eattedit_dialog.label.width_factor": "Width factor",
  "eattedit_dialog.label.oblique_angle": "Oblique angle",
  "eattedit_dialog.label.layer": "Layer",
  "eattedit_dialog.label.color": "Color",
  "eattedit_dialog.label.linetype": "Linetype",
  "eattedit_dialog.label.lineweight": "Lineweight",
  "eattedit_dialog.btn.ok": "OK",
  "eattedit_dialog.btn.apply": "Apply",
  "eattedit_dialog.btn.cancel": "Cancel",
  // Summaries
  "summary.attdef_created": "Attribute '{tag}' added to block '{block}'.",
  "summary.attribute_updated": "Attribute '{tag}' value updated.",
  "summary.attributes_updated": "Updated {count} attribute(s).",
  // Toasts
  "toast.attdef.not_in_block_editor":
    "ATTDEF can only be used in Block Editor (BEDIT).",
  "toast.attedit.no_insert_selected":
    "Select a block insertion with attributes.",
  "toast.attedit.no_attributes":
    "The selected insertion has no attributes to edit.",
  // BlockAttributeManagerDialog (BATTMAN — Phase 2.4)
  "battman_dialog.title": "Block Attribute Manager",
  "battman_dialog.label.block": "Block:",
  "battman_dialog.column.tag": "Tag",
  "battman_dialog.column.prompt": "Prompt",
  "battman_dialog.column.default": "Default",
  "battman_dialog.column.modes": "Modes",
  "battman_dialog.column.style": "Style",
  "battman_dialog.column.height": "Height",
  "battman_dialog.column.rotation": "Rotation",
  "battman_dialog.empty": "Selected block has no ATTDEF entries.",
  "battman_dialog.inserts": "Insertions in drawing: {count}",
  "battman_dialog.edit_title": "Edit attribute '{tag}'",
  "battman_dialog.btn.edit": "Edit…",
  "battman_dialog.btn.remove": "Remove",
  "battman_dialog.btn.move_up": "Move Up",
  "battman_dialog.btn.move_down": "Move Down",
  "battman_dialog.btn.sync": "Sync",
  "battman_dialog.btn.close": "Close",
  "battman_dialog.btn.ok": "OK",
  "battman_dialog.btn.cancel": "Cancel",
  "battman_dialog.remove_confirm.title": "Remove ATTDEF",
  "battman_dialog.remove_confirm.body":
    "Remove attribute '{tag}' from the block definition? Existing insertions: {count}.",
  "battman_dialog.remove_confirm.cascade":
    "Also remove matching ATTRIB rows from existing insertions",
  // Feed strings
  "feed.attsync":
    "ATTSYNC: ATTRIB rows on block '{block}' synced with template.",
  "feed.battman.edited": "BATTMAN: attribute {id} edited in block '{block}'.",
  "feed.battman.removed":
    "BATTMAN: attribute {id} removed from block '{block}'.",
  "feed.battman.reordered":
    "BATTMAN: attribute order updated in block '{block}'.",
  "feed.dyn.nothing_selected":
    "DYN: no block insert selected. Select an INSERT first, then re-run the command.",
  "feed.dynset.usage":
    "DYNSET: usage — DYNSET <param-name> <value>. Quote names or values that contain spaces.",
  "feed.dynset.parse_error_linear":
    "DYNSET: '{value}' is not a valid length. Use mm, cm, m, in, ft (e.g. 12м, 12mm, 0.5m).",
  "feed.dynset.parse_error_rotation":
    "DYNSET: '{value}' is not a valid angle. Use degrees (90, 90°), radians (1.5rad, π/2) or a plain number (45).",
  "feed.dynset.parse_error_coord":
    "DYNSET: '{value}' is not a valid coordinate pair. Use 'x,y' or 'x y'.",
  "feed.dynset.invalid_units": "DYNSET: unknown unit '{unit}'.",
  "inspector.dynamic.linear.value": "Value",
  "inspector.dynamic.rotation.angle": "Angle",
  "inspector.dynamic.xy.x": "X",
  "inspector.dynamic.xy.y": "Y",
  "inspector.dynamic.point.x": "X",
  "inspector.dynamic.point.y": "Y",
  "dynamic_block_params.section_label": "Dynamic block",
  "dynamic_block_params.section_title": "Dynamic block parameters",
  "dynamic_block_params.choices_prefix": "choices",
  "dxf.dynamic.unknown_class":
    "Unknown dynamic block class: {cls} — preserved as raw",
  "dxf.dynamic.orphan_action": "Action {id} references unknown parameter",
  // DYN-1.7 Block Authoring Palette
  "authoring.palette.title": "Block Authoring Palette",
  "authoring.palette.hint":
    "Pick a parameter or action type, then follow the prompts.",
  "authoring.tab.parameters": "Parameters",
  "authoring.tab.actions": "Actions",
  "authoring.tab.parameter_sets": "Parameter Sets",
  "authoring.tab.constraints": "Constraints",
  "authoring.tab.placeholder.tier2": "Available in Tier 2",
  "authoring.section.existing_parameters": "Existing parameters",
  "authoring.section.existing_actions": "Existing actions",
  "authoring.empty.parameters": "No parameters yet.",
  "authoring.empty.actions": "No actions yet.",
  "authoring.param.linear": "Linear",
  "authoring.param.flip": "Flip",
  "authoring.param.visibility": "Visibility",
  "authoring.param.basepoint": "Base Point",
  "authoring.param.point": "Point",
  "authoring.param.polar": "Polar",
  "authoring.param.xy": "XY",
  "authoring.param.rotation": "Rotation",
  "authoring.param.lookup": "Lookup",
  "authoring.param.constraint_horizontal": "Horizontal",
  "authoring.param.constraint_vertical": "Vertical",
  "authoring.param.constraint_angular": "Angular",
  "authoring.action.move": "Move",
  "authoring.action.stretch": "Stretch",
  "authoring.action.flip": "Flip",
  "authoring.action.rotate": "Rotate",
  "authoring.action.scale": "Scale",
  "authoring.action.array": "Array",
  "authoring.action.lookup": "Lookup",
  "authoring.action.requires.linear": "Requires a Linear parameter",
  "authoring.action.requires.linear_polar_xy":
    "Requires a Linear, Polar, or XY parameter",
  "authoring.action.requires.flip": "Requires a Flip parameter",
  "authoring.action.requires.rotation": "Requires a Rotation parameter",
  "authoring.action.requires.lookup": "Requires a Lookup parameter",
  "authoring.btn.add_parameter": "Add parameter",
  "authoring.btn.add_action": "Add action",
  "authoring.btn.edit": "Edit",
  "authoring.btn.delete": "Delete",
  "authoring.linked_actions": "Actions linked: {count}",
  "authoring.selection_count": "Objects: {count}",
  "authoring.param_link": "Parameter: {name}",
  "authoring.visibility_states": "Visibility states",
  "authoring.open_visibility_dialog": "Open Visibility States dialog",
  // DYN-1.7 ParameterPropertiesDialog
  "param_dialog.title.linear": "Linear parameter properties",
  "param_dialog.title.flip": "Flip parameter properties",
  "param_dialog.title.visibility": "Visibility parameter properties",
  "param_dialog.title.basepoint": "Base point properties",
  // DYN-2 ParameterPropertiesDialog Tier 2
  "param_dialog.title.point": "Point parameter properties",
  "param_dialog.title.polar": "Polar parameter properties",
  "param_dialog.title.xy": "XY parameter properties",
  "param_dialog.title.rotation": "Rotation parameter properties",
  "param_dialog.title.lookup": "Lookup parameter properties",
  // DYN-2 Batch C constraint params
  "param_dialog.title.constraint_horizontal":
    "Horizontal constraint properties",
  "param_dialog.title.constraint_vertical": "Vertical constraint properties",
  "param_dialog.title.constraint_angular": "Angular constraint properties",
  "param_dialog.label.point_a": "Point A (X, Y)",
  "param_dialog.label.point_b": "Point B (X, Y)",
  "param_dialog.label.vertex": "Vertex (X, Y)",
  "param_dialog.label.distance_value_set": "Distance value set",
  "param_dialog.label.angle_value_set": "Angle value set",
  "param_dialog.label.base_point": "Base point (X, Y)",
  "param_dialog.label.current_point": "Current point (X, Y)",
  "param_dialog.label.second_point": "Second point (X, Y)",
  "param_dialog.label.position": "Position (X, Y)",
  "param_dialog.label.default_x": "Default X",
  "param_dialog.label.default_y": "Default Y",
  "param_dialog.label.default_angle": "Default angle (°)",
  "param_dialog.label.radius": "Radius",
  "param_dialog.label.reverse_lookup": "Reverse lookup",
  "param_dialog.label.lookup_table": "Lookup table",
  "param_dialog.label.value_set_x": "X value set",
  "param_dialog.label.value_set_y": "Y value set",
  "param_dialog.label.value_set_distance": "Distance value set",
  "param_dialog.label.value_set_angle": "Angle value set",
  "param_dialog.lookup.pick_placeholder": "(pick a table)",
  "param_dialog.lookup.new_table": "New Table…",
  "param_dialog.lookup.new_table_tooltip_disabled":
    "Open the BLOOKUPTABLE dialog to create a table (landing in DYN-2.4).",
  "param_dialog.error.no_lookup_table": "Select a lookup table.",
  "param_dialog.label.name": "Name",
  "param_dialog.label.label": "Label",
  "param_dialog.label.description": "Description",
  "param_dialog.label.default_distance": "Default distance",
  "param_dialog.label.value_set_mode": "Value set",
  "param_dialog.label.value_set_increment": "Increment",
  "param_dialog.label.value_set_min": "Minimum",
  "param_dialog.label.value_set_max": "Maximum",
  "param_dialog.label.value_set_list": "List values (comma-separated)",
  "param_dialog.valueset.none": "None",
  "param_dialog.valueset.increment": "Increment",
  "param_dialog.valueset.list": "List",
  "param_dialog.label.flip_label": "Flip label",
  "param_dialog.label.base_label": "Base label",
  "param_dialog.label.default_value": "Default value",
  "param_dialog.label.show_grip": "Show grip",
  "param_dialog.label.chain_actions": "Chain actions",
  "param_dialog.btn.ok": "OK",
  "param_dialog.btn.cancel": "Cancel",
  "param_dialog.error.empty_name": "Parameter name cannot be empty.",
  "param_dialog.error.duplicate_name":
    "Parameter with name '{name}' already exists.",
  // DYN-1.7 ActionPropertiesDialog
  "action_dialog.title.move": "Move action properties",
  "action_dialog.title.stretch": "Stretch action properties",
  "action_dialog.title.flip": "Flip action properties",
  // DYN-2 ActionPropertiesDialog Tier 2
  "action_dialog.title.rotate": "Rotate action properties",
  "action_dialog.title.scale": "Scale action properties",
  "action_dialog.title.array": "Array action properties",
  "action_dialog.title.lookup": "Lookup action properties",
  // DYN-3 Batch E — PolarStretch action
  "action_dialog.title.polar_stretch": "Polar Stretch action properties",
  "action_dialog.error.requires_polar_parameter":
    "Polar Stretch action requires a Polar parameter.",
  "action_dialog.label.angle_multiplier": "Angle multiplier",
  "action_dialog.label.scale_type": "Scale type",
  "action_dialog.label.scale_multiplier": "Scale multiplier",
  "action_dialog.label.column_spacing": "Column spacing",
  "action_dialog.label.row_spacing": "Row spacing",
  "action_dialog.scale_type.uniform": "Uniform",
  "action_dialog.scale_type.x_only": "X only",
  "action_dialog.scale_type.y_only": "Y only",
  "action_dialog.error.requires_rotation_parameter":
    "This action requires a Rotation parameter.",
  "action_dialog.error.requires_linear_polar_xy":
    "This action requires a Linear, Polar, or XY parameter.",
  "action_dialog.error.requires_lookup_parameter":
    "This action requires a Lookup parameter.",
  "action_dialog.error.column_spacing_positive":
    "Column spacing must be a positive number.",
  "action_dialog.label.name": "Name",
  "action_dialog.label.parameter": "Parameter",
  "action_dialog.label.distance_multiplier": "Distance multiplier",
  "action_dialog.label.angle_offset": "Angle offset (°)",
  "action_dialog.label.crossing_frame": "Crossing frame",
  "action_dialog.label.crossing_corner1": "Corner 1 (X, Y)",
  "action_dialog.label.crossing_corner2": "Corner 2 (X, Y)",
  "action_dialog.label.selection": "Selected objects",
  "action_dialog.btn.ok": "OK",
  "action_dialog.btn.cancel": "Cancel",
  "action_dialog.error.empty_name": "Action name cannot be empty.",
  "action_dialog.error.no_parameter": "A parameter must be selected.",
  "action_dialog.error.wrong_parameter":
    "Selected parameter is not compatible with this action.",
  "action_dialog.error.no_selection":
    "Select at least one object for the action.",
  // DYN-1.8 VisibilityStatesDialog
  "visibility_dialog.title": "Visibility states",
  "visibility_dialog.parameter_label": "Parameter:",
  "visibility_dialog.section.states": "States",
  "visibility_dialog.section.entities": "Entities in state '{state}'",
  "visibility_dialog.column.state": "State",
  "visibility_dialog.column.default": "Default",
  "visibility_dialog.column.visible": "Visible",
  "visibility_dialog.column.hidden": "Hidden",
  "visibility_dialog.btn.add_state": "Add state",
  "visibility_dialog.btn.rename": "Rename",
  "visibility_dialog.btn.delete": "Delete",
  "visibility_dialog.btn.move_up": "Move Up",
  "visibility_dialog.btn.move_down": "Move Down",
  "visibility_dialog.btn.make_default": "Make default",
  "visibility_dialog.btn.close": "Close",
  // Phase K.4 — Set Current button + BVHIDE/BVSHOW commands
  "visibility_dialog.btn.set_current": "Set Current",
  "visibility_dialog.btn.bvhide": "BVHIDE: Hide selected objects",
  "visibility_dialog.btn.bvshow": "BVSHOW: Show selected objects",
  "visibility_dialog.btn.bvshow_all": "BVSHOW _All: Show in all states",
  "visibility_dialog.current_marker": "(current)",
  "visibility_dialog.empty_states": "No visibility states. Add your first.",
  "visibility_dialog.new_state_default_name": "State {n}",
  "visibility_dialog.rename_prompt": "New state name:",
  "visibility_dialog.delete_confirm": "Delete state '{name}'?",
  "visibility_dialog.empty_entities":
    "The block has no entities that visibility can control.",
  "visibility_dialog.entity_visible": "Visible",
  "visibility_dialog.entity_hidden": "Hidden",
  // Commands
  "command.bparameter.label": "BPARAMETER",
  "command.baction.label": "BACTION",
  "command.bauthorpalette.label": "BAUTHORPALETTE",
  "command.bvstate.label": "BVSTATE",
  "command.bcparameter.label": "BCPARAMETER",
  "command.dynlist.label": "DYNLIST (Dynamic Properties List)",
  "command.dynset.label": "DYNSET (Set Dynamic Parameter)",
  "command.dynreset.label": "DYNRESET (Reset Dynamic Parameters)",
  "command.gcconstraint.label": "GCCONSTRAINT (Geometric Constraint)",
  // Prompts
  "prompt.bparameter.select_kind":
    "BPARAMETER: select a parameter type in the palette.",
  "prompt.bparameter.linear.start": "LINEAR: specify start point.",
  "prompt.bparameter.linear.end": "LINEAR: specify end point.",
  "prompt.bparameter.linear.label_position":
    "LINEAR: specify label location <same as endpoint>.",
  "prompt.bparameter.flip.axis_start": "FLIP: specify axis start.",
  "prompt.bparameter.flip.axis_end": "FLIP: specify axis end.",
  "prompt.bparameter.visibility.position": "VISIBILITY: specify grip position.",
  "prompt.bparameter.basepoint.position": "BASEPOINT: specify point location.",
  "prompt.baction.select_parameter": "BACTION: select a parameter.",
  "prompt.baction.select_objects": "BACTION: select objects (Enter to finish).",
  "prompt.baction.crossing_corner1":
    "STRETCH: specify first corner of crossing frame.",
  "prompt.baction.crossing_corner2":
    "STRETCH: specify opposite corner of crossing frame.",
  // DYN-2 Batch C constraint prompts
  "prompt.constraint.horizontal.first_point":
    "HORIZONTAL: specify first point.",
  "prompt.constraint.horizontal.second_point":
    "HORIZONTAL: specify second point.",
  "prompt.constraint.vertical.first_point": "VERTICAL: specify first point.",
  "prompt.constraint.vertical.second_point": "VERTICAL: specify second point.",
  "prompt.constraint.angular.vertex": "ANGULAR: specify vertex.",
  "prompt.constraint.angular.first_ray": "ANGULAR: specify first ray endpoint.",
  "prompt.constraint.angular.second_ray":
    "ANGULAR: specify second ray endpoint.",
  "prompt.bcparameter.open":
    "BCPARAMETER: open the Constraints tab in the block palette.",
  // DYN-2 Batch C palette tab — Constraints
  "palette.constraints.dimensional": "Dimensional",
  "palette.constraints.geometric": "Geometric (Tier 3)",
  "palette.constraints.geometric.coincident": "Coincident",
  "palette.constraints.geometric.collinear": "Collinear",
  "palette.constraints.geometric.parallel": "Parallel",
  "palette.constraints.geometric.perpendicular": "Perpendicular",
  "palette.constraints.geometric.tangent": "Tangent",
  "palette.constraints.geometric.equal": "Equal",
  "palette.constraints.geometric.symmetric": "Symmetric",
  "palette.constraints.geometric.concentric": "Concentric",
  "palette.constraints.geometric.fix": "Fix",
  "palette.constraints.geometric.smooth": "Smooth",
  "palette.constraints.geometric.horizontal": "Horizontal",
  "palette.constraints.geometric.vertical": "Vertical",
  // DYN-3 Batch D — geometric constraint command prompts
  "prompt.geom.select_first_entity": "Select first entity:",
  "prompt.geom.select_second_entity": "Select second entity:",
  "prompt.geom.select_entity": "Select entity:",
  "prompt.geom.select_first_curve": "Select first curve:",
  "prompt.geom.select_second_curve": "Select second curve:",
  "prompt.geom.select_line": "Select a line:",
  "prompt.geom.select_symmetry_line": "Select symmetry line:",
  // DYN-3 Batch D — authoring palette labels for Alignment parameter
  "authoring.param.alignment": "Alignment",
  "authoring.param.alignment.perpendicular": "Perpendicular",
  "authoring.param.alignment.parallel": "Parallel",
  "authoring.action.polar_stretch": "Polar Stretch",
  // DYN-3 Batch D — grip label
  "grip.block.alignment": "Alignment grip: {name}",
  // DYN-3 Batch E — Tier 3 constraint grip labels
  "grip.block.constraint.aligned": "Aligned constraint: {name}",
  "grip.block.constraint.radial": "Radial constraint: {name}",
  "grip.block.constraint.diameter": "Diameter constraint: {name}",
  // DYN-3 Batch D — geometric-constraint commands
  "command.GCCOINCIDENT": "GCCOINCIDENT",
  "command.GCCOLLINEAR": "GCCOLLINEAR",
  "command.GCCONCENTRIC": "GCCONCENTRIC",
  "command.GCFIX": "GCFIX",
  "command.GCPARALLEL": "GCPARALLEL",
  "command.GCPERPENDICULAR": "GCPERPENDICULAR",
  "command.GCTANGENT": "GCTANGENT",
  "command.GCEQUAL": "GCEQUAL",
  "command.GCSYMMETRIC": "GCSYMMETRIC",
  "command.GCSMOOTH": "GCSMOOTH",
  "command.GCHORIZONTAL": "GCHORIZONTAL",
  "command.GCVERTICAL": "GCVERTICAL",
  // Audit events
  "audit.dynamic_parameter_added":
    "BPARAMETER: parameter added to block '{block}'.",
  "audit.dynamic_parameter_updated":
    "BPARAMETER: parameter {id} updated in block '{block}'.",
  "audit.dynamic_parameter_removed":
    "BPARAMETER: parameter {id} removed from block '{block}'.",
  "audit.dynlist":
    "DYNLIST: parameters of insert {id} printed to the transcript.",
  "audit.dynset":
    "DYNSET: parameter '{param}' of insert {id} set to '{value}'.",
  "audit.dynreset":
    "DYNRESET: parameters of insert {id} reset to master defaults.",
  "audit.dynamic_action_added": "BACTION: action added to block '{block}'.",
  "audit.dynamic_action_updated":
    "BACTION: action {id} updated in block '{block}'.",
  "audit.dynamic_action_removed":
    "BACTION: action {id} removed from block '{block}'.",
  "audit.visibility_state_added":
    "BVSTATE: added state '{name}' in block '{block}'.",
  "audit.visibility_state_renamed":
    "BVSTATE: state renamed to '{name}' in block '{block}'.",
  "audit.visibility_state_deleted":
    "BVSTATE: state {id} deleted in block '{block}'.",
  "audit.visibility_states_reordered":
    "BVSTATE: states reordered in block '{block}'.",
  "audit.visibility_default_set":
    "BVSTATE: state {id} set as default (block '{block}').",
  "audit.visibility_entity_toggled":
    "BVSTATE: visibility of entity {entity} toggled (block '{block}').",
  // Phase K.4 — Visibility States Manager additions
  "audit.visibility_current_set":
    "BVSTATE: current state set to {id} (block '{block}').",
  "audit.bvhide":
    "BVHIDE: hidden {count} entity(ies) in current state (block '{block}').",
  "audit.bvshow":
    "BVSHOW: shown {count} entity(ies) in current state (block '{block}').",
  "audit.bvshow_all":
    "BVSHOW _All: shown {count} entity(ies) in all states (block '{block}').",
  // Toasts
  "toast.authoring.not_in_block_editor":
    "This command is only available inside the Block Editor (BEDIT).",
  "toast.authoring.no_visibility_param":
    "The block has no visibility parameter. Add one first.",
  // DYN-1.6 Dynamic block grips
  "grip.block.linear": "Linear parameter: {name}",
  "grip.block.flip": "Flip parameter: {name}",
  "grip.block.visibility": "Visibility parameter: {name}",
  "grip.block.basepoint": "Base point: {name}",
  "grip.block.visibility.select_state": "Select visibility state",
  // DYN-2 grip tooltips
  "grip.block.point": "Point: {name}",
  "grip.block.polar.distance": "Polar parameter — distance: {name}",
  "grip.block.polar.angle": "Polar parameter — angle: {name}",
  "grip.block.xy.x": "XY — X: {name}",
  "grip.block.xy.y": "XY — Y: {name}",
  "grip.block.rotation": "Rotation parameter: {name}",
  "grip.block.lookup": "Lookup parameter: {name}",
  "grip.block.lookup.select_row": "Select lookup row",
  // DYN-2 Batch C constraint grip tooltips
  "grip.block.constraint.horizontal": "Horizontal constraint: {name}",
  "grip.block.constraint.vertical": "Vertical constraint: {name}",
  "grip.block.constraint.angular": "Angular constraint: {name}",
  // DYN-1.10 Properties palette — dynamic block Custom Properties
  "properties.custom": "Custom Properties",
  "property.custom.visibility": "Visibility",
  "property.custom.param_prefix": "Parameter",
  // DYN-2 property labels for Tier 2 parameters
  "property.custom.x": "X",
  "property.custom.y": "Y",
  "property.custom.distance": "Distance",
  "property.custom.angle": "Angle",
  "property.custom.lookup": "Lookup",
  // DYN-1.9 TESTBLOCK / CLOSETESTBLOCK
  "command.testblock.label": "TESTBLOCK",
  "command.closetestblock.label": "CLOSETESTBLOCK",
  "summary.testblock_entered":
    'Test Block mode opened for "{name}" (TESTBLOCK).',
  "summary.testblock_exited": "Test Block mode closed (CLOSETESTBLOCK).",
  "toast.testblock.not_in_block_editor":
    "TESTBLOCK is only available inside the Block Editor (BEDIT).",
  "toast.testblock.no_block":
    "Cannot determine the target block for Test Block mode.",
  "toast.testblock.not_in_test_block":
    "CLOSETESTBLOCK is only available while previewing a block in Test Block mode.",
  "banner.testblock.title": "Testing block: {name}. Edits here are not saved.",
  "banner.testblock.exit": "Exit Test Block",
  "authoring.toolbar.test_block": "Test Block",
  // DYN-1.11 Authoring palette command-line commands
  "command.bauthorpaletteclose.label": "BAUTHORPALETTECLOSE",
  "command.bgripset.label": "BGRIPSET",
  "summary.bauthorpalette_toggled":
    "Block Authoring Palette toggled (BAUTHORPALETTE).",
  "summary.bauthorpalette_closed":
    "Block Authoring Palette closed (BAUTHORPALETTECLOSE).",
  "feed.bgripset.tier1_notice":
    "BGRIPSET: full dialog ships in Tier 2. Grip visibility is configurable via the parameter properties.",
  // ── DYN-2 B.2 — Lookup Table editor (BLOOKUPTABLE) ──
  "lookup.dialog.title": "Lookup Table: {name}",
  "lookup.dialog.untitled": "untitled",
  "lookup.dialog.table_name": "Table name",
  "lookup.dialog.add_row": "+ Row",
  "lookup.dialog.remove_row": "Remove",
  "lookup.dialog.add_input_column": "+ Input Column",
  "lookup.dialog.remove_column": "Remove column",
  "lookup.dialog.edit_output": "Output column label",
  "lookup.dialog.pick_parameter": "Pick a parameter",
  "lookup.dialog.pick_property": "Pick a property",
  "lookup.dialog.no_parameters":
    "This block has no parameters eligible for lookup columns. Add a Linear / Polar / XY / Point / Rotation / Flip / Visibility parameter first.",
  "lookup.dialog.empty_rows": 'No rows yet. Click "+ Row" to add one.',
  "lookup.dialog.btn.apply": "Apply",
  "lookup.dialog.btn.ok": "OK",
  "lookup.dialog.btn.cancel": "Cancel",
  "lookup.dialog.error.no_columns":
    "The table must have at least one input column.",
  "lookup.dialog.error.duplicate_name":
    'A lookup table named "{name}" already exists.',
  "lookup.column.parameter": "Parameter",
  "lookup.column.property.currentDistance": "Distance",
  "lookup.column.property.currentAngle": "Angle",
  "lookup.column.property.currentX": "X",
  "lookup.column.property.currentY": "Y",
  "lookup.column.property.currentValue": "Value",
  "lookup.column.property.currentStateId": "State",
  "lookup.column.output": "Value",
  "lookup.picker.title": "Select a lookup table",
  "lookup.picker.new": "Create new…",
  "lookup.picker.new_prompt": "Name for the new lookup table:",
  "lookup.picker.new_default_name": "Table{n}",
  // ── DYN-2 B.2 — Block Properties Table editor (BTABLE) ──
  "block_props.dialog.title": "Block Properties Table",
  "block_props.dialog.add_property": "+ Property",
  "block_props.dialog.move_up": "▲",
  "block_props.dialog.move_down": "▼",
  "block_props.dialog.remove": "Remove",
  "block_props.dialog.empty":
    'No properties yet. Click "+ Property" to add one.',
  "block_props.dialog.btn.apply": "Apply",
  "block_props.dialog.btn.ok": "OK",
  "block_props.dialog.btn.cancel": "Cancel",
  "block_props.dialog.error.duplicate_key":
    'The key "{key}" already exists in the table.',
  "block_props.dialog.error.empty_key": "Key cannot be empty.",
  "block_props.col.key": "Key",
  "block_props.col.label": "Label",
  "block_props.col.type": "Type",
  "block_props.col.default": "Default value",
  "block_props.col.formula": "Formula",
  "block_props.type.string": "Text",
  "block_props.type.number": "Number",
  "block_props.type.boolean": "Yes / No",
  "block_props.default.true": "Yes",
  "block_props.default.false": "No",
  // ── DYN-4.1 — Formula expressions ──
  "formula.test": "Test",
  "formula.test.ok": "Result: {value}",
  "formula.error": "Formula error: {message}",
  "formula.circular_reference": "Circular reference",
  "formula.help": "Expression: arithmetic, Math.* functions, parameter refs",
  // ── DYN-4.2 — Reverse Lookup ──
  "lookup.reverse.placeholder": "Select a value",
  "lookup.reverse.none": "(no rows)",
  // ── DYN-4.4 — Performance profiler ──
  "profile.title": "Block profile",
  "profile.total": "Total",
  "profile.formulas": "Formulas",
  "profile.actions": "Actions",
  "profile.parameters": "Parameters",
  "profile.solver": "Solver",
  "profile.solver.iterations": "Iterations: {count}",
  "profile.solver.residual": "Residual: {value}",
  "profile.cache_misses": "Cache misses: {count}",
  "profile.ms": "{value} ms",
  // ── DYN-2 B.2 — Parameter Sets presets ──
  "authoring.param_set.heading": "Predefined sets",
  "authoring.param_set.hint":
    "Click a set — both the parameter and action are created in one step.",
  "authoring.param_set.linear_move": "Linear Move",
  "authoring.param_set.linear_stretch": "Linear Stretch",
  "authoring.param_set.linear_array": "Linear Array",
  "authoring.param_set.linear_scale": "Linear Scale",
  "authoring.param_set.polar_move": "Polar Move",
  "authoring.param_set.polar_stretch": "Polar Stretch",
  "authoring.param_set.polar_scale": "Polar Scale",
  "authoring.param_set.xy_move": "XY Move",
  "authoring.param_set.xy_scale_uniform": "XY Scale Uniform",
  "authoring.param_set.xy_array": "XY Array",
  "authoring.param_set.rotation_set": "Rotation Set",
  "authoring.param_set.flip_set": "Flip Set",
  "authoring.param_set.visibility_set": "Visibility Set",
  "authoring.param_set.lookup_set": "Lookup Set",
  "authoring.param_set.basepoint": "Base Point",
  "authoring.param_set.user_defined_placeholder":
    "User-defined sets will land in a later iteration.",
  // ── DYN-2 B.2 — Shell commands (BLOOKUPTABLE / BTABLE) ──
  "command.blookuptable.label": "BLOOKUPTABLE",
  "command.btable.label": "BTABLE",
  "prompt.blookuptable.select": "BLOOKUPTABLE: select a table to edit.",
  "prompt.blookuptable.none":
    "BLOOKUPTABLE: no tables exist. Create a new one?",
  "prompt.btable.opened": "BTABLE: block properties table editor opened.",
  "toast.blookuptable.not_in_block_editor":
    "BLOOKUPTABLE is only available inside the Block Editor (BEDIT).",
  "toast.btable.not_in_block_editor":
    "BTABLE is only available inside the Block Editor (BEDIT).",
  "summary.lookup_table_opened": 'Opened lookup table dialog for "{name}".',
  "summary.lookup_table_created": 'Created lookup table "{name}".',
  "summary.lookup_table_saved": 'Lookup table "{name}" saved.',
  "summary.block_properties_opened":
    'Opened block properties table dialog for "{block}".',
  "summary.block_properties_saved":
    'Block properties table saved for "{block}".',
  // ── DYN-2 B.2 — Audit entries ──
  "audit.lookup_table_created":
    'BLOOKUPTABLE: created a lookup table in block "{block}".',
  "audit.lookup_table_updated":
    'BLOOKUPTABLE: table {id} updated in block "{block}".',
  "audit.lookup_table_deleted":
    'BLOOKUPTABLE: table {id} removed from block "{block}".',
  "audit.block_properties_table_set":
    'BTABLE: block properties table saved for "{block}".',
  "audit.block_property_added": 'BTABLE: added a property to block "{block}".',
  "audit.block_property_updated":
    'BTABLE: property {key} updated in block "{block}".',
  "audit.block_property_removed":
    'BTABLE: property {key} removed from block "{block}".',
  "audit.block_properties_reordered":
    'BTABLE: property order changed for block "{block}".',

  // ── Phase N.5 — Block Properties Manager (BPM) audits ──
  "audit.bpm_parameter_renamed":
    'BPM: parameter {id} renamed to "{name}" in block "{block}".',
  "audit.bpm_parameter_description_updated":
    'BPM: parameter {id} description updated in block "{block}".',
  "audit.bpm_parameter_show_toggled":
    'BPM: parameter {id} Properties-palette visibility toggled in block "{block}".',
  "audit.bpm_parameters_reordered":
    'BPM: parameter order changed in block "{block}".',
  "audit.bpm_action_renamed":
    'BPM: action {id} renamed to "{name}" in block "{block}".',
  "audit.bpm_action_description_updated":
    'BPM: action {id} description updated in block "{block}".',
  "audit.bpm_actions_reordered":
    'BPM: action order changed in block "{block}".',

  // ── Phase N.5 — Block Properties Manager dialog labels ──
  "bpm.dialog.title": "Block Properties Manager",
  "bpm.dialog.blocks_section": "Blocks",
  "bpm.dialog.empty_blocks": "Drawing has no block definitions.",
  "bpm.dialog.no_block_selected":
    "Select a block on the left to view its parameters and actions.",
  "bpm.tab.parameters": "Parameters",
  "bpm.tab.actions": "Actions",
  "bpm.tab.visibility": "Visibility",
  "bpm.tab.attributes": "Attributes",
  "bpm.col.name": "Name",
  "bpm.col.description": "Description",
  "bpm.col.type": "Type",
  "bpm.col.default": "Default",
  "bpm.col.linked_parameter": "Parameter",
  "bpm.col.selected_count": "Entities",
  "bpm.col.show_in_palette": "Show in palette",
  "bpm.col.attribute_tag": "Tag",
  "bpm.col.attribute_prompt": "Prompt",
  "bpm.col.attribute_default": "Default",
  "bpm.col.row_handle": "↕",
  "bpm.empty.parameters": "This block has no parameters.",
  "bpm.empty.actions": "This block has no actions.",
  "bpm.empty.visibility": "This block has no visibility parameters.",
  "bpm.empty.attributes": "This block has no attributes (ATTDEFs).",
  "bpm.btn.rename": "Rename",
  "bpm.btn.delete": "Delete",
  "bpm.btn.move_up": "Up",
  "bpm.btn.move_down": "Down",
  "bpm.btn.sync": "Sync",
  "bpm.btn.audit": "Audit",
  "bpm.btn.export": "Export",
  "bpm.btn.close": "Close",
  "bpm.toolbar.sync_done": 'Evaluation graph re-synced for block "{block}".',
  "bpm.toolbar.audit_clean": 'Audit of block "{block}": no issues.',
  "bpm.toolbar.audit_issues":
    'Audit of block "{block}": {count} issue(s) found.',
  "bpm.toolbar.export_done":
    'CSV export of block "{block}" copied to clipboard.',
  "bpm.confirm.delete_parameter":
    'Delete parameter "{name}"? Linked actions ({count}) will be orphaned and removed too.',
  "bpm.prompt.rename_parameter": "New parameter name:",
  "bpm.prompt.rename_action": "New action name:",
  "bpm.visibility.parameter_label": "Visibility parameter",
  "bpm.visibility.state": "State",
  "bpm.visibility.default_marker": "(default)",
  "bpm.visibility.current_marker": "(current)",
  "command.bpm.label": "Block Properties Manager",

  // ── DYN-3 Batch C — Value Set editor (shared across parameter kinds) ──
  "value_set.mode.none": "None",
  "value_set.mode.increment": "Increment",
  "value_set.mode.list": "List",
  "value_set.increment_field": "Step",
  "value_set.min": "Min",
  "value_set.max": "Max",
  "value_set.list_values": "Values",
  "value_set.list_add": "Add value",
  "value_set.list_remove": "Remove",
  "value_set.list_move_up": "Move up",
  "value_set.list_move_down": "Move down",
  "value_set.list_empty": "List is empty — click 'Add value'.",

  // ── DYN-3 Tier 3 — Alignment / Aligned / Radial / Diameter forms ──
  "param_dialog.title.alignment": "Alignment Parameter",
  "param_dialog.title.constraint_aligned": "Aligned Distance Constraint",
  "param_dialog.title.constraint_radial": "Radial Constraint",
  "param_dialog.title.constraint_diameter": "Diameter Constraint",
  "param_dialog.label.alignment_type": "Alignment type",
  "param_dialog.label.alignment_perpendicular": "Perpendicular",
  "param_dialog.label.alignment_parallel": "Parallel",
  "param_dialog.label.center": "Center",
  "param_dialog.label.radius_point": "Radius point",
  "param_dialog.label.point_1": "Point 1",
  "param_dialog.label.point_2": "Point 2",
  "param_dialog.label.diameter": "Diameter",
  "param_dialog.label.default_radius": "Default radius",
  "param_dialog.label.default_diameter": "Default diameter",
  "param_dialog.label.radius_value_set": "Radius value set",
  "param_dialog.label.diameter_value_set": "Diameter value set",

  // ── DYN-3 Batch C — Chain Actions (pipeline authoring) ──
  "action.chain.section": "Chain Actions",
  "action.chain.include": "Include in chain",
  "action.chain.group.new": "New group",
  "action.chain.group.existing": "Existing group",
  "action.chain.group.label": "Chain group",
  "action.chain.group.pick_placeholder": "— Independent action —",
  "action.chain.group.option_none": "Independent action",
  "action.chain.group.option_new": "New group",
  "action.chain.group.option_existing": "Group {index}",
  "action.chain.remove": "Remove from chain",
  "audit.chain_group_updated": 'CHAIN: chain group updated in block "{block}".',

  // ── DYN-3 Batch C — Nested dynamic blocks (evaluator diagnostics) ──
  "evaluator.nested.cycle_detected":
    "Nested dynamic block cycle detected: {chain}",
  "evaluator.nested.depth_exceeded":
    "Nested dynamic block depth exceeded (max {max}).",

  // ── XREF suite (Phase FIN.3) ──────────────────────────────
  "command.xattach.label": "XATTACH (Attach External Reference)",
  "command.xbind.label": "XBIND (Bind External Reference)",
  "command.xclip.label": "XCLIP (Clip External Reference)",
  "command.xdetach.label": "XDETACH (Detach External Reference)",
  "command.xreload.label": "XRELOAD (Reload External Reference)",
  "command.xunload.label": "XUNLOAD (Unload External Reference)",
  "xref.manager.title": "External References",
  "xref.manager.attach": "Attach...",
  "xref.manager.attachment_type": "Type:",
  "xref.manager.empty": "No external references in this drawing.",
  "xref.manager.close": "Close",
  "xref.column.name": "Name",
  "xref.column.path": "Path",
  "xref.column.status": "Status",
  "xref.column.attachment_type": "Type",
  "xref.column.actions": "Actions",
  "xref.attachment.attach": "Attach",
  "xref.attachment.overlay": "Overlay",
  "xref.status.resolved": "Resolved",
  "xref.status.unloaded": "Unloaded",
  "xref.status.not_found": "Not found",
  "xref.action.unload": "Unload",
  "xref.action.reload": "Reload",
  "xref.action.bind": "Bind",
  "xref.action.detach": "Detach",
  "xref.action.clip": "Clip",
  "xref.action.open": "Open",
  "audit.xref_attached": 'External reference "{name}" attached ({path}).',
  "audit.xref_bound": 'External reference "{name}" bound to drawing.',
  "audit.xref_unloaded": 'External reference "{name}" unloaded.',
  "audit.xref_reloaded": 'External reference "{name}" reloaded.',
  "audit.xref_detached": 'External reference "{name}" detached.',
  "audit.xref_clipped": 'Clipping applied to xref "{name}".',
  "audit.xref_clip_cleared": 'Clipping cleared from xref "{name}".',
  "summary.xref_attached":
    'External reference "{name}" attached from "{path}".',
  "summary.xref_bound": 'Xref "{name}" converted to local block.',
  "summary.xref_unloaded": 'Xref "{name}" unloaded.',
  "summary.xref_reloaded": 'Xref "{name}" reloaded.',
  "summary.xref_detached": 'Xref "{name}" detached.',
  "summary.xref_clipped": 'Clipping applied to "{name}".',
  "toast.xref.attach_failed": "Failed to attach xref: {message}",
  "toast.xref.reload_failed": "Failed to reload xref: {message}",
  "toast.xref.unload_failed": "Failed to unload xref: {message}",
  "toast.xref.bind_failed": "Failed to bind xref: {message}",
  "toast.xref.detach_failed": "Failed to detach xref: {message}",
  "toast.xref.clip_failed": "Failed to apply clip: {message}",
  "toast.xref.open_unsupported":
    'Opening xref "{name}" in a new window is not supported yet.',
  // ── EATTEXT (Phase FIN.5) ─────────────────────────────────
  "eattext.dialog.title": "Extract Attributes",
  "eattext.filter.blocks": "Blocks",
  "eattext.filter.tags": "Tags",
  "eattext.empty_blocks": "No blocks available",
  "eattext.empty_tags": "No tags available",
  "eattext.include_invisible": "Include invisible",
  "eattext.include_constant": "Include constant",
  "eattext.preview": "Preview: {shown} of {total} rows",
  "eattext.col.block": "Block",
  "eattext.col.insert_id": "Insert ID",
  "eattext.download": "Download CSV",
  "eattext.btn.cancel": "Cancel",
  "summary.eattext_downloaded": "Attribute file saved ({count} rows).",
  // ── BCOUNT (Phase FIN.6) ──────────────────────────────────
  "bcount.summary": "Block instance count",
  // ── MINSERT grips (Phase FIN.7) ───────────────────────────
  "grip.minsert.columns": "MINSERT: column count",
  "grip.minsert.rows": "MINSERT: row count",
  "grip.minsert.column_spacing": "MINSERT: column spacing",
  "grip.minsert.row_spacing": "MINSERT: row spacing",
  // ── Variant A: editable dynamic-block parameter controls ──
  "dynamic_block_params.edit_aria": "Edit parameter {name}",
  "dynamic_block_params.reset_defaults": "Reset to defaults",
  "dynamic_block_params.reset_defaults_hint":
    "Restores every parameter from the master block (DYNRESET).",
  "dynamic_block_params.no_typed_metadata":
    "No typed metadata — fields edit as plain text / number.",
  "dynamic_block_params.unit_mm": "mm",
  "dynamic_block_params.unit_deg": "°",
  "dynamic_block_params.axis_x": "X",
  "dynamic_block_params.axis_y": "Y",
  // ── PROPERTIES / CHPROP / MATCHPROP — property system commands ──
  "command.properties.label": "PROPERTIES (Properties)",
  "command.chprop.label": "CHPROP (Change Properties)",
  "command.matchprop.label": "MATCHPROP (Match Properties)",
  // ── T7 (agent-bridge) — LAYER table-management command ──
  "command.layer.label": "LAYER (Layer Manager)",
  // ── Unified PROPERTY <id> <name> <value> command (Variant C) ──
  "command.property.label": "Property",
  "prompt.property.usage": "PROPERTY: usage: PROPERTY <id> <name> <value>",
  "audit.property.changed": "PROPERTY: {name} on {id} → {value}.",
  "error.property.entity_not_found": "PROPERTY: entity {id} not found.",
  "error.property.unknown_name": "PROPERTY: unknown property {name}.",
  "error.property.empty_name": "PROPERTY: property name missing.",
  "sysvar.feedback": "{name} = {value}",
  "sysvar.unknown": "Unknown variable: {name}",
  "sysvar.invalid_value": "Invalid value for {name}",
  "sysvar.current_value": "Current value of {name}: {value}",
  // ── Dynamic Input pointer field labels (Phase 1) ──
  "dynin.field.distance": "Distance",
  "dynin.field.angle": "Angle",
  "dynin.field.x": "X",
  "dynin.field.y": "Y",
  // ── Dynamic Input dimension field labels (Phase 2) ──
  "dynin.field.length": "Length",
  "dynin.field.radius": "Radius",
  "dynin.field.diameter": "Diameter",
  "dynin.field.rotation": "Rotation",
  "dynin.field.height": "Height",
  "dynin.field.width": "Width",
  "dynin.field.scale": "Scale",
  // ── DI coord-expression labels (AutoCAD parity: @ / # prefix splits field into two inputs) ──
  "dynin.field.coord_prefix_rel_cart": "@ΔX",
  "dynin.field.coord_prefix_abs_cart": "X",
  "dynin.field.coord_prefix_rel_polar": "@dist",
  "dynin.field.coord_prefix_abs_polar": "dist",
  "dynin.field.coord_sec_rel_cart": "ΔY",
  "dynin.field.coord_sec_abs_cart": "Y",
  "dynin.field.coord_sec_angle": "angle",
  // ── DI overlay (B8) ──
  "dynin.overlay.aria": "Dynamic Input",
  "dynin.overlay.settings": "Dynamic Input Settings",
  // ── DSettings Dialog (Phase 3) ──
  "dsettings.title": "Drafting Settings",
  "dsettings.tab.snap": "Snap and Grid",
  "dsettings.tab.polar": "Polar Tracking",
  "dsettings.tab.osnap": "Object Snap",
  "dsettings.tab.dyninput": "Dynamic Input",
  "dsettings.ok": "OK",
  "dsettings.cancel": "Cancel",
  "dsettings.dyninput.pointer.title": "Pointer Input",
  "dsettings.dyninput.pointer.enable": "Enable Pointer Input",
  "dsettings.dyninput.pointer.settings": "Settings...",
  "dsettings.dyninput.dim.title": "Dimension Input",
  "dsettings.dyninput.dim.enable": "Enable Dimension Input",
  "dsettings.dyninput.dim.settings": "Settings...",
  "dsettings.dyninput.prompt.title": "Dynamic Prompts",
  "dsettings.dyninput.prompt.enable": "Show command prompting near cursor",
  "dsettings.dyninput.appearance": "Appearance...",
  "dsettings.pi.polar": "Polar",
  "dsettings.pi.cartesian": "Cartesian",
  "dsettings.pi.relative": "Relative",
  "dsettings.pi.absolute": "Absolute",
  "dsettings.pi.vis.always": "Always show",
  "dsettings.pi.vis.typing": "When user types",
  "dsettings.pi.vis.command": "When requested",
  "dsettings.di.one": "One field",
  "dsettings.di.two": "Two fields",
  "dsettings.snap.title": "Snap",
  "dsettings.snap.enable": "Enable snap",
  "dsettings.snap.hint": "F9 toggles snap",
  "dsettings.grid.title": "Grid",
  "dsettings.grid.enable": "Enable grid",
  "dsettings.grid.hint": "F7 toggles grid",
  "dsettings.polar.enable": "Polar tracking",
  "dsettings.polar.hint": "F10 toggles tracking",
  "dsettings.polar.increment.label": "Increment angle:",
  "dsettings.polar.additional.enable": "Additional angles",
  "dsettings.polar.additional.placeholder": "e.g. 15;22.5",
  "dsettings.polar.additional.hint":
    "Up to 10 angles separated by ; or , (POLARADDANG)",
  "dsettings.polar.basis.title": "Polar Angle Measurement",
  "dsettings.polar.basis.absolute": "Absolute",
  "dsettings.polar.basis.relative": "Relative to last segment",
  "dsettings.osnap.otrackEnable": "Object Snap Tracking On (F11)",
  "dsettings.osnap.title": "Object snap modes",
  "dsettings.osnap.endpoint": "Endpoint",
  "dsettings.osnap.midpoint": "Midpoint",
  "dsettings.osnap.center": "Center",
  "dsettings.osnap.node": "Node",
  "dsettings.osnap.quadrant": "Quadrant",
  "dsettings.osnap.intersection": "Intersection",
  "dsettings.osnap.extension": "Extension",
  "dsettings.osnap.insertion": "Insertion",
  "dsettings.osnap.perpendicular": "Perpendicular",
  "dsettings.osnap.tangent": "Tangent",
  "dsettings.osnap.nearest": "Nearest",
  "dsettings.osnap.geomcenter": "Geometric Center",
  "dsettings.osnap.apparent": "Apparent Intersection",
  "dsettings.osnap.parallel": "Parallel",
  // OSNAP-OVERRIDE-MODES — the two temporary override-only modes (ТЗ §8.6).
  "dsettings.osnap.extint": "Extended Intersection",
  "dsettings.osnap.extappint": "Extended Apparent Intersection",
  "dsettings.osnap.pending":
    "Snap mode not yet implemented — see docs/test-gaps/sub-osnap.md",
  "dsettings.pi.title": "Pointer Input Settings",
  "dsettings.pi.format.title": "Coordinate format",
  "dsettings.pi.vis.title": "Field visibility",
  "dsettings.di.title": "Dimension Input Settings",
  "dsettings.di.fields.title": "Input fields",
  // ── Grip DYNDIGRIP field labels (Phase 4) ──
  "dynin.grip.resize": "Resize",
  "dynin.grip.length-change": "Length Change",
  "dynin.grip.angle": "Angle",
  "dynin.grip.arc-radius": "Radius",
  "dynin.grip.move-x": "ΔX",
  "dynin.grip.move-y": "ΔY",
  "dynin.grip.major-radius": "Major axis",
  "dynin.grip.minor-radius": "Minor axis",
  // ── Block Authoring Palette shell (Phase K.1) ──
  "block_authoring.title": "Block Authoring Palette",
  "block_authoring.close.aria": "Close palette",
  "block_authoring.tablist.aria": "Block Authoring Palette sections",
  "block_authoring.tab.parameters": "Parameters",
  "block_authoring.tab.actions": "Actions",
  "block_authoring.tab.parameter-sets": "Parameter Sets",
  // 10 parameter items (BPARAMETER kinds)
  "block_authoring.params.point": "Point",
  "block_authoring.params.linear": "Linear",
  "block_authoring.params.polar": "Polar",
  "block_authoring.params.xy": "XY",
  "block_authoring.params.rotation": "Rotation",
  "block_authoring.params.alignment": "Alignment",
  "block_authoring.params.flip": "Flip",
  "block_authoring.params.visibility": "Visibility",
  "block_authoring.params.lookup": "Lookup",
  "block_authoring.params.basepoint": "Base point",
  // 8 action items (BACTIONTOOL kinds)
  "block_authoring.actions.move": "Move",
  "block_authoring.actions.scale": "Scale",
  "block_authoring.actions.stretch": "Stretch",
  "block_authoring.actions.polar-stretch": "Polar Stretch",
  "block_authoring.actions.rotation": "Rotation",
  "block_authoring.actions.flip": "Flip",
  "block_authoring.actions.array": "Array",
  "block_authoring.actions.lookup": "Lookup",
  // 4 starter parameter sets (full 15 land in Phase K.4)
  "block_authoring.sets.linear-move": "Linear Move",
  "block_authoring.sets.linear-stretch": "Linear Stretch",
  "block_authoring.sets.polar-move": "Polar Move",
  "block_authoring.sets.rotation-set": "Rotation Set",
  // Toast surfaced when a placeholder placement fires (Phase K.2 wiring TODO).
  "block_authoring.toast.begin_place":
    'Placing "{label}" will be wired in Phase K.2/K.3.',
  // ── Phase K.2a — Linear parameter placement prompts ──
  "prompt.bparam.linear.base":
    "BPARAMETER: Specify start point of linear parameter:",
  "prompt.bparam.linear.end":
    "BPARAMETER: Specify endpoint of linear parameter:",
  "prompt.bparam.linear.label": "BPARAMETER: Specify label location:",
  "summary.bparam.linear_created": 'Linear parameter "{name}" added to block.',
  // ── Phase K.2b — remaining parameter placement prompts (EN) ──
  "prompt.bparam.point.place": "BPARAMETER: Specify point parameter location:",
  "prompt.bparam.basepoint.place": "BPARAMETER: Specify base point location:",
  "prompt.bparam.visibility.place":
    "BPARAMETER: Specify visibility parameter location:",
  "prompt.bparam.lookup.place":
    "BPARAMETER: Specify lookup parameter location:",
  "prompt.bparam.flip.base": "BPARAMETER: Specify flip axis start point:",
  "prompt.bparam.flip.end": "BPARAMETER: Specify flip axis end point:",
  "prompt.bparam.flip.label":
    "BPARAMETER: Specify flip parameter label location:",
  "prompt.bparam.polar.base": "BPARAMETER: Specify polar parameter base point:",
  "prompt.bparam.polar.end": "BPARAMETER: Specify polar parameter endpoint:",
  "prompt.bparam.polar.label":
    "BPARAMETER: Specify polar parameter label location:",
  "prompt.bparam.xy.base": "BPARAMETER: Specify XY parameter base point:",
  "prompt.bparam.xy.end": "BPARAMETER: Specify XY parameter endpoint:",
  "prompt.bparam.xy.label": "BPARAMETER: Specify XY parameter label location:",
  "prompt.bparam.rotation.base": "BPARAMETER: Specify rotation base point:",
  "prompt.bparam.rotation.radius_end":
    "BPARAMETER: Specify rotation radius point:",
  "prompt.bparam.rotation.label":
    "BPARAMETER: Specify rotation parameter label location:",
  "prompt.bparam.alignment.base":
    "BPARAMETER: Specify alignment point location:",
  "prompt.bparam.alignment.end":
    "BPARAMETER: Specify alignment direction point:",
  "summary.bparam.point_created": 'Point parameter "{name}" added to block.',
  "summary.bparam.basepoint_created":
    'Base point parameter "{name}" added to block.',
  "summary.bparam.visibility_created":
    'Visibility parameter "{name}" added to block.',
  "summary.bparam.lookup_created": 'Lookup parameter "{name}" added to block.',
  "summary.bparam.flip_created": 'Flip parameter "{name}" added to block.',
  "summary.bparam.polar_created": 'Polar parameter "{name}" added to block.',
  "summary.bparam.xy_created": 'XY parameter "{name}" added to block.',
  "summary.bparam.rotation_created":
    'Rotation parameter "{name}" added to block.',
  "summary.bparam.alignment_created":
    'Alignment parameter "{name}" added to block.',
  // ── Phase K.3 — Block Authoring action placement prompts ──
  "prompt.baction.move.pick_parameter": "BACTION Move: select a parameter.",
  "prompt.baction.move.select_objects":
    "BACTION Move: select objects (Enter to finish).",
  "prompt.baction.move.place": "BACTION Move: specify action icon location.",
  "prompt.baction.scale.pick_parameter": "BACTION Scale: select a parameter.",
  "prompt.baction.scale.select_objects":
    "BACTION Scale: select objects (Enter to finish).",
  "prompt.baction.scale.place": "BACTION Scale: specify action icon location.",
  "prompt.baction.stretch.pick_parameter":
    "BACTION Stretch: select a parameter.",
  "prompt.baction.stretch.select_objects":
    "BACTION Stretch: select objects (Enter to finish).",
  "prompt.baction.stretch.frame_corner_1":
    "BACTION Stretch: specify first corner of crossing frame.",
  "prompt.baction.stretch.frame_corner_2":
    "BACTION Stretch: specify opposite corner of crossing frame.",
  "prompt.baction.stretch.place":
    "BACTION Stretch: specify action icon location.",
  "prompt.baction.polar_stretch.pick_parameter":
    "BACTION Polar Stretch: select a parameter.",
  "prompt.baction.polar_stretch.select_objects":
    "BACTION Polar Stretch: select objects (Enter to finish).",
  "prompt.baction.polar_stretch.frame_corner_1":
    "BACTION Polar Stretch: specify first corner of crossing frame.",
  "prompt.baction.polar_stretch.frame_corner_2":
    "BACTION Polar Stretch: specify opposite corner of crossing frame.",
  "prompt.baction.polar_stretch.place":
    "BACTION Polar Stretch: specify action icon location.",
  "prompt.baction.rotation.pick_parameter":
    "BACTION Rotate: select a parameter.",
  "prompt.baction.rotation.select_objects":
    "BACTION Rotate: select objects (Enter to finish).",
  "prompt.baction.rotation.place":
    "BACTION Rotate: specify action icon location.",
  "prompt.baction.flip.pick_parameter": "BACTION Flip: select a parameter.",
  "prompt.baction.flip.select_objects":
    "BACTION Flip: select objects (Enter to finish).",
  "prompt.baction.flip.place": "BACTION Flip: specify action icon location.",
  "prompt.baction.array.pick_parameter": "BACTION Array: select a parameter.",
  "prompt.baction.array.select_objects":
    "BACTION Array: select objects (Enter to finish).",
  "prompt.baction.array.place": "BACTION Array: specify action icon location.",
  "prompt.baction.lookup.pick_parameter": "BACTION Lookup: select a parameter.",
  "prompt.baction.lookup.select_objects":
    "BACTION Lookup: select objects (Enter to finish).",
  "prompt.baction.lookup.place":
    "BACTION Lookup: specify action icon location.",
  "summary.baction.move_created": 'Move action "{name}" added to block.',
  "summary.baction.scale_created": 'Scale action "{name}" added to block.',
  "summary.baction.stretch_created": 'Stretch action "{name}" added to block.',
  "summary.baction.polar_stretch_created":
    'Polar Stretch action "{name}" added to block.',
  "summary.baction.rotation_created": 'Rotate action "{name}" added to block.',
  "summary.baction.flip_created": 'Flip action "{name}" added to block.',
  "summary.baction.array_created": 'Array action "{name}" added to block.',
  "summary.baction.lookup_created": 'Lookup action "{name}" added to block.',
  "error.baction.incompatible_parameter":
    'Parameter "{paramKind}" is not compatible with action "{actionKind}".',

  // ── PR-9 — SCALELISTEDIT / Annotative Object Scale dialogs ────────
  "audit.scalelistedit_opened": "Scale list editor opened.",
  "audit.annotative_object_scale_opened":
    "Annotative Object Scale dialog opened for {entityId}.",
  "audit.scalelist_added":
    'Scale "{name}" (1:{factor}) added to annotation list.',
  "audit.scalelist_removed": 'Scale "{name}" removed from annotation list.',
  // PR-17 — annotative command audit events. Mirror AutoCAD's
  // command-line echoes for the 9 annotative commands wired through
  // annotativeShellReducer.ts.
  "audit.cannoscale_changed": "Annotation scale changed to {scale}.",
  "audit.context_added": 'Scale context "{scale}" added to object {entityId}.',
  "audit.context_removed":
    'Scale context "{scale}" removed from object {entityId}.',
  "audit.annoreset": "Annotative object positions reset to master geometry.",
  "audit.annoupdate_done":
    "ANNOUPDATE: annotative object positions synchronised.",
  "audit.annoallvisible_toggled": "All-annotative visibility: {state}.",
  "audit.annoautoscale_toggled": "Annotation autoscale: {state}.",
  "audit.annomonitor_toggled": "Annotation monitor: {state}.",
  // Generic on/off labels for status-style audit summaries.
  "status.on": "on",
  "status.off": "off",
  "dialogs.scalelistedit.title": "Scale List Editor",
  "dialogs.scalelistedit.description":
    "Manage the annotation scale list (SCALELISTEDIT). Changes appear in the status bar immediately and persist on DXF export.",
  "dialogs.scalelistedit.col.name": "Name",
  "dialogs.scalelistedit.col.ratio": "Paper:Drawing",
  "dialogs.scalelistedit.col.factor": "Factor",
  "dialogs.scalelistedit.add": "Add",
  "dialogs.scalelistedit.edit": "Edit",
  "dialogs.scalelistedit.delete": "Delete",
  "dialogs.scalelistedit.move_up": "Up",
  "dialogs.scalelistedit.move_down": "Down",
  "dialogs.scalelistedit.reset": "Reset",
  "dialogs.scalelistedit.scale_name": "Scale name",
  "dialogs.scalelistedit.scale_factor": "Scale factor",
  "dialogs.scalelistedit.add_form_title": "Add scale",
  "dialogs.scalelistedit.add_form_hint":
    "Name in the form 1:50 or arbitrary (e.g. A3 1:100).",
  "dialogs.scalelistedit.cancel": "Cancel",
  "dialogs.scalelistedit.ok": "OK",
  "dialogs.scalelistedit.empty": "Scale list is empty. Add the first one.",
  "dialogs.scalelistedit.error.duplicate": 'Scale "{name}" already exists.',
  "dialogs.scalelistedit.error.invalid_factor":
    "Factor must be a positive number.",
  "dialogs.scalelistedit.error.empty_name": "Scale name cannot be empty.",
  "dialogs.annotative_object_scale.title": "Annotative Object Scale",
  "dialogs.annotative_object_scale.description":
    "Annotation scales attached to the selected object. Mirrors AutoCAD's OBJECTSCALE dialog.",
  "dialogs.annotative_object_scale.attached": "Attached scales",
  "dialogs.annotative_object_scale.available": "Available scales",
  "dialogs.annotative_object_scale.attach": "Attach",
  "dialogs.annotative_object_scale.detach": "Detach",
  "dialogs.annotative_object_scale.empty_attached":
    "Object has no attached scale contexts.",
  "dialogs.annotative_object_scale.empty_available":
    "All catalogue scales are already attached.",
  "dialogs.annotative_object_scale.cancel": "Close",
  "dialogs.annotative_object_scale.ok": "OK",
  "properties.annotative": "Annotative",
  "properties.annotation_scale": "Annotation scale",
  "properties.annotative_scales": "Annotative scales",
  "properties.edit_scales": "Edit scales…",
  "properties.annotative_yes": "Yes",
  "properties.annotative_no": "No",
  // PR-10 — annotative-style propagation prompts.
  "dialogs.dimstyle.propagation_prompt_title":
    "Propagate annotative behaviour to existing dimensions?",
  "dialogs.dimstyle.propagation_prompt_message_enable":
    'Style "{name}" became annotative. Add the current annotation scale ({scale}) to annotativeContexts on every dimension that uses this style?',
  "dialogs.dimstyle.propagation_prompt_message_disable":
    'Style "{name}" is no longer annotative. Remove annotative contexts from every dimension that uses this style?',
  "dialogs.mleaderstyle.propagation_prompt_title":
    "Propagate annotative behaviour to existing multileaders?",
  "dialogs.mleaderstyle.propagation_prompt_message":
    'Style "{name}" {action}. Apply the change to existing multileaders that use this style?',
  "dialogs.mleaderstyle.propagation_action_enable": "became annotative",
  "dialogs.mleaderstyle.propagation_action_disable": "is no longer annotative",
  "dialogs.common.confirm": "Apply",
  "dialogs.common.skip": "Skip",
  "audit.dimstyle.annotative_propagation_deferred":
    'DIMSTYLE "{name}": propagation of annotative behaviour to existing dimensions is deferred (DIMENSION schema migration lives in a follow-up PR).',
  "audit.mleaderstyle.annotative_propagation_deferred":
    'MLEADERSTYLE "{name}": propagation of annotative behaviour to existing multileaders is deferred (MLEADER schema migration lives in a follow-up PR).',
  // Right-click context menu (SHORTCUTMENU parity)
  "context_menu.title.default": "Drawing area",
  "context_menu.title.edit": "Selection",
  "context_menu.title.command": "Command",
  // W2.5 OSNAP-OVERRIDE-MODES-S2 — Shift+right-click Object Snap cursor menu.
  "context_menu.title.osnap": "Object Snap",
  "context_menu.repeat_last": "Repeat last command",
  "context_menu.recent_input": "Recent input",
  "context_menu.clipboard": "Clipboard",
  "context_menu.clipboard.cut": "Cut",
  "context_menu.clipboard.copy": "Copy",
  "context_menu.clipboard.copy_with_base": "Copy with base point",
  "context_menu.clipboard.paste": "Paste",
  "context_menu.clipboard.paste_as_block": "Paste as block",
  "context_menu.clipboard.paste_to_original": "Paste to original coordinates",
  "context_menu.undo": "Undo",
  "context_menu.redo": "Redo",
  "context_menu.pan": "Pan",
  "context_menu.zoom": "Zoom",
  "context_menu.steeringwheels": "SteeringWheels",
  "context_menu.action_recorder": "Action recorder",
  "context_menu.subobject_filter": "Subobject selection filter",
  "context_menu.quick_select": "Quick select",
  "context_menu.quickcalc": "QuickCalc",
  "context_menu.find": "Find",
  "context_menu.options": "Options",
  "context_menu.isolate": "Isolate objects",
  "context_menu.erase": "Erase",
  "context_menu.move": "Move",
  "context_menu.copy": "Copy",
  "context_menu.scale": "Scale",
  "context_menu.rotate": "Rotate",
  "context_menu.draw_order": "Draw order",
  "context_menu.group": "Group",
  "context_menu.add_selected": "Add selected",
  "context_menu.select_similar": "Select similar",
  "context_menu.deselect_all": "Deselect all",
  "context_menu.properties": "Properties",
  "context_menu.quick_properties": "Quick properties",
  "context_menu.command.enter": "ENTER",
  "context_menu.command.cancel": "Cancel",
  "context_menu.command.recent": "Recent input",
  "context_menu.command.options": "Command options",
  // W2.5 OSNAP-OVERRIDE-MODES-S2 — "osnap" mode menu item (opens DSETTINGS).
  "context_menu.osnap.settings": "Osnap Settings...",
  // ROLLOVERTIPS card field labels
  "tooltip.type": "Type",
  "tooltip.layer": "Layer",
  "tooltip.color": "Color",
  "tooltip.linetype": "Linetype",
  "tooltip.handle": "Handle",
  // B12 SELECTIONCYCLING (Ctrl+W / Shift+Space)
  "audit.selectioncycling_toggle":
    "SELECTIONCYCLING set to {value}.",
  "selection_cycling.dialog.title": "Selection",
  "selection_cycling.dialog.subtitle":
    "{count} entities lie under the cursor. Pick one to select.",
  "selection_cycling.badge_label": "Selection cycling",
  "selection_cycling.row.handle": "Handle: {handle}",
  "selection_cycling.row.layer": "Layer: {layer}",
  // ── ABB-S7c agent-bridge Connect dialog + header status pill ──
  "agent_connect.open": "Agent",
  "agent_connect.dialog.title": "Connect to agent",
  "agent_connect.label.url": "Companion address",
  "agent_connect.label.token": "Pairing token",
  "agent_connect.label.status": "Status",
  "agent_connect.btn.connect": "Connect",
  "agent_connect.btn.disconnect": "Disconnect",
  "agent_connect.btn.close": "Close",
  "agent_connect.pill.disconnected": "Agent: off",
  "agent_connect.pill.connecting": "Agent: connecting…",
  "agent_connect.pill.connected": "Agent: connected",
  "agent_connect.pill.paired": "Agent: active",
  "agent_connect.pill.error": "Agent: error",
  "agent_connect.status.disconnected": "Not connected",
  "agent_connect.status.connecting": "Connecting to companion…",
  "agent_connect.status.connected": "Connected (observer)",
  "agent_connect.status.paired": "Paired — active session",
  "agent_connect.status.error": "Connection error",
  "agent_connect.error.companion_unreachable":
    "The agent-bridge companion is not reachable on the loopback port.",
  "agent_connect.error.token_rejected":
    "The pairing token was rejected; re-enter the token from the companion.",
  "agent_connect.error.protocol_mismatch":
    "The companion protocol version does not match this build.",
  "agent_connect.error.connect_failed": "The agent-bridge connect failed.",
  // ── ABB enable-toggle — persisted default-OFF enable switch + consent copy ──
  "agent_connect.toggle.label": "AI agent (local bridge)",
  "agent_connect.toggle.aria": "Enable local AI agent bridge",
  "agent_connect.toggle.on": "On",
  "agent_connect.toggle.off": "Off",
  "agent_connect.consent.title": "What this enables",
  "agent_connect.consent.body":
    "Enabling lets a local AI agent read your drawing and run editing commands through the safe contour (validated, undoable, audited). The connection is loopback-only and protected by a pairing token. Only enable it for an agent you trust running on this machine. Any local process that knows the token can drive edits.",
  // B15 drag-and-drop drop-zone
  "dxfio.drop_zone_label": "Drop DXF to import",
  "dxfio.drop_zone_hint":
    "Supported: .dxf — model space and layouts load automatically.",
  "dxfio.drop_zone_invalid_type":
    "Unsupported file: expected .dxf, received \"{name}\".",
  // ── B11 PRINT / PLOT dialog ──
  "print.dialog.title": "Plot — Page Setup",
  "print.dialog.printer_label": "Printer/plotter:",
  "print.dialog.paper_size_label": "Paper size:",
  "print.dialog.orientation_label": "Orientation:",
  "print.dialog.orientation.portrait": "Portrait",
  "print.dialog.orientation.landscape": "Landscape",
  "print.dialog.margin_label": "Margins (mm):",
  "print.dialog.margin_left": "Left",
  "print.dialog.margin_right": "Right",
  "print.dialog.margin_top": "Top",
  "print.dialog.margin_bottom": "Bottom",
  "print.dialog.area_label": "Plot area:",
  "print.dialog.area.extents": "Extents",
  "print.dialog.area.layout": "Layout",
  "print.dialog.area.window": "Window",
  "print.dialog.window_pick": "Pick window <",
  "print.dialog.preview_label": "Preview (rectangle shown on canvas):",
  "print.dialog.confirm": "Print",
  "print.dialog.cancel": "Cancel",
  "print.feed.opened": "PLOT: Page setup dialog opened.",
  "print.feed.completed": "PLOT: Settings confirmed ({size}, {orientation}).",
  "print.feed.cancelled": "PLOT: Command cancelled.",
  "print.feed.exported": "PLOT: PDF created ({paths} object(s), {size}).",
  "print.feed.export_failed": "PLOT: PDF export failed — {message}.",
  "print.feed.window_pick_start": "PLOT: Specify first corner of the plot window.",
  "print.feed.window_pick_set": "PLOT: Plot window set.",
  "print.feed.window_pick_cancelled": "PLOT: Window pick cancelled.",
  // ── PDF-6.9a PAGESETUP — Page Setup Manager ──
  "page_setup.title": "Page Setup Manager",
  "page_setup.layout_list_title": "Layouts",
  "page_setup.paper_size_label": "Paper size:",
  "page_setup.orientation_label": "Orientation:",
  "page_setup.orientation.portrait": "Portrait",
  "page_setup.orientation.landscape": "Landscape",
  "page_setup.plot_area_label": "What to plot:",
  "page_setup.plot_area.layout": "Layout",
  "page_setup.plot_area.extents": "Extents",
  "page_setup.plot_area.window": "Window",
  "page_setup.scale_label": "Scale:",
  "page_setup.scale.custom": "Custom",
  "page_setup.scale.fit_to_paper": "Fit to paper",
  "page_setup.plot_style_label": "Plot style table (pen assignments):",
  "page_setup.plot_style.none": "None",
  "page_setup.rotation_label": "Drawing orientation:",
  "page_setup.rotation.none": "0°",
  "page_setup.rotation.90": "90° CCW",
  "page_setup.rotation.180": "180° (upside-down)",
  "page_setup.rotation.270": "270° CCW",
  "page_setup.offset_label": "Plot offset (mm):",
  "page_setup.offset.x": "X:",
  "page_setup.offset.y": "Y:",
  "page_setup.margins_label": "Margins (mm):",
  "page_setup.margin_left": "Left",
  "page_setup.margin_right": "Right",
  "page_setup.margin_top": "Top",
  "page_setup.margin_bottom": "Bottom",
  "page_setup.center_plot": "Centre the plot",
  "page_setup.scale_lineweights": "Scale lineweights",
  "page_setup.print_lineweights": "Plot object lineweights",
  "page_setup.apply": "Apply",
  "page_setup.ok": "OK",
  "page_setup.cancel": "Cancel",
  "page_setup.feed.opened": "PAGESETUP: Page Setup Manager opened.",
  "page_setup.feed.applied": "PAGESETUP: Applied changes to {count} layout(s).",
  "page_setup.feed.cancelled": "PAGESETUP: Changes cancelled.",
  "ribbon.button.page_setup": "Page Setup",
  // ── PDF-6.9b PDF Options sub-dialog ──
  "page_setup.pdf_options_button": "PDF Options…",
  "pdf_options.title": "PDF Options",
  "pdf_options.vector_dpi_label": "Vector quality (DPI):",
  "pdf_options.vector_dpi.custom": "Custom…",
  "pdf_options.font_mode_label": "Font handling:",
  "pdf_options.font_mode.ttf_embed": "TTF embed",
  "pdf_options.font_mode.shx_note":
    "SHX text is rendered as stroke geometry; PDFSHX support is a future slice.",
  "pdf_options.layers_label": "Include layers:",
  "pdf_options.bookmarks_label": "Create PDF bookmarks",
  "pdf_options.bookmarks_note": "Used only for PUBLISH (multi-sheet).",
  // ── PDF-6.4 — Lines merge / Overwrite (PC3 parity) ──
  "pdf_options.merge_control.label": "Lines merge",
  "pdf_options.merge_control.overwrite": "Overwrite (top objects hide bottom)",
  "pdf_options.merge_control.merge": "Merge (overlap darkens)",
  "pdf_options.merge_control.note":
    "Equivalent to AutoCAD PC3 'Lines merge'. PDF blend mode = Multiply.",
  "pdf_options.ok": "OK",
  "pdf_options.cancel": "Cancel",
  "pdf_options.feed.opened": "PDFOPTIONS: PDF Options dialog opened.",
  "pdf_options.feed.confirmed": "PDFOPTIONS: PDF Options applied.",
  "pdf_options.feed.cancelled": "PDFOPTIONS: Changes cancelled.",
  // ── PDF-6.3b — CTB / STB plot-style table loader ──
  "pdf_options.plot_style.label": "Plot Style Table:",
  "pdf_options.plot_style.load_button": "Load CTB/STB…",
  "pdf_options.plot_style.clear_button": "Clear",
  "pdf_options.plot_style.loaded_summary":
    "{kind} · {count} entries · {file}",
  "pdf_options.plot_style.error_prefix": "Failed to load table:",
  // ── B19 Block lookup grip menu ──
  "block.lookup.menu.title": "Lookup: {name}",
  "block.lookup.menu.empty": "No values in table.",
  "block.lookup.menu.cancel": "Cancel",
  "block.lookup.feed.applied":
    "BLOCK: Value \"{value}\" applied to parameter \"{name}\".",
  // ── B18 MLEADER grip context menu ──
  "mleader.menu.add_leader": "Add Leader",
  "mleader.menu.remove_leader": "Remove Leader",
  "mleader.menu.add_vertex": "Add Vertex",
  "mleader.menu.remove_vertex": "Remove Vertex",
  "mleader.menu.stretch": "Stretch",
  "mleader.feed.add_leader_prompt": "MLEADEREDIT: Specify new arrow point.",
  "mleader.feed.add_vertex_prompt": "MLEADEREDIT: Specify new vertex location.",
  "mleader.feed.vertex_added": "MLEADEREDIT: Vertex added.",
  "mleader.feed.vertex_removed": "MLEADEREDIT: Vertex removed.",
  "mleader.feed.leader_removed": "MLEADEREDIT: Leader removed.",
  "mleader.feed.stretch_hint": "MLEADEREDIT: Drag the grip to stretch.",
  // PR1b: reference healing on import (AUDIT/RECOVER).
  "reference.heal.summary": "Reference repairs: {count} ({breakdown})",
  "reference.heal.unhealed": "; unresolved: {count}",
  "reference.heal.breakdown_item": "{label}: {n}",
  "reference.heal.kind.layer": "layer→0",
  "reference.heal.kind.textstyle": "text style→Standard",
  "reference.heal.kind.dimstyle": "dimension style→Standard",
  "reference.heal.kind.mleaderstyle": "multileader style→Standard",
  "reference.heal.kind.linetype": "linetype→Continuous",
  "reference.heal.kind.annoscale": "annotation scale→1:1",
  // Warn-only pre-export gate (the save is never blocked).
  "reference.export.audit_warning":
    "DXF export: {count} unresolved reference(s) detected. The file was saved but may contain dangling references.",
  // ── CC-1d: admin audit console (/admin/audit) ──
  "admin.audit.title": "Audit Console",
  "admin.audit.subtitle": "Editor command log and telemetry (ТЗ §14)",
  "admin.audit.tab.events": "Event Log",
  "admin.audit.tab.dashboard": "Dashboard",
  "admin.audit.refresh": "Refresh",
  "admin.audit.back_to_editor": "Back to editor",
  "admin.audit.loading": "Loading…",
  "admin.audit.error": "Failed to load data: {message}",
  "admin.audit.retry": "Retry",
  "admin.audit.unauthorized.title": "Sign-in required",
  "admin.audit.unauthorized.body":
    "Sign in with an administrator account to view the audit log.",
  "admin.audit.forbidden.title": "Access denied",
  "admin.audit.forbidden.body":
    "The audit log is available to administrators only.",
  "admin.audit.empty": "No events found.",
  "admin.audit.filter.user": "User",
  "admin.audit.filter.command": "Command",
  "admin.audit.filter.source": "Source",
  "admin.audit.filter.source.all": "All sources",
  "admin.audit.filter.space": "Space",
  "admin.audit.filter.date_from": "From date",
  "admin.audit.filter.date_to": "To date",
  "admin.audit.filter.apply": "Apply",
  "admin.audit.filter.reset": "Reset",
  "admin.audit.col.time": "Time",
  "admin.audit.col.user": "User",
  "admin.audit.col.command": "Command",
  "admin.audit.col.source": "Source",
  "admin.audit.col.space": "Space",
  "admin.audit.col.status": "Status",
  "admin.audit.pagination.range": "{from}–{to} of {total}",
  "admin.audit.pagination.prev": "Previous",
  "admin.audit.pagination.next": "Next",
  "admin.audit.detail.title": "Event details",
  "admin.audit.detail.close": "Close",
  "admin.audit.detail.empty":
    "Select an event in the table to see its details.",
  "admin.audit.detail.field.role": "Role",
  "admin.audit.detail.field.alias": "Command alias",
  "admin.audit.detail.field.hotkey": "Hotkey",
  "admin.audit.detail.field.workspace": "Workspace",
  "admin.audit.detail.field.drawing": "Drawing",
  "admin.audit.detail.field.entities": "Entities",
  "admin.audit.detail.field.before": "Before",
  "admin.audit.detail.field.after": "After",
  "admin.audit.detail.params.title": "Operation parameters",
  "admin.audit.detail.params.category": "Category",
  "admin.audit.detail.params.op": "Operation",
  "admin.audit.detail.params.absent":
    "The server did not provide typed parameters for this event.",
  "admin.audit.detail.raw": "Raw JSON",
  "admin.value.yes": "Yes",
  "admin.value.no": "No",
  // CC-1d: telemetry dashboard (sub-telemetry·ui)
  "admin.dashboard.total": "Total events: {count}",
  "admin.dashboard.note":
    "Aggregates are computed server-side over the full telemetry stream.",
  "admin.dashboard.empty": "No telemetry collected yet.",
  "admin.dashboard.commands.title": "Command frequency",
  "admin.dashboard.top_note": "top {count}",
  "admin.dashboard.sources.title": "Source split",
  "admin.dashboard.status.title": "Status split",
  "admin.dashboard.timeline.title": "Events over time",
  // CC-1d: typed payload category labels (auditTaxonomy.ts)
  "admin.params.category.session": "Session",
  "admin.params.category.ui": "UI",
  "admin.params.category.layout": "Layout",
  "admin.params.category.selection": "Selection",
  "admin.params.category.lifecycle": "Lifecycle",
  "admin.params.category.property": "Properties",
  "admin.params.category.commandline": "Command line",
  "admin.params.category.create": "Create",
  "admin.params.category.viewport": "Viewport",
  "admin.params.category.modify": "Modify",
  "admin.params.category.block": "Block",
  "admin.params.category.attribute": "Attribute",
  "admin.params.category.multileader": "Multileader",
  "admin.params.category.annotation": "Annotation",
  "admin.params.category.constraint": "Constraint",
  "admin.params.category.header": "DXF header",
  "admin.params.category.history": "History",
  "admin.params.category.space": "Space",
  "admin.params.category.file": "File",
  "admin.params.category.system": "System",
  "admin.params.category.dynamic": "Dynamic block",
  "admin.params.category.geometry": "Geometry",
  "admin.params.category.view-op": "View operation",
  "admin.params.category.collab": "Collaboration",
  "admin.params.category.ui-click": "UI click",
  "admin.params.category.ui-toggle": "UI toggle",
  "admin.params.category.ui-nav": "UI navigation",
  "admin.params.category.other": "Other",
  // CC-1d: typed payload field labels (auditTaxonomy.ts)
  "admin.params.field.spaceId": "Space ID",
  "admin.params.field.contentLength": "Content length",
  "admin.params.field.styleName": "Style name",
  "admin.params.field.nominalHeight": "Nominal height",
  "admin.params.field.referenceWidth": "Reference width",
  "admin.params.field.insertionX": "Insertion X",
  "admin.params.field.insertionY": "Insertion Y",
  "admin.params.field.viewportId": "Viewport ID",
  "admin.params.field.locked": "Locked",
  "admin.params.field.shadePlotMode": "Shade plot mode",
  "admin.params.field.vplayerAction": "VPLAYER action",
  "admin.params.field.layerName": "Layer",
  "admin.params.field.boundsX1": "Bounds X1",
  "admin.params.field.boundsY1": "Bounds Y1",
  "admin.params.field.boundsX2": "Bounds X2",
  "admin.params.field.boundsY2": "Bounds Y2",
  "admin.params.field.scaleNum": "Scale numerator",
  "admin.params.field.scaleDenom": "Scale denominator",
  "admin.params.field.viewHeight": "View height",
  "admin.params.field.viewCenterX": "View center X",
  "admin.params.field.viewCenterY": "View center Y",
  "admin.params.field.entityId": "Entity ID",
  "admin.params.field.cuttingEdgeCount": "Cutting edge count",
  "admin.params.field.boundaryEdgeCount": "Boundary edge count",
  "admin.params.field.entityCount": "Entity count",
  "admin.params.field.propertyCount": "Property count",
  "admin.params.field.targetCount": "Target count",
  "admin.params.field.mode": "Mode",
  "admin.params.field.deltaX": "Delta X",
  "admin.params.field.deltaY": "Delta Y",
  "admin.params.field.factor": "Factor",
  "admin.params.field.target": "Target surface",
  "admin.params.field.controlId": "Control ID",
  "admin.params.field.setting": "Setting (sysvar)",
  "admin.params.field.enabled": "Enabled",
  "admin.params.field.navKind": "Navigation kind",
  "admin.params.field.from": "From",
  "admin.params.field.to": "To",
  "admin.params.field.reversedCommandType": "Reversed command",
  "admin.params.field.reappliedCommandType": "Reapplied command",
  "admin.params.field.headIndex": "History head index",
};

export const messages: Record<LanguageCode, Record<MessageKey, string>> = {
  ru,
  en,
};
